import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { verifySession, logout as authLogout } from '../services/auth';
import { getConversations, Conversation } from '../services/messages';
import MessagesDropdown from '../components/MessagesDropdown';
import AccountDropdown from '../components/AccountDropdown';
import ThemeToggle from '../components/ThemeToggle';
import ProxyImage from '../components/ProxyImage';
import MoodLogoWithTooltip from '../components/MoodLogoWithTooltip';
import * as XLSX from 'xlsx';
import {
  NewIcon, PhoneIcon, CheckCircleIcon, SettingsIcon, HandshakeIcon, UserIcon, AwardIcon, XCircleIcon, PlusIcon,
  UsersIcon, ZapIcon, ClipboardIcon, AlertTriangleIcon, FlameIcon, MessageCircleIcon, FileTextIcon,
  BuildingIcon, MicIcon, CalendarIcon, BookIcon, CheckIcon, XIcon, CircleIcon, ClockIcon,
  BarChartIcon, HomeIcon, SearchIcon, RefreshIcon, TagIcon, ChevronDownIcon, ChevronUpIcon, MapPinIcon,
  WhatsAppIcon,
  EnvelopeIcon,
  FilterIcon,
  LinkedInIcon,
  EditIcon
} from '../components/icons/AdminIcons';
import { tiposOrganizacion, participationModalities, thematicAxes, countries, eventProgramParticipation, areas, subareasPorArea, industrias, paisRegionOpcionesConPaises } from '../data/profileData';
import MultiSelectTags from '../components/MultiSelectTags';
import SearchableSelect from '../components/SearchableSelect';
import { ProfileData, ThematicAxis } from '../types/profile.types';
import { calculateProfileProgress } from '../utils/profileProgress';
import BasicInfoSection from '../components/sections/BasicInfoSection';
import ThematicAxesSection from '../components/sections/ThematicAxesSection';
import InstitutionalDescriptionSection from '../components/sections/InstitutionalDescriptionSection';
import SpeakersSection from '../components/sections/SpeakersSection';
import ParallelEventsSection from '../components/sections/ParallelEventsSection';
import InitiativesSection from '../components/sections/InitiativesSection';
import BusinessRoundsSection from '../components/sections/BusinessRoundsSection';
import ParticipationModalitySection from '../components/sections/ParticipationModalitySection';
import ExpoZoneSection from '../components/sections/ExpoZoneSection';
import CustomSelect from '../components/CustomSelect';
import PipelineStagesModal from '../components/PipelineStagesModal';
import PipelinePropertiesModal from '../components/PipelinePropertiesModal';
import type { PipelineFieldDef } from '../components/PipelinePropertiesModal';
import Modal from '../components/Modal';
import GmailSection from '../components/GmailSection';
import WhatsAppSection from '../components/WhatsAppSection';
import MessagingSection from '../components/MessagingSection';
import HistorialWhatsAppTab from '../components/HistorialWhatsAppTab';
import ComunicacionesSection from '../components/ComunicacionesSection';
import DatabaseSection from '../components/DatabaseSection';
import AdminNetworkingSection from '../components/admin/AdminNetworkingSection';
import CreateContactModal from '../components/CreateContactModal';
import CreateOrganizationModal from '../components/CreateOrganizationModal';
import CreateNegocioModal from '../components/CreateNegocioModal';
import Input from '../components/Input';
import PhoneInput from '../components/PhoneInput';
import FormField from '../components/FormField';
import '../components/FormField.css';
import '../components/CreateContactModal.css';
import '../components/CheckboxGroup.css';
import './AdminPanel.css';
import type { ContactInfo } from '../types/profile.types';

export {};

/** Etapas por defecto para desplegable Etapa en Empresas cuando el pipeline aún no tiene etapas cargadas */
const DEFAULT_EMPRESA_STAGES: { id: string; label: string; color: string; position: number }[] = [
  { id: 'nuevo', label: 'Nuevo', color: '#94a3b8', position: 0 },
  { id: 'contactado', label: 'Contactado', color: '#3b82f6', position: 1 },
  { id: 'calificado', label: 'Calificado', color: '#8b5cf6', position: 2 },
  { id: 'en_proceso', label: 'En proceso', color: '#f59e0b', position: 3 },
  { id: 'negociacion', label: 'Negociación', color: '#ec4899', position: 4 },
  { id: 'completado', label: 'Completado', color: '#22c55e', position: 5 },
  { id: 'perdido', label: 'Perdido', color: '#ef4444', position: 6 },
];

/** Normaliza tipo de organización: si es código antiguo devuelve etiqueta; si ya es texto largo, tal cual */
function getOrganizationTypeLabel(type: string | null | undefined): string {
  if (type == null || type === '') return '—';
  if (type.includes('/') || type.length > 15) return type;
  const oldTypeMap: Record<string, string> = {
    empresa: 'Empresa', ong: 'ONG', gobierno: 'Gobierno', startup: 'Startup', universidad: 'Universidad',
    organismo_internacional: 'Organismo Internacional', camara: 'Cámara', asociacion: 'Asociación', otro: 'Otro'
  };
  return oldTypeMap[type.toLowerCase()] ?? type;
}

/** Normaliza modalidad de participación por valor (código) → etiqueta */
function getModalityLabel(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  const opt = participationModalities.find((m) => m.value === value);
  return opt ? opt.label : value;
}

/** Normaliza importancia: valores conocidos a etiqueta; el resto tal cual */
const IMPORTANCIA_LABELS: Record<string, string> = { clave: 'Clave', no_clave: 'No clave', sin_clasificar: 'Sin clasificar' };
function getImportanciaLabel(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  const key = String(value).toLowerCase().replace(/\s+/g, '_');
  return IMPORTANCIA_LABELS[key] ?? value;
}

interface User {
  id: number;
  email: string;
  email_verified: boolean;
  role: string;
  created_at: string;
  last_login_date: string | null;
  profile_id?: number | null; // Deprecated: usar has_profile en su lugar
  personal_profile_id?: number | null;
  institutional_profile_id?: number | null;
  has_profile?: boolean;
  organization_name: string | null;
  first_name: string | null;
  last_name: string | null;
  position?: string | null;
  account_deleted: boolean | null;
  unreadCount?: number;
  // Datos adicionales para CRM
  organization_type?: string | null;
  speakers?: any[] | null;
  parallel_events?: any[] | null;
  catalog_sections?: any[] | null;
  initiatives?: any[] | null;
  business_rounds_participants?: any[] | null;
  perfil_personal_validado?: boolean;
  perfil_institucional_validado?: boolean;
  request_information?: boolean;
  request_expo_information?: boolean;
  // Modalidad y Zona Expo
  participation_modalities?: string[] | null;
  agreed_modality?: string | null;
  selected_stands_updated?: string[] | null;
  // Estado del perfil
  is_draft?: boolean | null;
  /** true = creado por admin desde CRM; false o undefined = se registró solo */
  created_by_admin?: boolean;
  /** Número de WhatsApp vinculado al usuario para ver chat en CRM */
  whatsapp?: string | null;
  /** URL del logo de la organización (vista Empresas) */
  organization_logo_url?: string | null;
  /** Permisos del editor (solo si role === 'editor'); presente en lista para que admin pueda editarlos */
  editor_permissions?: string | null;
}

// Contacto de la tabla crm_contacts (API /api/database/contacts). Un contacto puede tener varias organizaciones (organization_ids).
interface CrmContact {
  id: number;
  organization_id?: number | null;
  organization_ids?: number[];
  organization_name?: string | null;
  organization_name_from_org?: string | null;
  nombre?: string | null;
  apellido?: string | null;
  genero?: string | null;
  cargo?: string | null;
  area?: string | null;
  subarea?: string | null;
  correo?: string | null;
  telefono?: string | null;
  tematicas_interes?: string | null;
  importancia?: string | null;
  pais_region?: string | null;
  linkedin?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

// Organización de la tabla crm_organizations (API /api/database/organizations)
interface CrmOrganization {
  id: number;
  organizacion?: string | null;
  tipo_organizacion?: string | null;
  importancia?: string | null;
  industrias?: string | null;
  sitio_web?: string | null;
  pais_origen?: string | null;
  mercado?: string | null;
  descripcion?: string | null;
  ejes_tematicos?: string | null;
  siglas?: string | null;
  redes_sociales?: string | null;
  source_user_id?: number | null;
  logo?: string | null;
  dominio?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  /** Estado de aprobación del perfil institucional: pending | approved | rejected */
  approval_status?: 'pending' | 'approved' | 'rejected' | null;
  /** Id de la organización en CRM (para lookup de negocios SCESDE26 cuando la lista viene de perfiles institucionales) */
  crm_organization_id?: number | null;
}

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [crmContacts, setCrmContacts] = useState<CrmContact[]>([]);
  const [crmOrganizations, setCrmOrganizations] = useState<CrmOrganization[]>([]);
  const [organizationNegociosList, setOrganizationNegociosList] = useState<{ organizationId: number; pipelineId: number; pipelineName: string; pipelineStage: string; priority: string; data?: Record<string, unknown>; assignedContactIds?: number[] }[]>([]);
  /** Etapas por pipeline para desplegable Etapa en tarjetas Empresas (cada pipeline puede tener sus propias etapas) */
  const [stagesByPipelineId, setStagesByPipelineId] = useState<Record<number, { id: string; label: string; color: string; position: number }[]>>({});
  const [empresaNegociosExpandedId, setEmpresaNegociosExpandedId] = useState<number | null>(null);
  const [empresaContactosExpandedId, setEmpresaContactosExpandedId] = useState<number | null>(null);
  /** Contacto cuya sección Negocios está expandida (vista Contactos) */
  const [contactNegociosExpandedId, setContactNegociosExpandedId] = useState<number | null>(null);
  /** Contacto cuya sección Organización está expandida (vista Contactos) */
  const [contactOrgExpandedId, setContactOrgExpandedId] = useState<number | null>(null);
  /** Contacto para el modal Org/Negocios en la tabla de contactos */
  const [contactTableOrgNegociosModal, setContactTableOrgNegociosModal] = useState<CrmContact | null>(null);
  const [negocioCardOrgExpandedId, setNegocioCardOrgExpandedId] = useState<number | null>(null);
  const [negocioCardContactosExpandedId, setNegocioCardContactosExpandedId] = useState<number | null>(null);
  /** Perfil Institucional: desplegable Contacto por tarjeta (org id) */
  const [perfilInstContactExpandedId, setPerfilInstContactExpandedId] = useState<number | null>(null);
  /** Negocios del pipeline SCESDE26 (id 1) para tarjetas Perfil Institucional: orgId -> { pipelineStage, priority } */
  const [perfilInstScesde26Negocios, setPerfilInstScesde26Negocios] = useState<Record<number, { pipelineStage: string; priority: string }>>({});
  const [loadingContacts, setLoadingContacts] = useState<boolean>(false);
  const [loadingOrganizations, setLoadingOrganizations] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  /** Rol del usuario actual (admin | editor) */
  const [currentUserRole, setCurrentUserRole] = useState<'admin' | 'editor' | null>(null);
  /** Permisos del usuario actual cuando es editor; null cuando es admin (acceso total) */
  const [currentUserPermissions, setCurrentUserPermissions] = useState<string[] | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  /** Claves de pestañas que requieren permiso para editores. Acceso a una pestaña principal implica acceso a TODAS sus subpestañas (no se comprueban permisos por subpestaña). */
  const ADMIN_TAB_KEYS = ['crm', 'participations', 'conversations', 'comunicaciones', 'database', 'networking', 'monitoring'] as const;
  const canAccessTab = (tabKey: string) =>
    currentUserRole === 'admin' || (currentUserRole === 'editor' && currentUserPermissions != null && currentUserPermissions.includes(tabKey));
  const firstAllowedTab = (): typeof activeMainTab => {
    if (currentUserRole === 'admin') return 'crm';
    if (!currentUserPermissions || currentUserPermissions.length === 0) return 'crm';
    const first = ADMIN_TAB_KEYS.find((k) => currentUserPermissions.includes(k));
    return first || 'crm';
  };
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Cargar estado inicial desde sessionStorage
  const getInitialState = () => {
    try {
      const saved = sessionStorage.getItem('adminPanelState');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
                    activeMainTab: (parsed.activeMainTab === 'crm' || parsed.activeMainTab === 'monitoring' || parsed.activeMainTab === 'participations' || parsed.activeMainTab === 'conversations' || parsed.activeMainTab === 'comunicaciones' || parsed.activeMainTab === 'database' || parsed.activeMainTab === 'networking' || parsed.activeMainTab === 'accesos' || parsed.activeMainTab === 'correo' || parsed.activeMainTab === 'whatsapp') ? (parsed.activeMainTab === 'correo' || parsed.activeMainTab === 'whatsapp' ? 'conversations' : parsed.activeMainTab) : 'crm',
          activeViewMode: parsed.activeViewMode || 'cards',
          selectedCrmUserId: parsed.selectedCrmUserId || null,
          selectedCrmContactId: parsed.selectedCrmContactId || null,
          selectedCrmOrganizationId: parsed.selectedCrmOrganizationId || null,
          activeMainProfileTab: parsed.activeMainProfileTab || 'overview',
          activeProfileSubTab: parsed.activeProfileSubTab || 'contact',
          pipelineView: parsed.pipelineView || 'list',
          listViewMode: parsed.listViewMode || 'cards',
          activeCrmSubTab: (parsed.activeCrmSubTab === 'contactos' || parsed.activeCrmSubTab === 'empresas' || parsed.activeCrmSubTab === 'negocios') ? parsed.activeCrmSubTab : (parsed.pipelineView === 'pipeline' ? 'negocios' : 'contactos'),
          scrollPosition: parsed.scrollPosition || 0,
          activeParticipationsTab: parsed.activeParticipationsTab || 'speakers',
          activeConversationsSubTab: (parsed.activeConversationsSubTab === 'correo' || parsed.activeConversationsSubTab === 'whatsapp' || parsed.activeConversationsSubTab === 'messaging') ? parsed.activeConversationsSubTab : 'correo',
          activeComunicacionesSubTab: (parsed.activeComunicacionesSubTab === 'newsletters' || parsed.activeComunicacionesSubTab === 'personalizados' || parsed.activeComunicacionesSubTab === 'redes_sociales') ? parsed.activeComunicacionesSubTab : 'newsletters'
        };
      }
    } catch (e) {
    }
    return {
      activeMainTab: 'crm' as const,
      activeViewMode: 'cards' as const,
      selectedCrmUserId: null,
      selectedCrmContactId: null,
      selectedCrmOrganizationId: null,
      activeMainProfileTab: 'overview' as const,
      activeProfileSubTab: 'institutional' as const,
      pipelineView: 'list' as const,
      listViewMode: 'cards' as const,
      activeCrmSubTab: 'contactos' as const,
      scrollPosition: 0,
      activeParticipationsTab: 'speakers' as const,
      activeConversationsSubTab: 'correo' as const,
      activeComunicacionesSubTab: 'newsletters' as const
    };
  };
  const initialState = getInitialState();
  const [activeMainTab, setActiveMainTab] = useState<'crm' | 'monitoring' | 'participations' | 'conversations' | 'comunicaciones' | 'database' | 'networking' | 'accesos'>(initialState.activeMainTab);
  const [activeParticipationsTab, setActiveParticipationsTab] = useState<'speakers' | 'events' | 'initiatives' | 'business_rounds'>(initialState.activeParticipationsTab);
  const [activeViewMode, setActiveViewMode] = useState<'cards' | 'table'>(initialState.activeViewMode);
  
  // Estado para guardar la pestaña anterior antes de ver un usuario
  const [previousMainTab, setPreviousMainTab] = useState<'crm' | 'monitoring' | 'participations' | 'conversations' | 'comunicaciones' | 'database' | 'networking' | 'accesos' | null>(null);

  // Sección Accesos (solo admin): usuarios con acceso al panel, asignar por correo
  const [accessUsers, setAccessUsers] = useState<{ id: number; email: string; role: string; editor_permissions: string | null }[]>([]);
  const [loadingAccessUsers, setLoadingAccessUsers] = useState(false);
  const [accessEmail, setAccessEmail] = useState('');
  const [accessRole, setAccessRole] = useState<'user' | 'editor' | 'admin'>('editor');
  const [accessPermissions, setAccessPermissions] = useState<string[]>([]);
  const [savingAccess, setSavingAccess] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const ACCESS_PERMISSION_OPTIONS: { key: string; label: string }[] = [
    { key: 'crm', label: 'CRM' },
    { key: 'monitoring', label: 'Monitoreo' },
    { key: 'participations', label: 'Perfil / Participaciones' },
    { key: 'conversations', label: 'Conversaciones' },
    { key: 'comunicaciones', label: 'Comunicaciones' },
    { key: 'database', label: 'Base de datos' },
    { key: 'networking', label: 'Networking' },
    { key: 'mensajeria', label: 'Mensajería' },
  ];

  // Sub-pestaña dentro de Comunicaciones
  const [activeComunicacionesSubTab, setActiveComunicacionesSubTab] = useState<'newsletters' | 'personalizados' | 'redes_sociales'>(
    (initialState as { activeComunicacionesSubTab?: 'newsletters' | 'personalizados' | 'redes_sociales' }).activeComunicacionesSubTab || 'newsletters'
  );

  // Sub-pestaña dentro de Conversaciones: Correo | WhatsApp | Mensajería
  const [activeConversationsSubTab, setActiveConversationsSubTab] = useState<'correo' | 'whatsapp' | 'messaging'>(
    (initialState as { activeConversationsSubTab?: 'correo' | 'whatsapp' | 'messaging' }).activeConversationsSubTab || 'correo'
  );

  // Sub-pestaña dentro de Perfil: Perfil Personal | Perfil Institucional | Participaciones
  const [activePerfilSubTab, setActivePerfilSubTab] = useState<'perfil-personal' | 'perfil-institucional' | 'participaciones'>('perfil-personal');
  const [perfilOrganizations, setPerfilOrganizations] = useState<CrmOrganization[]>([]);
  const [loadingPerfilOrganizations, setLoadingPerfilOrganizations] = useState<boolean>(false);
  const [perfilPersonalPage, setPerfilPersonalPage] = useState(1);
  const [perfilInstitucionalPage, setPerfilInstitucionalPage] = useState(1);

  // Filtros para usuarios (CRM)
  const [filterOrganizationType, setFilterOrganizationType] = useState<string[]>([]);
  const [filterModalities, setFilterModalities] = useState<string[]>([]);
  const [filterExpoZone, setFilterExpoZone] = useState<'all' | 'yes' | 'no'>('all');
  const [filterParticipations, setFilterParticipations] = useState<string[]>([]);
  const [filterStates, setFilterStates] = useState<string[]>([]);
  const [filterPipelineStages, setFilterPipelineStages] = useState<string[]>([]);
  const [filterPriorities, setFilterPriorities] = useState<string[]>([]);
  const [filterPendingApprovals, setFilterPendingApprovals] = useState<'all' | 'yes' | 'no'>('all');
  /** Origen del contacto: todos, solo registrados, solo creados por admin */
  const [filterContactOrigin, setFilterContactOrigin] = useState<'all' | 'registered' | 'manual'>('all');
  
  // Filtros para pestaña de Participaciones
  const [filterParticipationStatus, setFilterParticipationStatus] = useState<'all' | 'complete' | 'incomplete' | 'pending_approval'>('all');
  const [filterParticipationCompletion, setFilterParticipationCompletion] = useState<'all' | 'complete' | 'incomplete'>('all');
  const [filterParticipationRequested, setFilterParticipationRequested] = useState<'all' | 'yes' | 'no'>('all');
  const [filterParticipationApproval, setFilterParticipationApproval] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  
  /** Sidebar derecho de filtros (estilo HubSpot) */
  const [filtersSidebarOpen, setFiltersSidebarOpen] = useState(false);

  /** Cerrar sidebars al salir de CRM o Participaciones */
  useEffect(() => {
    if (activeMainTab !== 'crm' && activeMainTab !== 'participations') {
      setFiltersSidebarOpen(false);
      setShowCreateContactModal(false);
    }
  }, [activeMainTab]);

  /** Cerrar desplegable de pipelines al hacer clic fuera */
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (pipelineSelectRef.current && !pipelineSelectRef.current.contains(e.target as Node)) {
        setPipelineSelectOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Ordenamiento fijo por fecha de registro descendente
  const sortBy = 'created';
  const sortOrder = 'desc';
  
  
  // Estado para CRM
  const [selectedCrmUser, setSelectedCrmUser] = useState<User | null>(null);
  /** Contacto de tabla crm_contacts seleccionado para ver su página individual */
  const [selectedCrmContact, setSelectedCrmContact] = useState<CrmContact | null>(null);
  /** Negocios asignados al contacto seleccionado (para página individual del contacto) */
  const [contactDetailNegocios, setContactDetailNegocios] = useState<{ organizationId: number; pipelineId: number; pipelineName: string; pipelineStage: string; priority: string; data?: Record<string, unknown>; organizationName: string }[]>([]);
  const [loadingContactDetailNegocios, setLoadingContactDetailNegocios] = useState(false);
  /** Índice del negocio activo cuando el contacto tiene 2+ negocios (subtabs) */
  const [activeContactDetailNegocioIndex, setActiveContactDetailNegocioIndex] = useState(0);
  /** Índice de la organización activa cuando el contacto tiene 2+ organizaciones (subtabs) */
  const [activeContactDetailOrgIndex, setActiveContactDetailOrgIndex] = useState(0);
  /** Modo edición en detalle del contacto: si true se muestran inputs en las tres secciones */
  const [contactDetailEditMode, setContactDetailEditMode] = useState(false);
  /** Modal de edición de contacto (abre al clic en Editar) */
  const [contactEditModalOpen, setContactEditModalOpen] = useState(false);
  /** Subtab activa dentro del modal de edición */
  const [contactEditModalSubTab, setContactEditModalSubTab] = useState<'contacto' | 'organizaciones' | 'negocios'>('contacto');
  /** Borrador del contacto al editar (copia de selectedCrmContact + cambios) */
  const [contactEditDraft, setContactEditDraft] = useState<Partial<CrmContact> | null>(null);
  /** Borrador de la organización activa al editar */
  const [contactOrgEditDraft, setContactOrgEditDraft] = useState<Partial<CrmOrganization> | null>(null);
  /** Borrador del negocio activo al editar (pipelineStage, priority, data) */
  const [contactNegocioEditDraft, setContactNegocioEditDraft] = useState<{ pipelineStage?: string; priority?: string; data?: Record<string, unknown> } | null>(null);
  const [contactDetailSaving, setContactDetailSaving] = useState(false);
  const [contactDetailSaveError, setContactDetailSaveError] = useState<string | null>(null);
  /** Subtab principal en detalle del contacto: Contacto | Organizaciones | Negocios (Mensajería fija en las tres) */
  const [contactDetailSubTab, setContactDetailSubTab] = useState<'contacto' | 'organizaciones' | 'negocios'>('contacto');
  /** Subtab de participaciones dentro de Negocios asociados (página individual del contacto) */
  const [activeContactNegocioParticipationsTab, setActiveContactNegocioParticipationsTab] = useState<'speakers' | 'events' | 'initiatives' | 'business_rounds'>('speakers');
  /** Organización seleccionada para ver su página individual (vista Empresas) */
  const [selectedCrmOrganization, setSelectedCrmOrganization] = useState<CrmOrganization | null>(null);
  /** Subtab en detalle de organización: Organización | Contactos | Negocios */
  const [organizationDetailSubTab, setOrganizationDetailSubTab] = useState<'organizacion' | 'contactos' | 'negocios'>('organizacion');
  /** Índice del negocio activo en detalle de organización (cuando hay 2+ negocios) */
  const [activeOrgDetailNegocioIndex, setActiveOrgDetailNegocioIndex] = useState(0);
  /** Subtab de participaciones en detalle de organización (Negocios) */
  const [activeOrgDetailNegocioParticipationsTab, setActiveOrgDetailNegocioParticipationsTab] = useState<'speakers' | 'events' | 'initiatives' | 'business_rounds'>('speakers');
  /** Modal de edición de organización (abre al clic en Editar en detalle de org) */
  const [orgEditModalOpen, setOrgEditModalOpen] = useState(false);
  const [orgEditModalSubTab, setOrgEditModalSubTab] = useState<'organizacion' | 'negocios'>('organizacion');
  const [orgEditDraft, setOrgEditDraft] = useState<Partial<CrmOrganization> | null>(null);
  const [orgNegocioEditDraft, setOrgNegocioEditDraft] = useState<{ pipelineStage?: string; priority?: string; data?: Record<string, unknown> } | null>(null);
  const [orgDetailSaving, setOrgDetailSaving] = useState(false);
  const [orgDetailSaveError, setOrgDetailSaveError] = useState<string | null>(null);
  // Estados para Modalidad y Zona Expo editables
  const [editableAgreedModality, setEditableAgreedModality] = useState<string>('');
  const [editableModalityObservations, setEditableModalityObservations] = useState<string>('');
  const [editableConfirmedStands, setEditableConfirmedStands] = useState<string[]>([]);
  // Estados para mensajes de confirmación
  const [modalitySaveMessage, setModalitySaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [standsSaveMessage, setStandsSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [crmNotes, setCrmNotes] = useState<Record<number, string>>({});
  const [crmTags, setCrmTags] = useState<Record<number, string[]>>({});
  const [crmPriority, setCrmPriority] = useState<Record<number, 'high' | 'medium' | 'low'>>({});
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [fullProfileData, setFullProfileData] = useState<ProfileData | null>(null);
  const [loadingFullProfile, setLoadingFullProfile] = useState<boolean>(false);
  const [activeMainProfileTab, setActiveMainProfileTab] = useState<'overview' | 'modality' | 'participations' | 'missing' | 'profile'>(initialState.activeMainProfileTab as 'overview' | 'modality' | 'participations' | 'missing' | 'profile');
  const [activeProfileSubTab, setActiveProfileSubTab] = useState<'institutional' | 'participation' | 'expo' | 'speakers' | 'events' | 'initiatives'>(initialState.activeProfileSubTab);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [expandedChecklistItems, setExpandedChecklistItems] = useState<Set<string>>(new Set());
  const [expandedPipelineCards, setExpandedPipelineCards] = useState<Set<number>>(new Set());
  const [showCreateContactModal, setShowCreateContactModal] = useState(false);
  const [showCreateOrganizationModal, setShowCreateOrganizationModal] = useState(false);
  const [showCreateNegocioModal, setShowCreateNegocioModal] = useState(false);
  // Subpestaña dentro de Historial de Mensajería (perfil CRM): Correo | WhatsApp | Mensajería
  const [activeHistorialSubTab, setActiveHistorialSubTab] = useState<'correo' | 'whatsapp' | 'messaging'>('messaging');

  // Asegurar que si no hay perfil institucional, se redirija a la pestaña de información de contacto
  useEffect(() => {
    if (fullProfileData && !fullProfileData.contactInfo?.wantsToManageProfile && activeMainProfileTab === 'profile') {
      setActiveMainProfileTab('overview');
    }
  }, [fullProfileData, activeMainProfileTab]);

  // Procesar el logo de la organización (similar a Sidebar)
  useEffect(() => {
    if (!fullProfileData?.basicInfo?.logo) {
      setLogoPreview(null);
      return;
    }

    const currentLogo = fullProfileData.basicInfo.logo;
    
    // Si el logo es un File, crear una URL de preview
    if (typeof currentLogo !== 'string') {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(currentLogo);
    } else if (typeof currentLogo === 'string' && currentLogo.trim() !== '') {
      // Convertir URL de Supabase a URL pública si es necesario
      let logoUrl = currentLogo.trim();
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
      
      // Si es una data URL (base64), usarla directamente
      if (logoUrl.startsWith('data:image/')) {
        setLogoPreview(logoUrl);
        return;
      }
      
      // Si ya es una URL completa válida (http/https), procesarla
      if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
        // Si es una URL de Supabase signed, convertirla a pública
        if (logoUrl.includes('supabase.co') && logoUrl.includes('/storage/v1/object/sign/')) {
          const match = logoUrl.match(/\/storage\/v1\/object\/sign\/([^?]+)/);
          if (match && supabaseUrl) {
            const filePath = match[1];
            const pathParts = filePath.split('/');
            if (pathParts.length >= 2) {
              const bucket = pathParts[0];
              const fileName = pathParts.slice(1).join('/');
              logoUrl = `${supabaseUrl}/storage/v1/object/public/${bucket}/${fileName}`;
            } else {
              logoUrl = `${supabaseUrl}/storage/v1/object/public/perfil-institucional/logos/${filePath}`;
            }
          }
        }
      }
      // Si es una ruta relativa que empieza con /storage/files/, convertirla a URL completa
      else if (logoUrl.startsWith('/storage/files/')) {
        const filePath = logoUrl.replace('/storage/files/', '');
        if (supabaseUrl) {
          logoUrl = `${supabaseUrl}/storage/v1/object/public/${filePath}`;
        }
      }
      // Si es una ruta relativa que empieza con /storage/v1/object/public/, agregar dominio
      else if (logoUrl.startsWith('/storage/v1/object/public/')) {
        if (supabaseUrl) {
          logoUrl = `${supabaseUrl}${logoUrl}`;
        }
      }
      // Si es solo un nombre de archivo o ruta relativa sin /storage/files/, intentar construir la URL
      else if (!logoUrl.includes('/') || logoUrl.split('/').length <= 2) {
        if (supabaseUrl) {
          logoUrl = `${supabaseUrl}/storage/v1/object/public/perfil-institucional/logos/${logoUrl}`;
        }
      }
      
      // Solo establecer el preview si la URL es válida
      if (logoUrl && (logoUrl.startsWith('http://') || logoUrl.startsWith('https://') || logoUrl.startsWith('data:image/'))) {
        setLogoPreview(logoUrl);
      } else {
        setLogoPreview(null);
      }
    } else {
      setLogoPreview(null);
    }
  }, [fullProfileData?.basicInfo?.logo]);
  
  // Pipeline de seguimiento (múltiples pipelines)
  type PipelineStage = string;
  const [pipelinesList, setPipelinesList] = useState<{ id: number; name: string; position: number; field_schema?: PipelineFieldDef[] }[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState<number | null>(null);
  const [crmPipeline, setCrmPipeline] = useState<Record<number, PipelineStage>>({});
  const [crmPipelineId, setCrmPipelineId] = useState<Record<number, number>>({});
  const [pipelineView, setPipelineView] = useState<'list' | 'pipeline'>(initialState.pipelineView);
  const [listViewMode, setListViewMode] = useState<'cards' | 'table'>(initialState.listViewMode);
  const [activeCrmSubTab, setActiveCrmSubTab] = useState<'contactos' | 'empresas' | 'negocios'>(
    (initialState as { activeCrmSubTab?: 'contactos' | 'empresas' | 'negocios' }).activeCrmSubTab || 'contactos'
  );
  const [crmContactsRefreshTrigger, setCrmContactsRefreshTrigger] = useState(0);

  /** Sincronizar pipelineView con activeCrmSubTab (p. ej. al restaurar estado) */
  useEffect(() => {
    setPipelineView(activeCrmSubTab === 'negocios' ? 'pipeline' : 'list');
  }, [activeCrmSubTab]);

  /** Hacer scroll a la fila de la tabla de negocios al volver desde el detalle de la org */
  useEffect(() => {
    if (activeCrmSubTab !== 'negocios' || !negociosScrollToRowRef.current) return;
    const target = { ...negociosScrollToRowRef.current };
    const tryScroll = (): boolean => {
      const row = document.querySelector(
        `.admin-negocios-table tbody tr[data-organization-id="${target.organizationId}"][data-pipeline-id="${target.pipelineId}"]`
      ) as HTMLElement | null;
      if (!row) return false;
      const container = row.closest('.admin-users-table-container') as HTMLElement | null;
      if (container) {
        const rowTop = row.offsetTop;
        const rowHeight = row.offsetHeight;
        const containerHeight = container.clientHeight;
        container.scrollTop = Math.max(0, rowTop - containerHeight / 2 + rowHeight / 2);
        container.scrollLeft = 0;
      } else {
        row.scrollIntoView({ block: 'center', behavior: 'auto' });
      }
      if (target.windowY != null && target.windowX != null) {
        window.scrollTo(target.windowX, target.windowY);
      }
      negociosScrollToRowRef.current = null;
      return true;
    };
    const delays = [80, 200, 400, 700, 1200];
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    delays.forEach((ms) => {
      const id = setTimeout(() => {
        if (!negociosScrollToRowRef.current) return;
        if (tryScroll()) timeouts.forEach(clearTimeout);
      }, ms);
      timeouts.push(id);
    });
    return () => timeouts.forEach(clearTimeout);
  }, [activeCrmSubTab]);

  const pipelineColumnsRef = useRef<HTMLDivElement>(null);
  const pipelineScrollContainerRef = useRef<HTMLDivElement>(null);
  const negociosTableContainerRef = useRef<HTMLDivElement>(null);
  const negociosScrollToRowRef = useRef<{ organizationId: number; pipelineId: number; windowY: number; windowX: number } | null>(null);
  const selectedUserIdRef = useRef<number | null>(null);
  const selectedParticipationIdRef = useRef<string | null>(null);
  const previousSelectedCrmUserId = useRef<number | null>(null);
  const [draggedUser, setDraggedUser] = useState<number | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<PipelineStage | null>(null);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isPipelineStagesModalOpen, setIsPipelineStagesModalOpen] = useState(false);
  const [isPipelinePropertiesModalOpen, setIsPipelinePropertiesModalOpen] = useState(false);
  const [isCreatePipelineModalOpen, setIsCreatePipelineModalOpen] = useState(false);
  const [isPipelineSelectOpen, setPipelineSelectOpen] = useState(false);
  const pipelineSelectRef = useRef<HTMLDivElement>(null);
  const [newPipelineName, setNewPipelineName] = useState('');
  /** Negocios del pipeline seleccionado: organizaciones con datos de ese pipeline (etapa, prioridad, data) */
  type PipelineNegocio = {
    organizationId: number;
    pipelineId: number;
    pipelineStage: string;
    priority: string;
    data: Record<string, unknown>;
    organizationName: string;
    organizationType?: string;
    assignedContactIds?: number[];
    assignedContacts?: { id: number; nombre?: string | null; apellido?: string | null; correo?: string | null; cargo?: string | null }[];
    sourceUserId?: number | null;
    updatedAt?: string | null;
  };
  const [pipelineNegocios, setPipelineNegocios] = useState<PipelineNegocio[]>([]);
  const [draggedNegocioOrganizationId, setDraggedNegocioOrganizationId] = useState<number | null>(null);
  const [selectedSpeakerForModal, setSelectedSpeakerForModal] = useState<{ speaker: any; user: User } | null>(null);
  const [selectedEventForModal, setSelectedEventForModal] = useState<{ event: any; user: User } | null>(null);
  const [selectedInitiativeForModal, setSelectedInitiativeForModal] = useState<{ initiative: any; user: User } | null>(null);
  const [selectedParticipantForModal, setSelectedParticipantForModal] = useState<{ participant: any; user: User } | null>(null);
  const [rejectionModal, setRejectionModal] = useState<{
    source: 'profile';
    isOpen: boolean;
    userId: number;
    participationType: 'speakers' | 'parallelEvents' | 'initiatives' | 'businessRoundsParticipants';
    participationId: string | number;
  } | {
    source: 'contactNegocio';
    isOpen: boolean;
    organizationId: number;
    pipelineId: number;
    participationType: 'speakers' | 'events' | 'initiatives' | 'business_rounds';
    itemIndex: number;
  } | null>(null);
  const [rejectionComment, setRejectionComment] = useState<string>('');
  /** Modal de detalle al hacer clic en una participación del negocio del contacto */
  const [selectedContactNegocioParticipation, setSelectedContactNegocioParticipation] = useState<{
    type: 'speakers' | 'events' | 'initiatives' | 'business_rounds';
    item: any;
    organizationName: string;
    organizationId: number;
    pipelineId: number;
    itemIndex: number;
  } | null>(null);
  
  // Etapas del pipeline - ahora es estado dinámico
  // Inicializar con etapas por defecto para que el pipeline se muestre inmediatamente
  const [pipelineStages, setPipelineStages] = useState<{ id: string; label: string; color: string; icon: React.ReactNode; position: number }[]>(() => {
    // Etapas por defecto que se mostrarán mientras se cargan desde el backend
    return [
      { id: 'nuevo', label: 'Nuevo', color: '#94a3b8', position: 0, icon: <NewIcon width={18} height={18} /> },
      { id: 'contactado', label: 'Contactado', color: '#3b82f6', position: 1, icon: <PhoneIcon width={18} height={18} /> },
      { id: 'calificado', label: 'Calificado', color: '#8b5cf6', position: 2, icon: <CheckCircleIcon width={18} height={18} /> },
      { id: 'en_proceso', label: 'En Proceso', color: '#f59e0b', position: 3, icon: <SettingsIcon width={18} height={18} /> },
      { id: 'negociacion', label: 'Negociación', color: '#ec4899', position: 4, icon: <HandshakeIcon width={18} height={18} /> },
      { id: 'completado', label: 'Completado', color: '#22c55e', position: 5, icon: <AwardIcon width={18} height={18} /> },
      { id: 'perdido', label: 'Perdido', color: '#ef4444', position: 6, icon: <XCircleIcon width={18} height={18} /> },
    ];
  });
  
  // Función para obtener el icono por nombre
  const getIconByName = (iconName: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      'new': <NewIcon width={18} height={18} />,
      'phone': <PhoneIcon width={18} height={18} />,
      'check': <CheckCircleIcon width={18} height={18} />,
      'settings': <SettingsIcon width={18} height={18} />,
      'handshake': <HandshakeIcon width={18} height={18} />,
      'award': <AwardIcon width={18} height={18} />,
      'x': <XCircleIcon width={18} height={18} />,
    };
    return iconMap[iconName] || <NewIcon width={18} height={18} />;
  };
  
  const loadPipelines = async () => {
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/crm/pipelines`, { method: 'GET', credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.pipelines)) {
          setPipelinesList(data.pipelines);
          if (data.pipelines.length > 0 && selectedPipelineId == null) {
            setSelectedPipelineId(data.pipelines[0].id);
          }
        }
      }
    } catch (e) {
    }
  };

  // Cargar negocios del pipeline (organizaciones con datos de ese pipeline)
  const loadPipelineNegocios = async (pipelineId: number | null) => {
    if (pipelineId == null) {
      setPipelineNegocios([]);
      return;
    }
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/crm/pipeline/${pipelineId}/negocios`, { method: 'GET', credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.negocios)) {
          setPipelineNegocios(data.negocios);
        } else {
          setPipelineNegocios([]);
        }
      } else {
        setPipelineNegocios([]);
      }
    } catch (e) {
      setPipelineNegocios([]);
    }
  };

  const saveNegocioStage = async (organizationId: number, newStage: string) => {
    if (selectedPipelineId == null) return;
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/crm/pipeline/${selectedPipelineId}/organization/${organizationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pipelineStage: newStage })
      });
      if (response.ok) {
        setPipelineNegocios(prev =>
          prev.map(n => n.organizationId === organizationId ? { ...n, pipelineStage: newStage } : n)
        );
      } else {
        const err = await response.json().catch(() => ({}));
      }
    } catch (e) {
    }
  };

  const saveNegocioPriority = async (organizationId: number, newPriority: 'high' | 'medium' | 'low') => {
    if (selectedPipelineId == null) return;
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/crm/pipeline/${selectedPipelineId}/organization/${organizationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ priority: newPriority })
      });
      if (response.ok) {
        setPipelineNegocios(prev =>
          prev.map(n => n.organizationId === organizationId ? { ...n, priority: newPriority } : n)
        );
      } else {
        const err = await response.json().catch(() => ({}));
      }
    } catch (e) {
    }
  };

  const saveNegocioDataField = async (organizationId: number, key: string, value: unknown) => {
    if (selectedPipelineId == null) return;
    const neg = pipelineNegocios.find(n => n.organizationId === organizationId && n.pipelineId === selectedPipelineId);
    if (!neg) return;
    const currentData = (neg.data && typeof neg.data === 'object' ? { ...neg.data } : {}) as Record<string, unknown>;
    if (value === '' || value == null) {
      delete currentData[key];
    } else {
      currentData[key] = value;
    }
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/crm/pipeline/${selectedPipelineId}/organization/${organizationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pipelineStage: neg.pipelineStage, priority: neg.priority, data: currentData })
      });
      if (response.ok) {
        setPipelineNegocios(prev =>
          prev.map(n => n.organizationId === organizationId && n.pipelineId === selectedPipelineId ? { ...n, data: currentData } : n)
        );
      } else {
        const err = await response.json().catch(() => ({}));
      }
    } catch (e) {
    }
  };

  // Cargar etapas del pipeline desde el backend (por pipelineId)
  const loadPipelineStages = async (pipelineId?: number | null) => {
    const pid = pipelineId ?? selectedPipelineId;
    try {
      const apiUrl = getApiUrl();
      const url = pid != null ? `${apiUrl}/crm/pipeline-stages?pipelineId=${pid}` : `${apiUrl}/crm/pipeline-stages`;
      const response = await fetch(url, { method: 'GET', credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.stages) {
          const sortedStages = data.stages
            .sort((a: any, b: any) => a.position - b.position)
            .map((stage: any) => ({
              ...stage,
              icon: getIconByName(stage.icon || 'new')
            }));
          setPipelineStages(sortedStages);
        } else {
          // Si no hay etapas, usar las por defecto
          const defaultStages = [
            { id: 'nuevo', label: 'Nuevo', color: '#94a3b8', position: 0, icon: <NewIcon width={18} height={18} /> },
            { id: 'contactado', label: 'Contactado', color: '#3b82f6', position: 1, icon: <PhoneIcon width={18} height={18} /> },
            { id: 'calificado', label: 'Calificado', color: '#8b5cf6', position: 2, icon: <CheckCircleIcon width={18} height={18} /> },
            { id: 'en_proceso', label: 'En Proceso', color: '#f59e0b', position: 3, icon: <SettingsIcon width={18} height={18} /> },
            { id: 'negociacion', label: 'Negociación', color: '#ec4899', position: 4, icon: <HandshakeIcon width={18} height={18} /> },
            { id: 'completado', label: 'Completado', color: '#22c55e', position: 5, icon: <AwardIcon width={18} height={18} /> },
            { id: 'perdido', label: 'Perdido', color: '#ef4444', position: 6, icon: <XCircleIcon width={18} height={18} /> },
          ];
          setPipelineStages(defaultStages);
        }
      } else {
        // Si falla, usar las por defecto
        const defaultStages = [
          { id: 'nuevo', label: 'Nuevo', color: '#94a3b8', position: 0, icon: <NewIcon width={18} height={18} /> },
          { id: 'contactado', label: 'Contactado', color: '#3b82f6', position: 1, icon: <PhoneIcon width={18} height={18} /> },
          { id: 'calificado', label: 'Calificado', color: '#8b5cf6', position: 2, icon: <CheckCircleIcon width={18} height={18} /> },
          { id: 'en_proceso', label: 'En Proceso', color: '#f59e0b', position: 3, icon: <SettingsIcon width={18} height={18} /> },
          { id: 'negociacion', label: 'Negociación', color: '#ec4899', position: 4, icon: <HandshakeIcon width={18} height={18} /> },
          { id: 'completado', label: 'Completado', color: '#22c55e', position: 5, icon: <AwardIcon width={18} height={18} /> },
          { id: 'perdido', label: 'Perdido', color: '#ef4444', position: 6, icon: <XCircleIcon width={18} height={18} /> },
        ];
        setPipelineStages(defaultStages);
      }
    } catch (error) {
      // Si falla, usar las por defecto
      const defaultStages = [
        { id: 'nuevo', label: 'Nuevo', color: '#94a3b8', position: 0, icon: <NewIcon width={18} height={18} /> },
        { id: 'contactado', label: 'Contactado', color: '#3b82f6', position: 1, icon: <PhoneIcon width={18} height={18} /> },
        { id: 'calificado', label: 'Calificado', color: '#8b5cf6', position: 2, icon: <CheckCircleIcon width={18} height={18} /> },
        { id: 'en_proceso', label: 'En Proceso', color: '#f59e0b', position: 3, icon: <SettingsIcon width={18} height={18} /> },
        { id: 'negociacion', label: 'Negociación', color: '#ec4899', position: 4, icon: <HandshakeIcon width={18} height={18} /> },
        { id: 'completado', label: 'Completado', color: '#22c55e', position: 5, icon: <AwardIcon width={18} height={18} /> },
        { id: 'perdido', label: 'Perdido', color: '#ef4444', position: 6, icon: <XCircleIcon width={18} height={18} /> },
      ];
      setPipelineStages(defaultStages);
    }
  };
  
  // Guardar etapas del pipeline en el backend (para el pipeline seleccionado)
  const savePipelineStages = async (stages: { id: string; label: string; color: string; position: number; icon?: string }[], pipelineId?: number | null) => {
    const pid = pipelineId ?? selectedPipelineId;
    if (pid == null) throw new Error('Selecciona un pipeline');
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/crm/pipeline-stages`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          pipelineId: pid,
          stages: stages.map(s => ({ id: s.id, label: s.label, color: s.color, position: s.position, icon: s.icon || 'new' }))
        })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) await loadPipelineStages(pid);
        else throw new Error(data.error || 'Error al guardar etapas');
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || 'Error al guardar etapas');
      }
    } catch (error: any) {
      throw error;
    }
  };

  const createPipeline = async (name: string) => {
    const apiUrl = getApiUrl();
    const response = await fetch(`${apiUrl}/crm/pipelines`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name: name.trim() })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Error al crear pipeline');
    }
    const data = await response.json();
    if (data.success && data.pipeline) {
      setPipelinesList(prev => [...prev, data.pipeline]);
      setSelectedPipelineId(data.pipeline.id);
      setIsCreatePipelineModalOpen(false);
      setNewPipelineName('');
      await loadPipelineStages(data.pipeline.id);
      setIsPipelineStagesModalOpen(true);
    }
  };

  const updatePipelineName = async (pipelineId: number, name: string) => {
    const apiUrl = getApiUrl();
    const response = await fetch(`${apiUrl}/crm/pipelines/${pipelineId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name: name.trim() })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Error al actualizar nombre');
    }
    const data = await response.json();
    if (data.success && data.pipeline) {
      setPipelinesList(prev => prev.map(p => p.id === pipelineId ? { ...p, name: data.pipeline.name } : p));
    }
  };

  const savePipelineFieldSchema = async (pipelineId: number, fieldSchema: PipelineFieldDef[]) => {
    const apiUrl = getApiUrl();
    const response = await fetch(`${apiUrl}/crm/pipelines/${pipelineId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ field_schema: fieldSchema })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Error al guardar propiedades');
    }
    const data = await response.json();
    if (data.success && data.pipeline) {
      setPipelinesList(prev => prev.map(p => p.id === pipelineId ? { ...p, field_schema: data.pipeline.field_schema || [] } : p));
    }
  };
  const [personalProfiles, setPersonalProfiles] = useState<any[]>([]);
  const [institutionalProfiles, setInstitutionalProfiles] = useState<any[]>([]);
  const [systemMetrics, setSystemMetrics] = useState<any>(null);
  const [errors, setErrors] = useState<any[]>([]);
  const [errorsStats, setErrorsStats] = useState<any[]>([]);
  const [errorsTotal, setErrorsTotal] = useState<number>(0);
  const [activeVisits, setActiveVisits] = useState<{total: number, uniqueUsers: number} | null>(null);

  // Verificar autenticación y rol (admin o editor); sin esto el panel se queda en "Cargando..."
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const result = await verifySession();
        if (result.success && result.user) {
          const role = result.user.role;
          if (role !== 'admin' && role !== 'editor') {
            navigate('/');
            return;
          }
          setUserEmail(result.user.email);
          setCurrentUserId(result.user.id);
          setCurrentUserRole(role as 'admin' | 'editor');
          if (role === 'editor' && Array.isArray(result.user.permissions)) {
            setCurrentUserPermissions(result.user.permissions);
          } else {
            setCurrentUserPermissions(null);
          }
        } else {
          navigate('/');
        }
      } catch (error) {
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  // Si el usuario es editor y la pestaña actual no está permitida, cambiar a la primera permitida
  useEffect(() => {
    if (currentUserRole === 'editor' && currentUserPermissions != null && currentUserPermissions.length > 0) {
      if (!currentUserPermissions.includes(activeMainTab)) {
        setActiveMainTab(firstAllowedTab());
      }
    }
  }, [currentUserRole, currentUserPermissions, activeMainTab]);

  // Cargar lista de accesos cuando se abre la pestaña Accesos (solo admin)
  useEffect(() => {
    if (activeMainTab !== 'accesos' || currentUserRole !== 'admin') return;
    setLoadingAccessUsers(true);
    setAccessError(null);
    fetch(`${getApiUrl()}/admin/access-users`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.users)) {
          setAccessUsers(data.users);
        }
      })
      .catch(() => setAccessError('Error al cargar la lista de accesos'))
      .finally(() => setLoadingAccessUsers(false));
  }, [activeMainTab, currentUserRole]);

  // Helper para obtener la URL base del API
  // En desarrollo local: usa ruta relativa /api (el proxy de React redirige a localhost:3001)
  // En producción (Vercel): usa ruta relativa /api porque front y back están en el mismo dominio
  const getApiUrl = () => {
    // Si REACT_APP_API_URL está definido, usarlo (tiene prioridad)
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }
    
    // Siempre usar ruta relativa /api
    // En desarrollo: el proxy configurado en package.json redirige /api a http://localhost:3001/api
    // En producción: /api apunta al mismo dominio (Vercel)
    return '/api';
  };

  // Función para cargar usuarios con datos completos
    const loadUsers = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/admin/users`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Error al cargar usuarios');
        }

        const data = await response.json();
        if (data.success) {
          let usersList = data.users || [];

          // Cargar perfiles institucionales completos para obtener datos adicionales
          try {
            const institutionalResponse = await fetch(`${getApiUrl()}/admin/tables/institutional-profiles`, {
              credentials: 'include'
            });
            if (institutionalResponse.ok) {
              const institutionalData = await institutionalResponse.json();
              if (institutionalData.success) {
                const institutionalProfilesMap = new Map(
                  (institutionalData.data || []).map((profile: any) => [profile.user_id, profile])
                );

                // Enriquecer usuarios con datos de perfiles institucionales
                usersList = usersList.map((user: User) => {
                  const institutionalProfile: any = institutionalProfilesMap.get(user.id);
                  if (institutionalProfile) {
                    // Parsear datos JSON almacenados como strings
                    let speakers = null;
                    let parallelEvents = null;
                    let catalogSections = null;
                    let initiatives = null;
                    let businessRoundsParticipants = null;

                    try {
                      if (institutionalProfile.speakers) {
                        speakers = typeof institutionalProfile.speakers === 'string' 
                          ? JSON.parse(institutionalProfile.speakers) 
                          : institutionalProfile.speakers;
                      }
                      if (institutionalProfile.parallel_events) {
                        parallelEvents = typeof institutionalProfile.parallel_events === 'string'
                          ? JSON.parse(institutionalProfile.parallel_events)
                          : institutionalProfile.parallel_events;
                      }
                      if (institutionalProfile.catalog_sections) {
                        catalogSections = typeof institutionalProfile.catalog_sections === 'string'
                          ? JSON.parse(institutionalProfile.catalog_sections)
                          : institutionalProfile.catalog_sections;
                      }
                      if (institutionalProfile.iniciativas) {
                        initiatives = typeof institutionalProfile.iniciativas === 'string'
                          ? JSON.parse(institutionalProfile.iniciativas)
                          : institutionalProfile.iniciativas;
                      }
                      if (institutionalProfile.business_rounds_participants) {
                        businessRoundsParticipants = typeof institutionalProfile.business_rounds_participants === 'string'
                          ? JSON.parse(institutionalProfile.business_rounds_participants)
                          : institutionalProfile.business_rounds_participants;
                      }
                    } catch (parseError) {
                      // Error al parsear - silenciado
                    }

                    // Parsear modalidades y stands
                    let participationModalities = null;
                    let selectedStandsUpdated = null;
                    try {
                      if (institutionalProfile.participation_modalities) {
                        if (typeof institutionalProfile.participation_modalities === 'string') {
                          // Intentar parsear como JSON
                          try {
                            participationModalities = JSON.parse(institutionalProfile.participation_modalities);
                          } catch (e) {
                            // Si falla, intentar como array simple
                            participationModalities = institutionalProfile.participation_modalities;
                          }
                        } else {
                          participationModalities = institutionalProfile.participation_modalities;
                        }
                        // Asegurar que sea un array
                        if (participationModalities && !Array.isArray(participationModalities)) {
                          participationModalities = [participationModalities];
                        }
                      }
                      if (institutionalProfile.selected_stands_updated) {
                        if (typeof institutionalProfile.selected_stands_updated === 'string') {
                          // Intentar parsear como JSON
                          try {
                            selectedStandsUpdated = JSON.parse(institutionalProfile.selected_stands_updated);
                          } catch (e) {
                            // Si falla, intentar como array simple
                            selectedStandsUpdated = institutionalProfile.selected_stands_updated;
                          }
                        } else {
                          selectedStandsUpdated = institutionalProfile.selected_stands_updated;
                        }
                        // Asegurar que sea un array
                        if (selectedStandsUpdated && !Array.isArray(selectedStandsUpdated)) {
                          selectedStandsUpdated = [selectedStandsUpdated];
                        }
                      }
                    } catch (parseError) {
                    }

                    // Debug: Log para verificar datos
                    if (user.id) {
                    }

                    return {
                      ...user,
                      // Misma lógica que combineProfileData en API: preferir institucional si tiene valor; si no, mantener el de la lista (COALESCE ip/pp del backend).
                      organization_type:
                        institutionalProfile.organization_type ||
                        user.organization_type ||
                        null,
                      position: institutionalProfile.position || null,
                      speakers: speakers,
                      parallel_events: parallelEvents,
                      catalog_sections: catalogSections,
                      initiatives: initiatives,
                      business_rounds_participants: businessRoundsParticipants,
                      perfil_personal_validado: institutionalProfile.perfil_personal_validado || false,
                      perfil_institucional_validado: institutionalProfile.perfil_institucional_validado || false,
                      request_information: institutionalProfile.request_information || false,
                      request_expo_information: institutionalProfile.request_expo_information || false,
                      participation_modalities: participationModalities,
                      agreed_modality: institutionalProfile.agreed_modality || null,
                      selected_stands_updated: selectedStandsUpdated,
                      is_draft: user.is_draft !== undefined ? user.is_draft : (institutionalProfile.is_draft !== undefined ? institutionalProfile.is_draft : null),
                    };
                  }
                  return user;
                });
              }
            }
          } catch (profileError) {
            // Error al cargar perfiles - silenciado
          }

          // Cargar conversaciones para obtener unreadCount
          try {
            const conversationsResponse = await getConversations();
            setConversations(conversationsResponse.conversations);

            // Mapear unreadCount a usuarios
            usersList = usersList.map((user: User) => {
              const conv = conversationsResponse.conversations.find(c => c.userId === user.id);
              const unreadCount = conv?.unreadCount || 0;
              return {
                ...user,
                unreadCount: unreadCount
              };
            });
          } catch (convError) {
          // Error al cargar conversaciones - silenciado
          }

          setUsers(usersList);
        } else {
          setError(data.error || 'Error al cargar usuarios');
        }
      } catch (err: any) {
        setError(err.message || 'Error al cargar usuarios');
      }
    };

    // Función para cargar datos del CRM desde la API
    const loadCrmData = async () => {
      try {
        const apiUrl = getApiUrl();
        const fullUrl = `${apiUrl}/crm/users`;
        
        const response = await fetch(fullUrl, {
        credentials: 'include'
      });

        if (!response.ok) {
          const errorText = await response.text();
          return;
        }

        const data = await response.json();
        if (data.success && data.data) {
          const pipelineIdData: Record<number, number> = {};
          const tagsData: Record<number, string[]> = {};
          const notesData: Record<number, string> = {};

          Object.entries(data.data).forEach(([userId, crmData]: [string, any]) => {
            const id = parseInt(userId);
            if (crmData.pipelineId != null) pipelineIdData[id] = crmData.pipelineId;
            if (crmData.tags) tagsData[id] = crmData.tags;
            if (crmData.notes) notesData[id] = crmData.notes;
          });

          setCrmPipelineId(pipelineIdData);
          setCrmTags(tagsData);
          setCrmNotes(notesData);
      }
    } catch (err: any) {
        // Si falla, usar valores por defecto (no crítico)
      }
    };

    // Función para guardar cambios de pipeline en la API
    const saveDraftStatus = async (userId: number, isDraft: boolean | null) => {
    try {
      const apiUrl = getApiUrl();
      
      
      // Actualizar el perfil personal
      const personalResponse = await fetch(`${apiUrl}/admin/save-section/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          section: 'contactInfo',
          data: {
            is_draft: isDraft
          }
        }),
      });

      if (!personalResponse.ok) {
        const errorData = await personalResponse.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || 'Error al guardar el estado');
      }

      const personalResult = await personalResponse.json();
      
      // Actualizar el perfil institucional si existe
      const user = users.find(u => u.id === userId);
      if (user && user.institutional_profile_id) {
        const institutionalResponse = await fetch(`${apiUrl}/admin/save-section/${userId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            section: 'basicInfo',
            data: {
              is_draft: isDraft
            }
          }),
        });

        if (!institutionalResponse.ok) {
          const errorData = await institutionalResponse.json().catch(() => ({ error: 'Error desconocido' }));
          throw new Error(errorData.error || 'Error al guardar el estado en perfil institucional');
        }

        const institutionalResult = await institutionalResponse.json();
      }

      // Actualizar el estado local inmediatamente (sin esperar recargar desde BD)
      setUsers(prevUsers => prevUsers.map(user => 
        user.id === userId ? { ...user, is_draft: isDraft } : user
      ));
      
      // Actualizar selectedCrmUser inmediatamente si es el mismo usuario
      if (selectedCrmUser && selectedCrmUser.id === userId) {
        setSelectedCrmUser({ ...selectedCrmUser, is_draft: isDraft });
      }
    } catch (error: any) {
      alert(`Error al guardar el estado: ${error.message || 'Error desconocido'}. Por favor, intente nuevamente.`);
    }
  };

  // Función para actualizar approval_status de una participación
  const updateParticipationApprovalStatus = async (
    userId: number,
    participationType: 'speakers' | 'parallelEvents' | 'initiatives' | 'businessRoundsParticipants',
    participationId: string | number,
    approvalStatus: 'pending' | 'approved' | 'rejected',
    rejectionComment?: string | null
  ) => {
    try {
      const apiUrl = getApiUrl();
      const user = users.find(u => u.id === userId);
      if (!user) {
        throw new Error('Usuario no encontrado');
      }

      // Obtener el array actual de participaciones
      let currentParticipations: any[] = [];
      
      switch (participationType) {
        case 'speakers':
          currentParticipations = user.speakers && Array.isArray(user.speakers) ? [...user.speakers] : [];
          break;
        case 'parallelEvents':
          currentParticipations = user.parallel_events && Array.isArray(user.parallel_events) ? [...user.parallel_events] : [];
          break;
        case 'initiatives':
          currentParticipations = user.initiatives && Array.isArray(user.initiatives) ? [...user.initiatives] : [];
          break;
        case 'businessRoundsParticipants':
          currentParticipations = user.business_rounds_participants && Array.isArray(user.business_rounds_participants) 
            ? [...user.business_rounds_participants] 
            : [];
          break;
        default:
          throw new Error('Tipo de participación inválido');
      }

      // Actualizar el approval_status del elemento específico
      // Comparar tanto por id como por conversión de tipos
      const updatedParticipations = currentParticipations.map((item: any) => {
        const itemId = item.id !== undefined ? String(item.id) : undefined;
        const searchId = String(participationId);
        
        if (itemId === searchId) {
          const updatedItem: any = {
            ...item,
            approval_status: approvalStatus
          };
          // Si es rechazo y hay comentario, agregarlo; si no hay comentario, no agregar el campo
          if (approvalStatus === 'rejected' && rejectionComment && rejectionComment.trim()) {
            updatedItem.rejection_comment = rejectionComment.trim();
          } else if (approvalStatus === 'rejected' && (!rejectionComment || !rejectionComment.trim())) {
            // Si se rechaza sin comentario, eliminar el campo si existe
            const { rejection_comment, ...rest } = updatedItem;
            return rest;
          } else if (approvalStatus !== 'rejected') {
            // Si el estado cambia a aprobado o pendiente, eliminar el comentario de rechazo si existe
            const { rejection_comment, ...rest } = updatedItem;
            return rest;
          }
          return updatedItem;
        }
        return item;
      });

      // ACTUALIZACIÓN OPTIMISTA: Actualizar el estado local INMEDIATAMENTE (antes de la llamada al servidor)
      setUsers(prevUsers => prevUsers.map(u => {
        if (u.id === userId) {
          const updatedUser = { ...u };
          switch (participationType) {
            case 'speakers':
              updatedUser.speakers = updatedParticipations;
              break;
            case 'parallelEvents':
              updatedUser.parallel_events = updatedParticipations;
              break;
            case 'initiatives':
              updatedUser.initiatives = updatedParticipations;
              break;
            case 'businessRoundsParticipants':
              updatedUser.business_rounds_participants = updatedParticipations;
              break;
          }
          return updatedUser;
        }
        return u;
      }));

      // Actualizar selectedCrmUser si es el mismo usuario (INMEDIATAMENTE)
      if (selectedCrmUser && selectedCrmUser.id === userId) {
        const updatedCrmUser = { ...selectedCrmUser };
        switch (participationType) {
          case 'speakers':
            updatedCrmUser.speakers = updatedParticipations;
            break;
          case 'parallelEvents':
            updatedCrmUser.parallel_events = updatedParticipations;
            break;
          case 'initiatives':
            updatedCrmUser.initiatives = updatedParticipations;
            break;
          case 'businessRoundsParticipants':
            updatedCrmUser.business_rounds_participants = updatedParticipations;
            break;
        }
        setSelectedCrmUser(updatedCrmUser);
      }

      // Actualizar los modales si están abiertos (INMEDIATAMENTE)
      if (selectedSpeakerForModal && selectedSpeakerForModal.user.id === userId && participationType === 'speakers') {
        const updatedSpeaker = updatedParticipations.find((s: any) => String(s.id) === String(participationId));
        if (updatedSpeaker) {
          setSelectedSpeakerForModal({
            ...selectedSpeakerForModal,
            speaker: updatedSpeaker,
            user: { ...selectedSpeakerForModal.user, speakers: updatedParticipations }
          });
        }
      }
      if (selectedEventForModal && selectedEventForModal.user.id === userId && participationType === 'parallelEvents') {
        const updatedEvent = updatedParticipations.find((e: any) => String(e.id) === String(participationId));
        if (updatedEvent) {
          setSelectedEventForModal({
            ...selectedEventForModal,
            event: updatedEvent,
            user: { ...selectedEventForModal.user, parallel_events: updatedParticipations }
          });
        }
      }
      if (selectedInitiativeForModal && selectedInitiativeForModal.user.id === userId && participationType === 'initiatives') {
        const updatedInitiative = updatedParticipations.find((i: any) => String(i.id) === String(participationId));
        if (updatedInitiative) {
          setSelectedInitiativeForModal({
            ...selectedInitiativeForModal,
            initiative: updatedInitiative,
            user: { ...selectedInitiativeForModal.user, initiatives: updatedParticipations }
          });
        }
      }
      if (selectedParticipantForModal && selectedParticipantForModal.user.id === userId && participationType === 'businessRoundsParticipants') {
        const updatedParticipant = updatedParticipations.find((p: any) => String(p.id) === String(participationId));
        if (updatedParticipant) {
          setSelectedParticipantForModal({
            ...selectedParticipantForModal,
            participant: updatedParticipant,
            user: { ...selectedParticipantForModal.user, business_rounds_participants: updatedParticipations }
          });
        }
      }

      // Enviar al backend (después de la actualización optimista)
      const sectionName = participationType === 'speakers' ? 'speakers' :
                         participationType === 'parallelEvents' ? 'parallelEvents' :
                         participationType === 'initiatives' ? 'initiatives' :
                         'businessRoundsParticipants';

      const response = await fetch(`${apiUrl}/admin/save-section/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          section: sectionName,
          data: updatedParticipations
        }),
      });

      if (!response.ok) {
        // Si falla, revertir los cambios
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        // Recargar usuarios para revertir al estado anterior
        await loadUsers();
        throw new Error(errorData.error || 'Error al actualizar el estado de aprobación');
      }

    } catch (error: any) {
      alert(`Error al actualizar el estado: ${error.message || 'Error desconocido'}. Por favor, intente nuevamente.`);
    }
  };

  // Actualizar approval_status del perfil institucional (solo tabla institutional_profiles)
  const updatePerfilInstOrgApproval = async (
    userId: number,
    approvalStatus: 'pending' | 'approved' | 'rejected'
  ) => {
    const apiUrl = getApiUrl();
    try {
      const res = await fetch(`${apiUrl}/database/organizations/approval-from-profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId, approval_status: approvalStatus }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || `Error ${res.status}`);
      setPerfilOrganizations((prev) => prev.map((o) => (o.id === userId ? { ...o, approval_status: approvalStatus } : o)));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Error al actualizar el estado de aprobación.');
    }
  };

  // Actualizar approval_status de una participación en el negocio del contacto (detalle contacto > Negocios)
  const updateContactNegocioParticipationApproval = async (
    organizationId: number,
    pipelineId: number,
    participationType: 'speakers' | 'events' | 'initiatives' | 'business_rounds',
    itemIndex: number,
    approvalStatus: 'approved' | 'rejected' | 'pending',
    rejectionComment?: string | null
  ) => {
    const neg = contactDetailNegocios.find((n) => n.organizationId === organizationId && n.pipelineId === pipelineId);
    if (!neg) {
      alert('No se encontró el negocio. Recarga la página e intenta de nuevo.');
      return;
    }
    const currentData = neg.data ?? {};
    const data = { ...currentData } as Record<string, unknown>;
    const getArr = (key: string): unknown[] => {
      const val = data[key];
      if (Array.isArray(val)) return [...val];
      if (val != null && typeof val === 'string') {
        try {
          const p = JSON.parse(val);
          return Array.isArray(p) ? p : [];
        } catch {
          return [];
        }
      }
      return [];
    };
    const setArr = (key: string, arr: unknown[]) => { data[key] = arr; };
    let arr: unknown[] = [];
    let key = '';
    switch (participationType) {
      case 'speakers':
        key = 'speakers';
        arr = getArr(key);
        break;
      case 'events':
        key = 'parallel_events';
        arr = getArr(key);
        break;
      case 'initiatives':
        key = (getArr('iniciativas').length > 0 ? 'iniciativas' : 'catalog_sections');
        arr = getArr(key);
        break;
      case 'business_rounds':
        key = 'business_rounds_participants';
        arr = getArr(key);
        break;
      default:
        return;
    }
    if (itemIndex < 0 || itemIndex >= arr.length) {
      alert('No se pudo actualizar: índice de participación inválido.');
      return;
    }
    const item = arr[itemIndex] as Record<string, unknown>;
    const updated = { ...item, approval_status: approvalStatus };
    if (approvalStatus === 'rejected' && rejectionComment != null && String(rejectionComment).trim()) {
      (updated as any).rejection_comment = String(rejectionComment).trim();
    } else if ('rejection_comment' in updated) {
      delete (updated as any).rejection_comment;
    }
    arr[itemIndex] = updated;
    setArr(key, arr);
    const apiUrl = getApiUrl();
    const url = `${apiUrl}/crm/pipeline/${pipelineId}/organization/${organizationId}`;
    try {
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pipelineStage: neg.pipelineStage, priority: neg.priority, data }),
      });
      const text = await res.text();
      let result: { success?: boolean; negocio?: { data?: Record<string, unknown> }; error?: string } = {};
      try {
        result = text ? JSON.parse(text) : {};
      } catch {
        if (!res.ok) throw new Error(text || `Error ${res.status}`);
      }
      if (res.ok) {
        const newData = result?.negocio?.data ?? data;
        setContactDetailNegocios((prev) => {
          const idx = prev.findIndex((n) => n.organizationId === organizationId && n.pipelineId === pipelineId);
          if (idx < 0) return prev;
          const next = [...prev];
          next[idx] = { ...next[idx], data: newData };
          return next;
        });
      } else {
        throw new Error(result?.error || text || `Error ${res.status}`);
      }
    } catch (e: any) {
      alert(e?.message || 'Error al actualizar el estado de aprobación. Comprueba la consola y que el backend esté en marcha.');
    }
  };

  const saveCrmPipeline = async (userId: number, pipelineStage: PipelineStage) => {
      try {
        const apiUrl = getApiUrl();
        const response = await fetch(`${apiUrl}/crm/user/${userId}/pipeline`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ pipelineStage, pipelineId: selectedPipelineId })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
          alert(`Error al guardar: ${errorData.error || 'Error desconocido'}`);
        } else {
          if (selectedPipelineId != null) {
            setCrmPipeline(prev => ({ ...prev, [userId]: pipelineStage }));
            setCrmPipelineId(prev => ({ ...prev, [userId]: selectedPipelineId }));
          }
        }
      } catch (err: any) {
        alert(`Error de conexión: ${err.message}`);
      }
    };

    // Función para guardar cambios de priority en la API
    const saveCrmPriority = async (userId: number, priority: 'high' | 'medium' | 'low') => {
      try {
        await fetch(`${getApiUrl()}/crm/user/${userId}/priority`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ priority })
        });
      } catch (err: any) {
      }
    };

    // Función para guardar cambios de tags en la API
    const saveCrmTags = async (userId: number, tags: string[]) => {
      try {
        await fetch(`${getApiUrl()}/crm/user/${userId}/tags`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ tags })
        });
      } catch (err: any) {
      }
    };

    // Función para guardar cambios de notes en la API
    const saveCrmNotes = async (userId: number, notes: string) => {
      try {
        await fetch(`${getApiUrl()}/crm/user/${userId}/notes`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ notes })
        });
      } catch (err: any) {
      }
    };

  // Función para cargar todas las tablas completas (solo la primera vez)
  const loadAllTables = async () => {
    try {
      // Cargar perfiles personales completos
      const personalResponse = await fetch(`${getApiUrl()}/admin/tables/personal-profiles`, {
        credentials: 'include'
      });
      if (personalResponse.ok) {
        const personalData = await personalResponse.json();
        if (personalData.success) {
          setPersonalProfiles(personalData.data || []);
        }
      }

      // Cargar perfiles institucionales completos
      const institutionalResponse = await fetch(`${getApiUrl()}/admin/tables/institutional-profiles`, {
        credentials: 'include'
      });
      if (institutionalResponse.ok) {
        const institutionalData = await institutionalResponse.json();
        if (institutionalData.success) {
          setInstitutionalProfiles(institutionalData.data || []);
        }
      }
    } catch (err: any) {
      // Error al cargar tablas - silenciado
    }
  };


  // Cargar datos iniciales
  useEffect(() => {
    if (userEmail) {
      loadUsers();
      loadCrmData();
      loadPipelines();
      loadAllTables();
      setLoading(false);
    }
  }, [userEmail]);

  // Cargar etapas cuando cambia el pipeline seleccionado
  useEffect(() => {
    if (selectedPipelineId != null) loadPipelineStages(selectedPipelineId);
  }, [selectedPipelineId]);

  // En pestaña Negocios: cargar negocios (organizaciones con datos del pipeline)
  useEffect(() => {
    if (activeCrmSubTab === 'negocios') {
      loadPipelineNegocios(selectedPipelineId);
    } else {
      setPipelineNegocios([]);
    }
  }, [activeCrmSubTab, selectedPipelineId]);

  // Cargar contactos, organizaciones y negocios para CRM cuando se tiene acceso a la pestaña.
  // Regla: si el usuario tiene acceso a la pestaña principal (CRM), tiene acceso a TODAS sus subpestañas (Contactos, Organizaciones, Negocios).
  // Carga cada recurso por separado para que un fallo en uno no vacíe los demás (p. ej. editores deben ver todas las subpestañas).
  useEffect(() => {
    if (!userEmail || activeMainTab !== 'crm') return;
    const apiBase = getApiUrl();
    setLoadingContacts(true);
    setLoadingOrganizations(true);

    // Contactos
    fetch(`${apiBase}/database/contacts`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(res.status === 403 ? 'Sin permiso' : 'Error al cargar contactos'))))
      .then((contactsData) => {
        if (contactsData.success && Array.isArray(contactsData.data)) setCrmContacts(contactsData.data);
        else setCrmContacts([]);
      })
      .catch(() => setCrmContacts([]))
      .finally(() => setLoadingContacts(false));

    // Organizaciones
    fetch(`${apiBase}/database/organizations`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(res.status === 403 ? 'Sin permiso' : 'Error al cargar organizaciones'))))
      .then((orgData) => {
        if (orgData.success && Array.isArray(orgData.data)) setCrmOrganizations(orgData.data);
        else setCrmOrganizations([]);
      })
      .catch(() => setCrmOrganizations([]))
      .finally(() => setLoadingOrganizations(false));

    // Negocios (lista por organización) — mismo permiso que la pestaña CRM
    fetch(`${apiBase}/crm/organization-negocios`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Error al cargar negocios'))))
      .then((negData) => {
        if (negData.success && Array.isArray(negData.list)) {
          const list = negData.list.map((n: { organizationId: number; pipelineId: number; pipelineName: string; pipelineStage: string; priority: string; data?: Record<string, unknown> | string; assignedContactIds?: number[] }) => ({
            organizationId: n.organizationId,
            pipelineId: n.pipelineId,
            pipelineName: n.pipelineName || '',
            pipelineStage: (n.pipelineStage || 'nuevo').toString(),
            priority: (n.priority || 'medium').toString(),
            data: typeof n.data === 'string' ? (() => { try { return JSON.parse(n.data as string); } catch { return {}; } })() : (n.data && typeof n.data === 'object' ? n.data as Record<string, unknown> : {}),
            assignedContactIds: Array.isArray(n.assignedContactIds) ? n.assignedContactIds : []
          }));
          setOrganizationNegociosList(list);
        } else setOrganizationNegociosList([]);
      })
      .catch(() => setOrganizationNegociosList([]));
  }, [userEmail, activeMainTab, crmContactsRefreshTrigger]);

  const requestedStagesPipelineIdsRef = useRef<Set<number>>(new Set());
  // Cargar etapas por pipeline para el desplegable Etapa en tarjetas Empresas (cada negocio usa las etapas de su pipeline)
  useEffect(() => {
    if (!organizationNegociosList.length) return;
    const pipelineIds = Array.from(new Set(organizationNegociosList.map((n) => n.pipelineId)));
    const toFetch = pipelineIds.filter((pid) => !requestedStagesPipelineIdsRef.current.has(pid));
    const apiUrl = getApiUrl();
    toFetch.forEach((pid) => {
      requestedStagesPipelineIdsRef.current.add(pid);
      fetch(`${apiUrl}/crm/pipeline-stages?pipelineId=${pid}`, { credentials: 'include' })
        .then((r) => r.json())
        .then((data) => {
          if (data.success && Array.isArray(data.stages) && data.stages.length > 0) {
            const sorted = data.stages
              .sort((a: { position: number }, b: { position: number }) => a.position - b.position)
              .map((s: { id: string; label: string; color: string; position: number }) => ({ id: s.id, label: s.label, color: s.color || '#94a3b8', position: s.position }));
            setStagesByPipelineId((p) => ({ ...p, [pid]: sorted }));
          }
        })
        .catch(() => {});
    });
  }, [organizationNegociosList]);

  // Cargar organizaciones de perfiles institucionales y negocios SCESDE26 al entrar a Perfil.
  // Regla: acceso a la pestaña Perfil implica acceso a todas sus subpestañas (Perfil personal, Oradores, Eventos, Iniciativas, Rondas, Perfil institucional).
  // Carga cada recurso por separado para que un fallo en uno no vacíe los demás.
  useEffect(() => {
    if (activeMainTab !== 'participations') return;
    setLoadingPerfilOrganizations(true);
    const apiBase = getApiUrl();
    const scesde26PipelineId = 1;

    // Organizaciones de perfiles institucionales (subpestaña Perfil institucional)
    fetch(`${apiBase}/database/organizations?only_from_profiles=true`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Error al cargar organizaciones'))))
      .then((data) => setPerfilOrganizations(data.success && Array.isArray(data.data) ? data.data : []))
      .catch(() => setPerfilOrganizations([]))
      .finally(() => setLoadingPerfilOrganizations(false));

    // Negocios SCESDE26 por organización (para estado en tarjetas de Perfil institucional)
    fetch(`${apiBase}/crm/pipeline/${scesde26PipelineId}/negocios`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : { success: false, negocios: [] }))
      .then((data) => {
        const negocios = data.success && Array.isArray(data.negocios) ? data.negocios : [];
        const byOrg: Record<number, { pipelineStage: string; priority: string }> = {};
        negocios.forEach((n: { organizationId: number; pipelineStage?: string; priority?: string }) => {
          byOrg[n.organizationId] = {
            pipelineStage: (n.pipelineStage ?? 'nuevo').toString(),
            priority: (n.priority ?? 'medium').toString()
          };
        });
        setPerfilInstScesde26Negocios(byOrg);
      })
      .catch(() => setPerfilInstScesde26Negocios({}));

    // Etapas del pipeline SCESDE26 (para desplegables en Perfil institucional)
    fetch(`${apiBase}/crm/pipeline-stages?pipelineId=${scesde26PipelineId}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        if (data.success && Array.isArray(data.stages) && data.stages.length > 0) {
          const sorted = data.stages
            .sort((a: { position: number }, b: { position: number }) => a.position - b.position)
            .map((s: { id: string; label: string; color: string; position: number }) => ({ id: s.id, label: s.label, color: s.color || '#94a3b8', position: s.position }));
          setStagesByPipelineId((p) => ({ ...p, [scesde26PipelineId]: sorted }));
        }
      })
      .catch(() => {});
  }, [activeMainTab]);

  // Recargar tablas cuando se cambia a modo tabla (para actualizar)
  useEffect(() => {
    if (userEmail && activeViewMode === 'table') {
      loadAllTables();
    }
  }, [activeViewMode, userEmail]);

  // Funciones helper para transformar datos del perfil
  const safeJsonParse = (value: any, defaultValue: any = null): any => {
    if (!value || value === '' || value === 'null' || value === 'undefined') {
      return defaultValue;
    }
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch (e) {
        return defaultValue;
      }
    }
    return value;
  };

  const parsePhone = (phone: string): { codigoPais: string; numeroTelefono: string } => {
    if (!phone || !phone.trim()) {
      return { codigoPais: 'AR', numeroTelefono: '' };
    }
    const phoneStr = String(phone).trim();
    const match = phoneStr.match(/^\+?(\d{1,4})\s*(.+)$/);
    if (match) {
      const dialCode = match[1];
      const number = match[2].replace(/\s/g, '');
      const dialCodeToCountry: Record<string, string> = {
        '54': 'AR', '55': 'BR', '56': 'CL', '57': 'CO', '52': 'MX', '51': 'PE',
        '598': 'UY', '58': 'VE', '591': 'BO', '593': 'EC', '595': 'PY', '506': 'CR',
        '507': 'PA', '502': 'GT', '504': 'HN', '503': 'SV', '505': 'NI', '53': 'CU',
        '1': 'US', '34': 'ES', '39': 'IT', '33': 'FR', '49': 'DE', '44': 'GB'
      };
      const countryCode = dialCodeToCountry[dialCode] || 'AR';
      return { codigoPais: countryCode, numeroTelefono: number };
    }
    return { codigoPais: 'AR', numeroTelefono: phoneStr.replace(/^\+?\d{1,4}\s*/, '') };
  };

  const transformDbProfileToProfileData = (dbProfile: any): ProfileData => {
    const phoneData = parsePhone(dbProfile.phone || '');
    // Procesar el logo: convertir URL de Supabase a URL pública si es necesario
    let logoUrl = dbProfile.logo || '';
    if (logoUrl && typeof logoUrl === 'string' && logoUrl.trim() !== '') {
      logoUrl = logoUrl.trim();
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
      
      if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
        if (logoUrl.includes('supabase.co') && logoUrl.includes('/storage/v1/object/sign/')) {
          const match = logoUrl.match(/\/storage\/v1\/object\/sign\/([^?]+)/);
          if (match && supabaseUrl) {
            const filePath = match[1];
            const pathParts = filePath.split('/');
            if (pathParts.length >= 2) {
              const bucket = pathParts[0];
              const fileName = pathParts.slice(1).join('/');
              logoUrl = `${supabaseUrl}/storage/v1/object/public/${bucket}/${fileName}`;
            } else {
              logoUrl = `${supabaseUrl}/storage/v1/object/public/perfil-institucional/logos/${filePath}`;
            }
          }
        }
      } else if (logoUrl.startsWith('/storage/files/')) {
        const filePath = logoUrl.replace('/storage/files/', '');
        if (supabaseUrl) {
          logoUrl = `${supabaseUrl}/storage/v1/object/public/${filePath}`;
        }
      } else if (!logoUrl.includes('/') || logoUrl.split('/').length <= 2) {
        if (supabaseUrl) {
          logoUrl = `${supabaseUrl}/storage/v1/object/public/perfil-institucional/logos/${logoUrl}`;
        }
      }
    }

    // Función helper para procesar iniciativas
    const processInitiatives = () => {
      try {
        let allInitiatives = [];
        if (dbProfile.iniciativas) {
          if (typeof dbProfile.iniciativas === 'string') {
            allInitiatives = JSON.parse(dbProfile.iniciativas);
          } else {
            allInitiatives = dbProfile.iniciativas;
          }
        }
        if (!Array.isArray(allInitiatives)) return [];
        return allInitiatives.map((init: any) => ({
          id: init.id || Date.now().toString() + Math.random().toString(36).substr(2, 9),
          type: init.type || '',
          name: init.name || '',
          shortDescription: init.shortDescription || '',
          description: init.description || '',
          currentStatus: init.currentStatus || '',
          thematicAxes: Array.isArray(init.thematicAxes) ? init.thematicAxes : [],
          organizationArea: init.organizationArea || '',
          territory: init.territory || '',
          population: init.population || '',
          results: init.results || '',
          approach: init.approach || '',
          alliances: init.alliances || '',
          expectedImpact: init.expectedImpact || '',
          specificNeeds: Array.isArray(init.specificNeeds) ? init.specificNeeds : [],
          supportDocuments: Array.isArray(init.supportDocuments) ? init.supportDocuments : [],
          requestApproval: init.requestApproval || false,
          noContact: init.noContact || false,
          contactFirstName: init.contactFirstName || '',
          contactLastName: init.contactLastName || '',
          contactPosition: init.contactPosition || '',
          contactEmail: init.contactEmail || '',
          contactPhone: init.contactPhone || '',
          contactCodigoPais: init.contactCodigoPais || '',
          contactNumeroTelefono: init.contactNumeroTelefono || '',
          interestedInBusinessRounds: init.interestedInBusinessRounds || false,
          interestedInBusinessRoundsOutsideEvent: init.interestedInBusinessRoundsOutsideEvent || false,
          solutionType: init.solutionType || '',
          technologiesUsed: Array.isArray(init.technologiesUsed) ? init.technologiesUsed : [],
          estimatedBeneficiaries: init.estimatedBeneficiaries || '',
          policyType: init.policyType || ''
        }));
      } catch (error) {
        return [];
      }
    };

    return {
      basicInfo: {
        organizationName: dbProfile.organization_name || '',
        organizationType: dbProfile.organization_type || '',
        country: dbProfile.country || '',
        website: dbProfile.website || '',
        industries: safeJsonParse(dbProfile.industries, []),
        thematicAxes: safeJsonParse(dbProfile.organization_thematic_axes, []),
        logo: logoUrl,
        socialNetworks: safeJsonParse(dbProfile.social_networks, {}),
        eventProgramParticipation: safeJsonParse(dbProfile.event_program_participation, []),
      },
      contactInfo: {
        firstName: dbProfile.first_name || '',
        lastName: dbProfile.last_name || '',
        position: dbProfile.position || '',
        email: dbProfile.email || '',
        phone: dbProfile.phone || '',
        codigoPais: dbProfile.codigo_pais || phoneData.codigoPais,
        numeroTelefono: dbProfile.numero_telefono || phoneData.numeroTelefono,
        area: dbProfile.area || '',
        subarea: dbProfile.subarea || '',
        thematicAxes: safeJsonParse(dbProfile.personal_thematic_axes, []),
        wantsToManageProfile: dbProfile.wants_to_manage_profile || false,
        wantsToParticipateBusinessRounds: dbProfile.wants_to_participate_business_rounds || false,
      },
      catalogSections: safeJsonParse(dbProfile.catalog_sections, []),
      institutionalDescription: {
        description: dbProfile.description || '',
      },
      participationModalities: safeJsonParse(dbProfile.participation_modalities, []),
      agreedModality: dbProfile.agreed_modality || '',
      modalityObservations: dbProfile.modality_observations || '',
      selectedStands: safeJsonParse(dbProfile.selected_stands_updated, []),
      selectedStandsUpdated: safeJsonParse(dbProfile.selected_stands_updated, undefined),
      confirmedStands: safeJsonParse(dbProfile.confirmed_stands, []),
      speakers: safeJsonParse(dbProfile.speakers, []),
      parallelEvents: safeJsonParse(dbProfile.parallel_events, []),
      businessRoundsParticipants: safeJsonParse(dbProfile.business_rounds_participants, []),
      initiatives: processInitiatives(),
      requestInformation: dbProfile.request_information === 1 || dbProfile.request_information === true,
      requestExpoInformation: dbProfile.request_expo_information === 1 || dbProfile.request_expo_information === true,
    };
  };

  // Función para cargar el perfil completo de un usuario
  const loadFullProfile = async (userId: number) => {
    setLoadingFullProfile(true);
    setFullProfileData(null); // Limpiar datos anteriores
    try {
      const response = await fetch(`${getApiUrl()}/admin/user-profile/${userId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setFullProfileData(null);
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        if (data.profile) {
          // Transformar los datos del perfil
          try {
            const transformedProfile = transformDbProfileToProfileData(data.profile);
            setFullProfileData(transformedProfile);
            // Inicializar valores editables
            setEditableAgreedModality(data.profile.agreed_modality || '');
            setEditableModalityObservations(data.profile.modality_observations || '');
            setEditableConfirmedStands(safeJsonParse(data.profile.confirmed_stands, []));
          } catch (transformError) {
            setFullProfileData(null);
          }
        } else {
          // No hay perfil, pero la respuesta fue exitosa
          setFullProfileData(null);
        }
      } else {
        setFullProfileData(null);
      }
    } catch (error) {
      setFullProfileData(null);
    } finally {
      setLoadingFullProfile(false);
    }
  };

  // Función para abrir el panel de detalle guardando el ID del usuario
  const handleOpenCrmDetail = (user: User, restoreTabs: boolean = false) => {
    // Guardar el ID del usuario seleccionado
    selectedUserIdRef.current = user.id;
    // Si no estamos en CRM, guardar el estado antes de cambiar
    const currentTab = activeMainTab;
    if (currentTab !== 'crm') {
      setPreviousMainTab(currentTab);
      setActiveMainTab('crm');
    } else {
      // Si ya estamos en CRM, limpiar el participationIdRef porque no viene de participaciones
      selectedParticipationIdRef.current = null;
    }
    setSelectedCrmUser(user);
    // Solo resetear las pestañas si no estamos restaurando desde sessionStorage
    if (!restoreTabs) {
      setActiveMainProfileTab('overview');
      setActiveProfileSubTab('institutional');
    }
    // Inicializar valores editables desde el usuario (valores temporales hasta que se cargue el perfil completo)
    setEditableAgreedModality(user.agreed_modality || '');
    setEditableModalityObservations(''); // Este campo no está en User, se cargará del perfil completo
    setEditableConfirmedStands([]); // Este campo no está en User, se cargará del perfil completo
    // Cargar el perfil completo
    loadFullProfile(user.id);
  };

  // Scroll hacia arriba cuando se abre el panel de detalle y hacer scroll al elemento al cerrar
  useEffect(() => {
    const currentUserId = selectedCrmUser?.id ?? null;
    const previousUserId = previousSelectedCrmUserId.current;
    
    // Solo hacer scroll si realmente se abrió o cerró el panel (cambio de null a usuario o viceversa)
    // No hacer scroll si solo se actualizó el mismo usuario
    if (selectedCrmUser && previousUserId === null) {
      // Se abrió el panel (de null a un usuario)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (!selectedCrmUser && previousUserId !== null) {
      // Se cerró el panel (de un usuario a null)
      if (selectedUserIdRef.current) {
        const userId = selectedUserIdRef.current;
        setTimeout(() => {
          const element = document.querySelector(`[data-user-id="${userId}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          selectedUserIdRef.current = null;
        }, 100);
      }
    }
    
    // Actualizar la referencia del usuario anterior
    previousSelectedCrmUserId.current = currentUserId;
  }, [selectedCrmUser]);

  // Redirigir a 'overview' si el usuario no tiene perfil institucional y está en una pestaña que lo requiere
  useEffect(() => {
    if (selectedCrmUser && fullProfileData) {
      const hasInstitutionalProfile = fullProfileData?.contactInfo?.wantsToManageProfile || false;
      if (!hasInstitutionalProfile && (activeMainProfileTab === 'modality' || activeMainProfileTab === 'participations' || activeMainProfileTab === 'profile')) {
        setActiveMainProfileTab('overview');
      }
    }
  }, [selectedCrmUser, fullProfileData, activeMainProfileTab]);

  // Función para guardar el estado en sessionStorage
  const saveState = () => {
    try {
      const state = {
        activeMainTab,
        activeParticipationsTab,
        activeViewMode,
        selectedCrmUserId: selectedCrmUser?.id || null,
        selectedCrmContactId: selectedCrmContact?.id || null,
        selectedCrmOrganizationId: selectedCrmOrganization?.id || null,
        activeMainProfileTab,
        activeProfileSubTab,
        pipelineView,
        listViewMode,
        activeCrmSubTab,
        scrollPosition: window.scrollY || window.pageYOffset,
        activeConversationsSubTab,
        activeComunicacionesSubTab
      };
      sessionStorage.setItem('adminPanelState', JSON.stringify(state));
    } catch (e) {
    }
  };

  // Guardar estado cuando cambie (pero no durante la restauración inicial)
  useEffect(() => {
    // No guardar si estamos en proceso de restauración
    if (isRestoringState.current) {
      return;
    }
    // Esperar un poco antes de guardar para evitar guardar durante la restauración inicial
    const timeoutId = setTimeout(() => {
      if (hasRestoredState.current) {
        saveState();
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [activeMainTab, activeParticipationsTab, activeViewMode, selectedCrmUser, selectedCrmContact, selectedCrmOrganization, activeMainProfileTab, activeProfileSubTab, pipelineView, listViewMode, activeCrmSubTab, activeConversationsSubTab, activeComunicacionesSubTab]);

  // Guardar posición del scroll periódicamente
  useEffect(() => {
    const handleScroll = () => {
      try {
        const currentState = sessionStorage.getItem('adminPanelState');
        if (currentState) {
          const parsed = JSON.parse(currentState);
          parsed.scrollPosition = window.scrollY || window.pageYOffset;
          sessionStorage.setItem('adminPanelState', JSON.stringify(parsed));
        }
      } catch (e) {
        // Ignorar errores
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Restaurar posición del scroll y usuario seleccionado al cargar (solo una vez)
  const hasRestoredState = useRef(false);
  const isRestoringState = useRef(false);
  useEffect(() => {
    if (!loading && users.length > 0 && !hasRestoredState.current) {
      hasRestoredState.current = true;
      isRestoringState.current = true;
      
      // Restaurar pestañas del perfil si hay un usuario seleccionado
      if (initialState.selectedCrmUserId) {
        const userToSelect = users.find(u => u.id === initialState.selectedCrmUserId);
        if (userToSelect) {
          // Restaurar pestañas antes de abrir el detalle
          setActiveMainProfileTab(initialState.activeMainProfileTab);
          setActiveProfileSubTab(initialState.activeProfileSubTab);
          
          // Pequeño delay para asegurar que el DOM esté listo
          setTimeout(() => {
            handleOpenCrmDetail(userToSelect, true); // true = restaurar pestañas
            // Restaurar posición del scroll después de que se cargue el perfil
            setTimeout(() => {
              window.scrollTo({ top: initialState.scrollPosition, behavior: 'auto' });
              // Marcar que terminó la restauración después de un delay adicional
              setTimeout(() => {
                isRestoringState.current = false;
              }, 500);
            }, 800);
          }, 100);
        } else {
          // Si no se encuentra el usuario, restaurar solo la posición del scroll
          setTimeout(() => {
            window.scrollTo({ top: initialState.scrollPosition, behavior: 'auto' });
            isRestoringState.current = false;
          }, 100);
        }
      } else if ((initialState as { selectedCrmContactId?: number }).selectedCrmContactId && crmContacts.length > 0) {
        const contactId = (initialState as { selectedCrmContactId?: number }).selectedCrmContactId;
        const contactToSelect = crmContacts.find(c => c.id === contactId);
        if (contactToSelect) {
          setSelectedCrmContact(contactToSelect);
          setActiveMainTab('crm');
          setActiveCrmSubTab('contactos');
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'auto' });
            isRestoringState.current = false;
          }, 100);
        } else {
          setTimeout(() => {
            window.scrollTo({ top: initialState.scrollPosition, behavior: 'auto' });
            isRestoringState.current = false;
          }, 100);
        }
      } else if ((initialState as { selectedCrmOrganizationId?: number }).selectedCrmOrganizationId) {
        setActiveMainTab('crm');
        setActiveCrmSubTab('empresas');
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'auto' });
          isRestoringState.current = false;
        }, 100);
      } else {
        // Si no hay usuario seleccionado, restaurar solo la posición del scroll
        setTimeout(() => {
          window.scrollTo({ top: initialState.scrollPosition, behavior: 'auto' });
          isRestoringState.current = false;
        }, 100);
      }
    }
  }, [loading, users]);

  // Restaurar contacto seleccionado cuando crmContacts estén cargados (p. ej. tras refrescar)
  useEffect(() => {
    if (crmContacts.length === 0 || selectedCrmContact != null || selectedCrmUser != null) return;
    try {
      const saved = sessionStorage.getItem('adminPanelState');
      if (!saved) return;
      const parsed = JSON.parse(saved);
      const contactId = parsed.selectedCrmContactId;
      if (contactId == null) return;
      const contact = crmContacts.find(c => c.id === contactId);
      if (contact) {
        setSelectedCrmContact(contact);
        setActiveMainTab('crm');
        setActiveCrmSubTab('contactos');
      }
    } catch (e) {
      // ignore
    }
  }, [crmContacts, selectedCrmContact, selectedCrmUser]);

  // Restaurar organización seleccionada cuando crmOrganizations estén cargados (p. ej. tras refrescar)
  useEffect(() => {
    if (crmOrganizations.length === 0 || selectedCrmOrganization != null || selectedCrmContact != null || selectedCrmUser != null) return;
    try {
      const saved = sessionStorage.getItem('adminPanelState');
      if (!saved) return;
      const parsed = JSON.parse(saved);
      const orgId = parsed.selectedCrmOrganizationId;
      if (orgId == null) return;
      const org = crmOrganizations.find(o => o.id === orgId);
      if (org) {
        setSelectedCrmOrganization(org);
        setActiveMainTab('crm');
        setActiveCrmSubTab('empresas');
      }
    } catch (e) {
      // ignore
    }
  }, [crmOrganizations, selectedCrmOrganization, selectedCrmContact, selectedCrmUser]);

  // Cargar negocios del contacto cuando se abre la página individual del contacto
  useEffect(() => {
    if (!selectedCrmContact) {
      setContactDetailNegocios([]);
      setActiveContactDetailNegocioIndex(0);
      setActiveContactDetailOrgIndex(0);
      return;
    }
    setActiveContactDetailNegocioIndex(0);
    setActiveContactDetailOrgIndex(0);
    setLoadingContactDetailNegocios(true);
    const apiUrl = getApiUrl();
    fetch(`${apiUrl}/crm/contacts/${selectedCrmContact.id}/negocios`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Error al cargar negocios del contacto'))))
      .then((data) => {
        if (data.success && Array.isArray(data.list)) {
          setContactDetailNegocios(data.list.map((n: { organizationId: number; pipelineId: number; pipelineName: string; pipelineStage: string; priority: string; data?: Record<string, unknown>; organizationName: string }) => ({
            organizationId: n.organizationId,
            pipelineId: n.pipelineId,
            pipelineName: n.pipelineName || '',
            pipelineStage: (n.pipelineStage || 'nuevo').toString(),
            priority: (n.priority || 'medium').toString(),
            data: n.data,
            organizationName: n.organizationName || ''
          })));
        } else {
          setContactDetailNegocios([]);
        }
      })
      .catch(() => setContactDetailNegocios([]))
      .finally(() => setLoadingContactDetailNegocios(false));
  }, [selectedCrmContact]);

  // Al volver de OAuth de Gmail, ir a Conversaciones > Correo y limpiar la URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gmail = params.get('gmail');
    if (gmail === 'connected' || gmail === 'error') {
      setActiveMainTab('conversations');
      setActiveConversationsSubTab('correo');
      const url = new URL(window.location.href);
      url.searchParams.delete('gmail');
      url.searchParams.delete('message');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, []);

  // Al volver de OAuth de Mailup, ir a la pestaña Base de datos y limpiar la URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mailup = params.get('mailup');
    if (mailup === 'connected' || mailup === 'error') {
      setActiveMainTab('database');
      const url = new URL(window.location.href);
      url.searchParams.delete('mailup');
      url.searchParams.delete('message');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, []);

  // Verificar tema oscuro
  useEffect(() => {
    const checkTheme = () => {
      const isDark = document.documentElement.classList.contains('dark-theme');
      setIsDarkMode(isDark);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Funciones para cargar métricas y errores
  const loadSystemMetrics = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/admin/system-metrics`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // El backend devuelve pools.main y pools.session
          setSystemMetrics({
            mainPool: data.pools?.main || {},
            sessionPool: data.pools?.session || {},
            limits: data.limits || {},
            recentErrors: data.recentErrors || {}
          });
          setActiveVisits(data.activeVisits || { total: 0, uniqueUsers: 0 });
        }
      }
    } catch (err) {
    }
  };

  const loadErrorLogs = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/admin/errors`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setErrors(data.errors || []);
          setErrorsStats(data.stats || []);
          setErrorsTotal(data.total || 0);
        }
      }
    } catch (err) {
    }
  };

  // Cargar métricas y errores cuando se activa el monitoreo
  useEffect(() => {
    if (activeMainTab === 'monitoring') {
      loadSystemMetrics();
      loadErrorLogs();
      
      // Actualizar cada 10 segundos mientras está activo (más rápido para detectar problemas)
      const interval = setInterval(() => {
        loadSystemMetrics();
        loadErrorLogs();
      }, 10000);
      
      return () => clearInterval(interval);
    }
  }, [activeMainTab]);


  // Manejar logout
  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      // Limpiar localStorage y sessionStorage
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      localStorage.removeItem('theme');
      sessionStorage.removeItem('oauthParams');
      sessionStorage.setItem('logoutTimestamp', Date.now().toString());

      // Disparar evento de logout para que App.tsx lo detecte
      localStorage.setItem('logoutEvent', Date.now().toString());
      localStorage.removeItem('logoutEvent'); // Remover inmediatamente para que el evento se dispare

      // Remover tema oscuro
      document.documentElement.classList.remove('dark-theme');
      document.documentElement.classList.add('light-theme');

      // Hacer logout en el servidor
      await authLogout();

      // Forzar recarga completa de la página para que App.tsx verifique la sesión
      window.location.href = '/';
    } catch (error) {

      // Aún así, forzar recarga para mostrar el modal de login
      window.location.href = '/';
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Manejar eliminación de cuenta
  const handleDeleteAccount = () => {
    // No permitir que el admin elimine su cuenta desde aquí
    alert('Los administradores no pueden eliminar su cuenta desde el panel.');
  };

  // Limpiar todos los filtros del CRM
  const handleClearFilters = () => {
    setFilterOrganizationType([]);
    setSearchTerm('');
  };

  // Limpiar todos los filtros de Participaciones
  const handleClearParticipationsFilters = () => {
    setFilterOrganizationType([]);
    setFilterModalities([]);
    setFilterExpoZone('all');
    setFilterParticipations([]);
    setFilterStates([]);
    setFilterPipelineStages([]);
    setFilterPriorities([]);
    setFilterPendingApprovals('all');
    setFilterParticipationStatus('all');
    setFilterParticipationCompletion('all');
    setFilterParticipationRequested('all');
    setFilterParticipationApproval('all');
    setSearchTerm('');
  };

  // Obtener modalidades únicas de todos los usuarios
  const uniqueModalities = useMemo(() => {
    const modalitiesSet = new Set<string>();
    users.forEach(user => {
      if (user.participation_modalities) {
        const mods = Array.isArray(user.participation_modalities) 
          ? user.participation_modalities 
          : (typeof user.participation_modalities === 'string' 
            ? (() => {
                try {
                  const parsed = JSON.parse(user.participation_modalities);
                  return Array.isArray(parsed) ? parsed : [parsed];
                } catch {
                  return [user.participation_modalities];
                }
              })()
            : []);
        mods.forEach((mod: string) => modalitiesSet.add(mod));
      }
    });
    return Array.from(modalitiesSet);
  }, [users]);

  /** Si hay algún filtro activo (para mostrar indicador en el botón Filtros). Solo Tipo Organización y búsqueda. */
  const hasActiveFilters = useMemo(() => {
    return searchTerm.trim() !== '' || filterOrganizationType.length > 0;
  }, [searchTerm, filterOrganizationType]);

  // Funciones helper para calcular el estado de las participaciones (reutilizables, deben estar antes de filteredUsers)
  const getSpeakerStatusHelper = (speaker: any): { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } => {
    if (!speaker) return { completionStatus: 'incomplete', hasRequestApproval: false };
    const hasPhoto = speaker.photo && (typeof speaker.photo === 'string' ? speaker.photo.trim() !== '' : true);
    const hasFirstName = speaker.firstName && speaker.firstName.toString().trim() !== '';
    const hasLastName = speaker.lastName && speaker.lastName.toString().trim() !== '';
    const hasNationality = speaker.nationality && speaker.nationality.toString().trim() !== '';
    const hasOrganization = speaker.organization && speaker.organization.toString().trim() !== '';
    const hasPosition = speaker.position && speaker.position.toString().trim() !== '';
    const hasDescription = speaker.description && speaker.description.toString().trim() !== '';
    const hasThematicAxes = speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0;
    const hasContentDescription = speaker.contentDescription && speaker.contentDescription.toString().trim() !== '';
    const hasLinkedin = speaker.linkedin && speaker.linkedin.toString().trim() !== '';
    const hasBiography = speaker.biography && speaker.biography.toString().trim() !== '';
    const hasValidBiography = hasLinkedin || hasBiography;
    const isComplete = hasPhoto && hasFirstName && hasLastName && hasNationality && hasOrganization &&
                       hasPosition && hasDescription && hasThematicAxes && hasContentDescription && hasValidBiography;
    return { completionStatus: isComplete ? 'complete' : 'incomplete', hasRequestApproval: speaker.requestApproval === true };
  };
  const getEventStatusHelper = (event: any): { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } => {
    if (!event) return { completionStatus: 'incomplete', hasRequestApproval: false };
    const hasName = event.name && event.name.toString().trim() !== '';
    const hasDescription = event.description && event.description.toString().trim() !== '';
    const hasSpeakers = (event.speakerIds && event.speakerIds.length > 0) || (event.localSpeakers && event.localSpeakers.length > 0);
    const hasDuration = event.duration && event.duration.toString().trim() !== '';
    const hasThematicAxes = event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0;
    const hasAttendeeProfiles = event.attendeeProfiles && Array.isArray(event.attendeeProfiles) && event.attendeeProfiles.length > 0;
    const isComplete = hasName && hasDescription && hasSpeakers && hasDuration && hasThematicAxes && hasAttendeeProfiles;
    return { completionStatus: isComplete ? 'complete' : 'incomplete', hasRequestApproval: event.requestApproval === true };
  };
  const getInitiativeStatusHelper = (initiative: any): { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } => {
    if (!initiative) return { completionStatus: 'incomplete', hasRequestApproval: false };
    const hasName = initiative.name && initiative.name.toString().trim() !== '';
    const hasShortDescription = initiative.shortDescription && initiative.shortDescription.toString().trim() !== '';
    const hasDescription = initiative.description && initiative.description.toString().trim() !== '';
    const hasThematicAxes = initiative.thematicAxes && Array.isArray(initiative.thematicAxes) && initiative.thematicAxes.length > 0;
    let isComplete = hasName && hasShortDescription && hasDescription && hasThematicAxes;
    if (!initiative.noContact) {
      const hasContactFirstName = initiative.contactFirstName && initiative.contactFirstName.trim() !== '';
      const hasContactLastName = initiative.contactLastName && initiative.contactLastName.trim() !== '';
      const hasContactPosition = initiative.contactPosition && initiative.contactPosition.trim() !== '';
      const hasContactEmail = initiative.contactEmail && initiative.contactEmail.trim() !== '';
      isComplete = isComplete && hasContactFirstName && hasContactLastName && hasContactPosition && hasContactEmail;
    }
    return { completionStatus: isComplete ? 'complete' : 'incomplete', hasRequestApproval: initiative.requestApproval === true };
  };
  const getBusinessRoundsStatusHelper = (participant: any): 'complete' | 'incomplete' => {
    if (!participant) return 'incomplete';
    const hasFirstName = participant.firstName && participant.firstName.trim() !== '';
    const hasLastName = participant.lastName && participant.lastName.trim() !== '';
    const hasPosition = participant.position && participant.position.trim() !== '';
    const hasOrganization = participant.organization && participant.organization.trim() !== '';
    const hasEmail = participant.email && participant.email.trim() !== '';
    const hasPhone = participant.phone && participant.phone.trim() !== '' || (participant.numeroTelefono && participant.numeroTelefono.trim() !== '');
    const hasThematicAxes = participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0;
    return (hasFirstName && hasLastName && hasPosition && hasOrganization && hasEmail && hasPhone && hasThematicAxes) ? 'complete' : 'incomplete';
  };

  // Filtrar usuarios con todos los filtros
  const filteredUsers = users.filter(user => {
    // Filtro de búsqueda por texto
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || (
      user.email.toLowerCase().includes(searchLower) ||
      (user.organization_name && user.organization_name.toLowerCase().includes(searchLower)) ||
      (user.first_name && user.first_name.toLowerCase().includes(searchLower)) ||
      (user.last_name && user.last_name.toLowerCase().includes(searchLower))
    );

    // Filtro por tipo de organización
    const matchesOrganizationType = filterOrganizationType.length === 0 || 
      (user.organization_type && filterOrganizationType.includes(user.organization_type));

    return matchesSearch && matchesOrganizationType;
  });

  // Ordenar usuarios filtrados por fecha de registro descendente (fijo)
  const sortedUsers = useMemo(() => {
    const sorted = [...filteredUsers];

    sorted.sort((a, b) => {
      const aCreated = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bCreated = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bCreated - aCreated; // Descendente
    });

    return sorted;
  }, [filteredUsers]);

  // Contactos CRM (tabla crm_contacts): filtrado por búsqueda y tipo de organización
  const filteredCrmContacts = useMemo(() => {
    let list = crmContacts;
    if (filterOrganizationType.length > 0) {
      const orgIdsByType = new Set(
        crmOrganizations
          .filter((o) => o.tipo_organizacion && filterOrganizationType.includes(o.tipo_organizacion))
          .map((o) => o.id)
      );
      list = list.filter((c) => {
        const ids = getContactOrgIds(c);
        return ids.some((id) => orgIdsByType.has(id));
      });
    }
    if (!searchTerm.trim()) return list;
    const term = searchTerm.toLowerCase().trim();
    return list.filter((c) => {
      const nombre = (c.nombre || '').toLowerCase();
      const apellido = (c.apellido || '').toLowerCase();
      const correo = (c.correo || '').toLowerCase();
      const org = ((c.organization_name || c.organization_name_from_org) || '').toLowerCase();
      const cargo = (c.cargo || '').toLowerCase();
      return nombre.includes(term) || apellido.includes(term) || correo.includes(term) || org.includes(term) || cargo.includes(term);
    });
  }, [crmContacts, crmOrganizations, searchTerm, filterOrganizationType]);

  // Organizaciones CRM (tabla crm_organizations): filtrado por búsqueda
  // Organizaciones de la subtab Perfil Institucional (backend ya devuelve solo source_user_id IS NOT NULL)
  const filteredPerfilOrganizations = useMemo(() => {
    if (!searchTerm.trim()) return perfilOrganizations;
    const term = searchTerm.toLowerCase().trim();
    return perfilOrganizations.filter((o) => {
      const org = (o.organizacion || '').toLowerCase();
      const tipo = (o.tipo_organizacion || '').toLowerCase();
      return org.includes(term) || tipo.includes(term);
    });
  }, [perfilOrganizations, searchTerm]);

  const PERFIL_CARDS_PER_PAGE = 50;
  const PERFIL_TABLE_ROWS_PER_PAGE = 100;
  const perfilPersonalPerPage = listViewMode === 'cards' ? PERFIL_CARDS_PER_PAGE : PERFIL_TABLE_ROWS_PER_PAGE;
  const perfilInstitucionalPerPage = listViewMode === 'cards' ? PERFIL_CARDS_PER_PAGE : PERFIL_TABLE_ROWS_PER_PAGE;

  const perfilPersonalPaginated = useMemo(() => {
    const start = (perfilPersonalPage - 1) * perfilPersonalPerPage;
    return sortedUsers.slice(start, start + perfilPersonalPerPage);
  }, [sortedUsers, perfilPersonalPage, perfilPersonalPerPage]);
  const totalPerfilPersonalPages = Math.max(1, Math.ceil(sortedUsers.length / perfilPersonalPerPage));

  const perfilInstitucionalPaginated = useMemo(() => {
    const start = (perfilInstitucionalPage - 1) * perfilInstitucionalPerPage;
    return filteredPerfilOrganizations.slice(start, start + perfilInstitucionalPerPage);
  }, [filteredPerfilOrganizations, perfilInstitucionalPage, perfilInstitucionalPerPage]);
  const totalPerfilInstitucionalPages = Math.max(1, Math.ceil(filteredPerfilOrganizations.length / perfilInstitucionalPerPage));

  const filteredCrmOrganizations = useMemo(() => {
    let list = crmOrganizations;
    if (filterOrganizationType.length > 0) {
      list = list.filter((o) => o.tipo_organizacion && filterOrganizationType.includes(o.tipo_organizacion));
    }
    if (!searchTerm.trim()) return list;
    const term = searchTerm.toLowerCase().trim();
    return list.filter((o) => {
      const org = (o.organizacion || '').toLowerCase();
      const tipo = (o.tipo_organizacion || '').toLowerCase();
      const desc = (o.descripcion || '').toLowerCase();
      return org.includes(term) || tipo.includes(term) || desc.includes(term);
    });
  }, [crmOrganizations, searchTerm, filterOrganizationType]);

  // Negocios por organización (para tarjetas Empresas: pipeline, etapa, prioridad, data; para Contactos se filtra por assignedContactIds)
  const negociosByOrgId = useMemo(() => {
    const map = new Map<number, { pipelineId: number; pipelineName: string; pipelineStage: string; priority: string; data?: Record<string, unknown>; assignedContactIds?: number[] }[]>();
    organizationNegociosList.forEach((n) => {
      const list = map.get(n.organizationId) || [];
      list.push({ pipelineId: n.pipelineId, pipelineName: n.pipelineName, pipelineStage: n.pipelineStage, priority: n.priority, data: n.data, assignedContactIds: n.assignedContactIds });
      map.set(n.organizationId, list);
    });
    return map;
  }, [organizationNegociosList]);

  // IDs de organizaciones de un contacto (varias si existe organization_ids, si no el organization_id)
  const getContactOrgIds = (contact: CrmContact): number[] => {
    const ids = contact.organization_ids;
    if (Array.isArray(ids) && ids.length > 0) return ids.filter((id) => id != null);
    if (contact.organization_id != null) return [contact.organization_id];
    return [];
  };

  // Helpers para teléfono (igual lógica que ContactInfoSection): parsear y formatear para CRM
  const DIAL_CODES: Record<string, string> = { AR: '+54', BR: '+55', CL: '+56', CO: '+57', MX: '+52', PE: '+51', UY: '+598', VE: '+58', BO: '+591', EC: '+593', PY: '+595', CR: '+506', PA: '+507', US: '+1', CA: '+1', ES: '+34', IT: '+39', FR: '+33', DE: '+49', GB: '+44', PT: '+351', NL: '+31', BE: '+32', CH: '+41', AT: '+43', SE: '+46', NO: '+47', DK: '+45', FI: '+358', PL: '+48', CZ: '+420', RU: '+7', CN: '+86', JP: '+81', KR: '+82', IN: '+91', AU: '+61', ZA: '+27' };
  const parsePhoneForCrm = (phone: string | null | undefined): { codigoPais: string; numeroTelefono: string } => {
    if (!phone || !phone.trim()) return { codigoPais: 'AR', numeroTelefono: '' };
    const trimmed = phone.trim();
    const entries = Object.entries(DIAL_CODES).sort((a, b) => (b[1].length - a[1].length));
    for (const [code, dialCode] of entries) {
      if (trimmed.startsWith(dialCode)) {
        return { codigoPais: code, numeroTelefono: trimmed.slice(dialCode.length).trim() };
      }
    }
    return { codigoPais: 'AR', numeroTelefono: trimmed };
  };
  const formatPhoneForCrm = (codigoPais: string, numeroTelefono: string): string => {
    const dial = DIAL_CODES[codigoPais] || '+54';
    return numeroTelefono ? `${dial} ${numeroTelefono}`.trim() : '';
  };

  const capitalizeFirstLetter = (str: string): string => {
    if (!str) return '';
    const firstNonSpaceIndex = str.search(/\S/);
    if (firstNonSpaceIndex === -1) return str;
    return str.slice(0, firstNonSpaceIndex) + str[firstNonSpaceIndex].toUpperCase() + str.slice(firstNonSpaceIndex + 1);
  };

  // Mapeo CRM contact <-> ContactInfo para reutilizar ContactInfoSection en el modal de edición
  const crmContactToContactInfo = (c: Partial<CrmContact> | null): ContactInfo => {
    if (!c) return { firstName: '', lastName: '', position: '', email: '', phone: '', codigoPais: 'AR', numeroTelefono: '', area: '', subarea: '', linkedin: '', thematicAxes: [] };
    const { codigoPais, numeroTelefono } = parsePhoneForCrm(c.telefono ?? '');
    let axes: string[] = [];
    try {
      const raw = c.tematicas_interes;
      if (raw != null && raw !== '') {
        const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
        axes = Array.isArray(arr) ? arr.map((x: unknown) => String(x)) : [];
      }
    } catch { /* ignore */ }
    return {
      firstName: c.nombre ?? '',
      lastName: c.apellido ?? '',
      position: c.cargo ?? '',
      email: c.correo ?? '',
      phone: c.telefono ?? '',
      codigoPais,
      numeroTelefono,
      area: c.area ?? '',
      subarea: c.subarea ?? '',
      linkedin: c.linkedin ?? '',
      thematicAxes: axes as ContactInfo['thematicAxes'],
    };
  };
  const contactInfoToCrmDraft = (data: ContactInfo): Partial<CrmContact> => ({
    nombre: data.firstName || undefined,
    apellido: data.lastName || undefined,
    cargo: data.position || undefined,
    correo: data.email || undefined,
    telefono: formatPhoneForCrm(data.codigoPais, data.numeroTelefono) || undefined,
    area: data.area || undefined,
    subarea: data.subarea || undefined,
    linkedin: data.linkedin || undefined,
    tematicas_interes: (data.thematicAxes && data.thematicAxes.length > 0) ? JSON.stringify(data.thematicAxes) : undefined,
  });

  // Contactos por organización (para tarjetas Empresas: un contacto aparece en cada org a la que está asociado)
  const contactsByOrgId = useMemo(() => {
    const map = new Map<number, CrmContact[]>();
    crmContacts.forEach((c) => {
      getContactOrgIds(c).forEach((orgId) => {
        const list = map.get(orgId) || [];
        list.push(c);
        map.set(orgId, list);
      });
    });
    return map;
  }, [crmContacts]);

  const saveEmpresaNegocioStage = async (organizationId: number, pipelineId: number, newStage: string) => {
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/crm/pipeline/${pipelineId}/organization/${organizationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pipelineStage: newStage })
      });
      if (response.ok) {
        setOrganizationNegociosList(prev =>
          prev.map(n => n.organizationId === organizationId && n.pipelineId === pipelineId ? { ...n, pipelineStage: newStage } : n)
        );
      }
    } catch (e) {
    }
  };

  const saveEmpresaNegocioPriority = async (organizationId: number, pipelineId: number, newPriority: 'high' | 'medium' | 'low') => {
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/crm/pipeline/${pipelineId}/organization/${organizationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ priority: newPriority })
      });
      if (response.ok) {
        setOrganizationNegociosList(prev =>
          prev.map(n => n.organizationId === organizationId && n.pipelineId === pipelineId ? { ...n, priority: newPriority } : n)
        );
      }
    } catch (e) {
    }
  };

  /** Guardar etapa del negocio SCESDE26 desde tarjetas Perfil Institucional (actualiza estado local) */
  const savePerfilInstNegocioStage = async (organizationId: number, newStage: string) => {
    const scesde26PipelineId = 1;
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/crm/pipeline/${scesde26PipelineId}/organization/${organizationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pipelineStage: newStage })
      });
      if (response.ok) {
        setPerfilInstScesde26Negocios(prev => ({
          ...prev,
          [organizationId]: { ...(prev[organizationId] || { pipelineStage: 'nuevo', priority: 'medium' }), pipelineStage: newStage }
        }));
      }
    } catch (e) {
    }
  };

  /** Guardar prioridad del negocio SCESDE26 desde tarjetas Perfil Institucional (actualiza estado local) */
  const savePerfilInstNegocioPriority = async (organizationId: number, newPriority: 'high' | 'medium' | 'low') => {
    const scesde26PipelineId = 1;
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/crm/pipeline/${scesde26PipelineId}/organization/${organizationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ priority: newPriority })
      });
      if (response.ok) {
        setPerfilInstScesde26Negocios(prev => ({
          ...prev,
          [organizationId]: { ...(prev[organizationId] || { pipelineStage: 'nuevo', priority: 'medium' }), priority: newPriority }
        }));
      }
    } catch (e) {
    }
  };

  // Ajustar altura de todas las columnas del pipeline para que sean iguales
  useEffect(() => {
    if (pipelineView === 'pipeline' && pipelineColumnsRef.current) {
      const adjustColumnHeights = () => {
        const columns = pipelineColumnsRef.current?.querySelectorAll('.admin-pipeline-column');
        if (columns && columns.length > 0) {
          let maxHeight = 0;
          // Resetear alturas primero para calcular correctamente
          columns.forEach((col) => {
            (col as HTMLElement).style.height = 'auto';
          });
          // Calcular altura máxima
          columns.forEach((col) => {
            const height = (col as HTMLElement).offsetHeight;
            if (height > maxHeight) {
              maxHeight = height;
            }
          });
          // Aplicar altura máxima a todas las columnas
          if (maxHeight > 0) {
            columns.forEach((col) => {
              (col as HTMLElement).style.height = `${maxHeight}px`;
            });
          }
        }
      };
      
      // Ejecutar después de que el DOM se actualice
      const timeoutId = setTimeout(adjustColumnHeights, 100);
      
      // Re-ejecutar cuando cambie el tamaño de la ventana
      window.addEventListener('resize', adjustColumnHeights);
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', adjustColumnHeights);
      };
    }
  }, [pipelineView, sortedUsers, crmPipeline]);

  // Alinear alturas de los títulos en cada fila
  useEffect(() => {
    if (activeViewMode !== 'cards' || filteredUsers.length === 0) return;

    const alignCardHeaders = () => {
      const cards = document.querySelectorAll('.admin-user-card');
      if (cards.length === 0) return;

      // Agrupar cards por fila basándonos en su posición top
      const rows: HTMLElement[][] = [];
      const cardPositions: { element: HTMLElement; top: number; index: number }[] = [];

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        cardPositions.push({
          element: card as HTMLElement,
          top: rect.top,
          index: 0
        });
      });

      // Ordenar por posición top
      cardPositions.sort((a, b) => a.top - b.top);

      // Agrupar en filas (misma posición top con tolerancia de 5px)
      let currentRow: HTMLElement[] = [];
      let currentTop = -1;
      const tolerance = 5;

      cardPositions.forEach(({ element, top }) => {
        if (currentTop === -1 || Math.abs(top - currentTop) > tolerance) {
          if (currentRow.length > 0) {
            rows.push(currentRow);
          }
          currentRow = [element];
          currentTop = top;
        } else {
          currentRow.push(element);
        }
      });

      if (currentRow.length > 0) {
        rows.push(currentRow);
      }

      // Para cada fila, calcular altura máxima del header y aplicarla
      rows.forEach((row) => {
        const headers = row.map((card) => 
          card.querySelector('.admin-user-card-header') as HTMLElement
        ).filter(Boolean);

        if (headers.length === 0) return;

        // Resetear alturas para calcular correctamente
        headers.forEach((header) => {
          header.style.minHeight = 'auto';
        });

        // Calcular altura máxima
        const maxHeight = Math.max(
          ...headers.map((header) => header.getBoundingClientRect().height)
        );

        // Aplicar altura máxima a todos los headers de la fila
        headers.forEach((header) => {
          header.style.minHeight = `${maxHeight}px`;
        });
      });
    };

    // Ejecutar después de que el DOM se actualice
    const timeoutId = setTimeout(alignCardHeaders, 100);

    // Re-ejecutar cuando cambie el tamaño de la ventana
    window.addEventListener('resize', alignCardHeaders);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', alignCardHeaders);
      // Limpiar estilos al desmontar
      const headers = document.querySelectorAll('.admin-user-card-header');
      headers.forEach((header) => {
        (header as HTMLElement).style.minHeight = 'auto';
      });
    };
  }, [filteredUsers, activeViewMode]);

  // Formatear fecha
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtener nombre del usuario
  const getUserDisplayName = (user: User) => {
    if (user.organization_name) {
      return user.organization_name;
    }
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.email;
  };

  // Funcion para exportar a Excel - Exporta datos crudos de la base de datos
  const handleExportToExcel = async () => {
    // Cargar datos si no están disponibles
    let currentPersonalProfiles = personalProfiles;
    let currentInstitutionalProfiles = institutionalProfiles;

    if (personalProfiles.length === 0 || institutionalProfiles.length === 0) {
      try {
        // Cargar perfiles personales completos
        const personalResponse = await fetch(`${getApiUrl()}/admin/tables/personal-profiles`, {
          credentials: 'include'
        });
        if (personalResponse.ok) {
          const personalData = await personalResponse.json();
          if (personalData.success) {
            currentPersonalProfiles = personalData.data || [];
          }
        }

        // Cargar perfiles institucionales completos
        const institutionalResponse = await fetch(`${getApiUrl()}/admin/tables/institutional-profiles`, {
          credentials: 'include'
        });
        if (institutionalResponse.ok) {
          const institutionalData = await institutionalResponse.json();
          if (institutionalData.success) {
            currentInstitutionalProfiles = institutionalData.data || [];
          }
        }
      } catch (err: any) {
      }
    }

    // Obtener IDs de usuarios filtrados
    const filteredUserIds = new Set(sortedUsers.map(user => user.id));

    // Filtrar perfiles personales que pertenecen a usuarios filtrados
    // El campo puede ser user_id o id dependiendo de la estructura de la tabla
    const filteredPersonalProfiles = currentPersonalProfiles.filter((profile: any) => {
      const userId = profile.user_id || profile.id;
      return filteredUserIds.has(userId);
    });

    // Filtrar perfiles institucionales que pertenecen a usuarios filtrados
    // El campo puede ser user_id o id dependiendo de la estructura de la tabla
    const filteredInstitutionalProfiles = currentInstitutionalProfiles.filter((profile: any) => {
      const userId = profile.user_id || profile.id;
      return filteredUserIds.has(userId);
    });

    // Función helper para convertir valores a formato exportable (formateado para Excel)
    const prepareValueForExport = (value: any): any => {
      if (value === null || value === undefined) {
        return '';
      }
      
      // Si es un string que parece JSON, intentar parsearlo primero
      if (typeof value === 'string' && (value.trim().startsWith('{') || value.trim().startsWith('['))) {
        try {
          const parsed = JSON.parse(value);
          value = parsed; // Continuar procesando el valor parseado
        } catch {
          // Si no se puede parsear, mantener el string tal cual
          return value;
        }
      }
      
      // Función helper para formatear un objeto con cada campo en una línea
      const formatObjectWithLineBreaks = (obj: any, indent: string = ''): string => {
        if (obj === null || obj === undefined) {
          return '';
        }
        
        if (Array.isArray(obj)) {
          if (obj.length === 0) {
            return '';
          }
          // Si el array contiene objetos, formatear cada objeto
          if (obj.length > 0 && typeof obj[0] === 'object') {
            return obj.map((item, idx) => {
              return `[${idx + 1}]\n${formatObjectWithLineBreaks(item, indent + '  ')}`;
            }).join('\n\n');
          }
          // Si el array contiene valores simples, unirlos con comas
          return obj.map(item => String(item)).join(', ');
        }
        
        if (typeof obj === 'object') {
          const entries = Object.entries(obj);
          if (entries.length === 0) {
            return '';
          }
          return entries.map(([key, val]) => {
            let formattedValue: string;
            if (val === null || val === undefined) {
              formattedValue = '';
            } else if (typeof val === 'object') {
              // Si el valor es un objeto o array anidado, formatearlo recursivamente
              formattedValue = formatObjectWithLineBreaks(val, indent + '  ');
            } else {
              formattedValue = String(val);
            }
            return `${key}: ${formattedValue}`;
          }).join('\n');
        }
        
        return String(obj);
      };
      
      // Si es un array
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return '';
        }
        // Si el array contiene objetos, formatear cada objeto con saltos de línea
        if (value.length > 0 && typeof value[0] === 'object') {
          return value.map((item, idx) => {
            return `[${idx + 1}]\n${formatObjectWithLineBreaks(item)}`;
          }).join('\n\n');
        }
        // Si el array contiene valores simples, unirlos con comas
        return value.map(item => String(item)).join(', ');
      }
      
      // Si es un objeto
      if (typeof value === 'object') {
        return formatObjectWithLineBreaks(value);
      }
      
      // Mantener valores primitivos tal cual
      return value;
    };

    // Preparar datos para perfiles personales - todos los campos de la tabla personal_profiles
    const personalData = filteredPersonalProfiles.map((profile: any) => {
      const exportData: any = {};
      // Exportar todos los campos del perfil tal cual están en la base de datos
      Object.keys(profile).forEach(key => {
        exportData[key] = prepareValueForExport(profile[key]);
      });
      return exportData;
    });

    // Preparar datos para perfiles institucionales - todos los campos de la tabla institutional_profiles
    const institutionalData = filteredInstitutionalProfiles.map((profile: any) => {
      const exportData: any = {};
      // Exportar todos los campos del perfil tal cual están en la base de datos
      Object.keys(profile).forEach(key => {
        exportData[key] = prepareValueForExport(profile[key]);
      });
      return exportData;
    });

    // Crear workbook
    const workbook = XLSX.utils.book_new();

    // Crear hojas
    const personalSheet = XLSX.utils.json_to_sheet(personalData);
    const institutionalSheet = XLSX.utils.json_to_sheet(institutionalData);

    // Agregar hojas al workbook
    XLSX.utils.book_append_sheet(workbook, personalSheet, 'Perfiles Personales');
    XLSX.utils.book_append_sheet(workbook, institutionalSheet, 'Perfiles Institucionales');

    // Generar nombre de archivo con fecha
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const fileName = `exportacion_usuarios_${dateStr}.xlsx`;

    // Descargar archivo
    XLSX.writeFile(workbook, fileName);
  };

  // Calcular métricas para el dashboard
  const dashboardMetrics = useMemo(() => {
    const pendingApprovals = users.filter(u => {
      const hasSpeakers = u.speakers && Array.isArray(u.speakers) && u.speakers.some((speaker: any) => speaker.requestApproval === true);
      const hasEvents = u.parallel_events && Array.isArray(u.parallel_events) && u.parallel_events.some((event: any) => event.requestApproval === true);
      const hasCatalog = u.initiatives && Array.isArray(u.initiatives) && u.initiatives.some((init: any) => init.requestApproval === true);
      return hasSpeakers || hasEvents || hasCatalog;
    }).length;
    const incompleteProfiles = users.filter(u => !(u.perfil_personal_validado && u.perfil_institucional_validado)).length;
    const highPriorityUsers = users.filter(u => crmPriority[u.id] === 'high').length;
    const unreadMessages = users.reduce((sum, u) => sum + (u.unreadCount || 0), 0);
    const usersWithMissingInfo = users.filter(u => {
      const missingFields: string[] = [];
      if (!u.organization_name) missingFields.push('Nombre de organización');
      if (!u.first_name || !u.last_name) missingFields.push('Nombre completo');
      if (!u.organization_type) missingFields.push('Tipo de organización');
      return missingFields.length > 0;
    }).length;

    const totalWithInstitutionalProfile = users.filter(u => u.institutional_profile_id != null).length;
    const totalWithParticipations = users.filter(u => {
      const hasSpeakers = u.speakers && Array.isArray(u.speakers) && u.speakers.length > 0;
      const hasEvents = u.parallel_events && Array.isArray(u.parallel_events) && u.parallel_events.length > 0;
      const hasInit = u.initiatives && Array.isArray(u.initiatives) && u.initiatives.length > 0;
      const hasBr = u.business_rounds_participants && Array.isArray(u.business_rounds_participants) && u.business_rounds_participants.length > 0;
      return hasSpeakers || hasEvents || hasInit || hasBr;
    }).length;

    return {
      totalUsers: users.length,
      totalWithInstitutionalProfile,
      totalWithParticipations,
      pendingApprovals,
      incompleteProfiles,
      highPriorityUsers,
      unreadMessages,
      usersWithMissingInfo
    };
  }, [users, crmPriority]);

  // Contar pendientes por tipo de participación
  const pendingSpeakersCount = useMemo(() => {
    let count = 0;
    users.forEach(user => {
      if (user.speakers && Array.isArray(user.speakers)) {
        user.speakers.forEach((speaker: any) => {
          const statusInfo = getSpeakerStatusHelper(speaker);
          const isPending = (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                           speaker.approval_status !== 'approved' && speaker.approval_status !== 'rejected';
          if (isPending) count++;
        });
      }
    });
    return count;
  }, [users]);

  const pendingEventsCount = useMemo(() => {
    let count = 0;
    users.forEach(user => {
      if (user.parallel_events && Array.isArray(user.parallel_events)) {
        user.parallel_events.forEach((event: any) => {
          const statusInfo = getEventStatusHelper(event);
          const isPending = (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                           event.approval_status !== 'approved' && event.approval_status !== 'rejected';
          if (isPending) count++;
        });
      }
    });
    return count;
  }, [users]);

  const pendingInitiativesCount = useMemo(() => {
    let count = 0;
    users.forEach(user => {
      if (user.initiatives && Array.isArray(user.initiatives)) {
        user.initiatives.forEach((initiative: any) => {
          const statusInfo = getInitiativeStatusHelper(initiative);
          const isPending = (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                           initiative.approval_status !== 'approved' && initiative.approval_status !== 'rejected';
          if (isPending) count++;
        });
      }
    });
    return count;
  }, [users]);

  const pendingBusinessRoundsCount = useMemo(() => {
    let count = 0;
    users.forEach(user => {
      if (user.business_rounds_participants && Array.isArray(user.business_rounds_participants)) {
        user.business_rounds_participants.forEach((participant: any) => {
          const status = getBusinessRoundsStatusHelper(participant);
          const isPending = status === 'complete' &&
                           participant.approval_status !== 'approved' && participant.approval_status !== 'rejected';
          if (isPending) count++;
        });
      }
    });
    return count;
  }, [users]);

  // Contar todas las participaciones pendientes (suma total)
  const pendingParticipationsCount = useMemo(() => {
    return pendingSpeakersCount + pendingEventsCount + pendingInitiativesCount + pendingBusinessRoundsCount;
  }, [pendingSpeakersCount, pendingEventsCount, pendingInitiativesCount, pendingBusinessRoundsCount]);

  // Obtener acciones pendientes agrupadas
  const pendingActions = useMemo(() => {
    const approvals = users.filter(u => {
      // Incluir speakers completos o con requestApproval pendientes de aprobación
      const hasSpeakers = u.speakers && Array.isArray(u.speakers) && u.speakers.some((speaker: any) => {
        const statusInfo = getSpeakerStatusHelper(speaker);
        return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
               speaker.approval_status !== 'approved' && speaker.approval_status !== 'rejected';
      });
      // Incluir eventos completos o con requestApproval pendientes de aprobación
      const hasEvents = u.parallel_events && Array.isArray(u.parallel_events) && u.parallel_events.some((event: any) => {
        const statusInfo = getEventStatusHelper(event);
        return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
               event.approval_status !== 'approved' && event.approval_status !== 'rejected';
      });
      // Incluir iniciativas completas o con requestApproval pendientes de aprobación
      const hasCatalogInitiatives = u.initiatives && Array.isArray(u.initiatives) && u.initiatives.some((init: any) => {
        const statusInfo = getInitiativeStatusHelper(init);
        return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
               init.approval_status !== 'approved' && init.approval_status !== 'rejected';
      });
      // Incluir participantes de rondas de negocios completos pendientes de aprobación
      const hasBusinessRounds = u.business_rounds_participants && Array.isArray(u.business_rounds_participants) && u.business_rounds_participants.some((participant: any) => {
        const status = getBusinessRoundsStatusHelper(participant);
        return status === 'complete' &&
               participant.approval_status !== 'approved' && participant.approval_status !== 'rejected';
      });
      return hasSpeakers || hasEvents || hasCatalogInitiatives || hasBusinessRounds;
    }).map(u => {
      const subtypes: string[] = [];
      // Incluir speakers si tienen pendientes
      if (u.speakers && Array.isArray(u.speakers) && u.speakers.some((speaker: any) => {
        const statusInfo = getSpeakerStatusHelper(speaker);
        return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
               speaker.approval_status !== 'approved' && speaker.approval_status !== 'rejected';
      })) subtypes.push('speakers');
      // Incluir eventos si tienen pendientes
      if (u.parallel_events && Array.isArray(u.parallel_events) && u.parallel_events.some((event: any) => {
        const statusInfo = getEventStatusHelper(event);
        return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
               event.approval_status !== 'approved' && event.approval_status !== 'rejected';
      })) subtypes.push('events');
      // Incluir catalog si hay iniciativas pendientes
      if (u.initiatives && Array.isArray(u.initiatives) && u.initiatives.some((init: any) => {
        const statusInfo = getInitiativeStatusHelper(init);
        return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
               init.approval_status !== 'approved' && init.approval_status !== 'rejected';
      })) subtypes.push('catalog');
      // Incluir business rounds si hay participantes pendientes
      if (u.business_rounds_participants && Array.isArray(u.business_rounds_participants) && u.business_rounds_participants.some((participant: any) => {
        const status = getBusinessRoundsStatusHelper(participant);
        return status === 'complete' &&
               participant.approval_status !== 'approved' && participant.approval_status !== 'rejected';
      })) subtypes.push('business_rounds');
      return {
        type: 'approval' as const,
        user: u,
        subtypes
      };
    });

    // Ordenar por prioridad
    const allActions = [...approvals];
    allActions.sort((a, b) => {
      const aPriority = crmPriority[a.user.id] || 'medium';
      const bPriority = crmPriority[b.user.id] || 'medium';
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[bPriority as keyof typeof priorityOrder] - priorityOrder[aPriority as keyof typeof priorityOrder];
    });

    return allActions;
  }, [users, crmPriority]);

  if (loading) {
    return (
      <div className="app">
        <div className="loading-overlay">
          <div className="loading-spinner">Cargando panel de administración...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-panel ${isDarkMode ? 'dark-theme' : ''}`}>
      {/* Header - Mismo que App.tsx */}
      <div className="app-header-bar">
        <div className="header-logo">
          <a 
            href="/" 
            className="header-logo-link"
          >
            <ProxyImage 
              src={isDarkMode 
                ? "https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/Logo-SCESDE26-Blanco-e1768488512211.png"
                : "https://smartcityexposantiagodelestero.com/wp-content/uploads/2025/03/Logo-SCESDE26-Negro-e1751508867641-1536x213.png"
              } 
              alt="Smart City Expo Santiago del Estero" 
              className="header-logo-image"
            />
          </a>
        </div>
        {/* Logo Mood para mobile - fuera de header-actions */}
        <MoodLogoWithTooltip
          isDarkMode={isDarkMode}
          className="header-mood-logo header-mood-logo-mobile"
          />
        <div className="header-actions">
          {(currentUserRole === 'admin' || (currentUserRole === 'editor' && currentUserPermissions != null && currentUserPermissions.includes('mensajeria'))) && (
            <>
              <button 
                type="button"
                className="save-profile-button"
                onClick={() => window.open('/soporte', '_blank')}
                title="Ir a mensajería"
              >
                Mensajería
              </button>
              <MessagesDropdown
                userRole="admin"
                userId={undefined}
              />
            </>
          )}
          <ThemeToggle />
          <AccountDropdown
            onLogout={handleLogout}
            onDeleteAccount={handleDeleteAccount}
            isLoggingOut={isLoggingOut}
          />
          {/* Logo Mood para tablet/desktop - dentro de header-actions */}
          <MoodLogoWithTooltip
            isDarkMode={isDarkMode}
            className="header-mood-logo header-mood-logo-desktop"
            />
        </div>
      </div>

      {/* Contenido del panel */}
      <div className="admin-panel-content">
        <div className="admin-panel-header-row">
          <div className="admin-panel-header">
            <h1>Panel de Administración</h1>
            <p>Gestiona usuarios y accede a sus perfiles públicos</p>
          </div>
          <div className="admin-panel-header-tabs">
            {canAccessTab('monitoring') && (
              <button
                type="button"
                className={`admin-main-tab admin-panel-header-tab ${activeMainTab === 'monitoring' ? 'active' : ''}`}
                onClick={() => {
                  setActiveMainTab('monitoring');
                  setPreviousMainTab(null);
                }}
              >
                <SearchIcon width={20} height={20} /> Monitoreo
              </button>
            )}
            {currentUserRole === 'admin' && (
              <button
                type="button"
                className={`admin-main-tab admin-panel-header-tab ${activeMainTab === 'accesos' ? 'active' : ''}`}
                onClick={() => {
                  setActiveMainTab('accesos');
                  setPreviousMainTab(null);
                }}
              >
                <SettingsIcon width={20} height={20} /> Accesos
              </button>
            )}
          </div>
        </div>

        {/* Pestañas principales (editores solo ven las que tienen permiso) */}
        <div className="admin-main-tabs-container">
          <div className="admin-main-tabs">
          {canAccessTab('crm') && (
            <button
              className={`admin-main-tab ${activeMainTab === 'crm' ? 'active' : ''}`}
              onClick={() => {
                setActiveMainTab('crm');
                setPreviousMainTab(null);
              }}
            >
              <BarChartIcon width={20} height={20} /> CRM
            </button>
          )}
            {canAccessTab('participations') && (
            <button
              className={`admin-main-tab ${activeMainTab === 'participations' ? 'active' : ''}`}
              onClick={() => {
                setActiveMainTab('participations');
                setPreviousMainTab(null);
              }}
            >
              <AwardIcon width={20} height={20} /> Perfil
              {pendingParticipationsCount > 0 && (
                <span className="admin-tab-badge">{pendingParticipationsCount}</span>
              )}
            </button>
            )}
            {canAccessTab('conversations') && (
            <button
              className={`admin-main-tab ${activeMainTab === 'conversations' ? 'active' : ''}`}
              onClick={() => {
                setActiveMainTab('conversations');
                setPreviousMainTab(null);
              }}
            >
              <MessageCircleIcon width={20} height={20} /> Conversaciones
            </button>
            )}
            {canAccessTab('comunicaciones') && (
            <button
              className={`admin-main-tab ${activeMainTab === 'comunicaciones' ? 'active' : ''}`}
              onClick={() => {
                setActiveMainTab('comunicaciones');
                setPreviousMainTab(null);
              }}
            >
              <FileTextIcon width={20} height={20} /> Comunicaciones
            </button>
            )}
            {canAccessTab('database') && (
            <button
              className={`admin-main-tab ${activeMainTab === 'database' ? 'active' : ''}`}
              onClick={() => {
                setActiveMainTab('database');
                setPreviousMainTab(null);
              }}
            >
              <ClipboardIcon width={20} height={20} /> Base de datos
            </button>
            )}
            {canAccessTab('networking') && (
            <button
              className={`admin-main-tab ${activeMainTab === 'networking' ? 'active' : ''}`}
              onClick={() => {
                setActiveMainTab('networking');
                setPreviousMainTab(null);
              }}
            >
              <UserIcon width={20} height={20} /> Networking
            </button>
            )}
          </div>
        </div>

        {/* Contenido según pestaña principal */}

        {/* Accesos: asignar rol por correo (solo admin, desvinculado del CRM) */}
        {activeMainTab === 'accesos' && currentUserRole === 'admin' && (
          <div id="admin-access-form" className="admin-crm-section-card" style={{ marginTop: '24px', maxWidth: '800px' }}>
            <h2 style={{ marginBottom: '20px', fontSize: '1.25rem' }}>Accesos al panel</h2>
            <p style={{ color: '#64748b', marginBottom: '24px', fontSize: '14px' }}>
              Asigna o cambia el rol de acceso al panel por correo. El usuario debe existir en el sistema (haberse registrado alguna vez).
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '24px' }}>
              <div className="admin-access-form-row">
                <div className="admin-access-field">
                  <label className="info-label" style={{ display: 'block', marginBottom: '6px' }}>Correo electrónico</label>
                  <input
                    type="email"
                    value={accessEmail}
                    onChange={(e) => { setAccessEmail(e.target.value.trim().toLowerCase()); setAccessError(null); }}
                    placeholder="ejemplo@correo.com"
                    className="admin-filter-select admin-access-email-input"
                    style={{ width: '100%', minWidth: '200px', maxWidth: '360px' }}
                  />
                </div>
                <div className="admin-access-field">
                  <label className="info-label" style={{ display: 'block', marginBottom: '6px' }}>Rol</label>
                  <CustomSelect
                    className="admin-filter-select"
                    optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                    value={accessRole}
                    onChange={(value) => setAccessRole(value as 'user' | 'editor' | 'admin')}
                    options={[
                      { value: 'user', label: 'Usuario (sin acceso al panel)' },
                      { value: 'editor', label: 'Editor' },
                      { value: 'admin', label: 'Administrador' }
                    ]}
                  />
                </div>
              </div>
              {accessRole === 'editor' && (
                <div>
                  <span className="info-label" style={{ display: 'block', marginBottom: '8px' }}>Permisos del editor</span>
                  <div className="checkbox-group admin-access-permissions">
                    {ACCESS_PERMISSION_OPTIONS.map(({ key, label }) => (
                      <label key={key} className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={accessPermissions.includes(key)}
                          onChange={(e) => {
                            if (e.target.checked) setAccessPermissions((p) => [...p, key]);
                            else setAccessPermissions((p) => p.filter((x) => x !== key));
                          }}
                        />
                        <span className="checkbox-label">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {accessError && <p style={{ color: '#ef4444', fontSize: '14px' }}>{accessError}</p>}
              <button
                type="button"
                className="admin-action-button-small"
                disabled={savingAccess || !accessEmail}
                onClick={async () => {
                  setAccessError(null);
                  setSavingAccess(true);
                  try {
                    const res = await fetch(`${getApiUrl()}/admin/access`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({
                        email: accessEmail.trim().toLowerCase(),
                        role: accessRole,
                        editor_permissions: accessRole === 'editor' ? accessPermissions : [],
                      }),
                    });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.error || 'Error al guardar');
                    setAccessEmail('');
                    setAccessRole('editor');
                    setAccessPermissions([]);
                    // Refrescar lista (incluye quitar de la lista si se asignó rol "Usuario")
                    const refresh = await fetch(`${getApiUrl()}/admin/access-users`, { credentials: 'include' });
                    const refreshData = await refresh.json();
                    if (refreshData.success && Array.isArray(refreshData.users)) setAccessUsers(refreshData.users);
                  } catch (e) {
                    setAccessError(e instanceof Error ? e.message : 'Error al guardar');
                  } finally {
                    setSavingAccess(false);
                  }
                }}
              >
                {savingAccess ? 'Guardando...' : 'Asignar / actualizar acceso'}
              </button>
            </div>
            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '24px 0' }} />
            <h3 style={{ marginBottom: '12px', fontSize: '1rem' }}>Usuarios con acceso al panel</h3>
            {loadingAccessUsers ? (
              <p style={{ color: '#64748b' }}>Cargando...</p>
            ) : accessUsers.length === 0 ? (
              <p style={{ color: '#64748b' }}>Ninguno aún.</p>
            ) : (
              <div className="admin-users-table-container" style={{ overflowX: 'auto' }}>
                <table className="admin-users-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>Correo</th>
                      <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>Rol</th>
                      <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>Permisos (editor)</th>
                      <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessUsers.map((u) => {
                      let perms: string[] = [];
                      if (u.role === 'editor' && u.editor_permissions) {
                        try {
                          perms = typeof u.editor_permissions === 'string' ? JSON.parse(u.editor_permissions) : u.editor_permissions;
                        } catch { perms = []; }
                      }
                      return (
                        <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 12px' }}>{u.email}</td>
                          <td style={{ padding: '8px 12px' }}>{u.role === 'admin' ? 'Administrador' : u.role === 'editor' ? 'Editor' : 'Usuario'}</td>
                          <td style={{ padding: '8px 12px' }}>{u.role === 'editor' ? (perms.length ? perms.join(', ') : '—') : '—'}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <button
                              type="button"
                              className="admin-action-button-small"
                              onClick={() => {
                                setAccessEmail(u.email);
                                setAccessRole((u.role === 'admin' || u.role === 'editor' || u.role === 'user' ? u.role : 'editor') as 'user' | 'editor' | 'admin');
                                setAccessPermissions(perms);
                                setAccessError(null);
                                document.getElementById('admin-access-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }}
                            >
                              Editar
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Conversaciones: Correo | WhatsApp | Mensajería */}
        {activeMainTab === 'conversations' && (
          <div className="admin-conversations-section">
            <div className="admin-conversations-subtabs">
              <button
                type="button"
                className={`admin-conv-subtab ${activeConversationsSubTab === 'correo' ? 'active' : ''}`}
                onClick={() => setActiveConversationsSubTab('correo')}
              >
                <EnvelopeIcon width={18} height={18} /> Correo
              </button>
              <button
                type="button"
                className={`admin-conv-subtab ${activeConversationsSubTab === 'whatsapp' ? 'active' : ''}`}
                onClick={() => setActiveConversationsSubTab('whatsapp')}
              >
                <WhatsAppIcon width={18} height={18} /> WhatsApp
              </button>
              <button
                type="button"
                className={`admin-conv-subtab ${activeConversationsSubTab === 'messaging' ? 'active' : ''}`}
                onClick={() => setActiveConversationsSubTab('messaging')}
              >
                <MessageCircleIcon width={18} height={18} /> Mensajería
              </button>
            </div>
            <div className="admin-conversations-content">
              {activeConversationsSubTab === 'correo' && <GmailSection isDarkMode={isDarkMode} />}
              {activeConversationsSubTab === 'whatsapp' && <WhatsAppSection isDarkMode={isDarkMode} />}
              {activeConversationsSubTab === 'messaging' && (
                <MessagingSection
                  isDarkMode={isDarkMode}
                  sendAsSupport={currentUserRole === 'editor' && currentUserPermissions != null && currentUserPermissions.includes('mensajeria')}
                />
              )}
            </div>
          </div>
        )}

        {/* Comunicaciones: Newsletters | Personalizados | Redes Sociales */}
        {activeMainTab === 'comunicaciones' && (
          <div className="admin-conversations-section">
            <div className="admin-conversations-subtabs">
              <button
                type="button"
                className={`admin-conv-subtab ${activeComunicacionesSubTab === 'newsletters' ? 'active' : ''}`}
                onClick={() => setActiveComunicacionesSubTab('newsletters')}
              >
                <FileTextIcon width={18} height={18} /> Newsletters
              </button>
              <button
                type="button"
                className={`admin-conv-subtab ${activeComunicacionesSubTab === 'personalizados' ? 'active' : ''}`}
                onClick={() => setActiveComunicacionesSubTab('personalizados')}
              >
                <FileTextIcon width={18} height={18} /> Personalizados
              </button>
              <button
                type="button"
                className={`admin-conv-subtab ${activeComunicacionesSubTab === 'redes_sociales' ? 'active' : ''}`}
                onClick={() => setActiveComunicacionesSubTab('redes_sociales')}
              >
                <FileTextIcon width={18} height={18} /> Redes Sociales
              </button>
            </div>
            <div className="admin-conversations-content">
              <ComunicacionesSection activeSubTab={activeComunicacionesSubTab} isDarkMode={isDarkMode} />
            </div>
          </div>
        )}

        {/* Base de datos - CRUD + Conexión Mailup */}
        {activeMainTab === 'database' && (
          <div className="admin-database-section">
            <DatabaseSection isDarkMode={isDarkMode} />
          </div>
        )}

        {activeMainTab === 'networking' && (
          <div className="admin-networking-section">
            <AdminNetworkingSection isDarkMode={isDarkMode} />
          </div>
        )}

        {/* CRM - Subtabs: Contactos | Organizaciones | Negocios */}
        {activeMainTab === 'crm' && (
          <div className="admin-crm-hubspot">
            {selectedCrmContact && activeCrmSubTab === 'contactos' ? (
              /* Vista de detalle del contacto (tabla crm_contacts) - misma navegación que perfiles */
              (() => {
                const contact = selectedCrmContact;
                const orgIds = getContactOrgIds(contact);
                const contactOrgs = orgIds.map((oid) => crmOrganizations.find((o) => o.id === oid)).filter(Boolean) as CrmOrganization[];
                const linkedUser = contact.correo ? users.find((u) => (u.email || '').toLowerCase() === (contact.correo || '').toLowerCase()) : null;
                return (
                  <>
                  <div className="admin-crm-detail-view">
                    <div className="admin-crm-detail-header">
                      <button
                        className="admin-back-button"
                        onClick={() => {
                          const contactIdToScroll = contact.id;
                          const returnToOrg = selectedCrmOrganization != null;
                          try {
                            const saved = sessionStorage.getItem('adminPanelState');
                            if (saved) {
                              const parsed = JSON.parse(saved);
                              parsed.selectedCrmContactId = null;
                              if (returnToOrg) {
                                parsed.selectedCrmOrganizationId = selectedCrmOrganization?.id ?? parsed.selectedCrmOrganizationId;
                              }
                              sessionStorage.setItem('adminPanelState', JSON.stringify(parsed));
                            }
                          } catch (_) { /* ignore */ }
                          setSelectedCrmContact(null);
                          setActiveContactDetailOrgIndex(0);
                          setContactDetailSubTab('contacto');
                          setContactDetailEditMode(false);
                          setContactEditDraft(null);
                          setContactOrgEditDraft(null);
                          setContactNegocioEditDraft(null);
                          setActiveMainTab('crm');
                          setActiveCrmSubTab(returnToOrg ? 'empresas' : 'contactos');
                          if (!returnToOrg) {
                            setTimeout(() => {
                              const el = document.querySelector(`[data-contact-id="${contactIdToScroll}"]`);
                              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 100);
                          } else {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                        }}
                      >
                        ← Volver
                      </button>
                      <div className="admin-crm-detail-title">
                        <h2>{[contact.nombre, contact.apellido].filter(Boolean).join(' ') || 'Sin nombre'}</h2>
                        <p className="admin-request-email">{contact.correo || '—'}</p>
                      </div>
                      <div className="admin-crm-detail-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                        <button
                          type="button"
                          className="admin-action-button-small chat-button"
                          onClick={() => {
                            setContactEditDraft({ ...contact });
                            const org = contactOrgs[Math.min(activeContactDetailOrgIndex, contactOrgs.length - 1)];
                            setContactOrgEditDraft(org ? { ...org } : null);
                            const neg = contactDetailNegocios[Math.min(activeContactDetailNegocioIndex, contactDetailNegocios.length - 1)];
                            setContactNegocioEditDraft(neg ? { pipelineStage: neg.pipelineStage, priority: neg.priority, data: neg.data ? { ...neg.data } : {} } : null);
                            setContactEditModalOpen(true);
                            setContactEditModalSubTab('contacto');
                            setContactDetailSaveError(null);
                          }}
                          title="Editar contacto"
                        >
                          <EditIcon width={18} height={18} /> Editar
                        </button>
                      </div>
                    </div>
                    {contactDetailSaveError && (
                      <div className="admin-crm-empty-state" style={{ color: '#ef4444', marginTop: 8 }}>{contactDetailSaveError}</div>
                    )}
                    <div className="admin-crm-detail-content">
                      <div className="admin-conversations-subtabs" style={{ marginBottom: 16 }}>
                        <button type="button" className={`admin-conv-subtab ${contactDetailSubTab === 'contacto' ? 'active' : ''}`} onClick={() => setContactDetailSubTab('contacto')}>
                          <FileTextIcon width={18} height={18} /> Contacto
                        </button>
                        <button type="button" className={`admin-conv-subtab ${contactDetailSubTab === 'organizaciones' ? 'active' : ''}`} onClick={() => setContactDetailSubTab('organizaciones')}>
                          <BuildingIcon width={18} height={18} /> Organizaciones
                        </button>
                        <button type="button" className={`admin-conv-subtab ${contactDetailSubTab === 'negocios' ? 'active' : ''}`} onClick={() => setContactDetailSubTab('negocios')}>
                          <RefreshIcon width={18} height={18} /> Negocios
                        </button>
                      </div>
                      <div className="admin-profile-tabs-container">
                        <div className="admin-crm-detail-body" style={{ display: 'block' }}>
                          {contactDetailSubTab === 'contacto' && (
                          <div className="admin-crm-section-card">
                            <h3><FileTextIcon width={18} height={18} /> Datos del contacto</h3>
                              <div className="admin-crm-contact-info-grid">
                                {([
                                  { key: 'organization_name', label: 'Nombre organización' },
                                  { key: 'nombre_completo', label: 'Nombre' },
                                  { key: 'genero', label: 'Género' },
                                  { key: 'cargo', label: 'Cargo' },
                                  { key: 'area', label: 'Área' },
                                  { key: 'subarea', label: 'Subárea' },
                                  { key: 'correo', label: 'Correo' },
                                  { key: 'telefono', label: 'Teléfono' },
                                  { key: 'tematicas_interes', label: 'Temáticas de interés' },
                                  { key: 'importancia', label: 'Importancia' },
                                  { key: 'pais_region', label: 'País / Región' },
                                  { key: 'linkedin', label: 'LinkedIn' },
                                  { key: 'created_at', label: 'Creado' },
                                  { key: 'updated_at', label: 'Actualizado' }
                                ] as const).map(({ key, label }) => {
                                  const val = key === 'nombre_completo'
                                    ? ([contact.nombre, contact.apellido].filter(Boolean).join(' ') || null)
                                    : (contact as unknown as Record<string, unknown>)[key];
                                  const getThematicLabel = (v: string) => thematicAxes.find(a => a.value === v)?.label ?? v;
                                  const display = val == null || val === '' ? '—' : key === 'tematicas_interes' && val ? (() => {
                                    try {
                                      const arr = typeof val === 'string' ? JSON.parse(val) : val;
                                      const items = Array.isArray(arr) ? arr.map((x: unknown) => String(x)) : String(val).split(/[,;]/).map((s: string) => s.trim()).filter(Boolean);
                                      return items.length > 0 ? <div className="admin-crm-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{items.map((item, i) => <span key={i} className="tag-badge-small">{getThematicLabel(item)}</span>)}</div> : '—';
                                    } catch { return String(val); }
                                  })() : key === 'pais_region' && val ? (() => {
                                    try {
                                      const parsed = typeof val === 'string' ? JSON.parse(val) : val;
                                      const items: string[] = Array.isArray(parsed) ? parsed.map((x: unknown) => String(x)) : [String(val)];
                                      const labels: string[] = items.map((v) => { const c = countries.find((co: { code: string; name: string }) => co.code === v); return c ? c.name : v; });
                                      return labels.length > 0 ? <div className="admin-crm-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{labels.map((item, i) => <span key={i} className="tag-badge-small">{item}</span>)}</div> : '—';
                                    } catch {
                                      const c = countries.find((co: { code: string; name: string }) => co.code === String(val));
                                      return c ? c.name : String(val);
                                    }
                                  })() : key === 'linkedin' && val ? (
                                    <a href={String(val).startsWith('http') ? String(val) : `https://${val}`} target="_blank" rel="noopener noreferrer">{String(val)}</a>
                                  ) : key === 'created_at' || key === 'updated_at' ? formatDate(String(val)) : String(val);
                                  return (
                                    <div key={key} className="admin-crm-info-item-full">
                                      <span className="info-label">{label}:</span>
                                      <span className="info-value">{display}</span>
                                    </div>
                                  );
                                })}
                              </div>
                          </div>
                          )}
                          {contactDetailSubTab === 'organizaciones' && (
                          <div className="admin-crm-section-card">
                            <h3><BuildingIcon width={18} height={18} /> Organizaciones asociadas</h3>
                            {contactOrgs.length === 0 ? (
                              <p className="admin-crm-empty-state">No hay organizaciones asociadas.</p>
                            ) : (
                              <>
                                <div className="admin-conversations-subtabs" style={{ marginTop: 12, marginBottom: 12 }}>
                                  {contactOrgs.map((org, idx) => (
                                    <button
                                      key={org.id}
                                      type="button"
                                      className={`admin-conv-subtab ${activeContactDetailOrgIndex === idx ? 'active' : ''}`}
                                      onClick={() => setActiveContactDetailOrgIndex(idx)}
                                    >
                                      {org.organizacion || 'Sin nombre'}
                                    </button>
                                  ))}
                                </div>
                                {(() => {
                                  const safeIdx = Math.min(activeContactDetailOrgIndex, contactOrgs.length - 1);
                                  const org = contactOrgs[safeIdx];
                                  if (!org) return null;
                                  const orgFields = [
                                    { key: 'tipo_organizacion' as const, label: 'Tipo' }, { key: 'importancia' as const, label: 'Importancia' },
                                    { key: 'industrias' as const, label: 'Industrias' }, { key: 'sitio_web' as const, label: 'Sitio web' },
                                    { key: 'pais_origen' as const, label: 'País origen' }, { key: 'mercado' as const, label: 'Mercado' },
                                    { key: 'descripcion' as const, label: 'Descripción' }, { key: 'ejes_tematicos' as const, label: 'Ejes temáticos' },
                                    { key: 'siglas' as const, label: 'Siglas' }, { key: 'redes_sociales' as const, label: 'Redes sociales' },
                                    { key: 'dominio' as const, label: 'Dominio' },
                                    { key: 'created_at' as const, label: 'Creado' }, { key: 'updated_at' as const, label: 'Actualizado' }
                                  ] as Array<{ key: keyof CrmOrganization; label: string }>;
                                  return (
                                    <div className="admin-crm-contact-info-grid">
                                      {org.logo && (
                                        <div key="logo" className="admin-crm-info-item-full">
                                          <span className="info-label">Logo:</span>
                                          <span className="info-value"><ProxyImage src={org.logo} alt="" className="admin-crm-empresa-logo-img" style={{ maxWidth: 120, maxHeight: 60, objectFit: 'contain' }} /></span>
                                        </div>
                                      )}
                                      {orgFields.map(({ key, label }) => {
                                        const raw = org[key];
                                        const val = raw == null || raw === '' ? null : raw;
                                        let display: React.ReactNode = val == null ? '—' : String(val);
                                        if (key === 'sitio_web' && val) display = <a href={String(val).startsWith('http') ? String(val) : `https://${val}`} target="_blank" rel="noopener noreferrer">{String(val)}</a>;
                                        else if (key === 'created_at' || key === 'updated_at') display = val ? formatDate(String(val)) : '—';
                                        else if (key === 'pais_origen' && val) {
                                          const code = String(val).trim();
                                          display = (countries.find((c: { code: string; name: string }) => c.code === code)?.name ?? code) || '—';
                                        } else if (key === 'ejes_tematicos' && val) {
                                          try {
                                            const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                            const items = Array.isArray(arr) ? arr.map((x: unknown) => String(x)) : String(raw).split(/[,;]/).map((s: string) => s.trim()).filter(Boolean);
                                            const getThematicLabel = (v: string) => thematicAxes.find((a: { value: string; label: string }) => a.value === v)?.label ?? v;
                                            display = items.length > 0 ? <div className="admin-crm-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{items.map((item, i) => <span key={i} className="tag-badge-small">{getThematicLabel(item)}</span>)}</div> : '—';
                                          } catch { display = String(val); }
                                        } else if (key === 'industrias' && val) {
                                          try {
                                            const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                            const items = Array.isArray(arr) ? arr.map((x: unknown) => String(x)) : String(raw).split(/[,;]/).map((s: string) => s.trim()).filter(Boolean);
                                            display = items.length > 0 ? <div className="admin-crm-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{items.map((item, i) => <span key={i} className="tag-badge-small">{item}</span>)}</div> : '—';
                                          } catch { display = String(val); }
                                        } else if (key === 'redes_sociales' && val) {
                                          try {
                                            const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                            if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
                                              const entries = Object.entries(obj).filter(([, u]) => u && String(u).trim());
                                              const labels: Record<string, string> = { facebook: 'Facebook', twitter: 'Twitter', linkedin: 'LinkedIn', instagram: 'Instagram', youtube: 'YouTube', other: 'Otra' };
                                              display = entries.length > 0 ? <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{entries.map(([k, url]) => <span key={k}><strong>{labels[k] || k}:</strong> <a href={String(url).startsWith('http') ? String(url) : `https://${url}`} target="_blank" rel="noopener noreferrer">{String(url)}</a></span>)}</div> : '—';
                                            }
                                          } catch { display = String(val); }
                                        }
                                        return (
                                          <div key={key} className="admin-crm-info-item-full">
                                            <span className="info-label">{label}:</span>
                                            <span className="info-value">{display}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                              </>
                            )}
                          </div>
                          )}
                          {contactDetailSubTab === 'negocios' && (
                          <div className="admin-crm-section-card">
                            <h3><RefreshIcon width={18} height={18} /> Negocios asociados</h3>
                            {loadingContactDetailNegocios ? (
                              <p className="admin-crm-empty-state">Cargando negocios…</p>
                            ) : contactDetailNegocios.length === 0 ? (
                              <p className="admin-crm-empty-state">No hay negocios asignados a este contacto.</p>
                            ) : (
                              <>
                                <div className="admin-conversations-subtabs" style={{ marginTop: 12, marginBottom: 12 }}>
                                  {contactDetailNegocios.map((n, idx) => (
                                    <button
                                      key={`${n.organizationId}-${n.pipelineId}`}
                                      type="button"
                                      className={`admin-conv-subtab ${activeContactDetailNegocioIndex === idx ? 'active' : ''}`}
                                      onClick={() => setActiveContactDetailNegocioIndex(idx)}
                                    >
                                      {n.pipelineName}
                                      <span style={{ opacity: 0.85, fontWeight: 500 }}> · {n.organizationName}</span>
                                    </button>
                                  ))}
                                </div>
                                {(() => {
                                  const safeIdx = Math.min(activeContactDetailNegocioIndex, contactDetailNegocios.length - 1);
                                  const n = contactDetailNegocios[safeIdx];
                                  if (!n) return null;
                                  const fieldSchema = pipelinesList.find((p) => p.id === n.pipelineId)?.field_schema ?? [];
                                  const dataKeys = fieldSchema.length > 0
                                    ? fieldSchema.map((f) => f.key)
                                    : (n.data && typeof n.data === 'object' ? Object.keys(n.data).filter((k) => k !== 'pipeline_stage' && k !== 'priority') : []);
                                  const getLabel = (key: string) => fieldSchema.find((f) => f.key === key)?.label ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                                  const keyNorm = (k: string) => k.toLowerCase().replace(/-/g, '_').normalize('NFD').replace(/\p{Diacritic}/gu, '');
                                  const getCatalogForKey = (k: string): { value: string; label: string }[] | null => {
                                    const n = keyNorm(k);
                                    const labelNorm = (fieldSchema.find((f) => f.key === k)?.label ?? '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
                                    if (n.includes('event_program') || (n.includes('participacion') && n.includes('programa')) || labelNorm.includes('participacion') && labelNorm.includes('programa')) return eventProgramParticipation;
                                    if (n.includes('modalidad') || n.includes('modalities') || labelNorm.includes('modalidad') || labelNorm.includes('modalidades')) return participationModalities;
                                    return null;
                                  };
                                  const formatValue = (val: unknown, key?: string): React.ReactNode => {
                                    if (val == null) return '—';
                                    if (typeof val === 'boolean') return val ? 'Sí' : 'No';
                                    const catalog = key ? getCatalogForKey(key) : null;
                                    const strip = (s: string) => s.trim().replace(/^["']|["']$/g, '');
                                    const toLabel = (v: string) => (catalog ? (catalog.find((c) => c.value.toLowerCase() === strip(v).toLowerCase())?.label ?? strip(v)) : strip(v));
                                    let arr: unknown[] | null = null;
                                    if (Array.isArray(val)) arr = val;
                                    else if (typeof val === 'string' && (val.trim().startsWith('[') || val.includes(','))) {
                                      if (val.trim().startsWith('[')) {
                                        try { arr = JSON.parse(val) as unknown[]; } catch { arr = val.split(',').map((s) => s.trim()).filter(Boolean); }
                                      } else {
                                        arr = val.split(',').map((s) => s.trim()).filter(Boolean);
                                      }
                                    }
                                    if (arr && arr.length > 0) {
                                      const first = arr[0];
                                      if (typeof first === 'object' && first !== null) return `${arr.length} ítem(s)`;
                                      return <div className="admin-crm-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{arr.map((v, i) => <span key={i} className="tag-badge-small">{toLabel(String(v))}</span>)}</div>;
                                    }
                                    if (typeof val === 'object') return JSON.stringify(val).length > 80 ? `${JSON.stringify(val).slice(0, 80)}…` : JSON.stringify(val);
                                    return toLabel(String(val));
                                  };
                                  const stages = stagesByPipelineId[n.pipelineId] ?? [];
                                  const standKeys = ['selected_stands_updated', 'confirmed_stands', 'selected_stands', 'request_expo_information'];
                                  const participationFieldKeys = ['speakers', 'parallel_events', 'iniciativas', 'catalog_sections', 'business_rounds_participants'];
                                  const dataKeysFiltered = dataKeys.filter((k) => !standKeys.includes(k) && !participationFieldKeys.includes(k));
                                  const priorityLabel = n.priority === 'high' ? 'Alta' : n.priority === 'low' ? 'Baja' : 'Media';
                                  const parseArrNeg = (val: unknown): string[] => {
                                    if (val == null) return [];
                                    if (Array.isArray(val)) return val.map((v) => String(v));
                                    if (typeof val === 'string') {
                                      try {
                                        const p = JSON.parse(val);
                                        return Array.isArray(p) ? p.map((x: unknown) => String(x)) : [val];
                                      } catch {
                                        return [val];
                                      }
                                    }
                                    return [];
                                  };
                                  const parseParticipationNeg = (val: unknown): unknown[] => {
                                    if (val == null) return [];
                                    if (Array.isArray(val)) return val;
                                    if (typeof val === 'string') {
                                      try {
                                        const p = JSON.parse(val);
                                        return Array.isArray(p) ? p : [];
                                      } catch {
                                        return [];
                                      }
                                    }
                                    return [];
                                  };
                                  const negocioData = (n.data || {}) as Record<string, unknown>;
                                  const negocioStands = parseArrNeg(negocioData.selected_stands_updated);
                                  const negocioSpeakers = parseParticipationNeg(negocioData.speakers);
                                  const negocioParallelEvents = parseParticipationNeg(negocioData.parallel_events);
                                  const negocioInitiatives = parseParticipationNeg(negocioData.iniciativas).length > 0 ? parseParticipationNeg(negocioData.iniciativas) : parseParticipationNeg(negocioData.catalog_sections);
                                  const negocioBusinessRounds = parseParticipationNeg(negocioData.business_rounds_participants);
                                  const hasAnyParticipationNeg = negocioSpeakers.length > 0 || negocioParallelEvents.length > 0 || negocioInitiatives.length > 0 || negocioBusinessRounds.length > 0;
                                  const getThematicAxisLabelNeg = (value: string | { value?: string; label?: string }): string => {
                                    if (typeof value === 'object' && value !== null) {
                                      if (value.label) return value.label;
                                      if (value.value) {
                                        const axis = thematicAxes.find((t) => t.value === value.value);
                                        return axis?.label || value.value;
                                      }
                                      return String(value);
                                    }
                                    const axis = thematicAxes.find((t) => t.value === value);
                                    return axis?.label || value;
                                  };
                                  return (
                                    <>
                                      <div className="admin-crm-contact-info-grid">
                                        <div className="admin-crm-info-item-full">
                                          <span className="info-label">Etapa:</span>
                                          <span className="info-value">{n.pipelineStage}</span>
                                        </div>
                                        <div className="admin-crm-info-item-full">
                                          <span className="info-label">Prioridad:</span>
                                          <span className="info-value">{priorityLabel}</span>
                                        </div>
                                        {dataKeysFiltered.map((key) => {
                                          const val = negocioData[key];
                                          return (
                                            <div key={key} className="admin-crm-info-item-full">
                                              <span className="info-label">{getLabel(key)}:</span>
                                              <span className="info-value">{formatValue(val, key)}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                      <div className="admin-crm-stands-map-wrap" style={{ marginTop: '24px' }}>
                                        <span className="info-label" style={{ display: 'block', marginBottom: '8px' }}>Zona Expo (mapa):</span>
                                        <ExpoZoneSection
                                          selectedStands={negocioStands}
                                          confirmedStands={parseArrNeg(negocioData.confirmed_stands)}
                                          onChange={() => {}}
                                          onConfirmedChange={() => {}}
                                          readOnly
                                          hideTitle
                                          hideLegend={false}
                                          hideCheckbox
                                        />
                                      </div>
                                      {hasAnyParticipationNeg && (
                                        <div className="admin-profile-tab-panel" style={{ marginTop: '24px' }}>
                                          <span className="info-label" style={{ display: 'block', marginBottom: '8px' }}>Participaciones:</span>
                                          <div className="admin-participations-subtabs" style={{ marginBottom: 12 }}>
                                            <button type="button" className={`admin-participations-subtab ${activeContactNegocioParticipationsTab === 'speakers' ? 'active' : ''}`} onClick={() => setActiveContactNegocioParticipationsTab('speakers')}>
                                              <MicIcon width={16} height={16} /> Oradores {negocioSpeakers.length > 0 && <span className="admin-tab-badge">{negocioSpeakers.length}</span>}
                                            </button>
                                            <button type="button" className={`admin-participations-subtab ${activeContactNegocioParticipationsTab === 'events' ? 'active' : ''}`} onClick={() => setActiveContactNegocioParticipationsTab('events')}>
                                              <CalendarIcon width={16} height={16} /> Eventos Paralelos {negocioParallelEvents.length > 0 && <span className="admin-tab-badge">{negocioParallelEvents.length}</span>}
                                            </button>
                                            <button type="button" className={`admin-participations-subtab ${activeContactNegocioParticipationsTab === 'initiatives' ? 'active' : ''}`} onClick={() => setActiveContactNegocioParticipationsTab('initiatives')}>
                                              <BookIcon width={16} height={16} /> Iniciativas {negocioInitiatives.length > 0 && <span className="admin-tab-badge">{negocioInitiatives.length}</span>}
                                            </button>
                                            <button type="button" className={`admin-participations-subtab ${activeContactNegocioParticipationsTab === 'business_rounds' ? 'active' : ''}`} onClick={() => setActiveContactNegocioParticipationsTab('business_rounds')}>
                                              <UsersIcon width={16} height={16} /> Rondas de Negocios {negocioBusinessRounds.length > 0 && <span className="admin-tab-badge">{negocioBusinessRounds.length}</span>}
                                            </button>
                                          </div>
                                          <div className="admin-participations-grid">
                                            {activeContactNegocioParticipationsTab === 'speakers' && (negocioSpeakers.length > 0 ? negocioSpeakers.map((speaker: any, idx: number) => {
                                              const statusInfo = getSpeakerStatusHelper(speaker);
                                              return (
                                              <div key={`sp-${idx}`} className="admin-participation-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedContactNegocioParticipation({ type: 'speakers', item: speaker, organizationName: n.organizationName, organizationId: n.organizationId, pipelineId: n.pipelineId, itemIndex: idx })}>
                                                <div className="admin-participation-card-status-badges">
                                                  <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
                                                    {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                                                    {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                                                  </span>
                                                  {statusInfo.hasRequestApproval && (
                                                    <span className="participation-status-badge status-requested">
                                                      <AlertTriangleIcon width={12} height={12} /> Solicitado
                                                    </span>
                                                  )}
                                                  {speaker.approval_status === 'approved' ? (
                                                    <span className="participation-status-badge status-approved">
                                                      <CheckCircleIcon width={12} height={12} /> Aprobado
                                                    </span>
                                                  ) : speaker.approval_status === 'rejected' ? (
                                                    <span className="participation-status-badge status-rejected">
                                                      <XCircleIcon width={12} height={12} /> Rechazado
                                                    </span>
                                                  ) : (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) ? (
                                                    <span className="participation-status-badge status-pending">
                                                      <AlertTriangleIcon width={12} height={12} /> Pendiente
                                                    </span>
                                                  ) : null}
                                                </div>
                                                <div className="admin-participation-card-header">
                                                  <div>
                                                    <h3>{speaker.firstName} {speaker.lastName}</h3>
                                                    <span className="admin-participation-org">{n.organizationName}</span>
                                                  </div>
                                                </div>
                                                <div className="admin-participation-card-body">
                                                  {speaker.position && <div className="admin-participation-info-item"><span className="info-label">Cargo:</span><span className="info-value">{speaker.position}</span></div>}
                                                  {speaker.organization && <div className="admin-participation-info-item"><span className="info-label">Organización:</span><span className="info-value">{speaker.organization}</span></div>}
                                                  {speaker.nationality && <div className="admin-participation-info-item"><span className="info-label">Nacionalidad:</span><span className="info-value">{speaker.nationality}</span></div>}
                                                  {speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0 && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Ejes temáticos:</span>
                                                      <div className="admin-participation-tags">{speaker.thematicAxes.map((axis: any, ai: number) => <span key={ai} className="tag-badge-small">{getThematicAxisLabelNeg(axis)}</span>)}</div>
                                                    </div>
                                                  )}
                                                </div>
                                                {(statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (
                                                  <div className="admin-participation-card-footer">
                                                    <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                      {speaker.approval_status !== 'approved' && (
                                                        <button type="button" className="admin-action-button-small approve-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(n.organizationId, n.pipelineId, 'speakers', idx, 'approved'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Aprobar</button>
                                                      )}
                                                      {speaker.approval_status !== 'rejected' && (
                                                        <button type="button" className="admin-action-button-small reject-button" onClick={(e) => { e.stopPropagation(); setRejectionModal({ source: 'contactNegocio', isOpen: true, organizationId: n.organizationId, pipelineId: n.pipelineId, participationType: 'speakers', itemIndex: idx }); setRejectionComment(''); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Rechazar</button>
                                                      )}
                                                      {(speaker.approval_status === 'approved' || speaker.approval_status === 'rejected') && (
                                                        <button type="button" className="admin-action-button-small pending-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(n.organizationId, n.pipelineId, 'speakers', idx, 'pending'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Pendiente</button>
                                                      )}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            ); }) : <div className="admin-empty-state"><p>No hay oradores</p></div>)}
                                            {activeContactNegocioParticipationsTab === 'events' && (negocioParallelEvents.length > 0 ? negocioParallelEvents.map((event: any, idx: number) => {
                                              const statusInfo = getEventStatusHelper(event);
                                              return (
                                              <div key={`ev-${idx}`} className="admin-participation-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedContactNegocioParticipation({ type: 'events', item: event, organizationName: n.organizationName, organizationId: n.organizationId, pipelineId: n.pipelineId, itemIndex: idx })}>
                                                <div className="admin-participation-card-status-badges">
                                                  <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
                                                    {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                                                    {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                                                  </span>
                                                  {statusInfo.hasRequestApproval && (
                                                    <span className="participation-status-badge status-requested">
                                                      <AlertTriangleIcon width={12} height={12} /> Solicitado
                                                    </span>
                                                  )}
                                                  {event.approval_status === 'approved' ? (
                                                    <span className="participation-status-badge status-approved">
                                                      <CheckCircleIcon width={12} height={12} /> Aprobado
                                                    </span>
                                                  ) : event.approval_status === 'rejected' ? (
                                                    <span className="participation-status-badge status-rejected">
                                                      <XCircleIcon width={12} height={12} /> Rechazado
                                                    </span>
                                                  ) : (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) ? (
                                                    <span className="participation-status-badge status-pending">
                                                      <AlertTriangleIcon width={12} height={12} /> Pendiente
                                                    </span>
                                                  ) : null}
                                                </div>
                                                <div className="admin-participation-card-header">
                                                  <div>
                                                    <h3>{event.name || 'Sin nombre'}</h3>
                                                    <span className="admin-participation-org">{n.organizationName}</span>
                                                  </div>
                                                </div>
                                                <div className="admin-participation-card-body">
                                                  {event.description && <div className="admin-participation-info-item"><span className="info-label">Descripción:</span><span className="info-value">{event.description}</span></div>}
                                                  {event.duration && <div className="admin-participation-info-item"><span className="info-label">Duración:</span><span className="info-value">{event.duration}</span></div>}
                                                  {event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0 && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Ejes temáticos:</span>
                                                      <div className="admin-participation-tags">{event.thematicAxes.map((axis: any, ai: number) => <span key={ai} className="tag-badge-small">{getThematicAxisLabelNeg(axis)}</span>)}</div>
                                                    </div>
                                                  )}
                                                </div>
                                                {(statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (
                                                  <div className="admin-participation-card-footer">
                                                    <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                      {event.approval_status !== 'approved' && (
                                                        <button type="button" className="admin-action-button-small approve-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(n.organizationId, n.pipelineId, 'events', idx, 'approved'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Aprobar</button>
                                                      )}
                                                      {event.approval_status !== 'rejected' && (
                                                        <button type="button" className="admin-action-button-small reject-button" onClick={(e) => { e.stopPropagation(); setRejectionModal({ source: 'contactNegocio', isOpen: true, organizationId: n.organizationId, pipelineId: n.pipelineId, participationType: 'events', itemIndex: idx }); setRejectionComment(''); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Rechazar</button>
                                                      )}
                                                      {(event.approval_status === 'approved' || event.approval_status === 'rejected') && (
                                                        <button type="button" className="admin-action-button-small pending-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(n.organizationId, n.pipelineId, 'events', idx, 'pending'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Pendiente</button>
                                                      )}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            ); }) : <div className="admin-empty-state"><p>No hay eventos paralelos</p></div>)}
                                            {activeContactNegocioParticipationsTab === 'initiatives' && (negocioInitiatives.length > 0 ? negocioInitiatives.map((init: any, idx: number) => {
                                              const statusInfo = getInitiativeStatusHelper(init);
                                              return (
                                              <div key={`in-${idx}`} className="admin-participation-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedContactNegocioParticipation({ type: 'initiatives', item: init, organizationName: n.organizationName, organizationId: n.organizationId, pipelineId: n.pipelineId, itemIndex: idx })}>
                                                <div className="admin-participation-card-status-badges">
                                                  <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
                                                    {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                                                    {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                                                  </span>
                                                  {statusInfo.hasRequestApproval && (
                                                    <span className="participation-status-badge status-requested">
                                                      <AlertTriangleIcon width={12} height={12} /> Solicitado
                                                    </span>
                                                  )}
                                                  {init.approval_status === 'approved' ? (
                                                    <span className="participation-status-badge status-approved">
                                                      <CheckCircleIcon width={12} height={12} /> Aprobado
                                                    </span>
                                                  ) : init.approval_status === 'rejected' ? (
                                                    <span className="participation-status-badge status-rejected">
                                                      <XCircleIcon width={12} height={12} /> Rechazado
                                                    </span>
                                                  ) : (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) ? (
                                                    <span className="participation-status-badge status-pending">
                                                      <AlertTriangleIcon width={12} height={12} /> Pendiente
                                                    </span>
                                                  ) : null}
                                                </div>
                                                <div className="admin-participation-card-header">
                                                  <div>
                                                    <h3>{init.name || 'Sin nombre'}</h3>
                                                    <span className="admin-participation-org">{n.organizationName}</span>
                                                  </div>
                                                </div>
                                                <div className="admin-participation-card-body">
                                                  {init.shortDescription && <div className="admin-participation-info-item"><span className="info-label">Descripción corta:</span><span className="info-value">{init.shortDescription}</span></div>}
                                                  {init.thematicAxes && Array.isArray(init.thematicAxes) && init.thematicAxes.length > 0 && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Ejes temáticos:</span>
                                                      <div className="admin-participation-tags">{init.thematicAxes.map((axis: any, ai: number) => <span key={ai} className="tag-badge-small">{getThematicAxisLabelNeg(axis)}</span>)}</div>
                                                    </div>
                                                  )}
                                                </div>
                                                {(statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (
                                                  <div className="admin-participation-card-footer">
                                                    <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                      {init.approval_status !== 'approved' && (
                                                        <button type="button" className="admin-action-button-small approve-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(n.organizationId, n.pipelineId, 'initiatives', idx, 'approved'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Aprobar</button>
                                                      )}
                                                      {init.approval_status !== 'rejected' && (
                                                        <button type="button" className="admin-action-button-small reject-button" onClick={(e) => { e.stopPropagation(); setRejectionModal({ source: 'contactNegocio', isOpen: true, organizationId: n.organizationId, pipelineId: n.pipelineId, participationType: 'initiatives', itemIndex: idx }); setRejectionComment(''); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Rechazar</button>
                                                      )}
                                                      {(init.approval_status === 'approved' || init.approval_status === 'rejected') && (
                                                        <button type="button" className="admin-action-button-small pending-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(n.organizationId, n.pipelineId, 'initiatives', idx, 'pending'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Pendiente</button>
                                                      )}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            ); }) : <div className="admin-empty-state"><p>No hay iniciativas</p></div>)}
                                            {activeContactNegocioParticipationsTab === 'business_rounds' && (negocioBusinessRounds.length > 0 ? negocioBusinessRounds.map((participant: any, idx: number) => {
                                              const brStatus = getBusinessRoundsStatusHelper(participant);
                                              return (
                                              <div key={`br-${idx}`} className="admin-participation-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedContactNegocioParticipation({ type: 'business_rounds', item: participant, organizationName: n.organizationName, organizationId: n.organizationId, pipelineId: n.pipelineId, itemIndex: idx })}>
                                                <div className="admin-participation-card-status-badges">
                                                  <span className={`participation-status-badge status-${brStatus}`}>
                                                    {brStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                                                    {brStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                                                  </span>
                                                  {participant.approval_status === 'approved' ? (
                                                    <span className="participation-status-badge status-approved">
                                                      <CheckCircleIcon width={12} height={12} /> Aprobado
                                                    </span>
                                                  ) : participant.approval_status === 'rejected' ? (
                                                    <span className="participation-status-badge status-rejected">
                                                      <XCircleIcon width={12} height={12} /> Rechazado
                                                    </span>
                                                  ) : brStatus === 'complete' ? (
                                                    <span className="participation-status-badge status-pending">
                                                      <AlertTriangleIcon width={12} height={12} /> Pendiente
                                                    </span>
                                                  ) : null}
                                                </div>
                                                <div className="admin-participation-card-header">
                                                  <div>
                                                    <h3>{participant.firstName} {participant.lastName}</h3>
                                                    <span className="admin-participation-org">{n.organizationName}</span>
                                                  </div>
                                                </div>
                                                <div className="admin-participation-card-body">
                                                  {participant.position && <div className="admin-participation-info-item"><span className="info-label">Cargo:</span><span className="info-value">{participant.position}</span></div>}
                                                  {participant.organization && <div className="admin-participation-info-item"><span className="info-label">Organización:</span><span className="info-value">{participant.organization}</span></div>}
                                                  {participant.email && <div className="admin-participation-info-item"><span className="info-label">Email:</span><span className="info-value">{participant.email}</span></div>}
                                                  {participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0 && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Ejes temáticos:</span>
                                                      <div className="admin-participation-tags">{participant.thematicAxes.map((axis: any, ai: number) => <span key={ai} className="tag-badge-small">{getThematicAxisLabelNeg(axis)}</span>)}</div>
                                                    </div>
                                                  )}
                                                </div>
                                                {brStatus === 'complete' && (
                                                  <div className="admin-participation-card-footer">
                                                    <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                      {participant.approval_status !== 'approved' && (
                                                        <button type="button" className="admin-action-button-small approve-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(n.organizationId, n.pipelineId, 'business_rounds', idx, 'approved'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Aprobar</button>
                                                      )}
                                                      {participant.approval_status !== 'rejected' && (
                                                        <button type="button" className="admin-action-button-small reject-button" onClick={(e) => { e.stopPropagation(); setRejectionModal({ source: 'contactNegocio', isOpen: true, organizationId: n.organizationId, pipelineId: n.pipelineId, participationType: 'business_rounds', itemIndex: idx }); setRejectionComment(''); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Rechazar</button>
                                                      )}
                                                      {(participant.approval_status === 'approved' || participant.approval_status === 'rejected') && (
                                                        <button type="button" className="admin-action-button-small pending-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(n.organizationId, n.pipelineId, 'business_rounds', idx, 'pending'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Pendiente</button>
                                                      )}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            ); }) : <div className="admin-empty-state"><p>No hay participantes en rondas</p></div>)}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </>
                            )}
                          </div>
                          )}
                          {/* Historial de mensajería — fijo en las tres subtabs */}
                          <div className="admin-crm-section-card" style={{ marginTop: '24px' }}>
                            <h3><MessageCircleIcon width={18} height={18} /> Historial de Mensajería</h3>
                            <div className="admin-conversations-subtabs" style={{ marginTop: 12, marginBottom: 12 }}>
                              <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'correo' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('correo')}>
                                <EnvelopeIcon width={18} height={18} /> Correo
                              </button>
                              <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'whatsapp' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('whatsapp')}>
                                <WhatsAppIcon width={18} height={18} /> WhatsApp
                              </button>
                              <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'messaging' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('messaging')}>
                                <MessageCircleIcon width={18} height={18} /> Mensajería
                              </button>
                            </div>
                            <div>
                              {activeHistorialSubTab === 'correo' && (
                                <GmailSection isDarkMode={isDarkMode} filterByEmail={contact.correo || undefined} />
                              )}
                              {activeHistorialSubTab === 'whatsapp' && (
                                linkedUser ? (
                                  <HistorialWhatsAppTab
                                    userId={linkedUser.id}
                                    whatsapp={linkedUser.whatsapp ?? null}
                                    onSaveWhatsapp={() => {}}
                                    getApiUrl={getApiUrl}
                                    isDarkMode={isDarkMode}
                                  />
                                ) : (
                                  <div className="admin-crm-empty-state">
                                    <p>Este contacto no está registrado como usuario. WhatsApp disponible solo para usuarios registrados.</p>
                                  </div>
                                )
                              )}
                              {activeHistorialSubTab === 'messaging' && (
                                linkedUser ? (() => {
                                  const userConversation = conversations.find(c => c.userId === linkedUser.id);
                                  if (!userConversation) {
                                    return (
                                      <div className="admin-crm-empty-state">
                                        <p>No hay mensajes con este contacto</p>
                                        <button
                                          className="admin-action-button-small chat-button"
                                          onClick={() => {
                                            sessionStorage.setItem('chatsPage_asUserId', linkedUser.id.toString());
                                            window.open(`${window.location.origin}/soporte?asUserId=${linkedUser.id}`, '_blank');
                                          }}
                                        >
                                          Iniciar Conversación
                                        </button>
                                      </div>
                                    );
                                  }
                                  const lastDate = userConversation.lastMessageDate ? new Date(userConversation.lastMessageDate) : null;
                                  const lastMessageIsFromAdmin = currentUserId && userConversation.lastMessageFromUserId && parseInt(String(userConversation.lastMessageFromUserId)) === currentUserId;
                                  const daysSince = lastDate && lastMessageIsFromAdmin ? Math.floor((Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
                                  return (
                                    <div className="admin-crm-messaging-info">
                                      <div className="admin-crm-info-row">
                                        <span className="info-label">Último mensaje:</span>
                                        <span>{userConversation.lastMessage || 'Sin mensajes'}</span>
                                      </div>
                                      <div className="admin-crm-info-row">
                                        <span className="info-label">Fecha:</span>
                                        <span>{lastDate ? formatDate(userConversation.lastMessageDate) : 'Nunca'}</span>
                                      </div>
                                      <div className="admin-crm-info-row">
                                        <span className="info-label">Mensajes no leídos:</span>
                                        <span className={userConversation.unreadCount > 0 ? 'status-badge-small request-badge' : ''}>{userConversation.unreadCount}</span>
                                      </div>
                                      <div className="admin-crm-actions">
                                        <button
                                          className="admin-action-button-small chat-button"
                                          onClick={() => {
                                            sessionStorage.setItem('chatsPage_asUserId', linkedUser.id.toString());
                                            window.open(`${window.location.origin}/soporte?asUserId=${linkedUser.id}`, '_blank');
                                          }}
                                        >
                                          Abrir Chat
                                          {userConversation.unreadCount > 0 && <span className="chat-unread-badge-small">{userConversation.unreadCount}</span>}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })() : (
                                  <div className="admin-crm-empty-state">
                                    <p>Este contacto no está registrado como usuario. La mensajería interna está disponible solo para usuarios registrados.</p>
                                    <p style={{ marginTop: 8, fontSize: 13 }}>Puede ver el historial de correo filtrado por su email arriba.</p>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Modal Editar contacto: subtabs por sección con todos los campos */}
                  {contactEditModalOpen && contactEditDraft && (
                    <Modal
                      isOpen={true}
                      onClose={() => {
                        setContactEditModalOpen(false);
                        setContactEditDraft(null);
                        setContactOrgEditDraft(null);
                        setContactNegocioEditDraft(null);
                        setContactDetailSaveError(null);
                      }}
                      title={`Editar contacto: ${[contactEditDraft.nombre ?? contact.nombre, contactEditDraft.apellido ?? contact.apellido].filter(Boolean).join(' ') || 'Sin nombre'}`}
                      customFooter={
                        <div className="create-contact-modal-footer" style={{ margin: 0, border: 'none', padding: 0 }}>
                          <button type="button" className="create-contact-footer-btn secondary" onClick={() => { setContactEditModalOpen(false); setContactEditDraft(null); setContactOrgEditDraft(null); setContactNegocioEditDraft(null); setContactDetailSaveError(null); }}>Cancelar</button>
                          <button
                            type="button"
                            className="create-contact-footer-btn primary"
                            disabled={contactDetailSaving}
                            onClick={async () => {
                              if (!contactEditDraft) return;
                              setContactDetailSaving(true);
                              setContactDetailSaveError(null);
                              const apiUrl = getApiUrl();
                              try {
                                const bodyContact: Record<string, unknown> = {
                                  nombre: contactEditDraft.nombre ?? contact.nombre,
                                  apellido: contactEditDraft.apellido ?? contact.apellido,
                                  genero: contactEditDraft.genero ?? contact.genero,
                                  cargo: contactEditDraft.cargo ?? contact.cargo,
                                  area: contactEditDraft.area ?? contact.area,
                                  subarea: contactEditDraft.subarea ?? contact.subarea,
                                  correo: contactEditDraft.correo ?? contact.correo,
                                  telefono: contactEditDraft.telefono ?? contact.telefono,
                                  tematicas_interes: contactEditDraft.tematicas_interes ?? contact.tematicas_interes,
                                  importancia: contactEditDraft.importancia ?? contact.importancia,
                                  pais_region: contactEditDraft.pais_region ?? contact.pais_region,
                                  linkedin: contactEditDraft.linkedin ?? contact.linkedin,
                                  organization_ids: getContactOrgIds(contact),
                                };
                                const resContact = await fetch(`${apiUrl}/database/contacts/${contact.id}`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  credentials: 'include',
                                  body: JSON.stringify(bodyContact),
                                });
                                const dataContact = await resContact.json().catch(() => ({}));
                                if (!resContact.ok) {
                                  setContactDetailSaveError(dataContact?.error || 'Error al guardar contacto');
                                  return;
                                }
                                if (dataContact?.data) {
                                  setSelectedCrmContact({ ...contact, ...dataContact.data });
                                  setCrmContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, ...dataContact.data } : c)));
                                }
                                const org = contactOrgs[Math.min(activeContactDetailOrgIndex, contactOrgs.length - 1)];
                                if (org && contactOrgEditDraft && org.id) {
                                  const bodyOrg: Record<string, unknown> = {
                                    organizacion: contactOrgEditDraft.organizacion ?? org.organizacion,
                                    tipo_organizacion: contactOrgEditDraft.tipo_organizacion ?? org.tipo_organizacion,
                                    importancia: contactOrgEditDraft.importancia ?? org.importancia,
                                    industrias: (() => { const v = contactOrgEditDraft.industrias ?? org.industrias; return Array.isArray(v) ? JSON.stringify(v) : v; })(),
                                    sitio_web: contactOrgEditDraft.sitio_web ?? org.sitio_web,
                                    pais_origen: contactOrgEditDraft.pais_origen ?? org.pais_origen,
                                    mercado: contactOrgEditDraft.mercado ?? org.mercado,
                                    descripcion: contactOrgEditDraft.descripcion ?? org.descripcion,
                                    ejes_tematicos: contactOrgEditDraft.ejes_tematicos ?? org.ejes_tematicos,
                                    siglas: contactOrgEditDraft.siglas ?? org.siglas,
                                    redes_sociales: contactOrgEditDraft.redes_sociales ?? org.redes_sociales,
                                    logo: contactOrgEditDraft.logo ?? org.logo,
                                  };
                                  const resOrg = await fetch(`${apiUrl}/database/organizations/${org.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    credentials: 'include',
                                    body: JSON.stringify(bodyOrg),
                                  });
                                  const dataOrg = await resOrg.json().catch(() => ({}));
                                  if (resOrg.ok && dataOrg?.data) {
                                    setCrmOrganizations((prev) => prev.map((o) => (o.id === org.id ? { ...o, ...dataOrg.data } : o)));
                                  }
                                }
                                const neg = contactDetailNegocios[Math.min(activeContactDetailNegocioIndex, contactDetailNegocios.length - 1)];
                                if (neg && contactNegocioEditDraft && neg.organizationId && neg.pipelineId) {
                                  const bodyNeg: Record<string, unknown> = {
                                    pipelineStage: contactNegocioEditDraft.pipelineStage ?? neg.pipelineStage,
                                    priority: contactNegocioEditDraft.priority ?? neg.priority,
                                  };
                                  if (contactNegocioEditDraft.data && Object.keys(contactNegocioEditDraft.data).length >= 0) {
                                    bodyNeg.data = contactNegocioEditDraft.data;
                                  }
                                  const resNeg = await fetch(`${apiUrl}/crm/pipeline/${neg.pipelineId}/organization/${neg.organizationId}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    credentials: 'include',
                                    body: JSON.stringify(bodyNeg),
                                  });
                                  const dataNeg = await resNeg.json().catch(() => ({}));
                                  if (resNeg.ok && dataNeg?.negocio) {
                                    setContactDetailNegocios((prev) => {
                                      const idx = prev.findIndex((n) => n.organizationId === neg.organizationId && n.pipelineId === neg.pipelineId);
                                      if (idx < 0) return prev;
                                      const next = [...prev];
                                      next[idx] = { ...next[idx], pipelineStage: dataNeg.negocio.pipelineStage, priority: dataNeg.negocio.priority, data: dataNeg.negocio.data ?? next[idx].data };
                                      return next;
                                    });
                                  }
                                }
                                setContactEditModalOpen(false);
                                setContactEditDraft(null);
                                setContactOrgEditDraft(null);
                                setContactNegocioEditDraft(null);
                              } catch (e) {
                                setContactDetailSaveError(e instanceof Error ? e.message : 'Error al guardar');
                              } finally {
                                setContactDetailSaving(false);
                              }
                            }}
                          >
                            {contactDetailSaving ? 'Guardando…' : 'Guardar'}
                          </button>
                        </div>
                      }
                      contentClassName={isDarkMode ? 'admin-panel dark-theme' : ''}
                    >
                      <div className="create-contact-modal-body create-contact-modal-body--no-scroll">
                        <div className="admin-conversations-subtabs" style={{ marginBottom: 16 }}>
                          <button type="button" className={`admin-conv-subtab ${contactEditModalSubTab === 'contacto' ? 'active' : ''}`} onClick={() => setContactEditModalSubTab('contacto')}>
                            <FileTextIcon width={18} height={18} /> Contacto
                          </button>
                          <button type="button" className={`admin-conv-subtab ${contactEditModalSubTab === 'organizaciones' ? 'active' : ''}`} onClick={() => setContactEditModalSubTab('organizaciones')}>
                            <BuildingIcon width={18} height={18} /> Organizaciones
                          </button>
                          <button type="button" className={`admin-conv-subtab ${contactEditModalSubTab === 'negocios' ? 'active' : ''}`} onClick={() => setContactEditModalSubTab('negocios')}>
                            <RefreshIcon width={18} height={18} /> Negocios
                          </button>
                        </div>
                        {contactDetailSaveError && (
                          <div className="create-contact-modal-error">{contactDetailSaveError}</div>
                        )}
                        {contactEditModalSubTab === 'contacto' && (
                          <section className="create-contact-section">
                            <h3>Datos del contacto</h3>
                            {/* Orden igual que en la sección de solo lectura: organización, nombre, género, cargo, área, subárea, correo, teléfono, temáticas, importancia, país, linkedin, creado, actualizado */}
                            <div className="create-contact-deal-row">
                              <label className="create-contact-label">Nombre organización</label>
                              <div className="create-contact-input create-contact-readonly" style={{ cursor: 'default' }} aria-readonly="true">
                                {contact.organization_name ?? contact.organization_name_from_org ?? '—'}
                              </div>
                            </div>
                            <div className="create-contact-deal-row">
                              <label className="create-contact-label">Nombre</label>
                              <Input
                                value={contactEditDraft.nombre ?? contact.nombre ?? ''}
                                onChange={(e) => setContactEditDraft((d) => d ? { ...d, nombre: e.target.value || undefined } : null)}
                                onBlur={(e) => { const v = e.target.value; const cap = capitalizeFirstLetter(v); if (cap !== v) setContactEditDraft((d) => d ? { ...d, nombre: cap || undefined } : null); }}
                                placeholder="Nombre"
                              />
                            </div>
                            <div className="create-contact-deal-row">
                              <label className="create-contact-label">Apellido</label>
                              <Input
                                value={contactEditDraft.apellido ?? contact.apellido ?? ''}
                                onChange={(e) => setContactEditDraft((d) => d ? { ...d, apellido: e.target.value || undefined } : null)}
                                onBlur={(e) => { const v = e.target.value; const cap = capitalizeFirstLetter(v); if (cap !== v) setContactEditDraft((d) => d ? { ...d, apellido: cap || undefined } : null); }}
                                placeholder="Apellido"
                              />
                            </div>
                            <div className="create-contact-deal-row">
                              <label className="create-contact-label">Género</label>
                              <SearchableSelect
                                value={contactEditDraft.genero ?? contact.genero ?? ''}
                                onChange={(value) => setContactEditDraft((d) => d ? { ...d, genero: value || undefined } : null)}
                                options={['Masculino', 'Femenino']}
                                placeholder="Seleccionar..."
                                allowCustomInput={false}
                                allowSearch={false}
                              />
                            </div>
                            <div className="create-contact-deal-row">
                              <label className="create-contact-label">Cargo</label>
                              <Input
                                value={contactEditDraft.cargo ?? contact.cargo ?? ''}
                                onChange={(e) => setContactEditDraft((d) => d ? { ...d, cargo: e.target.value || undefined } : null)}
                                onBlur={(e) => { const v = e.target.value; const cap = capitalizeFirstLetter(v); if (cap !== v) setContactEditDraft((d) => d ? { ...d, cargo: cap || undefined } : null); }}
                                placeholder="Ej: Director de Innovación"
                              />
                            </div>
                            <FormField label="Área">
                              <SearchableSelect
                                value={contactEditDraft.area ?? contact.area ?? ''}
                                onChange={(value) => setContactEditDraft((d) => d ? { ...d, area: value || undefined, subarea: d.subarea && value && !(subareasPorArea[value as keyof typeof subareasPorArea]?.subareas?.includes(d.subarea)) ? undefined : d.subarea } : null)}
                                options={areas}
                                placeholder="Seleccionar área..."
                                allowCustomInput={false}
                                allowSearch={false}
                              />
                            </FormField>
                            <FormField label="Subárea">
                              <SearchableSelect
                                value={contactEditDraft.subarea ?? contact.subarea ?? ''}
                                onChange={(value) => setContactEditDraft((d) => d ? { ...d, subarea: value || undefined } : null)}
                                options={(subareasPorArea[contactEditDraft?.area ?? contact.area as keyof typeof subareasPorArea]?.subareas) ?? []}
                                placeholder={(contactEditDraft?.area ?? contact.area) ? 'Seleccionar subárea...' : 'Primero selecciona el área'}
                                allowCustomInput={false}
                                allowSearch={false}
                                disabled={!(contactEditDraft?.area ?? contact.area) || !((subareasPorArea[contactEditDraft?.area ?? contact.area as keyof typeof subareasPorArea]?.subareas)?.length)}
                              />
                            </FormField>
                            <div className="create-contact-deal-row">
                              <label className="create-contact-label">Correo electrónico</label>
                              <Input type="email" value={contactEditDraft.correo ?? contact.correo ?? ''} onChange={(e) => setContactEditDraft((d) => d ? { ...d, correo: e.target.value || undefined } : null)} placeholder="correo@ejemplo.com" />
                            </div>
                            <div className="create-contact-deal-row">
                              <label className="create-contact-label">Teléfono</label>
                              <PhoneInput
                                codigoPais={parsePhoneForCrm(contactEditDraft?.telefono ?? contact.telefono).codigoPais}
                                numeroTelefono={parsePhoneForCrm(contactEditDraft?.telefono ?? contact.telefono).numeroTelefono}
                                onCodigoPaisChange={(code) => {
                                  const { numeroTelefono } = parsePhoneForCrm(contactEditDraft?.telefono ?? contact.telefono);
                                  setContactEditDraft((d) => d ? { ...d, telefono: formatPhoneForCrm(code, numeroTelefono) || undefined } : null);
                                }}
                                onNumeroTelefonoChange={(num) => {
                                  const { codigoPais } = parsePhoneForCrm(contactEditDraft?.telefono ?? contact.telefono);
                                  setContactEditDraft((d) => d ? { ...d, telefono: formatPhoneForCrm(codigoPais, num) || undefined } : null);
                                }}
                              />
                            </div>
                            <div className="create-contact-deal-row">
                              <label className="create-contact-label">Temáticas de interés</label>
                              <div className="checkbox-group thematic-axes-two-columns" style={{ marginTop: 4 }}>
                                <label className="checkbox-item select-all-checkbox" style={{ fontWeight: 600, marginBottom: 8, width: '100%' }}>
                                  <input
                                    type="checkbox"
                                    checked={(() => {
                                      const raw = contactEditDraft?.tematicas_interes ?? contact.tematicas_interes;
                                      let vals: string[] = [];
                                      try { const arr = raw == null || raw === '' ? [] : (typeof raw === 'string' ? JSON.parse(raw) : raw); vals = Array.isArray(arr) ? arr : []; } catch { }
                                      return vals.length > 0 && vals.length === thematicAxes.length;
                                    })()}
                                    onChange={(e) => setContactEditDraft((d) => d ? { ...d, tematicas_interes: e.target.checked ? JSON.stringify(thematicAxes.map((t) => t.value)) : undefined } : null)}
                                  />
                                  <span className="checkbox-label">Seleccionar todos</span>
                                </label>
                                {thematicAxes.map((option) => {
                                  const raw = contactEditDraft?.tematicas_interes ?? contact.tematicas_interes;
                                  let vals: string[] = [];
                                  try { const arr = raw == null || raw === '' ? [] : (typeof raw === 'string' ? JSON.parse(raw) : raw); vals = Array.isArray(arr) ? arr : []; } catch { }
                                  const checked = vals.includes(option.value);
                                  return (
                                    <label key={option.value} className="checkbox-item">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                          const newVals = e.target.checked ? [...vals, option.value] : vals.filter((v) => v !== option.value);
                                          setContactEditDraft((d) => d ? { ...d, tematicas_interes: newVals.length > 0 ? JSON.stringify(newVals) : undefined } : null);
                                        }}
                                      />
                                      <span className="checkbox-label">{option.label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="create-contact-deal-row">
                              <label className="create-contact-label">Importancia</label>
                              <SearchableSelect
                                value={contactEditDraft.importancia ?? contact.importancia ?? ''}
                                onChange={(value) => setContactEditDraft((d) => d ? { ...d, importancia: value || undefined } : null)}
                                options={['1', '2', '3', '4', '5']}
                                placeholder="Seleccionar..."
                                allowCustomInput={false}
                                allowSearch={false}
                              />
                            </div>
                            <div className="create-contact-deal-row">
                              <label className="create-contact-label">País / Región</label>
                              <MultiSelectTags
                                selectedValues={(() => {
                                  const raw = contactEditDraft?.pais_region ?? contact.pais_region;
                                  if (raw == null || raw === '') return [];
                                  try {
                                    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                    return Array.isArray(parsed) ? parsed.map((x: unknown) => String(x)) : [raw];
                                  } catch {
                                    const c = countries.find((co: { code: string; name: string }) => co.code === String(raw));
                                    return c ? [c.name] : [String(raw)];
                                  }
                                })()}
                                onChange={(values) => setContactEditDraft((d) => d ? { ...d, pais_region: values.length > 0 ? JSON.stringify(values) : undefined } : null)}
                                options={paisRegionOpcionesConPaises}
                                placeholder="Seleccionar regiones..."
                              />
                            </div>
                            <div className="create-contact-deal-row">
                              <label className="create-contact-label">LinkedIn</label>
                              <Input type="url" value={contactEditDraft.linkedin ?? contact.linkedin ?? ''} onChange={(e) => setContactEditDraft((d) => d ? { ...d, linkedin: e.target.value || undefined } : null)} placeholder="https://www.linkedin.com/in/..." />
                            </div>
                            <div className="create-contact-deal-row">
                              <label className="create-contact-label">Creado</label>
                              <span style={{ fontSize: 14 }}>{contact.created_at ? formatDate(contact.created_at) : '—'}</span>
                            </div>
                            <div className="create-contact-deal-row">
                              <label className="create-contact-label">Actualizado</label>
                              <span style={{ fontSize: 14 }}>{contact.updated_at ? formatDate(contact.updated_at) : '—'}</span>
                            </div>
                          </section>
                        )}
                        {contactEditModalSubTab === 'organizaciones' && (contactOrgs.length === 0 ? (
                          <section className="create-contact-section"><p className="create-contact-hint">No hay organizaciones asociadas.</p></section>
                        ) : (() => {
                          const safeIdx = Math.min(activeContactDetailOrgIndex, contactOrgs.length - 1);
                          const org = contactOrgs[safeIdx];
                          const draft = contactOrgEditDraft;
                          if (!org) return null;
                          return (
                            <section className="create-contact-section">
                              <h3>Organización: {org.organizacion || 'Sin nombre'}</h3>
                              <div className="admin-conversations-subtabs" style={{ marginBottom: 12 }}>
                                {contactOrgs.map((o, idx) => (
                                  <button key={o.id} type="button" className={`admin-conv-subtab ${activeContactDetailOrgIndex === idx ? 'active' : ''}`} onClick={() => { setActiveContactDetailOrgIndex(idx); setContactOrgEditDraft(contactOrgs[idx] ? { ...contactOrgs[idx] } : null); }}>{o.organizacion || 'Sin nombre'}</button>
                                ))}
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Organización</label>
                                <Input value={draft?.organizacion ?? org.organizacion ?? ''} onChange={(e) => setContactOrgEditDraft((d) => d ? { ...d, organizacion: e.target.value || undefined } : null)} placeholder="Nombre organización" />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Tipo</label>
                                <SearchableSelect value={draft?.tipo_organizacion ?? org.tipo_organizacion ?? ''} onChange={(value) => setContactOrgEditDraft((d) => d ? { ...d, tipo_organizacion: value || undefined } : null)} options={tiposOrganizacion} placeholder="Seleccionar tipo..." allowCustomInput={false} allowSearch={false} />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Importancia</label>
                                <SearchableSelect value={draft?.importancia ?? org.importancia ?? ''} onChange={(value) => setContactOrgEditDraft((d) => d ? { ...d, importancia: value || undefined } : null)} options={['Clave', 'No clave', 'Sin clasificar']} placeholder="Seleccionar..." allowCustomInput={false} allowSearch={false} />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Industrias</label>
                                <MultiSelectTags
                                  selectedValues={(() => { const raw = draft?.industrias ?? org.industrias; if (raw == null) return []; if (Array.isArray(raw)) return raw; try { const p = typeof raw === 'string' ? JSON.parse(raw) : raw; return Array.isArray(p) ? p : []; } catch { return []; } })()}
                                  onChange={(values) => setContactOrgEditDraft((d) => d ? { ...d, industrias: values.length > 0 ? JSON.stringify(values) : undefined } : null)}
                                  options={industrias}
                                  placeholder="Seleccionar industrias..."
                                />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Sitio web</label>
                                <Input type="url" value={draft?.sitio_web ?? org.sitio_web ?? ''} onChange={(e) => setContactOrgEditDraft((d) => d ? { ...d, sitio_web: e.target.value || undefined } : null)} placeholder="https://www.ejemplo.com" />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">País origen</label>
                                <SearchableSelect
                                  value={(() => { const code = draft?.pais_origen ?? org.pais_origen; if (!code) return ''; const c = countries.find((co: { code: string; name: string }) => co.code === code); return c ? c.name : code; })()}
                                  onChange={(value) => { const country = countries.find((c: { code: string; name: string }) => c.name === value); setContactOrgEditDraft((d) => d ? { ...d, pais_origen: country ? country.code : (value || undefined) } : null); }}
                                  options={countries.map((c: { code: string; name: string }) => c.name)}
                                  placeholder="Seleccionar país..."
                                  allowCustomInput={false}
                                  allowSearch={true}
                                />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Mercado</label>
                                <MultiSelectTags
                                  selectedValues={(() => {
                                    const raw = draft?.mercado ?? org.mercado;
                                    if (raw == null || raw === '') return [];
                                    try {
                                      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                      return Array.isArray(parsed) ? parsed.map((x: unknown) => String(x)) : [String(raw)];
                                    } catch { return [String(raw)]; }
                                  })()}
                                  onChange={(values) => setContactOrgEditDraft((d) => d ? { ...d, mercado: values.length > 0 ? JSON.stringify(values) : undefined } : null)}
                                  options={paisRegionOpcionesConPaises}
                                  placeholder="Seleccionar mercados..."
                                />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Descripción</label>
                                <textarea className="create-contact-input" rows={3} value={draft?.descripcion ?? org.descripcion ?? ''} onChange={(e) => setContactOrgEditDraft((d) => d ? { ...d, descripcion: e.target.value || undefined } : null)} style={{ resize: 'vertical', width: '100%' }} placeholder="Descripción de la organización" />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Ejes temáticos</label>
                                <div className="checkbox-group thematic-axes-two-columns" style={{ marginTop: 4 }}>
                                  <label className="checkbox-item select-all-checkbox" style={{ fontWeight: 600, marginBottom: 8, width: '100%' }}>
                                    <input
                                      type="checkbox"
                                      checked={(() => {
                                        const raw = draft?.ejes_tematicos ?? org.ejes_tematicos;
                                        let vals: string[] = [];
                                        try { const arr = raw == null || raw === '' ? [] : (typeof raw === 'string' ? JSON.parse(raw) : raw); vals = Array.isArray(arr) ? arr : []; } catch { }
                                        return vals.length > 0 && vals.length === thematicAxes.length;
                                      })()}
                                      onChange={(e) => setContactOrgEditDraft((d) => d ? { ...d, ejes_tematicos: e.target.checked ? JSON.stringify(thematicAxes.map((t) => t.value)) : undefined } : null)}
                                    />
                                    <span className="checkbox-label">Seleccionar todos</span>
                                  </label>
                                  {thematicAxes.map((option) => {
                                    const raw = draft?.ejes_tematicos ?? org.ejes_tematicos;
                                    let vals: string[] = [];
                                    try { const arr = raw == null || raw === '' ? [] : (typeof raw === 'string' ? JSON.parse(raw) : raw); vals = Array.isArray(arr) ? arr : []; } catch { }
                                    const checked = vals.includes(option.value);
                                    return (
                                      <label key={option.value} className="checkbox-item">
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={(e) => {
                                            const newVals = e.target.checked ? [...vals, option.value] : vals.filter((v) => v !== option.value);
                                            setContactOrgEditDraft((d) => d ? { ...d, ejes_tematicos: newVals.length > 0 ? JSON.stringify(newVals) : undefined } : null);
                                          }}
                                        />
                                        <span className="checkbox-label">{option.label}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Siglas</label>
                                <Input value={draft?.siglas ?? org.siglas ?? ''} onChange={(e) => setContactOrgEditDraft((d) => d ? { ...d, siglas: e.target.value || undefined } : null)} placeholder="Siglas" />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Redes sociales</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                  {(['facebook', 'twitter', 'linkedin', 'instagram', 'youtube', 'other'] as const).map((key) => {
                                    const labels: Record<string, string> = { facebook: 'Facebook', twitter: 'Twitter/X', linkedin: 'LinkedIn', instagram: 'Instagram', youtube: 'YouTube', other: 'Otra' };
                                    const placeholders: Record<string, string> = {
                                      facebook: 'https://www.facebook.com/...',
                                      twitter: 'https://twitter.com/...',
                                      linkedin: 'https://www.linkedin.com/company/...',
                                      instagram: 'https://www.instagram.com/...',
                                      youtube: 'https://www.youtube.com/...',
                                      other: 'https://...'
                                    };
                                    let obj: Record<string, string> = {};
                                    try {
                                      const raw = draft?.redes_sociales ?? org.redes_sociales;
                                      if (raw && typeof raw === 'string') obj = JSON.parse(raw) || {}; else if (raw && typeof raw === 'object' && !Array.isArray(raw)) obj = raw as Record<string, string>;
                                    } catch { }
                                    const value = obj[key] ?? '';
                                    return (
                                      <div key={key} className="create-contact-deal-row" style={{ marginBottom: 0 }}>
                                        <label className="create-contact-label">{labels[key]}</label>
                                        <Input
                                          type="url"
                                          value={value}
                                          onChange={(e) => {
                                            const next = { ...obj, [key]: e.target.value || undefined };
                                            if (!e.target.value) delete next[key];
                                            setContactOrgEditDraft((d) => d ? { ...d, redes_sociales: Object.keys(next).length > 0 ? JSON.stringify(next) : undefined } : null);
                                          }}
                                          placeholder={placeholders[key]}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Dominio</label>
                                <span style={{ fontSize: 14 }}>{org.dominio ?? '—'}</span>
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Creado</label>
                                <span style={{ fontSize: 14 }}>{org.created_at ? formatDate(org.created_at) : '—'}</span>
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Actualizado</label>
                                <span style={{ fontSize: 14 }}>{org.updated_at ? formatDate(org.updated_at) : '—'}</span>
                              </div>
                            </section>
                          );
                        })())}
                        {contactEditModalSubTab === 'negocios' && (contactDetailNegocios.length === 0 ? (
                          <section className="create-contact-section"><p className="create-contact-hint">No hay negocios asignados.</p></section>
                        ) : (() => {
                          const safeIdx = Math.min(activeContactDetailNegocioIndex, contactDetailNegocios.length - 1);
                          const n = contactDetailNegocios[safeIdx];
                          const draft = contactNegocioEditDraft;
                          if (!n || !draft) return null;
                          const fieldSchema = pipelinesList.find((p) => p.id === n.pipelineId)?.field_schema ?? [];
                          const dataKeys = fieldSchema.length > 0 ? fieldSchema.map((f) => f.key) : (n.data && typeof n.data === 'object' ? Object.keys(n.data).filter((k) => k !== 'pipeline_stage' && k !== 'priority') : []);
                          const getLabel = (key: string) => fieldSchema.find((f) => f.key === key)?.label ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                          const standKeys = ['selected_stands_updated', 'confirmed_stands', 'selected_stands', 'request_expo_information'];
                          const participationFieldKeys = ['speakers', 'parallel_events', 'iniciativas', 'catalog_sections', 'business_rounds_participants'];
                          const dataKeysFiltered = dataKeys.filter((k) => !standKeys.includes(k) && !participationFieldKeys.includes(k));
                          const stages = stagesByPipelineId[n.pipelineId] ?? [];
                          const data = (draft.data ?? n.data ?? {}) as Record<string, unknown>;
                          const parseArrNeg = (val: unknown): string[] => { if (val == null) return []; if (Array.isArray(val)) return val.map((v) => String(v)); if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p.map((x: unknown) => String(x)) : [val]; } catch { return [val]; } } return []; };
                          return (
                            <section className="create-contact-section">
                              <h3>Negocio: {n.pipelineName} · {n.organizationName}</h3>
                              <div className="admin-conversations-subtabs" style={{ marginBottom: 12 }}>
                                {contactDetailNegocios.map((neg, idx) => (
                                  <button key={`${neg.organizationId}-${neg.pipelineId}`} type="button" className={`admin-conv-subtab ${activeContactDetailNegocioIndex === idx ? 'active' : ''}`} onClick={() => { setActiveContactDetailNegocioIndex(idx); setContactNegocioEditDraft(contactDetailNegocios[idx] ? { pipelineStage: contactDetailNegocios[idx].pipelineStage, priority: contactDetailNegocios[idx].priority, data: contactDetailNegocios[idx].data ? { ...contactDetailNegocios[idx].data } : {} } : null); }}>{neg.pipelineName} · {neg.organizationName}</button>
                                ))}
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Etapa</label>
                                {stages.length > 0 ? (
                                  <SearchableSelect
                                    value={stages.find((s) => s.id === (draft.pipelineStage ?? n.pipelineStage))?.label ?? ''}
                                    onChange={(label) => { const s = stages.find((st) => st.label === label); setContactNegocioEditDraft((d) => d ? { ...d, pipelineStage: s?.id ?? '' } : null); }}
                                    options={stages.map((s) => s.label)}
                                    placeholder="Seleccionar etapa..."
                                    allowCustomInput={false}
                                    allowSearch={false}
                                    getOptionColor={(label) => stages.find((s) => s.label === label)?.color}
                                    prefix={
                                      <span
                                        style={{
                                          width: 14,
                                          height: 14,
                                          borderRadius: '50%',
                                          backgroundColor: stages.find((s) => s.id === (draft.pipelineStage ?? n.pipelineStage))?.color ?? '#94a3b8'
                                        }}
                                        aria-hidden
                                      />
                                    }
                                  />
                                ) : (
                                  <Input value={draft.pipelineStage ?? n.pipelineStage ?? ''} onChange={(e) => setContactNegocioEditDraft((d) => d ? { ...d, pipelineStage: e.target.value } : null)} placeholder="Etapa" />
                                )}
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Prioridad</label>
                                <SearchableSelect
                                  value={draft.priority === 'high' ? 'Alta' : draft.priority === 'low' ? 'Baja' : 'Media'}
                                  onChange={(label) => setContactNegocioEditDraft((d) => d ? { ...d, priority: (label === 'Alta' ? 'high' : label === 'Baja' ? 'low' : 'medium') as 'high' | 'medium' | 'low' } : null)}
                                  options={['Alta', 'Media', 'Baja']}
                                  placeholder="Seleccionar..."
                                  allowCustomInput={false}
                                  allowSearch={false}
                                  getOptionColor={(label) => label === 'Alta' ? '#ef4444' : label === 'Baja' ? '#10b981' : '#f59e0b'}
                                  prefix={
                                    <span
                                      style={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        backgroundColor: draft.priority === 'high' ? '#ef4444' : draft.priority === 'low' ? '#10b981' : '#f59e0b'
                                      }}
                                      aria-hidden
                                    />
                                  }
                                />
                              </div>
                              {(() => {
                                const keyNorm = (k: string) => k.toLowerCase().replace(/-/g, '_').normalize('NFD').replace(/\p{Diacritic}/gu, '');
                                const labelNorm = (k: string) => (fieldSchema.find((f) => f.key === k)?.label ?? '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
                                const getCatalogForKey = (k: string): { value: string; label: string }[] | null => {
                                  const nk = keyNorm(k);
                                  const ln = labelNorm(k);
                                  if (nk.includes('event_program') || (nk.includes('participacion') && nk.includes('programa')) || ln.includes('participacion') && ln.includes('programa')) return eventProgramParticipation;
                                  if (nk.includes('modalidad') || nk.includes('modalities') || ln.includes('modalidad') || ln.includes('modalidades')) return participationModalities;
                                  return null;
                                };
                                return dataKeysFiltered.map((key) => {
                                  const catalog = getCatalogForKey(key);
                                  if (catalog) {
                                    const raw = data[key];
                                    let vals: string[] = [];
                                    if (raw != null) {
                                      if (Array.isArray(raw)) vals = raw.map((v) => String(v));
                                      else if (typeof raw === 'string') { try { const p = JSON.parse(raw); vals = Array.isArray(p) ? p.map((x: unknown) => String(x)) : [raw]; } catch { vals = [raw]; } }
                                      else vals = [String(raw)];
                                    }
                                    return (
                                      <div key={key} className="create-contact-deal-row">
                                        <label className="create-contact-label">{getLabel(key)}</label>
                                        <div className="checkbox-group thematic-axes-two-columns" style={{ marginTop: 4 }}>
                                          {catalog.map((opt) => {
                                            const checked = vals.includes(opt.value);
                                            return (
                                              <label key={opt.value} className="checkbox-item">
                                                <input
                                                  type="checkbox"
                                                  checked={checked}
                                                  onChange={(e) => {
                                                    const newVals = e.target.checked ? [...vals, opt.value] : vals.filter((v) => v !== opt.value);
                                                    setContactNegocioEditDraft((d) => {
                                                      if (!d) return null;
                                                      const nextData = { ...(d.data ?? n.data ?? {}) } as Record<string, unknown>;
                                                      nextData[key] = newVals.length > 0 ? newVals : undefined;
                                                      if (newVals.length === 0) delete nextData[key];
                                                      return { ...d, data: nextData };
                                                    });
                                                  }}
                                                />
                                                <span className="checkbox-label">{opt.label}</span>
                                              </label>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  }
                                  const val = data[key];
                                  const strVal = val == null ? '' : typeof val === 'object' ? JSON.stringify(val) : String(val);
                                  return (
                                    <div key={key} className="create-contact-deal-row">
                                      <label className="create-contact-label">{getLabel(key)}</label>
                                      <Input value={strVal} onChange={(e) => { const v = e.target.value; setContactNegocioEditDraft((d) => { if (!d) return null; const nextData = { ...(d.data ?? n.data ?? {}) } as Record<string, unknown>; if (v === '') delete nextData[key]; else nextData[key] = v; return { ...d, data: nextData }; }); }} />
                                    </div>
                                  );
                                });
                              })()}
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Zona Expo (mapa)</label>
                                <div className="admin-crm-stands-map-wrap" style={{ marginTop: 4 }}>
                                  <ExpoZoneSection selectedStands={parseArrNeg(draft.data?.selected_stands_updated)} confirmedStands={parseArrNeg((draft.data ?? {})?.confirmed_stands)} onChange={() => {}} onConfirmedChange={() => {}} readOnly hideTitle hideLegend={false} hideCheckbox />
                                </div>
                              </div>
                            </section>
                          );
                        })())}
                      </div>
                    </Modal>
                  )}
                  </>
                );
              })()
            ) : selectedCrmOrganization && activeCrmSubTab === 'empresas' ? (
              /* Vista detalle de la organización - sin subtabs ni filtros (igual que contacto) */
              (() => {
                const org = selectedCrmOrganization;
                const orgContactos = contactsByOrgId.get(org.id) || [];
                const orgNegocios = negociosByOrgId.get(org.id) || [];
                return (
                  <>
                  <div className="admin-crm-detail-view">
                    <div className="admin-crm-detail-header">
                      <button
                        className="admin-back-button"
                        onClick={() => {
                          try {
                            const saved = sessionStorage.getItem('adminPanelState');
                            if (saved) {
                              const parsed = JSON.parse(saved);
                              parsed.selectedCrmOrganizationId = null;
                              parsed.activeCrmSubTab = 'negocios';
                              parsed.listViewMode = 'table';
                              sessionStorage.setItem('adminPanelState', JSON.stringify(parsed));
                            }
                          } catch (_) { /* ignore */ }
                          setSelectedCrmOrganization(null);
                          setOrganizationDetailSubTab('organizacion');
                          setActiveOrgDetailNegocioIndex(0);
                          setListViewMode('table');
                          setActiveCrmSubTab('negocios');
                        }}
                      >
                        ← Volver
                      </button>
                      <div className="admin-crm-detail-title">
                        <h2>{org.organizacion || 'Sin nombre'}</h2>
                        <p className="admin-request-email">{org.sitio_web ? <a href={org.sitio_web.startsWith('http') ? org.sitio_web : `https://${org.sitio_web}`} target="_blank" rel="noopener noreferrer">{org.sitio_web}</a> : '—'}</p>
                      </div>
                      <div className="admin-crm-detail-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                        <button
                          type="button"
                          className="admin-action-button-small chat-button"
                          onClick={() => {
                            setOrgEditDraft({ ...org });
                            const neg = orgNegocios[Math.min(activeOrgDetailNegocioIndex, orgNegocios.length - 1)];
                            setOrgNegocioEditDraft(neg ? { pipelineStage: neg.pipelineStage, priority: neg.priority, data: neg.data ? { ...neg.data } : {} } : null);
                            setOrgEditModalOpen(true);
                            setOrgEditModalSubTab('organizacion');
                            setOrgDetailSaveError(null);
                          }}
                          title="Editar organización"
                        >
                          <EditIcon width={18} height={18} /> Editar
                        </button>
                      </div>
                    </div>
                    {orgDetailSaveError && (
                      <div className="admin-crm-empty-state" style={{ color: '#ef4444', marginTop: 8 }}>{orgDetailSaveError}</div>
                    )}
                    <div className="admin-crm-detail-content">
                      <div className="admin-conversations-subtabs" style={{ marginBottom: 16 }}>
                        <button type="button" className={`admin-conv-subtab ${organizationDetailSubTab === 'organizacion' ? 'active' : ''}`} onClick={() => setOrganizationDetailSubTab('organizacion')}>
                          <BuildingIcon width={18} height={18} /> Organización
                        </button>
                        <button type="button" className={`admin-conv-subtab ${organizationDetailSubTab === 'contactos' ? 'active' : ''}`} onClick={() => setOrganizationDetailSubTab('contactos')}>
                          <UsersIcon width={18} height={18} /> Contactos
                        </button>
                        <button type="button" className={`admin-conv-subtab ${organizationDetailSubTab === 'negocios' ? 'active' : ''}`} onClick={() => setOrganizationDetailSubTab('negocios')}>
                          <RefreshIcon width={18} height={18} /> Negocios
                        </button>
                      </div>
                      <div className="admin-profile-tabs-container">
                        <div className="admin-crm-detail-body" style={{ display: 'block' }}>
                          {organizationDetailSubTab === 'organizacion' && (
                          <div className="admin-crm-section-card">
                            <h3><BuildingIcon width={18} height={18} /> Datos de la organización</h3>
                              <div className="admin-crm-contact-info-grid">
                                {org.logo && (
                                  <div className="admin-crm-info-item-full">
                                    <span className="info-label">Logo:</span>
                                    <span className="info-value"><ProxyImage src={org.logo} alt="" className="admin-crm-empresa-logo-img" style={{ maxWidth: 120, maxHeight: 60, objectFit: 'contain' }} /></span>
                                  </div>
                                )}
                                {([
                                  { key: 'tipo_organizacion' as const, label: 'Tipo' }, { key: 'importancia' as const, label: 'Importancia' },
                                  { key: 'industrias' as const, label: 'Industrias' }, { key: 'sitio_web' as const, label: 'Sitio web' },
                                  { key: 'pais_origen' as const, label: 'País origen' }, { key: 'mercado' as const, label: 'Mercado' },
                                  { key: 'descripcion' as const, label: 'Descripción' }, { key: 'ejes_tematicos' as const, label: 'Ejes temáticos' },
                                  { key: 'siglas' as const, label: 'Siglas' }, { key: 'redes_sociales' as const, label: 'Redes sociales' },
                                  { key: 'dominio' as const, label: 'Dominio' },
                                  { key: 'created_at' as const, label: 'Creado' }, { key: 'updated_at' as const, label: 'Actualizado' }
                                ] as Array<{ key: keyof CrmOrganization; label: string }>).map(({ key, label }) => {
                                  const raw = org[key];
                                  const val = raw == null || raw === '' ? null : raw;
                                  let display: React.ReactNode = val == null ? '—' : String(val);
                                  if (key === 'sitio_web' && val) display = <a href={String(val).startsWith('http') ? String(val) : `https://${val}`} target="_blank" rel="noopener noreferrer">{String(val)}</a>;
                                  else if (key === 'created_at' || key === 'updated_at') display = val ? formatDate(String(val)) : '—';
                                  else if (key === 'pais_origen' && val) {
                                    const code = String(val).trim();
                                    display = (countries.find((c: { code: string; name: string }) => c.code === code)?.name ?? code) || '—';
                                  } else if (key === 'ejes_tematicos' && val) {
                                    try {
                                      const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                      const items = Array.isArray(arr) ? arr.map((x: unknown) => String(x)) : String(raw).split(/[,;]/).map((s: string) => s.trim()).filter(Boolean);
                                      const getThematicLabel = (v: string) => thematicAxes.find((a: { value: string; label: string }) => a.value === v)?.label ?? v;
                                      display = items.length > 0 ? <div className="admin-crm-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{items.map((item, i) => <span key={i} className="tag-badge-small">{getThematicLabel(item)}</span>)}</div> : '—';
                                    } catch { display = String(val); }
                                  } else if (key === 'industrias' && val) {
                                    try {
                                      const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                      const items = Array.isArray(arr) ? arr.map((x: unknown) => String(x)) : String(raw).split(/[,;]/).map((s: string) => s.trim()).filter(Boolean);
                                      display = items.length > 0 ? <div className="admin-crm-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{items.map((item, i) => <span key={i} className="tag-badge-small">{item}</span>)}</div> : '—';
                                    } catch { display = String(val); }
                                  } else if (key === 'redes_sociales' && val) {
                                    try {
                                      const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                      if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
                                        const entries = Object.entries(obj).filter(([, u]) => u && String(u).trim());
                                        const labels: Record<string, string> = { facebook: 'Facebook', twitter: 'Twitter', linkedin: 'LinkedIn', instagram: 'Instagram', youtube: 'YouTube', other: 'Otra' };
                                        display = entries.length > 0 ? <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{entries.map(([k, url]) => <span key={k}><strong>{labels[k] || k}:</strong> <a href={String(url).startsWith('http') ? String(url) : `https://${url}`} target="_blank" rel="noopener noreferrer">{String(url)}</a></span>)}</div> : '—';
                                      }
                                    } catch { display = String(val); }
                                  }
                                  return (
                                    <div key={key} className="admin-crm-info-item-full">
                                      <span className="info-label">{label}:</span>
                                      <span className="info-value">{display}</span>
                                    </div>
                                  );
                                })}
                              </div>
                          </div>
                          )}
                          {organizationDetailSubTab === 'contactos' && (
                          <div className="admin-crm-section-card">
                            <h3><UsersIcon width={18} height={18} /> Contactos de la organización</h3>
                            {orgContactos.length === 0 ? (
                              <p className="admin-crm-empty-state">No hay contactos asociados.</p>
                            ) : (
                              <div className="admin-crm-contacts-grid">
                                {orgContactos.map((contact) => (
                                  <div
                                    key={contact.id}
                                    className="admin-crm-contact-card"
                                    data-contact-id={contact.id}
                                    onClick={() => {
                                      setSelectedCrmContact(contact);
                                      setActiveCrmSubTab('contactos');
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSelectedCrmContact(contact);
                                        setActiveCrmSubTab('contactos');
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                      }
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <div className="admin-crm-contact-card-header">
                                      <div className="admin-crm-contact-name-section">
                                        <h3>{[contact.nombre, contact.apellido].filter(Boolean).join(' ') || 'Sin nombre'}</h3>
                                        <p className="admin-crm-contact-email">{contact.correo || '—'}</p>
                                      </div>
                                    </div>
                                    <div className="admin-crm-contact-card-body">
                                      <div className="admin-crm-contact-info-grid">
                                        <div className="admin-crm-contact-info-item">
                                          <span className="info-icon"><BuildingIcon width={18} height={18} /></span>
                                          <div>
                                            <span className="info-label">Organización:</span>
                                            <span className="info-value">{org.organizacion || '—'}</span>
                                          </div>
                                        </div>
                                        <div className="admin-crm-contact-info-item">
                                          <span className="info-icon"><ClipboardIcon width={18} height={18} /></span>
                                          <div>
                                            <span className="info-label">Cargo:</span>
                                            <span className="info-value">{contact.cargo ?? '—'}</span>
                                          </div>
                                        </div>
                                        <div className="admin-crm-contact-info-item">
                                          <span className="info-icon"><PhoneIcon width={18} height={18} /></span>
                                          <div>
                                            <span className="info-label">Teléfono:</span>
                                            <span className="info-value">{contact.telefono ?? '—'}</span>
                                          </div>
                                        </div>
                                        <div className="admin-crm-contact-info-item">
                                          <span className="info-icon"><LinkedInIcon width={18} height={18} /></span>
                                          <div>
                                            <span className="info-label">LinkedIn:</span>
                                            <span className="info-value">
                                              {contact.linkedin ? (
                                                <a href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{contact.linkedin}</a>
                                              ) : '—'}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="admin-crm-contact-info-item">
                                          <span className="info-icon"><ClockIcon width={18} height={18} /></span>
                                          <div>
                                            <span className="info-label">Actualizado:</span>
                                            <span className="info-value">{contact.updated_at ? formatDate(contact.updated_at) : '—'}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          )}
                          {organizationDetailSubTab === 'negocios' && (
                          <div className="admin-crm-section-card">
                            <h3><RefreshIcon width={18} height={18} /> Negocios de la organización</h3>
                            {orgNegocios.length === 0 ? (
                              <p className="admin-crm-empty-state">No hay negocios para esta organización.</p>
                            ) : (
                              <>
                                <div className="admin-conversations-subtabs" style={{ marginTop: 12, marginBottom: 12 }}>
                                  {orgNegocios.map((neg, idx) => (
                                    <button
                                      key={`${neg.pipelineId}-${idx}`}
                                      type="button"
                                      className={`admin-conv-subtab ${activeOrgDetailNegocioIndex === idx ? 'active' : ''}`}
                                      onClick={() => setActiveOrgDetailNegocioIndex(idx)}
                                    >
                                      {neg.pipelineName}
                                    </button>
                                  ))}
                                </div>
                                {(() => {
                                  const safeIdx = Math.min(activeOrgDetailNegocioIndex, Math.max(0, orgNegocios.length - 1));
                                  const n = orgNegocios[safeIdx];
                                  if (!n) return null;
                                  const fieldSchema = pipelinesList.find((p) => p.id === n.pipelineId)?.field_schema ?? [];
                                  const dataKeys = fieldSchema.length > 0 ? fieldSchema.map((f) => f.key) : (n.data && typeof n.data === 'object' ? Object.keys(n.data).filter((k) => k !== 'pipeline_stage' && k !== 'priority') : []);
                                  const getLabel = (key: string) => fieldSchema.find((f) => f.key === key)?.label ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                                  const keyNorm = (k: string) => k.toLowerCase().replace(/-/g, '_').normalize('NFD').replace(/\p{Diacritic}/gu, '');
                                  const getCatalogForKey = (k: string): { value: string; label: string }[] | null => {
                                    const norm = keyNorm(k);
                                    const labelNorm = (fieldSchema.find((f) => f.key === k)?.label ?? '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
                                    if (norm.includes('event_program') || (norm.includes('participacion') && norm.includes('programa')) || (labelNorm.includes('participacion') && labelNorm.includes('programa'))) return eventProgramParticipation;
                                    if (norm.includes('modalidad') || norm.includes('modalities') || labelNorm.includes('modalidad') || labelNorm.includes('modalidades')) return participationModalities;
                                    return null;
                                  };
                                  const formatValue = (val: unknown, key?: string): React.ReactNode => {
                                    if (val == null) return '—';
                                    if (typeof val === 'boolean') return val ? 'Sí' : 'No';
                                    const catalog = key ? getCatalogForKey(key) : null;
                                    const strip = (s: string) => s.trim().replace(/^["']|["']$/g, '');
                                    const toLabel = (v: string) => (catalog ? (catalog.find((c) => c.value.toLowerCase() === strip(v).toLowerCase())?.label ?? strip(v)) : strip(v));
                                    let arr: unknown[] | null = null;
                                    if (Array.isArray(val)) arr = val;
                                    else if (typeof val === 'string' && (val.trim().startsWith('[') || val.includes(','))) {
                                      if (val.trim().startsWith('[')) { try { arr = JSON.parse(val) as unknown[]; } catch { arr = val.split(',').map((s) => s.trim()).filter(Boolean); } } else { arr = val.split(',').map((s) => s.trim()).filter(Boolean); }
                                    }
                                    if (arr && arr.length > 0) {
                                      const first = arr[0];
                                      if (typeof first === 'object' && first !== null) return `${arr.length} ítem(s)`;
                                      return <div className="admin-crm-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{arr.map((v, i) => <span key={i} className="tag-badge-small">{toLabel(String(v))}</span>)}</div>;
                                    }
                                    if (typeof val === 'object') return JSON.stringify(val).length > 80 ? `${JSON.stringify(val).slice(0, 80)}…` : JSON.stringify(val);
                                    return toLabel(String(val));
                                  };
                                  const stages = stagesByPipelineId[n.pipelineId] ?? [];
                                  const standKeys = ['selected_stands_updated', 'confirmed_stands', 'selected_stands', 'request_expo_information'];
                                  const participationFieldKeys = ['speakers', 'parallel_events', 'iniciativas', 'catalog_sections', 'business_rounds_participants'];
                                  const dataKeysFiltered = dataKeys.filter((k) => !standKeys.includes(k) && !participationFieldKeys.includes(k));
                                  const priorityLabel = n.priority === 'high' ? 'Alta' : n.priority === 'low' ? 'Baja' : 'Media';
                                  const parseArrNeg = (val: unknown): string[] => {
                                    if (val == null) return [];
                                    if (Array.isArray(val)) return val.map((v) => String(v));
                                    if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p.map((x: unknown) => String(x)) : [val]; } catch { return [val]; } }
                                    return [];
                                  };
                                  const parseParticipationNeg = (val: unknown): unknown[] => {
                                    if (val == null) return [];
                                    if (Array.isArray(val)) return val;
                                    if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
                                    return [];
                                  };
                                  const negocioData = (n.data || {}) as Record<string, unknown>;
                                  const negocioStands = parseArrNeg(negocioData.selected_stands_updated);
                                  const negocioSpeakers = parseParticipationNeg(negocioData.speakers);
                                  const negocioParallelEvents = parseParticipationNeg(negocioData.parallel_events);
                                  const negocioInitiatives = parseParticipationNeg(negocioData.iniciativas).length > 0 ? parseParticipationNeg(negocioData.iniciativas) : parseParticipationNeg(negocioData.catalog_sections);
                                  const negocioBusinessRounds = parseParticipationNeg(negocioData.business_rounds_participants);
                                  const hasAnyParticipationNeg = negocioSpeakers.length > 0 || negocioParallelEvents.length > 0 || negocioInitiatives.length > 0 || negocioBusinessRounds.length > 0;
                                  const getThematicAxisLabelNeg = (value: string | { value?: string; label?: string }): string => {
                                    if (typeof value === 'object' && value !== null) {
                                      if (value.label) return value.label;
                                      if (value.value) { const axis = thematicAxes.find((t) => t.value === value.value); return axis?.label || value.value; }
                                      return String(value);
                                    }
                                    const axis = thematicAxes.find((t) => t.value === value);
                                    return axis?.label || value;
                                  };
                                  const orgNameForNeg = org.organizacion || '—';
                                  const nWithOrgId = { ...n, organizationId: org.id } as typeof n & { organizationId: number };
                                  return (
                                    <>
                                      <div className="admin-crm-contact-info-grid">
                                        <div className="admin-crm-info-item-full">
                                          <span className="info-label">Etapa:</span>
                                          <span className="info-value">{stages.find((s) => s.id === n.pipelineStage)?.label ?? n.pipelineStage ?? '—'}</span>
                                        </div>
                                        <div className="admin-crm-info-item-full">
                                          <span className="info-label">Prioridad:</span>
                                          <span className="info-value">{priorityLabel}</span>
                                        </div>
                                        {dataKeysFiltered.map((key) => {
                                          const val = negocioData[key];
                                          return (
                                            <div key={key} className="admin-crm-info-item-full">
                                              <span className="info-label">{getLabel(key)}:</span>
                                              <span className="info-value">{formatValue(val, key)}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                      <div className="admin-crm-stands-map-wrap" style={{ marginTop: '24px' }}>
                                        <span className="info-label" style={{ display: 'block', marginBottom: '8px' }}>Zona Expo (mapa):</span>
                                        <ExpoZoneSection
                                          selectedStands={negocioStands}
                                          confirmedStands={parseArrNeg(negocioData.confirmed_stands)}
                                          onChange={() => {}}
                                          onConfirmedChange={() => {}}
                                          readOnly
                                          hideTitle
                                          hideLegend={false}
                                          hideCheckbox
                                        />
                                      </div>
                                      {hasAnyParticipationNeg && (
                                        <div className="admin-profile-tab-panel" style={{ marginTop: '24px' }}>
                                          <span className="info-label" style={{ display: 'block', marginBottom: '8px' }}>Participaciones:</span>
                                          <div className="admin-participations-subtabs" style={{ marginBottom: 12 }}>
                                            <button type="button" className={`admin-participations-subtab ${activeOrgDetailNegocioParticipationsTab === 'speakers' ? 'active' : ''}`} onClick={() => setActiveOrgDetailNegocioParticipationsTab('speakers')}>
                                              <MicIcon width={16} height={16} /> Oradores {negocioSpeakers.length > 0 && <span className="admin-tab-badge">{negocioSpeakers.length}</span>}
                                            </button>
                                            <button type="button" className={`admin-participations-subtab ${activeOrgDetailNegocioParticipationsTab === 'events' ? 'active' : ''}`} onClick={() => setActiveOrgDetailNegocioParticipationsTab('events')}>
                                              <CalendarIcon width={16} height={16} /> Eventos Paralelos {negocioParallelEvents.length > 0 && <span className="admin-tab-badge">{negocioParallelEvents.length}</span>}
                                            </button>
                                            <button type="button" className={`admin-participations-subtab ${activeOrgDetailNegocioParticipationsTab === 'initiatives' ? 'active' : ''}`} onClick={() => setActiveOrgDetailNegocioParticipationsTab('initiatives')}>
                                              <BookIcon width={16} height={16} /> Iniciativas {negocioInitiatives.length > 0 && <span className="admin-tab-badge">{negocioInitiatives.length}</span>}
                                            </button>
                                            <button type="button" className={`admin-participations-subtab ${activeOrgDetailNegocioParticipationsTab === 'business_rounds' ? 'active' : ''}`} onClick={() => setActiveOrgDetailNegocioParticipationsTab('business_rounds')}>
                                              <UsersIcon width={16} height={16} /> Rondas de Negocios {negocioBusinessRounds.length > 0 && <span className="admin-tab-badge">{negocioBusinessRounds.length}</span>}
                                            </button>
                                          </div>
                                          <div className="admin-participations-grid">
                                            {activeOrgDetailNegocioParticipationsTab === 'speakers' && (negocioSpeakers.length > 0 ? negocioSpeakers.map((speaker: any, idx: number) => {
                                              const statusInfo = getSpeakerStatusHelper(speaker);
                                              return (
                                                <div key={`sp-${idx}`} className="admin-participation-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedContactNegocioParticipation({ type: 'speakers', item: speaker, organizationName: orgNameForNeg, organizationId: nWithOrgId.organizationId, pipelineId: n.pipelineId, itemIndex: idx })}>
                                                  <div className="admin-participation-card-status-badges">
                                                    <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>{statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}{statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}</span>
                                                    {statusInfo.hasRequestApproval && <span className="participation-status-badge status-requested"><AlertTriangleIcon width={12} height={12} /> Solicitado</span>}
                                                    {speaker.approval_status === 'approved' ? <span className="participation-status-badge status-approved"><CheckCircleIcon width={12} height={12} /> Aprobado</span> : speaker.approval_status === 'rejected' ? <span className="participation-status-badge status-rejected"><XCircleIcon width={12} height={12} /> Rechazado</span> : (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) ? <span className="participation-status-badge status-pending"><AlertTriangleIcon width={12} height={12} /> Pendiente</span> : null}
                                                  </div>
                                                  <div className="admin-participation-card-header"><div><h3>{speaker.firstName} {speaker.lastName}</h3><span className="admin-participation-org">{orgNameForNeg}</span></div></div>
                                                  <div className="admin-participation-card-body">
                                                    {speaker.position && <div className="admin-participation-info-item"><span className="info-label">Cargo:</span><span className="info-value">{speaker.position}</span></div>}
                                                    {speaker.organization && <div className="admin-participation-info-item"><span className="info-label">Organización:</span><span className="info-value">{speaker.organization}</span></div>}
                                                    {speaker.nationality && <div className="admin-participation-info-item"><span className="info-label">Nacionalidad:</span><span className="info-value">{speaker.nationality}</span></div>}
                                                    {speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0 && <div className="admin-participation-info-item"><span className="info-label">Ejes temáticos:</span><div className="admin-participation-tags">{speaker.thematicAxes.map((axis: any, ai: number) => <span key={ai} className="tag-badge-small">{getThematicAxisLabelNeg(axis)}</span>)}</div></div>}
                                                  </div>
                                                  {(statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (
                                                    <div className="admin-participation-card-footer">
                                                      <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                        {speaker.approval_status !== 'approved' && <button type="button" className="admin-action-button-small approve-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(nWithOrgId.organizationId, n.pipelineId, 'speakers', idx, 'approved'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Aprobar</button>}
                                                        {speaker.approval_status !== 'rejected' && <button type="button" className="admin-action-button-small reject-button" onClick={(e) => { e.stopPropagation(); setRejectionModal({ source: 'contactNegocio', isOpen: true, organizationId: nWithOrgId.organizationId, pipelineId: n.pipelineId, participationType: 'speakers', itemIndex: idx }); setRejectionComment(''); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Rechazar</button>}
                                                        {(speaker.approval_status === 'approved' || speaker.approval_status === 'rejected') && <button type="button" className="admin-action-button-small pending-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(nWithOrgId.organizationId, n.pipelineId, 'speakers', idx, 'pending'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Pendiente</button>}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              ); }) : <div className="admin-empty-state"><p>No hay oradores</p></div>)}
                                            {activeOrgDetailNegocioParticipationsTab === 'events' && (negocioParallelEvents.length > 0 ? negocioParallelEvents.map((event: any, idx: number) => {
                                              const statusInfo = getEventStatusHelper(event);
                                              return (
                                                <div key={`ev-${idx}`} className="admin-participation-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedContactNegocioParticipation({ type: 'events', item: event, organizationName: orgNameForNeg, organizationId: nWithOrgId.organizationId, pipelineId: n.pipelineId, itemIndex: idx })}>
                                                  <div className="admin-participation-card-status-badges">
                                                    <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>{statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}{statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}</span>
                                                    {statusInfo.hasRequestApproval && <span className="participation-status-badge status-requested"><AlertTriangleIcon width={12} height={12} /> Solicitado</span>}
                                                    {event.approval_status === 'approved' ? <span className="participation-status-badge status-approved"><CheckCircleIcon width={12} height={12} /> Aprobado</span> : event.approval_status === 'rejected' ? <span className="participation-status-badge status-rejected"><XCircleIcon width={12} height={12} /> Rechazado</span> : (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) ? <span className="participation-status-badge status-pending"><AlertTriangleIcon width={12} height={12} /> Pendiente</span> : null}
                                                  </div>
                                                  <div className="admin-participation-card-header"><div><h3>{event.name || 'Sin nombre'}</h3><span className="admin-participation-org">{orgNameForNeg}</span></div></div>
                                                  <div className="admin-participation-card-body">
                                                    {event.description && <div className="admin-participation-info-item"><span className="info-label">Descripción:</span><span className="info-value">{event.description}</span></div>}
                                                    {event.duration && <div className="admin-participation-info-item"><span className="info-label">Duración:</span><span className="info-value">{event.duration}</span></div>}
                                                    {event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0 && <div className="admin-participation-info-item"><span className="info-label">Ejes temáticos:</span><div className="admin-participation-tags">{event.thematicAxes.map((axis: any, ai: number) => <span key={ai} className="tag-badge-small">{getThematicAxisLabelNeg(axis)}</span>)}</div></div>}
                                                  </div>
                                                  {(statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (
                                                    <div className="admin-participation-card-footer">
                                                      <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                        {event.approval_status !== 'approved' && <button type="button" className="admin-action-button-small approve-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(nWithOrgId.organizationId, n.pipelineId, 'events', idx, 'approved'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Aprobar</button>}
                                                        {event.approval_status !== 'rejected' && <button type="button" className="admin-action-button-small reject-button" onClick={(e) => { e.stopPropagation(); setRejectionModal({ source: 'contactNegocio', isOpen: true, organizationId: nWithOrgId.organizationId, pipelineId: n.pipelineId, participationType: 'events', itemIndex: idx }); setRejectionComment(''); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Rechazar</button>}
                                                        {(event.approval_status === 'approved' || event.approval_status === 'rejected') && <button type="button" className="admin-action-button-small pending-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(nWithOrgId.organizationId, n.pipelineId, 'events', idx, 'pending'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Pendiente</button>}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              ); }) : <div className="admin-empty-state"><p>No hay eventos paralelos</p></div>)}
                                            {activeOrgDetailNegocioParticipationsTab === 'initiatives' && (negocioInitiatives.length > 0 ? negocioInitiatives.map((init: any, idx: number) => {
                                              const statusInfo = getInitiativeStatusHelper(init);
                                              return (
                                                <div key={`in-${idx}`} className="admin-participation-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedContactNegocioParticipation({ type: 'initiatives', item: init, organizationName: orgNameForNeg, organizationId: nWithOrgId.organizationId, pipelineId: n.pipelineId, itemIndex: idx })}>
                                                  <div className="admin-participation-card-status-badges">
                                                    <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>{statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}{statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}</span>
                                                    {statusInfo.hasRequestApproval && <span className="participation-status-badge status-requested"><AlertTriangleIcon width={12} height={12} /> Solicitado</span>}
                                                    {init.approval_status === 'approved' ? <span className="participation-status-badge status-approved"><CheckCircleIcon width={12} height={12} /> Aprobado</span> : init.approval_status === 'rejected' ? <span className="participation-status-badge status-rejected"><XCircleIcon width={12} height={12} /> Rechazado</span> : (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) ? <span className="participation-status-badge status-pending"><AlertTriangleIcon width={12} height={12} /> Pendiente</span> : null}
                                                  </div>
                                                  <div className="admin-participation-card-header"><div><h3>{init.name || 'Sin nombre'}</h3><span className="admin-participation-org">{orgNameForNeg}</span></div></div>
                                                  <div className="admin-participation-card-body">
                                                    {init.shortDescription && <div className="admin-participation-info-item"><span className="info-label">Descripción corta:</span><span className="info-value">{init.shortDescription}</span></div>}
                                                    {init.thematicAxes && Array.isArray(init.thematicAxes) && init.thematicAxes.length > 0 && <div className="admin-participation-info-item"><span className="info-label">Ejes temáticos:</span><div className="admin-participation-tags">{init.thematicAxes.map((axis: any, ai: number) => <span key={ai} className="tag-badge-small">{getThematicAxisLabelNeg(axis)}</span>)}</div></div>}
                                                  </div>
                                                  {(statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (
                                                    <div className="admin-participation-card-footer">
                                                      <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                        {init.approval_status !== 'approved' && <button type="button" className="admin-action-button-small approve-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(nWithOrgId.organizationId, n.pipelineId, 'initiatives', idx, 'approved'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Aprobar</button>}
                                                        {init.approval_status !== 'rejected' && <button type="button" className="admin-action-button-small reject-button" onClick={(e) => { e.stopPropagation(); setRejectionModal({ source: 'contactNegocio', isOpen: true, organizationId: nWithOrgId.organizationId, pipelineId: n.pipelineId, participationType: 'initiatives', itemIndex: idx }); setRejectionComment(''); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Rechazar</button>}
                                                        {(init.approval_status === 'approved' || init.approval_status === 'rejected') && <button type="button" className="admin-action-button-small pending-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(nWithOrgId.organizationId, n.pipelineId, 'initiatives', idx, 'pending'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Pendiente</button>}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              ); }) : <div className="admin-empty-state"><p>No hay iniciativas</p></div>)}
                                            {activeOrgDetailNegocioParticipationsTab === 'business_rounds' && (negocioBusinessRounds.length > 0 ? negocioBusinessRounds.map((participant: any, idx: number) => {
                                              const brStatus = getBusinessRoundsStatusHelper(participant);
                                              return (
                                                <div key={`br-${idx}`} className="admin-participation-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedContactNegocioParticipation({ type: 'business_rounds', item: participant, organizationName: orgNameForNeg, organizationId: nWithOrgId.organizationId, pipelineId: n.pipelineId, itemIndex: idx })}>
                                                  <div className="admin-participation-card-status-badges">
                                                    <span className={`participation-status-badge status-${brStatus}`}>{brStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}{brStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}</span>
                                                    {participant.approval_status === 'approved' ? <span className="participation-status-badge status-approved"><CheckCircleIcon width={12} height={12} /> Aprobado</span> : participant.approval_status === 'rejected' ? <span className="participation-status-badge status-rejected"><XCircleIcon width={12} height={12} /> Rechazado</span> : brStatus === 'complete' ? <span className="participation-status-badge status-pending"><AlertTriangleIcon width={12} height={12} /> Pendiente</span> : null}
                                                  </div>
                                                  <div className="admin-participation-card-header"><div><h3>{participant.firstName} {participant.lastName}</h3><span className="admin-participation-org">{orgNameForNeg}</span></div></div>
                                                  <div className="admin-participation-card-body">
                                                    {participant.position && <div className="admin-participation-info-item"><span className="info-label">Cargo:</span><span className="info-value">{participant.position}</span></div>}
                                                    {participant.organization && <div className="admin-participation-info-item"><span className="info-label">Organización:</span><span className="info-value">{participant.organization}</span></div>}
                                                    {participant.email && <div className="admin-participation-info-item"><span className="info-label">Email:</span><span className="info-value">{participant.email}</span></div>}
                                                    {participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0 && <div className="admin-participation-info-item"><span className="info-label">Ejes temáticos:</span><div className="admin-participation-tags">{participant.thematicAxes.map((axis: any, ai: number) => <span key={ai} className="tag-badge-small">{getThematicAxisLabelNeg(axis)}</span>)}</div></div>}
                                                  </div>
                                                  {brStatus === 'complete' && (
                                                    <div className="admin-participation-card-footer">
                                                      <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                        {participant.approval_status !== 'approved' && <button type="button" className="admin-action-button-small approve-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(nWithOrgId.organizationId, n.pipelineId, 'business_rounds', idx, 'approved'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Aprobar</button>}
                                                        {participant.approval_status !== 'rejected' && <button type="button" className="admin-action-button-small reject-button" onClick={(e) => { e.stopPropagation(); setRejectionModal({ source: 'contactNegocio', isOpen: true, organizationId: nWithOrgId.organizationId, pipelineId: n.pipelineId, participationType: 'business_rounds', itemIndex: idx }); setRejectionComment(''); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Rechazar</button>}
                                                        {(participant.approval_status === 'approved' || participant.approval_status === 'rejected') && <button type="button" className="admin-action-button-small pending-button" onClick={(e) => { e.stopPropagation(); updateContactNegocioParticipationApproval(nWithOrgId.organizationId, n.pipelineId, 'business_rounds', idx, 'pending'); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Pendiente</button>}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              ); }) : <div className="admin-empty-state"><p>No hay participantes en rondas</p></div>)}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </>
                            )}
                          </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Modal Editar organización: Organización | Negocios */}
                  {orgEditModalOpen && orgEditDraft && (
                    <Modal
                      isOpen={true}
                      onClose={() => {
                        setOrgEditModalOpen(false);
                        setOrgEditDraft(null);
                        setOrgNegocioEditDraft(null);
                        setOrgDetailSaveError(null);
                      }}
                      title={`Editar: ${orgEditDraft.organizacion ?? org.organizacion ?? 'Organización'}`}
                      customFooter={
                        <div className="create-contact-modal-footer" style={{ margin: 0, border: 'none', padding: 0 }}>
                          <button type="button" className="create-contact-footer-btn secondary" onClick={() => { setOrgEditModalOpen(false); setOrgEditDraft(null); setOrgNegocioEditDraft(null); setOrgDetailSaveError(null); }}>Cancelar</button>
                          <button
                            type="button"
                            className="create-contact-footer-btn primary"
                            disabled={orgDetailSaving}
                            onClick={async () => {
                              if (!orgEditDraft) return;
                              setOrgDetailSaving(true);
                              setOrgDetailSaveError(null);
                              const apiUrl = getApiUrl();
                              try {
                                const bodyOrg: Record<string, unknown> = {
                                  organizacion: orgEditDraft.organizacion ?? org.organizacion,
                                  tipo_organizacion: orgEditDraft.tipo_organizacion ?? org.tipo_organizacion,
                                  importancia: orgEditDraft.importancia ?? org.importancia,
                                  industrias: (() => { const v = orgEditDraft.industrias ?? org.industrias; return Array.isArray(v) ? JSON.stringify(v) : v; })(),
                                  sitio_web: orgEditDraft.sitio_web ?? org.sitio_web,
                                  pais_origen: orgEditDraft.pais_origen ?? org.pais_origen,
                                  mercado: orgEditDraft.mercado ?? org.mercado,
                                  descripcion: orgEditDraft.descripcion ?? org.descripcion,
                                  ejes_tematicos: orgEditDraft.ejes_tematicos ?? org.ejes_tematicos,
                                  siglas: orgEditDraft.siglas ?? org.siglas,
                                  redes_sociales: orgEditDraft.redes_sociales ?? org.redes_sociales,
                                  logo: orgEditDraft.logo ?? org.logo,
                                };
                                const resOrg = await fetch(`${apiUrl}/database/organizations/${org.id}`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  credentials: 'include',
                                  body: JSON.stringify(bodyOrg),
                                });
                                const dataOrg = await resOrg.json().catch(() => ({}));
                                if (!resOrg.ok) {
                                  setOrgDetailSaveError(dataOrg?.error || 'Error al guardar organización');
                                  return;
                                }
                                if (resOrg.ok && dataOrg?.data) {
                                  setCrmOrganizations((prev) => prev.map((o) => (o.id === org.id ? { ...o, ...dataOrg.data } : o)));
                                  setSelectedCrmOrganization((prev) => prev?.id === org.id ? { ...prev, ...dataOrg.data } : prev);
                                }
                                const neg = orgNegocios[Math.min(activeOrgDetailNegocioIndex, orgNegocios.length - 1)];
                                if (neg && orgNegocioEditDraft && neg.pipelineId) {
                                  const bodyNeg: Record<string, unknown> = {
                                    pipelineStage: orgNegocioEditDraft.pipelineStage ?? neg.pipelineStage,
                                    priority: orgNegocioEditDraft.priority ?? neg.priority,
                                  };
                                  if (orgNegocioEditDraft.data != null && Object.keys(orgNegocioEditDraft.data).length >= 0) {
                                    bodyNeg.data = orgNegocioEditDraft.data;
                                  }
                                  const resNeg = await fetch(`${apiUrl}/crm/pipeline/${neg.pipelineId}/organization/${org.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    credentials: 'include',
                                    body: JSON.stringify(bodyNeg),
                                  });
                                  const dataNeg = await resNeg.json().catch(() => ({}));
                                  if (resNeg.ok && dataNeg?.negocio) {
                                    setOrganizationNegociosList((prev) => prev.map((n) => (
                                      n.organizationId === org.id && n.pipelineId === neg.pipelineId
                                        ? { ...n, pipelineStage: dataNeg.negocio.pipelineStage, priority: dataNeg.negocio.priority, data: dataNeg.negocio.data ?? n.data }
                                        : n
                                    )));
                                  }
                                }
                                setOrgEditModalOpen(false);
                                setOrgEditDraft(null);
                                setOrgNegocioEditDraft(null);
                              } catch (e) {
                                setOrgDetailSaveError(e instanceof Error ? e.message : 'Error al guardar');
                              } finally {
                                setOrgDetailSaving(false);
                              }
                            }}
                          >
                            {orgDetailSaving ? 'Guardando…' : 'Guardar'}
                          </button>
                        </div>
                      }
                      contentClassName={isDarkMode ? 'admin-panel dark-theme' : ''}
                    >
                      <div className="create-contact-modal-body create-contact-modal-body--no-scroll">
                        <div className="admin-conversations-subtabs" style={{ marginBottom: 16 }}>
                          <button type="button" className={`admin-conv-subtab ${orgEditModalSubTab === 'organizacion' ? 'active' : ''}`} onClick={() => setOrgEditModalSubTab('organizacion')}>
                            <BuildingIcon width={18} height={18} /> Organización
                          </button>
                          <button type="button" className={`admin-conv-subtab ${orgEditModalSubTab === 'negocios' ? 'active' : ''}`} onClick={() => setOrgEditModalSubTab('negocios')}>
                            <RefreshIcon width={18} height={18} /> Negocios
                          </button>
                        </div>
                        {orgDetailSaveError && (
                          <div className="create-contact-modal-error">{orgDetailSaveError}</div>
                        )}
                        {orgEditModalSubTab === 'organizacion' && (() => {
                          const draft = orgEditDraft;
                          if (!draft) return null;
                          return (
                            <section className="create-contact-section">
                              <h3>Datos de la organización</h3>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Organización</label>
                                <Input value={draft.organizacion ?? org.organizacion ?? ''} onChange={(e) => setOrgEditDraft((d) => d ? { ...d, organizacion: e.target.value || undefined } : null)} placeholder="Nombre organización" />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Tipo</label>
                                <SearchableSelect value={draft.tipo_organizacion ?? org.tipo_organizacion ?? ''} onChange={(value) => setOrgEditDraft((d) => d ? { ...d, tipo_organizacion: value || undefined } : null)} options={tiposOrganizacion} placeholder="Seleccionar tipo..." allowCustomInput={false} allowSearch={false} />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Importancia</label>
                                <SearchableSelect value={draft.importancia ?? org.importancia ?? ''} onChange={(value) => setOrgEditDraft((d) => d ? { ...d, importancia: value || undefined } : null)} options={['Clave', 'No clave', 'Sin clasificar']} placeholder="Seleccionar..." allowCustomInput={false} allowSearch={false} />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Industrias</label>
                                <MultiSelectTags
                                  selectedValues={(() => { const raw = draft.industrias ?? org.industrias; if (raw == null) return []; if (Array.isArray(raw)) return raw; try { const p = typeof raw === 'string' ? JSON.parse(raw) : raw; return Array.isArray(p) ? p : []; } catch { return []; } })()}
                                  onChange={(values) => setOrgEditDraft((d) => d ? { ...d, industrias: values.length > 0 ? JSON.stringify(values) : undefined } : null)}
                                  options={industrias}
                                  placeholder="Seleccionar industrias..."
                                />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Sitio web</label>
                                <Input type="url" value={draft.sitio_web ?? org.sitio_web ?? ''} onChange={(e) => setOrgEditDraft((d) => d ? { ...d, sitio_web: e.target.value || undefined } : null)} placeholder="https://www.ejemplo.com" />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">País origen</label>
                                <SearchableSelect
                                  value={(() => { const code = draft.pais_origen ?? org.pais_origen; if (!code) return ''; const c = countries.find((co: { code: string; name: string }) => co.code === code); return c ? c.name : code; })()}
                                  onChange={(value) => { const country = countries.find((c: { code: string; name: string }) => c.name === value); setOrgEditDraft((d) => d ? { ...d, pais_origen: country ? country.code : (value || undefined) } : null); }}
                                  options={countries.map((c: { code: string; name: string }) => c.name)}
                                  placeholder="Seleccionar país..."
                                  allowCustomInput={false}
                                  allowSearch={true}
                                />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Mercado</label>
                                <MultiSelectTags
                                  selectedValues={(() => {
                                    const raw = draft.mercado ?? org.mercado;
                                    if (raw == null || raw === '') return [];
                                    try {
                                      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                      return Array.isArray(parsed) ? parsed.map((x: unknown) => String(x)) : [String(raw)];
                                    } catch { return [String(raw)]; }
                                  })()}
                                  onChange={(values) => setOrgEditDraft((d) => d ? { ...d, mercado: values.length > 0 ? JSON.stringify(values) : undefined } : null)}
                                  options={paisRegionOpcionesConPaises}
                                  placeholder="Seleccionar mercados..."
                                />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Descripción</label>
                                <textarea className="create-contact-input" rows={3} value={draft.descripcion ?? org.descripcion ?? ''} onChange={(e) => setOrgEditDraft((d) => d ? { ...d, descripcion: e.target.value || undefined } : null)} style={{ resize: 'vertical', width: '100%' }} placeholder="Descripción de la organización" />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Ejes temáticos</label>
                                <div className="checkbox-group thematic-axes-two-columns" style={{ marginTop: 4 }}>
                                  <label className="checkbox-item select-all-checkbox" style={{ fontWeight: 600, marginBottom: 8, width: '100%' }}>
                                    <input
                                      type="checkbox"
                                      checked={(() => {
                                        const raw = draft.ejes_tematicos ?? org.ejes_tematicos;
                                        let vals: string[] = [];
                                        try { const arr = raw == null || raw === '' ? [] : (typeof raw === 'string' ? JSON.parse(raw) : raw); vals = Array.isArray(arr) ? arr : []; } catch { }
                                        return vals.length > 0 && vals.length === thematicAxes.length;
                                      })()}
                                      onChange={(e) => setOrgEditDraft((d) => d ? { ...d, ejes_tematicos: e.target.checked ? JSON.stringify(thematicAxes.map((t) => t.value)) : undefined } : null)}
                                    />
                                    <span className="checkbox-label">Seleccionar todos</span>
                                  </label>
                                  {thematicAxes.map((option) => {
                                    const raw = draft.ejes_tematicos ?? org.ejes_tematicos;
                                    let vals: string[] = [];
                                    try { const arr = raw == null || raw === '' ? [] : (typeof raw === 'string' ? JSON.parse(raw) : raw); vals = Array.isArray(arr) ? arr : []; } catch { }
                                    const checked = vals.includes(option.value);
                                    return (
                                      <label key={option.value} className="checkbox-item">
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={(e) => {
                                            const newVals = e.target.checked ? [...vals, option.value] : vals.filter((v) => v !== option.value);
                                            setOrgEditDraft((d) => d ? { ...d, ejes_tematicos: newVals.length > 0 ? JSON.stringify(newVals) : undefined } : null);
                                          }}
                                        />
                                        <span className="checkbox-label">{option.label}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Siglas</label>
                                <Input value={draft.siglas ?? org.siglas ?? ''} onChange={(e) => setOrgEditDraft((d) => d ? { ...d, siglas: e.target.value || undefined } : null)} placeholder="Siglas" />
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Redes sociales</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                  {(['facebook', 'twitter', 'linkedin', 'instagram', 'youtube', 'other'] as const).map((key) => {
                                    const labels: Record<string, string> = { facebook: 'Facebook', twitter: 'Twitter/X', linkedin: 'LinkedIn', instagram: 'Instagram', youtube: 'YouTube', other: 'Otra' };
                                    const placeholders: Record<string, string> = {
                                      facebook: 'https://www.facebook.com/...',
                                      twitter: 'https://twitter.com/...',
                                      linkedin: 'https://www.linkedin.com/company/...',
                                      instagram: 'https://www.instagram.com/...',
                                      youtube: 'https://www.youtube.com/...',
                                      other: 'https://...'
                                    };
                                    let obj: Record<string, string> = {};
                                    try {
                                      const raw = draft.redes_sociales ?? org.redes_sociales;
                                      if (raw && typeof raw === 'string') obj = JSON.parse(raw) || {}; else if (raw && typeof raw === 'object' && !Array.isArray(raw)) obj = raw as Record<string, string>;
                                    } catch { }
                                    const value = obj[key] ?? '';
                                    return (
                                      <div key={key} className="create-contact-deal-row" style={{ marginBottom: 0 }}>
                                        <label className="create-contact-label">{labels[key]}</label>
                                        <Input
                                          type="url"
                                          value={value}
                                          onChange={(e) => {
                                            const next = { ...obj, [key]: e.target.value || undefined };
                                            if (!e.target.value) delete next[key];
                                            setOrgEditDraft((d) => d ? { ...d, redes_sociales: Object.keys(next).length > 0 ? JSON.stringify(next) : undefined } : null);
                                          }}
                                          placeholder={placeholders[key]}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </section>
                          );
                        })()}
                        {orgEditModalSubTab === 'negocios' && (orgNegocios.length === 0 ? (
                          <section className="create-contact-section"><p className="create-contact-hint">No hay negocios para esta organización.</p></section>
                        ) : (() => {
                          const safeIdx = Math.min(activeOrgDetailNegocioIndex, orgNegocios.length - 1);
                          const n = orgNegocios[safeIdx];
                          const draft = orgNegocioEditDraft;
                          if (!n || !draft) return null;
                          const fieldSchema = pipelinesList.find((p) => p.id === n.pipelineId)?.field_schema ?? [];
                          const dataKeys = fieldSchema.length > 0 ? fieldSchema.map((f) => f.key) : (n.data && typeof n.data === 'object' ? Object.keys(n.data).filter((k) => k !== 'pipeline_stage' && k !== 'priority') : []);
                          const getLabel = (key: string) => fieldSchema.find((f) => f.key === key)?.label ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                          const standKeys = ['selected_stands_updated', 'confirmed_stands', 'selected_stands', 'request_expo_information'];
                          const participationFieldKeys = ['speakers', 'parallel_events', 'iniciativas', 'catalog_sections', 'business_rounds_participants'];
                          const dataKeysFiltered = dataKeys.filter((k) => !standKeys.includes(k) && !participationFieldKeys.includes(k));
                          const stages = stagesByPipelineId[n.pipelineId] ?? [];
                          const data = (draft.data ?? n.data ?? {}) as Record<string, unknown>;
                          const parseArrNeg = (val: unknown): string[] => { if (val == null) return []; if (Array.isArray(val)) return val.map((v) => String(v)); if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p.map((x: unknown) => String(x)) : [val]; } catch { return [val]; } } return []; };
                          return (
                            <section className="create-contact-section">
                              <h3>Negocio: {n.pipelineName}</h3>
                              <div className="admin-conversations-subtabs" style={{ marginBottom: 12 }}>
                                {orgNegocios.map((neg, idx) => (
                                  <button key={`${neg.pipelineId}-${idx}`} type="button" className={`admin-conv-subtab ${activeOrgDetailNegocioIndex === idx ? 'active' : ''}`} onClick={() => { setActiveOrgDetailNegocioIndex(idx); setOrgNegocioEditDraft(orgNegocios[idx] ? { pipelineStage: orgNegocios[idx].pipelineStage, priority: orgNegocios[idx].priority, data: orgNegocios[idx].data ? { ...orgNegocios[idx].data } : {} } : null); }}>{neg.pipelineName}</button>
                                ))}
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Etapa</label>
                                {stages.length > 0 ? (
                                  <SearchableSelect
                                    value={stages.find((s) => s.id === (draft.pipelineStage ?? n.pipelineStage))?.label ?? ''}
                                    onChange={(label) => { const s = stages.find((st) => st.label === label); setOrgNegocioEditDraft((d) => d ? { ...d, pipelineStage: s?.id ?? '' } : null); }}
                                    options={stages.map((s) => s.label)}
                                    placeholder="Seleccionar etapa..."
                                    allowCustomInput={false}
                                    allowSearch={false}
                                    getOptionColor={(label) => stages.find((s) => s.label === label)?.color}
                                    prefix={<span style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: stages.find((s) => s.id === (draft.pipelineStage ?? n.pipelineStage))?.color ?? '#94a3b8' }} aria-hidden />}
                                  />
                                ) : (
                                  <Input value={draft.pipelineStage ?? n.pipelineStage ?? ''} onChange={(e) => setOrgNegocioEditDraft((d) => d ? { ...d, pipelineStage: e.target.value } : null)} placeholder="Etapa" />
                                )}
                              </div>
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Prioridad</label>
                                <SearchableSelect
                                  value={draft.priority === 'high' ? 'Alta' : draft.priority === 'low' ? 'Baja' : 'Media'}
                                  onChange={(label) => setOrgNegocioEditDraft((d) => d ? { ...d, priority: (label === 'Alta' ? 'high' : label === 'Baja' ? 'low' : 'medium') as 'high' | 'medium' | 'low' } : null)}
                                  options={['Alta', 'Media', 'Baja']}
                                  placeholder="Seleccionar..."
                                  allowCustomInput={false}
                                  allowSearch={false}
                                  getOptionColor={(label) => label === 'Alta' ? '#ef4444' : label === 'Baja' ? '#10b981' : '#f59e0b'}
                                  prefix={<span style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: draft.priority === 'high' ? '#ef4444' : draft.priority === 'low' ? '#10b981' : '#f59e0b' }} aria-hidden />}
                                />
                              </div>
                              {(() => {
                                const keyNorm = (k: string) => k.toLowerCase().replace(/-/g, '_').normalize('NFD').replace(/\p{Diacritic}/gu, '');
                                const labelNorm = (k: string) => (fieldSchema.find((f) => f.key === k)?.label ?? '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
                                const getCatalogForKey = (k: string): { value: string; label: string }[] | null => {
                                  const nk = keyNorm(k);
                                  const ln = labelNorm(k);
                                  if (nk.includes('event_program') || (nk.includes('participacion') && nk.includes('programa')) || ln.includes('participacion') && ln.includes('programa')) return eventProgramParticipation;
                                  if (nk.includes('modalidad') || nk.includes('modalities') || ln.includes('modalidad') || ln.includes('modalidades')) return participationModalities;
                                  return null;
                                };
                                return dataKeysFiltered.map((key) => {
                                  const catalog = getCatalogForKey(key);
                                  if (catalog) {
                                    const raw = data[key];
                                    let vals: string[] = [];
                                    if (raw != null) {
                                      if (Array.isArray(raw)) vals = raw.map((v) => String(v));
                                      else if (typeof raw === 'string') { try { const p = JSON.parse(raw); vals = Array.isArray(p) ? p.map((x: unknown) => String(x)) : [raw]; } catch { vals = [raw]; } }
                                      else vals = [String(raw)];
                                    }
                                    return (
                                      <div key={key} className="create-contact-deal-row">
                                        <label className="create-contact-label">{getLabel(key)}</label>
                                        <div className="checkbox-group thematic-axes-two-columns" style={{ marginTop: 4 }}>
                                          {catalog.map((opt) => {
                                            const checked = vals.includes(opt.value);
                                            return (
                                              <label key={opt.value} className="checkbox-item">
                                                <input
                                                  type="checkbox"
                                                  checked={checked}
                                                  onChange={(e) => {
                                                    const newVals = e.target.checked ? [...vals, opt.value] : vals.filter((v) => v !== opt.value);
                                                    setOrgNegocioEditDraft((d) => {
                                                      if (!d) return null;
                                                      const nextData = { ...(d.data ?? n.data ?? {}) } as Record<string, unknown>;
                                                      nextData[key] = newVals.length > 0 ? newVals : undefined;
                                                      if (newVals.length === 0) delete nextData[key];
                                                      return { ...d, data: nextData };
                                                    });
                                                  }}
                                                />
                                                <span className="checkbox-label">{opt.label}</span>
                                              </label>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  }
                                  const val = data[key];
                                  const strVal = val == null ? '' : typeof val === 'object' ? JSON.stringify(val) : String(val);
                                  return (
                                    <div key={key} className="create-contact-deal-row">
                                      <label className="create-contact-label">{getLabel(key)}</label>
                                      <Input value={strVal} onChange={(e) => { const v = e.target.value; setOrgNegocioEditDraft((d) => { if (!d) return null; const nextData = { ...(d.data ?? n.data ?? {}) } as Record<string, unknown>; if (v === '') delete nextData[key]; else nextData[key] = v; return { ...d, data: nextData }; }); }} />
                                    </div>
                                  );
                                });
                              })()}
                              <div className="create-contact-deal-row">
                                <label className="create-contact-label">Zona Expo (mapa)</label>
                                <div className="admin-crm-stands-map-wrap" style={{ marginTop: 4 }}>
                                  <ExpoZoneSection selectedStands={parseArrNeg(draft.data?.selected_stands_updated)} confirmedStands={parseArrNeg((draft.data ?? {})?.confirmed_stands)} onChange={() => {}} onConfirmedChange={() => {}} readOnly hideTitle hideLegend={false} hideCheckbox />
                                </div>
                              </div>
                            </section>
                          );
                        })())}
                      </div>
                    </Modal>
                  )}
                  </>
                );
              })()
            ) : !selectedCrmUser ? (
              /* Vista principal con subtabs */
              <div className="admin-crm-list-view">
                <div className="admin-crm-list-header">
                  <div>
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <BarChartIcon width={20} height={20} /> CRM
                    </h2>
                    <p>Gestiona, activa y da seguimiento a tus usuarios</p>
                  </div>
                </div>

                <div className="admin-conversations-subtabs admin-crm-subtabs">
                  <button
                    type="button"
                    className={`admin-conv-subtab ${activeCrmSubTab === 'contactos' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveCrmSubTab('contactos');
                      setPipelineView('list');
                    }}
                  >
                    <UsersIcon width={18} height={18} /> Contactos
                  </button>
                  <button
                    type="button"
                    className={`admin-conv-subtab ${activeCrmSubTab === 'empresas' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveCrmSubTab('empresas');
                      setPipelineView('list');
                    }}
                  >
                    <BuildingIcon width={18} height={18} /> Organizaciones
                  </button>
                  <button
                    type="button"
                    className={`admin-conv-subtab ${activeCrmSubTab === 'negocios' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveCrmSubTab('negocios');
                      setPipelineView('pipeline');
                    }}
                  >
                    <RefreshIcon width={18} height={18} /> Negocios
                  </button>
                </div>

                {/* Barra única: (pipeline: selector primero) + buscador + filtros + (pipeline: rueda | lista: cards/tabla). Altura y estilos unificados. */}
                <div className="admin-crm-filters-bar admin-crm-filters-bar-compact">
                  <div className="admin-crm-toolbar-row">
                    {pipelineView === 'pipeline' && (
                      <div className="admin-pipeline-select-wrap admin-toolbar-control-wrap" ref={pipelineSelectRef}>
                        <button
                          type="button"
                          className="admin-pipeline-select-trigger admin-toolbar-control"
                          onClick={() => setPipelineSelectOpen((o) => !o)}
                          aria-expanded={isPipelineSelectOpen}
                          aria-haspopup="listbox"
                        >
                          <span>{pipelinesList.find(p => p.id === selectedPipelineId)?.name ?? pipelinesList[0]?.name ?? 'Seleccionar pipeline'}</span>
                          <ChevronDownIcon width={16} height={16} />
                        </button>
                        {isPipelineSelectOpen && (
                          <div className="admin-pipeline-select-dropdown" role="listbox">
                            {pipelinesList.map((p) => (
                              <div
                                key={p.id}
                                role="option"
                                className="admin-pipeline-select-option"
                                onClick={() => {
                                  setSelectedPipelineId(p.id);
                                  setPipelineSelectOpen(false);
                                }}
                              >
                                {p.name}
                              </div>
                            ))}
                            <div
                              className="admin-pipeline-select-add"
                              onClick={() => {
                                setIsCreatePipelineModalOpen(true);
                                setPipelineSelectOpen(false);
                              }}
                            >
                              <PlusIcon width={16} height={16} />
                              <span>Nuevo pipeline</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <input
                      type="text"
                      placeholder="Buscar por nombre, email, organización..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="admin-search-input admin-toolbar-control"
                    />
                    <button
                      type="button"
                      className={`admin-filters-trigger-btn admin-toolbar-control ${filtersSidebarOpen ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
                      onClick={() => setFiltersSidebarOpen((open) => !open)}
                      aria-label={filtersSidebarOpen ? 'Cerrar filtros' : 'Abrir filtros'}
                    >
                      <FilterIcon width={18} height={18} />
                      <span>Filtros</span>
                      {hasActiveFilters && <span className="admin-filters-trigger-badge" aria-hidden />}
                    </button>
                    {activeCrmSubTab === 'negocios' && (
                      <button
                        type="button"
                        className="admin-pipeline-settings-btn admin-toolbar-control"
                        onClick={() => setIsPipelineStagesModalOpen(true)}
                        title="Configurar etapas y nombre del pipeline"
                      >
                        <SettingsIcon width={18} height={18} />
                      </button>
                    )}
                    {(activeCrmSubTab === 'contactos' || activeCrmSubTab === 'empresas' || activeCrmSubTab === 'negocios') && (
                      <div className="admin-view-toggle">
                        <button
                          className={`admin-view-toggle-btn admin-toolbar-control ${listViewMode === 'cards' ? 'active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); setListViewMode('cards'); }}
                          title="Vista de tarjetas"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="7" height="7"></rect>
                            <rect x="14" y="3" width="7" height="7"></rect>
                            <rect x="3" y="14" width="7" height="7"></rect>
                            <rect x="14" y="14" width="7" height="7"></rect>
                          </svg>
                        </button>
                        <button
                          className={`admin-view-toggle-btn admin-toolbar-control ${listViewMode === 'table' ? 'active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); setListViewMode('table'); }}
                          title="Vista de tabla"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="3" y1="3" x2="21" y2="3"></line>
                            <line x1="3" y1="8" x2="21" y2="8"></line>
                            <line x1="3" y1="13" x2="21" y2="13"></line>
                            <line x1="3" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="23" x2="21" y2="23"></line>
                          </svg>
                        </button>
                      </div>
                    )}
                    <div className="admin-toolbar-actions">
                      <button
                        type="button"
                        className="admin-toolbar-control admin-toolbar-btn-export"
                        onClick={handleExportToExcel}
                      >
                        Exportar
                      </button>
                      {activeCrmSubTab === 'contactos' && (
                        <button
                          type="button"
                          className="admin-toolbar-control admin-toolbar-btn-create"
                          onClick={() => setShowCreateContactModal(true)}
                        >
                          <PlusIcon width={16} height={16} />
                          Crear contacto
                        </button>
                      )}
                      {activeCrmSubTab === 'empresas' && (
                        <button
                          type="button"
                          className="admin-toolbar-control admin-toolbar-btn-create"
                          onClick={() => setShowCreateOrganizationModal(true)}
                        >
                          <PlusIcon width={16} height={16} />
                          Crear Organización
                        </button>
                      )}
                      {activeCrmSubTab === 'negocios' && (
                        <button
                          type="button"
                          className="admin-toolbar-control admin-toolbar-btn-create"
                          onClick={() => setShowCreateNegocioModal(true)}
                        >
                          <PlusIcon width={16} height={16} />
                          Crear negocio
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contenido según subtab: Negocios (pipeline) | Contactos (lista) | Organizaciones */}
                {activeCrmSubTab === 'negocios' ? (
                  listViewMode === 'table' ? (
                  /* Vista tabla - negocios (organización, pipeline, etapa, prioridad) */
                  <div className="admin-crm-contacts-list">
                    {pipelineNegocios.length === 0 ? (
                      <div className="admin-empty-state">
                        <p>No hay negocios en el pipeline seleccionado</p>
                      </div>
                    ) : (() => {
                      const rawFieldSchema = pipelinesList.find(p => p.id === selectedPipelineId)?.field_schema ?? [];
                      // No mostrar como columnas Oradores, Eventos Paralelos, Iniciativas ni Participantes de Rondas (ya están en Participaciones)
                      const participationFieldKeys = ['speakers', 'oradores', 'parallel_events', 'eventos_paralelos', 'iniciativas', 'catalog_sections', 'business_rounds_participants', 'participantes_rondas', 'rondas'];
                      const participationLabels = ['oradores', 'eventos paralelos', 'iniciativas', 'participantes de las rondas', 'participantes rondas', 'rondas de negocios'];
                      const negociosTableFieldSchema = rawFieldSchema.filter((f) => {
                        const keyNorm = (f.key || '').toLowerCase().replace(/-/g, '_');
                        const labelNorm = (f.label || '').toLowerCase();
                        const isParticipationKey = participationFieldKeys.some((k) => keyNorm === k || keyNorm.includes(k));
                        const isParticipationLabel = participationLabels.some((l) => labelNorm.includes(l) || l.includes(labelNorm));
                        return !isParticipationKey && !isParticipationLabel;
                      });
                      const parseCellToArray = (val: unknown): string[] => {
                        if (val == null) return [];
                        if (Array.isArray(val)) return val.map((v) => (v != null ? String(v).trim() : '')).filter(Boolean);
                        if (typeof val === 'string') {
                          const s = val.trim();
                          if (s === '') return [];
                          if (s.startsWith('[')) { try { const p = JSON.parse(s); return Array.isArray(p) ? p.map((v: unknown) => (v != null ? String(v).trim() : '')).filter(Boolean) : []; } catch { return [s]; } }
                          return [s];
                        }
                        return [];
                      };
                      const formatNegocioDataCell = (field: { key: string; label?: string }, val: unknown): string => {
                        const key = (field.key || '').toLowerCase().replace(/-/g, '_');
                        const labelNorm = (field.label || '').toLowerCase();
                        if (val == null || val === '') return '—';
                        if (key === 'tipo_organizacion' || (labelNorm.includes('tipo') && !labelNorm.includes('importancia'))) return getOrganizationTypeLabel(val as string);
                        if (key === 'importancia' || labelNorm.includes('importancia')) return getImportanciaLabel(val as string);
                        if ((key === 'agreed_modality' || key === 'modalidad') || (labelNorm.includes('modalidad') && !labelNorm.includes('modalidades'))) return getModalityLabel(val as string);
                        if (key === 'pais_origen' || key.includes('pais') || labelNorm.includes('país') || labelNorm.includes('pais')) {
                          const code = String(val).trim();
                          const c = countries.find((co: { code: string; name: string }) => co.code === code);
                          return c ? c.name : (code || '—');
                        }
                        const isEventProgram = key.includes('event_program') || (key.includes('participacion') && key.includes('programa')) || (labelNorm.includes('programa') && (labelNorm.includes('participacion') || labelNorm.includes('participación')));
                        const isModalities = key === 'participation_modalities' || key.includes('modalidades') || (labelNorm.includes('modalidades') && labelNorm.includes('participacion'));
                        const isStands = key.includes('stand') || key.includes('zona') || labelNorm.includes('zona') || labelNorm.includes('stand');
                        if (isEventProgram) {
                          const codes = parseCellToArray(val);
                          if (codes.length === 0) return '—';
                          const labels = codes.map((code) => eventProgramParticipation.find((p) => p.value === code)?.label ?? code);
                          return labels.join(', ');
                        }
                        if (isModalities) {
                          const codes = parseCellToArray(val);
                          if (codes.length === 0) return '—';
                          const labels = codes.map((code) => participationModalities.find((m) => m.value === code)?.label ?? code);
                          return labels.join(', ');
                        }
                        if (isStands) {
                          const items = parseCellToArray(val);
                          if (items.length === 0) return '—';
                          return items.join(', ');
                        }
                        if (Array.isArray(val)) {
                          return val.map((v) => String(v)).join(', ');
                        }
                        if (typeof val === 'object') return JSON.stringify(val);
                        return String(val);
                      };
                      const getTagLabelsForField = (field: { key: string; label?: string }, val: unknown): string[] | null => {
                        const key = (field.key || '').toLowerCase().replace(/-/g, '_');
                        const labelNorm = (field.label || '').toLowerCase();
                        const isEventProgram = key.includes('event_program') || (key.includes('participacion') && key.includes('programa')) || (labelNorm.includes('programa') && (labelNorm.includes('participacion') || labelNorm.includes('participación')));
                        const isModalities = key === 'participation_modalities' || key.includes('modalidades') || (labelNorm.includes('modalidades') && labelNorm.includes('participacion'));
                        const isAgreedModality = key === 'agreed_modality' || labelNorm.includes('modalidad acordada');
                        const isStands = key.includes('stand') || key.includes('zona') || labelNorm.includes('zona') || labelNorm.includes('stand');
                        if (isEventProgram) {
                          const codes = parseCellToArray(val);
                          const labels = codes.map((code) => eventProgramParticipation.find((p) => p.value === code)?.label ?? code);
                          return labels;
                        }
                        if (isModalities) {
                          const codes = parseCellToArray(val);
                          const labels = codes.map((code) => participationModalities.find((m) => m.value === code)?.label ?? code);
                          return labels;
                        }
                        if (isAgreedModality) {
                          const codes = parseCellToArray(val);
                          const labels = codes.map((code) => getModalityLabel(code));
                          return labels.filter((l) => l !== '—');
                        }
                        if (isStands) {
                          return parseCellToArray(val);
                        }
                        return null;
                      };
                      const parseParticipation = (val: unknown): unknown[] => {
                        if (val == null) return [];
                        if (Array.isArray(val)) return val;
                        if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
                        return [];
                      };
                      return (
                      <div ref={negociosTableContainerRef} className="admin-users-table-container">
                        <table className="admin-users-table admin-negocios-table">
                          <thead>
                            <tr>
                              <th>Organización</th>
                              <th>Etapa</th>
                              <th>Prioridad</th>
                              <th>Participaciones</th>
                              <th>Acciones pendientes</th>
                              {negociosTableFieldSchema.map((field) => (
                                <th key={field.key}>{(field.label || field.key).replace(/\s*\(actualizado\)\s*/gi, '').trim() || field.key}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {pipelineNegocios.map((neg) => {
                              const d = neg.data && typeof neg.data === 'object' ? (neg.data as Record<string, unknown>) : null;
                              const isScesde26 = neg.pipelineId === 1 || pipelinesList.find(p => p.id === neg.pipelineId)?.name === 'Scesde26';
                              const speakers = d ? parseParticipation(d.speakers) : [];
                              const parallelEvents = d ? parseParticipation(d.parallel_events) : [];
                              const initiatives = d ? (parseParticipation(d.iniciativas).length > 0 ? parseParticipation(d.iniciativas) : parseParticipation(d.catalog_sections)) : [];
                              const businessRounds = d ? parseParticipation(d.business_rounds_participants) : [];
                              const hasAnyParticipation = speakers.length > 0 || parallelEvents.length > 0 || initiatives.length > 0 || businessRounds.length > 0;
                              const hasPendingApprovals = isScesde26 && d && (
                                (speakers.length > 0 && (speakers as any[]).some((s: any) => (getSpeakerStatusHelper(s).completionStatus === 'complete' || getSpeakerStatusHelper(s).hasRequestApproval) && s?.approval_status !== 'approved' && s?.approval_status !== 'rejected')) ||
                                (parallelEvents.length > 0 && (parallelEvents as any[]).some((e: any) => (getEventStatusHelper(e).completionStatus === 'complete' || getEventStatusHelper(e).hasRequestApproval) && e?.approval_status !== 'approved' && e?.approval_status !== 'rejected')) ||
                                (initiatives.length > 0 && (initiatives as any[]).some((i: any) => (getInitiativeStatusHelper(i).completionStatus === 'complete' || getInitiativeStatusHelper(i).hasRequestApproval) && i?.approval_status !== 'approved' && i?.approval_status !== 'rejected')) ||
                                (businessRounds.length > 0 && (businessRounds as any[]).some((p: any) => getBusinessRoundsStatusHelper(p) === 'complete' && p?.approval_status !== 'approved' && p?.approval_status !== 'rejected'))
                              );
                              const orgForLink = crmOrganizations.find((o) => o.id === neg.organizationId) ?? { id: neg.organizationId, organizacion: neg.organizationName ?? '—' } as CrmOrganization;
                              return (
                              <tr key={`${neg.organizationId}-${neg.pipelineId}`} data-organization-id={neg.organizationId} data-pipeline-id={neg.pipelineId}>
                                <td>
                                  <span
                                    className="admin-negocios-org-link"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      negociosScrollToRowRef.current = { organizationId: neg.organizationId, pipelineId: neg.pipelineId, windowY: window.scrollY, windowX: window.scrollX };
                                      setActiveCrmSubTab('empresas');
                                      setSelectedCrmOrganization(orgForLink);
                                      setOrganizationDetailSubTab('negocios');
                                      setActiveMainTab('crm');
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        negociosScrollToRowRef.current = { organizationId: neg.organizationId, pipelineId: neg.pipelineId, windowY: window.scrollY, windowX: window.scrollX };
                                        setActiveCrmSubTab('empresas');
                                        setSelectedCrmOrganization(orgForLink);
                                        setOrganizationDetailSubTab('negocios');
                                        setActiveMainTab('crm');
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                      }
                                    }}
                                  >
                                    {neg.organizationName ?? '—'}
                                  </span>
                                </td>
                                <td onClick={(e) => e.stopPropagation()}>
                                  <CustomSelect
                                    className="admin-crm-priority-select-small admin-negocios-table-select admin-negocios-etapa-select"
                                    optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                                    value={neg.pipelineStage || 'nuevo'}
                                    onChange={(newStage) => saveNegocioStage(neg.organizationId, newStage)}
                                    options={(pipelineStages.length > 0 ? pipelineStages : DEFAULT_EMPRESA_STAGES).map((s) => ({ value: s.id, label: s.label, color: s.color }))}
                                  />
                                </td>
                                <td onClick={(e) => e.stopPropagation()}>
                                  <CustomSelect
                                    className="admin-crm-priority-select-small admin-negocios-table-select"
                                    optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                                    value={neg.priority === 'high' ? 'high' : neg.priority === 'low' ? 'low' : 'medium'}
                                    onChange={(newValue) => saveNegocioPriority(neg.organizationId, (newValue === 'high' || newValue === 'low' ? newValue : 'medium') as 'high' | 'medium' | 'low')}
                                    options={[
                                      { value: 'high', label: 'Alta', color: '#ef4444' },
                                      { value: 'medium', label: 'Media', color: '#fbbf24' },
                                      { value: 'low', label: 'Baja', color: '#10b981' }
                                    ]}
                                  />
                                </td>
                                <td className="admin-negocios-table-custom-cell">
                                  {isScesde26 && d ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                                      {speakers.length > 0 && <span className="feature-badge-small speakers-badge" title={`${speakers.length} orador(es)`}><MicIcon width={14} height={14} /> {speakers.length}</span>}
                                      {parallelEvents.length > 0 && <span className="feature-badge-small events-badge" title={`${parallelEvents.length} evento(s)`}><CalendarIcon width={14} height={14} /> {parallelEvents.length}</span>}
                                      {initiatives.length > 0 && <span className="feature-badge-small catalog-badge" title="En catálogo"><BookIcon width={14} height={14} /></span>}
                                      {businessRounds.length > 0 && <span className="feature-badge-small rounds-badge" title={`${businessRounds.length} en rondas`}><UsersIcon width={14} height={14} /></span>}
                                      {!hasAnyParticipation && <span style={{ color: '#9ca3af', fontSize: '12px' }}>Sin participaciones</span>}
                                    </div>
                                  ) : '—'}
                                </td>
                                <td className="admin-negocios-table-custom-cell">
                                  {hasPendingApprovals ? (
                                    <span className="status-badge-small approval-badge"><CheckCircleIcon width={14} height={14} /> Aprobaciones</span>
                                  ) : '—'}
                                </td>
                                {negociosTableFieldSchema.map((field) => (
                                  <td key={field.key} className="admin-negocios-table-custom-cell">
                                    {(() => {
                                      const tagLabels = getTagLabelsForField(field, neg.data?.[field.key]);
                                      if (tagLabels !== null) {
                                        if (tagLabels.length === 0) return '—';
                                        return (
                                          <div className="admin-negocios-table-tags admin-participation-tags">
                                            {tagLabels.map((label, i) => (
                                              <span key={`${field.key}-${i}-${String(label).slice(0, 30)}`} className="tag-badge-small">{label}</span>
                                            ))}
                                          </div>
                                        );
                                      }
                                      return formatNegocioDataCell(field, neg.data?.[field.key]);
                                    })()}
                                  </td>
                                ))}
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      );
                    })()}
                  </div>
                  ) : (
                  /* Vista de Pipeline tipo Kanban */
                  <div className="admin-crm-pipeline-view">
                    <div className="admin-crm-pipeline-scroll-container" ref={pipelineScrollContainerRef}>
                      <div className="admin-pipeline-board" ref={pipelineColumnsRef}>
                      {pipelineStages.map((stage) => {
                        const stageNegocios = pipelineNegocios.filter(n => n.pipelineStage === stage.id);

                        return (
                          <div 
                            key={stage.id} 
                            className={`admin-pipeline-column ${dragOverColumn === stage.id ? 'drag-over' : ''}`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              setDragOverColumn(stage.id);
                            }}
                            onDragLeave={() => {
                              setDragOverColumn(null);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              if (draggedNegocioOrganizationId != null) {
                                saveNegocioStage(draggedNegocioOrganizationId, stage.id);
                                setDraggedNegocioOrganizationId(null);
                                setDragOverColumn(null);
                              }
                            }}
                          >
                            <div className="admin-pipeline-column-header" style={{ borderTopColor: stage.color }}>
                              <div className="admin-pipeline-column-title">
                                <span className="admin-pipeline-stage-label">{stage.label}</span>
                                <span className="admin-pipeline-stage-count">({stageNegocios.length})</span>
                              </div>
                            </div>
                            <div className="admin-pipeline-column-body">
                              {stageNegocios.length === 0 ? (
                                <div className="admin-pipeline-empty">
                                  <p>Sin organizaciones</p>
                                </div>
                              ) : (
                                stageNegocios.map((negocio) => {
                                  const priority = (negocio.priority === 'high' || negocio.priority === 'low') ? negocio.priority : 'medium';
                                  const userForDetail = negocio.sourceUserId != null ? users.find(u => u.id === negocio.sourceUserId) : null;

                                  return (
                                    <div
                                      key={negocio.organizationId}
                                      data-organization-id={negocio.organizationId}
                                      className={`admin-pipeline-card priority-${priority} ${draggedNegocioOrganizationId === negocio.organizationId ? 'dragging' : ''}`}
                                      draggable
                                      onDragStart={(e) => {
                                        setDraggedNegocioOrganizationId(negocio.organizationId);
                                        e.dataTransfer.effectAllowed = 'move';
                                        e.dataTransfer.setData('text/plain', String(negocio.organizationId));
                                      }}
                                      onDrag={(e) => {
                                        if (!pipelineScrollContainerRef.current) return;
                                        const container = pipelineScrollContainerRef.current;
                                        const rect = container.getBoundingClientRect();
                                        const mouseX = e.clientX;
                                        const scrollThreshold = 50;
                                        const scrollSpeed = 10;
                                        if (scrollIntervalRef.current) {
                                          clearInterval(scrollIntervalRef.current);
                                          scrollIntervalRef.current = null;
                                        }
                                        if (mouseX - rect.left < scrollThreshold) {
                                          scrollIntervalRef.current = setInterval(() => {
                                            if (pipelineScrollContainerRef.current) {
                                              pipelineScrollContainerRef.current.scrollLeft -= scrollSpeed;
                                            }
                                          }, 16);
                                        } else if (rect.right - mouseX < scrollThreshold) {
                                          scrollIntervalRef.current = setInterval(() => {
                                            if (pipelineScrollContainerRef.current) {
                                              pipelineScrollContainerRef.current.scrollLeft += scrollSpeed;
                                            }
                                          }, 16);
                                        }
                                      }}
                                      onDragEnd={() => {
                                        setDraggedNegocioOrganizationId(null);
                                        if (scrollIntervalRef.current) {
                                          clearInterval(scrollIntervalRef.current);
                                          scrollIntervalRef.current = null;
                                        }
                                      }}
                                      onClick={() => { if (userForDetail) handleOpenCrmDetail(userForDetail); }}
                                    >
                                      {/* Prioridad: arriba, todo el ancho */}
                                      <div className="admin-pipeline-card-priority-fullwidth" onClick={(e) => e.stopPropagation()}>
                                        <CustomSelect
                                          className="admin-crm-priority-select-small"
                                          optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                                          value={priority}
                                          onChange={(newValue) => {
                                            const p = (newValue === 'high' || newValue === 'low') ? newValue : 'medium';
                                            saveNegocioPriority(negocio.organizationId, p);
                                          }}
                                          options={[
                                            { value: 'high', label: 'Alta', color: '#ef4444' },
                                            { value: 'medium', label: 'Media', color: '#fbbf24' },
                                            { value: 'low', label: 'Baja', color: '#10b981' }
                                          ]}
                                        />
                                      </div>
                                      <div className="admin-pipeline-card-header">
                                        <div className="admin-pipeline-card-header-content">
                                          <h4>{negocio.organizationName || `Org #${negocio.organizationId}`}</h4>
                                        </div>
                                      </div>
                                      <div className="admin-pipeline-card-body">
                                        {/* Pipeline Scesde26 (id 1): misma vista que tarjetas de perfiles/contactos */}
                                        {negocio.data && typeof negocio.data === 'object' && (selectedPipelineId === 1 || pipelinesList.find(p => p.id === selectedPipelineId)?.name === 'Scesde26') ? (() => {
                                          const d = negocio.data as Record<string, unknown>;
                                          const parseArr = (val: unknown): string[] => {
                                            if (val == null) return [];
                                            if (Array.isArray(val)) return val.map(v => String(v));
                                            if (typeof val === 'string') {
                                              try {
                                                const p = JSON.parse(val);
                                                return Array.isArray(p) ? p.map((x: unknown) => String(x)) : [val];
                                              } catch {
                                                return [val];
                                              }
                                            }
                                            return [];
                                          };
                                          const mods = parseArr(d.participation_modalities);
                                          const agreed = d.agreed_modality != null && d.agreed_modality !== '' ? String(d.agreed_modality) : '';
                                          const modalityLabel = agreed
                                            ? (participationModalities.find(m => m.value === agreed)?.label ?? agreed)
                                            : mods.length > 0
                                              ? mods.map((mod: string) => participationModalities.find(m => m.value === mod)?.label ?? mod).join(', ')
                                              : 'N/A';
                                          const stands = parseArr(d.selected_stands_updated);
                                          const standsLabel = stands.length > 0 ? stands.join(', ') : 'N/A';
                                          // Participaciones pueden venir como array o como string JSON desde el backend (JSONB con texto)
                                          const parseParticipation = (val: unknown): unknown[] => {
                                            if (val == null) return [];
                                            if (Array.isArray(val)) return val;
                                            if (typeof val === 'string') {
                                              try {
                                                const p = JSON.parse(val);
                                                return Array.isArray(p) ? p : [];
                                              } catch {
                                                return [];
                                              }
                                            }
                                            return [];
                                          };
                                          const speakers = parseParticipation(d.speakers);
                                          const parallelEvents = parseParticipation(d.parallel_events);
                                          const initiatives = parseParticipation(d.iniciativas).length > 0 ? parseParticipation(d.iniciativas) : parseParticipation(d.catalog_sections);
                                          const businessRounds = parseParticipation(d.business_rounds_participants);
                                          const hasAnyParticipation = speakers.length > 0 || parallelEvents.length > 0 || initiatives.length > 0 || businessRounds.length > 0;
                                          // Misma lógica que tarjetas de perfiles: aprobaciones pendientes en oradores, eventos, iniciativas, rondas
                                          const hasPendingApprovals = (speakers.length > 0 && speakers.some((speaker: any) => {
                                            const statusInfo = getSpeakerStatusHelper(speaker);
                                            return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                                              (speaker?.approval_status !== 'approved' && speaker?.approval_status !== 'rejected');
                                          })) ||
                                            (parallelEvents.length > 0 && parallelEvents.some((event: any) => {
                                              const statusInfo = getEventStatusHelper(event);
                                              return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                                                (event?.approval_status !== 'approved' && event?.approval_status !== 'rejected');
                                            })) ||
                                            (initiatives.length > 0 && initiatives.some((init: any) => {
                                              const statusInfo = getInitiativeStatusHelper(init);
                                              return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                                                (init?.approval_status !== 'approved' && init?.approval_status !== 'rejected');
                                            })) ||
                                            (businessRounds.length > 0 && businessRounds.some((participant: any) => {
                                              const status = getBusinessRoundsStatusHelper(participant);
                                              return status === 'complete' &&
                                                (participant?.approval_status !== 'approved' && participant?.approval_status !== 'rejected');
                                            }));
                                          const assignedContacts = Array.isArray(negocio.assignedContacts) ? negocio.assignedContacts : [];
                                          return (
                                            <>
                                              <div className="admin-crm-contact-info-grid">
                                                {assignedContacts.map((contact) => (
                                                  <div key={contact.id} className="admin-crm-contact-info-item">
                                                    <span className="info-icon"><UsersIcon width={18} height={18} /></span>
                                                    <div>
                                                      <span className="info-label">Contacto:</span>
                                                      <span className="info-value">
                                                        {contact.nombre || contact.apellido ? `${contact.nombre || ''} ${contact.apellido || ''}`.trim() : 'N/A'}
                                                        {contact.cargo && (
                                                          <span style={{ display: 'block', fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{contact.cargo}</span>
                                                        )}
                                                      </span>
                                                    </div>
                                                  </div>
                                                ))}
                                                {negocio.organizationType != null && String(negocio.organizationType).trim() !== '' && (
                                                  <div className="admin-crm-contact-info-item">
                                                    <span className="info-icon"><BuildingIcon width={18} height={18} /></span>
                                                    <div>
                                                      <span className="info-label">Tipo:</span>
                                                      <span className="info-value">{negocio.organizationType}</span>
                                                    </div>
                                                  </div>
                                                )}
                                                <div className="admin-crm-contact-info-item">
                                                  <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                                  <div>
                                                    <span className="info-label">Modalidad:</span>
                                                    <span className="info-value">{modalityLabel}</span>
                                                  </div>
                                                </div>
                                                <div className="admin-crm-contact-info-item">
                                                  <span className="info-icon"><MapPinIcon width={18} height={18} /></span>
                                                  <div>
                                                    <span className="info-label">Zona Expo:</span>
                                                    <span className="info-value">{standsLabel}</span>
                                                  </div>
                                                </div>
                                                {d.modality_observations != null && String(d.modality_observations).trim() !== '' && (
                                                  <div className="admin-crm-contact-info-item">
                                                    <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                                    <div>
                                                      <span className="info-label">Observaciones modalidad:</span>
                                                      <span className="info-value">{String(d.modality_observations)}</span>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                              <div className="admin-crm-contact-participations">
                                                <span className="info-label">Participa en:</span>
                                                <div className="admin-crm-participations-badges">
                                                  {speakers.length > 0 && (
                                                    <span className="feature-badge-small speakers-badge" title={`${speakers.length} orador(es)`}>
                                                      <MicIcon width={14} height={14} /> {speakers.length}
                                                    </span>
                                                  )}
                                                  {parallelEvents.length > 0 && (
                                                    <span className="feature-badge-small events-badge" title={`${parallelEvents.length} evento(s)`}>
                                                      <CalendarIcon width={14} height={14} /> {parallelEvents.length}
                                                    </span>
                                                  )}
                                                  {initiatives.length > 0 && (
                                                    <span className="feature-badge-small catalog-badge" title="En catálogo">
                                                      <BookIcon width={14} height={14} />
                                                    </span>
                                                  )}
                                                  {businessRounds.length > 0 && (
                                                    <span className="feature-badge-small rounds-badge" title={`${businessRounds.length} participante(s) en rondas`}>
                                                      <UsersIcon width={14} height={14} />
                                                    </span>
                                                  )}
                                                  {!hasAnyParticipation && (
                                                    <span style={{ color: '#9ca3af', fontSize: '12px' }}>Sin participaciones</span>
                                                  )}
                                                </div>
                                              </div>

                                              {hasPendingApprovals && (
                                                <div className="admin-crm-contact-pending-actions">
                                                  <span className="info-label">Acciones pendientes:</span>
                                                  <div className="admin-crm-pending-badges">
                                                    <span className="status-badge-small approval-badge"><CheckCircleIcon width={14} height={14} /> Aprobaciones</span>
                                                  </div>
                                                </div>
                                              )}
                                            </>
                                          );
                                        })() : (() => {
                                          const fieldSchema = pipelinesList.find(p => p.id === selectedPipelineId)?.field_schema ?? [];
                                          const hasDataKeys = negocio.data && typeof negocio.data === 'object' && Object.keys(negocio.data).filter(k => k !== 'pipeline_stage' && k !== 'priority').length > 0;
                                          if (fieldSchema.length === 0 && !hasDataKeys) return null;
                                          /* Otros pipelines: grid genérico con todos los campos + contactos asignados */
                                          const assignedContactsOther = Array.isArray(negocio.assignedContacts) ? negocio.assignedContacts : [];
                                          const keysToShow = fieldSchema.length > 0
                                            ? fieldSchema.map(f => f.key)
                                            : Object.keys(negocio.data || {}).filter(k => k !== 'pipeline_stage' && k !== 'priority');
                                          const getLabel = (key: string) => fieldSchema.find(f => f.key === key)?.label ?? key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                                          const formatValue = (val: unknown): React.ReactNode => {
                                            if (val == null) return '—';
                                            if (typeof val === 'boolean') return val ? 'Sí' : 'No';
                                            if (Array.isArray(val)) {
                                              if (val.length === 0) return '—';
                                              const first = val[0];
                                              if (typeof first === 'object' && first !== null) return `${val.length} ítem(s)`;
                                              return val.map(v => String(v)).join(', ');
                                            }
                                            if (typeof val === 'object') return JSON.stringify(val).length > 80 ? `${JSON.stringify(val).slice(0, 80)}…` : JSON.stringify(val);
                                            return String(val);
                                          };
                                          return (
                                            <div className="admin-crm-contact-info-grid">
                                              {assignedContactsOther.map((contact) => (
                                                <div key={contact.id} className="admin-crm-contact-info-item">
                                                  <span className="info-icon"><UsersIcon width={18} height={18} /></span>
                                                  <div>
                                                    <span className="info-label">Contacto:</span>
                                                    <span className="info-value">
                                                      {contact.nombre || contact.apellido ? `${contact.nombre || ''} ${contact.apellido || ''}`.trim() : 'N/A'}
                                                      {contact.cargo && (
                                                        <span style={{ display: 'block', fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{contact.cargo}</span>
                                                      )}
                                                    </span>
                                                  </div>
                                                </div>
                                              ))}
                                              {keysToShow.map((key) => {
                                                const val = (negocio.data as Record<string, unknown>)[key];
                                                return (
                                                  <div key={key} className="admin-crm-contact-info-item">
                                                    <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                                    <div>
                                                      <span className="info-label">{getLabel(key)}:</span>
                                                      <span className="info-value">{formatValue(val)}</span>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          );
                                        })()}
                                        {/* Desplegable Organización — abajo de todo */}
                                        {(() => {
                                          const org = crmOrganizations.find((o) => o.id === negocio.organizationId);
                                          if (!org) return null;
                                          const isOrgExp = negocioCardOrgExpandedId === negocio.organizationId;
                                          return (
                                            <div className="admin-crm-contact-info-item admin-crm-empresa-negocios" style={{ marginBottom: 8 }} onClick={(e) => e.stopPropagation()}>
                                              <span className="info-icon"><BuildingIcon width={18} height={18} /></span>
                                              <div className="admin-crm-empresa-negocios-wrap">
                                                <button type="button" className="admin-crm-empresa-negocios-toggle" onClick={() => setNegocioCardOrgExpandedId(isOrgExp ? null : negocio.organizationId)} aria-expanded={isOrgExp}>
                                                  <span className="info-label">Organización</span>
                                                  {isOrgExp ? <ChevronUpIcon width={18} height={18} /> : <ChevronDownIcon width={18} height={18} />}
                                                </button>
                                                {isOrgExp && (
                                                  <>
                                                    <div className="admin-crm-contact-card-header" style={{ marginTop: 8 }}>
                                                      {org.logo ? (
                                                        <div className="admin-crm-empresa-logo-container">
                                                          <ProxyImage src={org.logo} alt="" className="admin-crm-empresa-logo-img" />
                                                        </div>
                                                      ) : null}
                                                      <div className="admin-crm-contact-name-section" style={{ flex: 1, minWidth: 0 }}>
                                                        <h3>{org.organizacion || 'Sin nombre'}</h3>
                                                        {org.sitio_web && (
                                                          <p className="admin-crm-contact-email">
                                                            <a href={org.sitio_web.startsWith('http') ? org.sitio_web : `https://${org.sitio_web}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{org.sitio_web}</a>
                                                          </p>
                                                        )}
                                                      </div>
                                                    </div>
                                                    <div className="admin-crm-contact-info-grid admin-crm-empresa-contacto-body" style={{ marginTop: 8 }}>
                                                      <div className="admin-crm-contact-info-item">
                                                        <span className="info-icon"><BookIcon width={18} height={18} /></span>
                                                        <div><span className="info-label">Tipo:</span><span className="info-value">{getOrganizationTypeLabel(org.tipo_organizacion)}</span></div>
                                                      </div>
                                                      <div className="admin-crm-contact-info-item">
                                                        <span className="info-icon"><AwardIcon width={18} height={18} /></span>
                                                        <div><span className="info-label">Importancia:</span><span className="info-value">{getImportanciaLabel(org.importancia)}</span></div>
                                                      </div>
                                                      <div className="admin-crm-contact-info-item admin-crm-contact-info-item-label-block">
                                                        <span className="info-icon"><TagIcon width={18} height={18} /></span>
                                                        <div>
                                                          <span className="info-label">Industrias:</span>
                                                          <span className="info-value">
                                                            {(() => {
                                                              const raw = org.industrias;
                                                              if (!raw || String(raw).trim() === '') return '—';
                                                              let items: string[] = [];
                                                              try {
                                                                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                                                items = Array.isArray(parsed) ? parsed.map((s: string) => String(s).trim()).filter(Boolean) : (typeof parsed === 'string' ? parsed.split(/[,;]/).map(s => s.trim()).filter(Boolean) : []);
                                                              } catch { items = String(raw).split(/[,;]/).map(s => s.trim()).filter(Boolean); }
                                                              if (items.length === 0) return '—';
                                                              return <div className="admin-crm-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>{items.map((item, idx) => <span key={idx} className="tag-badge-small">{item}</span>)}</div>;
                                                            })()}
                                                          </span>
                                                        </div>
                                                      </div>
                                                      <div className="admin-crm-contact-info-item admin-crm-contact-info-item-label-block">
                                                        <span className="info-icon"><MessageCircleIcon width={18} height={18} /></span>
                                                        <div>
                                                          <span className="info-label">Redes sociales:</span>
                                                          <span className="info-value">
                                                            {(() => {
                                                              const raw = org.redes_sociales;
                                                              if (!raw || String(raw).trim() === '') return '—';
                                                              let obj: Record<string, string> = {};
                                                              try {
                                                                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                                                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) obj = parsed;
                                                              } catch { return String(raw); }
                                                              const labels: Record<string, string> = { facebook: 'Facebook', twitter: 'Twitter', linkedin: 'LinkedIn', instagram: 'Instagram', youtube: 'YouTube', other: 'Otra' };
                                                              const entries = Object.entries(obj).filter(([, url]) => url && String(url).trim() !== '');
                                                              if (entries.length === 0) return '—';
                                                              return <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>{entries.map(([key, url]) => { const label = labels[key] || key.charAt(0).toUpperCase() + key.slice(1); const href = url.startsWith('http') ? url : `https://${url}`; return <span key={key} style={{ display: 'block' }}><span style={{ fontWeight: 600, marginRight: '6px' }}>{label}:</span><a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{url}</a></span>; })}</div>;
                                                            })()}
                                                          </span>
                                                        </div>
                                                      </div>
                                                      <div className="admin-crm-contact-info-item">
                                                        <span className="info-icon"><ClockIcon width={18} height={18} /></span>
                                                        <div><span className="info-label">Actualizado:</span><span className="info-value">{org.updated_at ? formatDate(org.updated_at) : '—'}</span></div>
                                                      </div>
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })()}
                                        {/* Desplegable Contactos asociados — abajo de todo */}
                                        {(() => {
                                          const contactos = contactsByOrgId.get(negocio.organizationId) || [];
                                          if (contactos.length === 0) return null;
                                          const isContactosExp = negocioCardContactosExpandedId === negocio.organizationId;
                                          return (
                                            <div className="admin-crm-contact-info-item admin-crm-empresa-contactos" style={{ marginBottom: 8 }} onClick={(e) => e.stopPropagation()}>
                                              <span className="info-icon"><UsersIcon width={18} height={18} /></span>
                                              <div className="admin-crm-empresa-negocios-wrap">
                                                <button type="button" className="admin-crm-empresa-negocios-toggle" onClick={() => setNegocioCardContactosExpandedId(isContactosExp ? null : negocio.organizationId)} aria-expanded={isContactosExp}>
                                                  <span className="info-label">Contactos ({contactos.length})</span>
                                                  {isContactosExp ? <ChevronUpIcon width={18} height={18} /> : <ChevronDownIcon width={18} height={18} />}
                                                </button>
                                                {isContactosExp && (
                                                  <ul className="admin-crm-empresa-negocios-list admin-crm-empresa-contactos-list">
                                                    {contactos.map((contact) => (
                                                      <li key={contact.id} className="admin-crm-empresa-contacto-row">
                                                        <div className="admin-crm-empresa-contacto-header">
                                                          <strong>{[contact.nombre, contact.apellido].filter(Boolean).join(' ') || 'Sin nombre'}</strong>
                                                          {contact.correo && <span className="admin-crm-empresa-contacto-email">{contact.correo}</span>}
                                                        </div>
                                                        <div className="admin-crm-contact-info-grid admin-crm-empresa-contacto-body">
                                                          <div className="admin-crm-contact-info-item">
                                                            <span className="info-icon"><ClipboardIcon width={18} height={18} /></span>
                                                            <div><span className="info-label">Cargo:</span><span className="info-value">{contact.cargo ?? '—'}</span></div>
                                                          </div>
                                                          <div className="admin-crm-contact-info-item">
                                                            <span className="info-icon"><PhoneIcon width={18} height={18} /></span>
                                                            <div><span className="info-label">Teléfono:</span><span className="info-value">{contact.telefono ?? '—'}</span></div>
                                                          </div>
                                                          <div className="admin-crm-contact-info-item">
                                                            <span className="info-icon"><LinkedInIcon width={18} height={18} /></span>
                                                            <div>
                                                              <span className="info-label">LinkedIn:</span>
                                                              <span className="info-value">
                                                                {contact.linkedin ? <a href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{contact.linkedin}</a> : '—'}
                                                              </span>
                                                            </div>
                                                          </div>
                                                          <div className="admin-crm-contact-info-item">
                                                            <span className="info-icon"><ClockIcon width={18} height={18} /></span>
                                                            <div><span className="info-label">Actualizado:</span><span className="info-value">{contact.updated_at ? formatDate(contact.updated_at) : '—'}</span></div>
                                                          </div>
                                                        </div>
                                                      </li>
                                                    ))}
                                                  </ul>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        );
                      })}
                        </div>
                      </div>
                  </div>
                  ) ) : activeCrmSubTab === 'contactos' ? (
                  loadingContacts ? (
                    <div className="admin-crm-contacts-list"><div className="admin-empty-state"><p>Cargando contactos...</p></div></div>
                  ) : listViewMode === 'table' ? (
                  <>
                  {/* Vista de tabla - contactos: mismos 7 campos que las tarjetas. Columnas 1 y 2 fijas. */}
                  <div className="admin-crm-contacts-list">
                    {filteredCrmContacts.length === 0 ? (
                      <div className="admin-empty-state">
                        <p>{searchTerm ? 'No se encontraron contactos' : 'No hay contactos para mostrar'}</p>
                      </div>
                    ) : (
                      <div className="admin-users-table-container">
                        <table className="admin-users-table admin-crm-contacts-table">
                          <thead>
                            <tr>
                              <th>Organización</th>
                              <th>Nombre</th>
                              <th>Correo</th>
                              <th>Cargo</th>
                              <th>Teléfono</th>
                              <th>LinkedIn</th>
                              <th>Actualizado</th>
                              <th>Org. / Negocios</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredCrmContacts.map((contact) => {
                              const orgIds = getContactOrgIds(contact);
                              const negociosWithOrg = orgIds.flatMap((orgId) =>
                                (negociosByOrgId.get(orgId) || [])
                                  .filter((neg) => (neg.assignedContactIds || []).includes(contact.id))
                                  .map((neg) => ({ ...neg, orgId }))
                              );
                              const hasOrgNeg = orgIds.length > 0 || negociosWithOrg.length > 0;
                              return (
                                <tr
                                  key={contact.id}
                                  data-contact-id={contact.id}
                                >
                                  <td>
                                    {orgIds.length === 0 ? (contact.organization_name || contact.organization_name_from_org || '—') : (
                                      (() => {
                                        const names = orgIds.map((oid) => crmOrganizations.find((o) => o.id === oid)?.organizacion).filter(Boolean);
                                        return names.length > 0 ? names.join(', ') : (contact.organization_name || contact.organization_name_from_org || '—');
                                      })()
                                    )}
                                  </td>
                                  <td>
                                    <a
                                      href="#"
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedCrmContact(contact); setActiveHistorialSubTab('correo'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                      className="admin-crm-contact-table-name-link"
                                    >
                                      {[contact.nombre, contact.apellido].filter(Boolean).join(' ') || 'Sin nombre'}
                                    </a>
                                  </td>
                                  <td style={{ color: '#64748b' }}>{contact.correo ?? '—'}</td>
                                  <td>{contact.cargo ?? '—'}</td>
                                  <td>{contact.telefono ?? '—'}</td>
                                  <td>
                                    {contact.linkedin ? (
                                      <a href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`} target="_blank" rel="noopener noreferrer">{contact.linkedin}</a>
                                    ) : '—'}
                                  </td>
                                  <td>{contact.updated_at ? formatDate(contact.updated_at) : '—'}</td>
                                  <td>
                                    {hasOrgNeg ? (
                                      <button
                                        type="button"
                                        className="admin-crm-table-ver-org-negocios"
                                        onClick={(e) => { e.stopPropagation(); setContactTableOrgNegociosModal(contact); }}
                                      >
                                        Ver {orgIds.length > 0 || negociosWithOrg.length > 0 ? `(${orgIds.length} org., ${negociosWithOrg.length} neg.)` : ''}
                                      </button>
                                    ) : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  {contactTableOrgNegociosModal && (() => {
                    const contact = contactTableOrgNegociosModal;
                    const orgIds = getContactOrgIds(contact);
                    const negociosWithOrg = orgIds.flatMap((orgId) =>
                      (negociosByOrgId.get(orgId) || [])
                        .filter((neg) => (neg.assignedContactIds || []).includes(contact.id))
                        .map((neg) => ({ ...neg, orgId }))
                    );
                    const parseArr = (val: unknown): string[] => {
                      if (val == null) return [];
                      if (Array.isArray(val)) return val.map(v => String(v));
                      if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p.map((x: unknown) => String(x)) : [val]; } catch { return [val]; } }
                      return [];
                    };
                    const parseParticipation = (val: unknown): unknown[] => {
                      if (val == null) return [];
                      if (Array.isArray(val)) return val;
                      if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
                      return [];
                    };
                    const priorityLabel = (p: string) => p === 'high' ? 'Alta' : p === 'low' ? 'Baja' : 'Media';
                    return (
                      <Modal
                        isOpen={true}
                        onClose={() => setContactTableOrgNegociosModal(null)}
                        title={`Organizaciones y negocios — ${[contact.nombre, contact.apellido].filter(Boolean).join(' ') || 'Sin nombre'}`}
                        contentClassName={isDarkMode ? 'admin-panel dark-theme' : ''}
                      >
                        <div className="admin-crm-contact-org-negocios-modal-body" style={{ padding: '8px 0' }}>
                          {orgIds.length > 0 && (
                            <section style={{ marginBottom: 24 }}>
                              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Organizaciones ({orgIds.length})</h3>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {orgIds.map((orgId) => {
                                  const org = crmOrganizations.find((o) => o.id === orgId);
                                  if (!org) return <div key={orgId}>—</div>;
                                  return (
                                    <div key={orgId} className="admin-crm-section-card" style={{ padding: 16 }}>
                                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                                        {org.logo && <ProxyImage src={org.logo} alt="" style={{ maxWidth: 80, maxHeight: 40, objectFit: 'contain' }} />}
                                        <div>
                                          <div style={{ fontWeight: 600 }}>{org.organizacion || 'Sin nombre'}</div>
                                          {org.sitio_web && <a href={org.sitio_web.startsWith('http') ? org.sitio_web : `https://${org.sitio_web}`} target="_blank" rel="noopener noreferrer">{org.sitio_web}</a>}
                                        </div>
                                      </div>
                                      <div className="admin-crm-contact-info-grid" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <div className="admin-crm-contact-info-item"><span className="info-label">Tipo:</span><span className="info-value">{getOrganizationTypeLabel(org.tipo_organizacion)}</span></div>
                                        <div className="admin-crm-contact-info-item"><span className="info-label">Importancia:</span><span className="info-value">{getImportanciaLabel(org.importancia)}</span></div>
                                        {org.industrias && (() => {
                                          let items: string[] = [];
                                          try { const p = typeof org.industrias === 'string' ? JSON.parse(org.industrias) : org.industrias; items = Array.isArray(p) ? p : []; } catch { }
                                          if (items.length === 0) return null;
                                          return <div className="admin-crm-contact-info-item"><span className="info-label">Industrias:</span><span className="info-value">{items.join(', ')}</span></div>;
                                        })()}
                                        <div className="admin-crm-contact-info-item"><span className="info-label">Actualizado:</span><span className="info-value">{org.updated_at ? formatDate(org.updated_at) : '—'}</span></div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </section>
                          )}
                          {negociosWithOrg.length > 0 && (
                            <section>
                              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Negocios ({negociosWithOrg.length})</h3>
                              <ul className="admin-crm-empresa-negocios-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {negociosWithOrg.map((neg, idx) => {
                                  const d = neg.data && typeof neg.data === 'object' ? (neg.data as Record<string, unknown>) : null;
                                  const isScesde26 = neg.pipelineId === 1 || neg.pipelineName === 'Scesde26';
                                  const orgName = crmOrganizations.find((o) => o.id === neg.orgId)?.organizacion;
                                  const stageLabel = (stagesByPipelineId[neg.pipelineId] || []).find((s) => s.id === neg.pipelineStage)?.label ?? neg.pipelineStage;
                                  return (
                                    <li key={`${neg.orgId}-${neg.pipelineId}-${idx}`} className="admin-crm-empresa-negocio-row" style={{ marginBottom: 16, padding: 12, border: '1px solid #e2e8f0', borderRadius: 8 }}>
                                      <div style={{ fontWeight: 600, marginBottom: 8 }}>{neg.pipelineName}{orgName ? ` (${orgName})` : ''}</div>
                                      <div className="admin-crm-contact-info-grid" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                                        <div className="admin-crm-contact-info-item"><span className="info-label">Etapa:</span><span className="info-value">{stageLabel}</span></div>
                                        <div className="admin-crm-contact-info-item"><span className="info-label">Prioridad:</span><span className="info-value">{priorityLabel(neg.priority)}</span></div>
                                      </div>
                                      {d && isScesde26 && (
                                        <div className="admin-crm-contact-info-grid" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                          {(() => {
                                            const agreed = d.agreed_modality != null && d.agreed_modality !== '' ? String(d.agreed_modality) : '';
                                            const modalityLabel = agreed ? (participationModalities.find(m => m.value === agreed)?.label ?? agreed) : (parseArr(d.participation_modalities).length > 0 ? parseArr(d.participation_modalities).map((mod: string) => participationModalities.find(m => m.value === mod)?.label ?? mod).join(', ') : 'N/A');
                                            const stands = parseArr(d.selected_stands_updated);
                                            const standsLabel = stands.length > 0 ? stands.join(', ') : 'N/A';
                                            const speakers = parseParticipation(d.speakers);
                                            const parallelEvents = parseParticipation(d.parallel_events);
                                            const initiatives = parseParticipation(d.iniciativas).length > 0 ? parseParticipation(d.iniciativas) : parseParticipation(d.catalog_sections);
                                            const businessRounds = parseParticipation(d.business_rounds_participants);
                                            const hasAny = speakers.length > 0 || parallelEvents.length > 0 || initiatives.length > 0 || businessRounds.length > 0;
                                            return (
                                              <>
                                                <div className="admin-crm-contact-info-item"><span className="info-label">Modalidad:</span><span className="info-value">{modalityLabel}</span></div>
                                                <div className="admin-crm-contact-info-item"><span className="info-label">Zona Expo:</span><span className="info-value">{standsLabel}</span></div>
                                                {hasAny && (
                                                  <div className="admin-crm-contact-info-item">
                                                    <span className="info-label">Participa en:</span>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                                                      {speakers.length > 0 && <span className="feature-badge-small speakers-badge"><MicIcon width={14} height={14} /> {speakers.length}</span>}
                                                      {parallelEvents.length > 0 && <span className="feature-badge-small events-badge"><CalendarIcon width={14} height={14} /> {parallelEvents.length}</span>}
                                                      {initiatives.length > 0 && <span className="feature-badge-small catalog-badge"><BookIcon width={14} height={14} /></span>}
                                                      {businessRounds.length > 0 && <span className="feature-badge-small rounds-badge"><UsersIcon width={14} height={14} /></span>}
                                                    </div>
                                                  </div>
                                                )}
                                              </>
                                            );
                                          })()}
                                        </div>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </section>
                          )}
                          {orgIds.length === 0 && negociosWithOrg.length === 0 && (
                            <p className="admin-crm-empty-state">Sin organizaciones ni negocios asociados.</p>
                          )}
                        </div>
                      </Modal>
                    );
                  })()}
                  </>
                  ) : (
                  /* Vista tarjetas - contactos de tabla crm_contacts */
                  <div className="admin-crm-contacts-list">
                    {filteredCrmContacts.length === 0 ? (
                      <div className="admin-empty-state">
                        <p>{searchTerm ? 'No se encontraron contactos' : 'No hay contactos para mostrar'}</p>
                      </div>
                    ) : (
                      <div className="admin-crm-contacts-grid">
                        {filteredCrmContacts.map((contact) => (
                          <div
                            key={contact.id}
                            className="admin-crm-contact-card"
                            data-contact-id={contact.id}
                            onClick={() => {
                              setSelectedCrmContact(contact);
                              setActiveHistorialSubTab('correo');
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedCrmContact(contact); setActiveHistorialSubTab('correo'); window.scrollTo({ top: 0, behavior: 'smooth' }); } }}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="admin-crm-contact-card-header">
                              <div className="admin-crm-contact-name-section">
                                <h3>{[contact.nombre, contact.apellido].filter(Boolean).join(' ') || 'Sin nombre'}</h3>
                                <p className="admin-crm-contact-email">{contact.correo || '—'}</p>
                              </div>
                            </div>
                            <div className="admin-crm-contact-card-body">
                              <div className="admin-crm-contact-info-grid">
                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><BuildingIcon width={18} height={18} /></span>
                                  <div>
                                    <span className="info-label">Organización{getContactOrgIds(contact).length > 1 ? 'es' : ''}:</span>
                                    <span className="info-value">
                                      {(() => {
                                        const orgIds = getContactOrgIds(contact);
                                        if (orgIds.length === 0) return contact.organization_name || contact.organization_name_from_org || '—';
                                        const names = orgIds.map((oid) => crmOrganizations.find((o) => o.id === oid)?.organizacion).filter(Boolean);
                                        return names.length > 0 ? names.join(', ') : (contact.organization_name || contact.organization_name_from_org || '—');
                                      })()}
                                    </span>
                                  </div>
                                </div>
                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><ClipboardIcon width={18} height={18} /></span>
                                  <div>
                                    <span className="info-label">Cargo:</span>
                                    <span className="info-value">{contact.cargo ?? '—'}</span>
                                  </div>
                                </div>
                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><PhoneIcon width={18} height={18} /></span>
                                  <div>
                                    <span className="info-label">Teléfono:</span>
                                    <span className="info-value">{contact.telefono ?? '—'}</span>
                                  </div>
                                </div>
                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><LinkedInIcon width={18} height={18} /></span>
                                  <div>
                                    <span className="info-label">LinkedIn:</span>
                                    <span className="info-value">
                                      {contact.linkedin ? (
                                        <a href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{contact.linkedin}</a>
                                      ) : '—'}
                                    </span>
                                  </div>
                                </div>
                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><ClockIcon width={18} height={18} /></span>
                                  <div>
                                    <span className="info-label">Actualizado:</span>
                                    <span className="info-value">{contact.updated_at ? formatDate(contact.updated_at) : '—'}</span>
                                  </div>
                                </div>
                                {getContactOrgIds(contact).length > 0 && (() => {
                                  const orgIds = getContactOrgIds(contact);
                                  const isOrgExpanded = contactOrgExpandedId === contact.id;
                                  return (
                                    <div className="admin-crm-contact-info-item admin-crm-empresa-negocios" onClick={(e) => e.stopPropagation()}>
                                      <span className="info-icon"><BuildingIcon width={18} height={18} /></span>
                                      <div className="admin-crm-empresa-negocios-wrap">
                                        <button
                                          type="button"
                                          className="admin-crm-empresa-negocios-toggle"
                                          onClick={() => setContactOrgExpandedId(isOrgExpanded ? null : contact.id)}
                                          aria-expanded={isOrgExpanded}
                                        >
                                          <span className="info-label">Organización{orgIds.length > 1 ? 'es' : ''} ({orgIds.length})</span>
                                          {isOrgExpanded ? <ChevronUpIcon width={18} height={18} /> : <ChevronDownIcon width={18} height={18} />}
                                        </button>
                                        {isOrgExpanded && orgIds.map((orgId) => {
                                          const org = crmOrganizations.find((o) => o.id === orgId);
                                          if (!org) return <span key={orgId} className="info-value" style={{ marginTop: 8, display: 'block' }}>—</span>;
                                          return (
                                            <div key={orgId} style={{ marginTop: 8 }}>
                                              <div className="admin-crm-contact-card-header">
                                                {org.logo ? (
                                                  <div className="admin-crm-empresa-logo-container">
                                                    <ProxyImage src={org.logo} alt="" className="admin-crm-empresa-logo-img" />
                                                  </div>
                                                ) : null}
                                                <div className="admin-crm-contact-name-section" style={{ flex: 1, minWidth: 0 }}>
                                                  <h3>{org.organizacion || 'Sin nombre'}</h3>
                                                  {org.sitio_web && (
                                                    <p className="admin-crm-contact-email">
                                                      <a href={org.sitio_web.startsWith('http') ? org.sitio_web : `https://${org.sitio_web}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{org.sitio_web}</a>
                                                    </p>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="admin-crm-contact-info-grid admin-crm-empresa-contacto-body" style={{ marginTop: 8 }}>
                                                <div className="admin-crm-contact-info-item">
                                                  <span className="info-icon"><BookIcon width={18} height={18} /></span>
                                                  <div>
                                                    <span className="info-label">Tipo:</span>
                                                    <span className="info-value">{getOrganizationTypeLabel(org.tipo_organizacion)}</span>
                                                  </div>
                                                </div>
                                                <div className="admin-crm-contact-info-item">
                                                  <span className="info-icon"><AwardIcon width={18} height={18} /></span>
                                                  <div>
                                                    <span className="info-label">Importancia:</span>
                                                    <span className="info-value">{getImportanciaLabel(org.importancia)}</span>
                                                  </div>
                                                </div>
                                                <div className="admin-crm-contact-info-item admin-crm-contact-info-item-label-block">
                                                  <span className="info-icon"><TagIcon width={18} height={18} /></span>
                                                  <div>
                                                    <span className="info-label">Industrias:</span>
                                                    <span className="info-value">
                                                      {(() => {
                                                        const raw = org.industrias;
                                                        if (!raw || String(raw).trim() === '') return '—';
                                                        let items: string[] = [];
                                                        try {
                                                          const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                                          items = Array.isArray(parsed) ? parsed.map((s: string) => String(s).trim()).filter(Boolean) : (typeof parsed === 'string' ? parsed.split(/[,;]/).map(s => s.trim()).filter(Boolean) : []);
                                                        } catch {
                                                          items = String(raw).split(/[,;]/).map(s => s.trim()).filter(Boolean);
                                                        }
                                                        if (items.length === 0) return '—';
                                                        return (
                                                          <div className="admin-crm-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                                            {items.map((item, idx) => (
                                                              <span key={idx} className="tag-badge-small">{item}</span>
                                                            ))}
                                                          </div>
                                                        );
                                                      })()}
                                                    </span>
                                                  </div>
                                                </div>
                                                <div className="admin-crm-contact-info-item admin-crm-contact-info-item-label-block">
                                                  <span className="info-icon"><MessageCircleIcon width={18} height={18} /></span>
                                                  <div>
                                                    <span className="info-label">Redes sociales:</span>
                                                    <span className="info-value">
                                                      {(() => {
                                                        const raw = org.redes_sociales;
                                                        if (!raw || String(raw).trim() === '') return '—';
                                                        let obj: Record<string, string> = {};
                                                        try {
                                                          const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                                          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) obj = parsed;
                                                        } catch { return String(raw); }
                                                        const labels: Record<string, string> = { facebook: 'Facebook', twitter: 'Twitter', linkedin: 'LinkedIn', instagram: 'Instagram', youtube: 'YouTube', other: 'Otra' };
                                                        const entries = Object.entries(obj).filter(([, url]) => url && String(url).trim() !== '');
                                                        if (entries.length === 0) return '—';
                                                        return (
                                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                                                            {entries.map(([key, url]) => {
                                                              const label = labels[key] || key.charAt(0).toUpperCase() + key.slice(1);
                                                              const href = url.startsWith('http') ? url : `https://${url}`;
                                                              return (
                                                                <span key={key} style={{ display: 'block' }}>
                                                                  <span style={{ fontWeight: 600, marginRight: '6px' }}>{label}:</span>
                                                                  <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{url}</a>
                                                                </span>
                                                              );
                                                            })}
                                                          </div>
                                                        );
                                                      })()}
                                                    </span>
                                                  </div>
                                                </div>
                                                <div className="admin-crm-contact-info-item">
                                                  <span className="info-icon"><ClockIcon width={18} height={18} /></span>
                                                  <div>
                                                    <span className="info-label">Actualizado:</span>
                                                    <span className="info-value">{org.updated_at ? formatDate(org.updated_at) : '—'}</span>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })()}
                                {(() => {
                                  const contactOrgIds = getContactOrgIds(contact);
                                  const negociosWithOrg = contactOrgIds.flatMap((orgId) =>
                                    (negociosByOrgId.get(orgId) || [])
                                      .filter((neg) => (neg.assignedContactIds || []).includes(contact.id))
                                      .map((neg) => ({ ...neg, orgId }))
                                  );
                                  if (negociosWithOrg.length === 0) return null;
                                  const isExpanded = contactNegociosExpandedId === contact.id;
                                  return (
                                    <div className="admin-crm-contact-info-item admin-crm-empresa-negocios" onClick={(e) => e.stopPropagation()}>
                                      <span className="info-icon"><BuildingIcon width={18} height={18} /></span>
                                      <div className="admin-crm-empresa-negocios-wrap">
                                        <button
                                          type="button"
                                          className="admin-crm-empresa-negocios-toggle"
                                          onClick={() => setContactNegociosExpandedId(isExpanded ? null : contact.id)}
                                          aria-expanded={isExpanded}
                                        >
                                          <span className="info-label">Negocios ({negociosWithOrg.length})</span>
                                          {isExpanded ? <ChevronUpIcon width={18} height={18} /> : <ChevronDownIcon width={18} height={18} />}
                                        </button>
                                        {isExpanded && (
                                          <ul className="admin-crm-empresa-negocios-list">
                                            {negociosWithOrg.map((neg, idx) => {
                                              const isScesde26 = neg.pipelineId === 1 || neg.pipelineName === 'Scesde26';
                                              const d = neg.data && typeof neg.data === 'object' ? (neg.data as Record<string, unknown>) : null;
                                              const fieldSchema = pipelinesList.find(p => p.id === neg.pipelineId)?.field_schema ?? [];
                                              const parseArr = (val: unknown): string[] => {
                                                if (val == null) return [];
                                                if (Array.isArray(val)) return val.map(v => String(v));
                                                if (typeof val === 'string') {
                                                  try {
                                                    const p = JSON.parse(val);
                                                    return Array.isArray(p) ? p.map((x: unknown) => String(x)) : [val];
                                                  } catch {
                                                    return [val];
                                                  }
                                                }
                                                return [];
                                              };
                                              const parseParticipation = (val: unknown): unknown[] => {
                                                if (val == null) return [];
                                                if (Array.isArray(val)) return val;
                                                if (typeof val === 'string') {
                                                  try {
                                                    const p = JSON.parse(val);
                                                    return Array.isArray(p) ? p : [];
                                                  } catch {
                                                    return [];
                                                  }
                                                }
                                                return [];
                                              };
                                              const orgName = crmOrganizations.find((o) => o.id === neg.orgId)?.organizacion;
                                              return (
                                                <li key={`${neg.orgId}-${neg.pipelineId}-${idx}`} className="admin-crm-empresa-negocio-row">
                                                  <div className="admin-crm-empresa-negocio-header">
                                                    <strong>{neg.pipelineName}{orgName ? ` (${orgName})` : ''}</strong>
                                                    <div className="admin-crm-empresa-negocio-selects-row" onClick={(e) => e.stopPropagation()}>
                                                      <label className="admin-crm-empresa-negocio-select-label">
                                                        <span className="admin-crm-empresa-negocio-select-label-text">Etapa</span>
                                                        <CustomSelect
                                                          className="admin-crm-priority-select-small admin-crm-empresa-etapa-select"
                                                          optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                                                          value={neg.pipelineStage}
                                                          onChange={(newStage) => neg.orgId != null && saveEmpresaNegocioStage(neg.orgId, neg.pipelineId, newStage)}
                                                        options={(stagesByPipelineId[neg.pipelineId] || DEFAULT_EMPRESA_STAGES).map((s) => ({ value: s.id, label: s.label, color: s.color }))}
                                                        />
                                                      </label>
                                                      <label className="admin-crm-empresa-negocio-select-label">
                                                        <span className="admin-crm-empresa-negocio-select-label-text">Prioridad</span>
                                                        <CustomSelect
                                                          className="admin-crm-priority-select-small"
                                                          optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                                                          value={neg.priority}
                                                          onChange={(newPriority) => neg.orgId != null && saveEmpresaNegocioPriority(neg.orgId, neg.pipelineId, newPriority as 'high' | 'medium' | 'low')}
                                                          options={[
                                                            { value: 'high', label: 'Alta', color: '#ef4444' },
                                                            { value: 'medium', label: 'Media', color: '#fbbf24' },
                                                            { value: 'low', label: 'Baja', color: '#10b981' }
                                                          ]}
                                                        />
                                                      </label>
                                                    </div>
                                                  </div>
                                                  {d && isScesde26 ? (
                                                    <div className="admin-crm-contact-info-grid admin-crm-empresa-negocio-body">
                                                      {(() => {
                                                        const mods = parseArr(d.participation_modalities);
                                                        const agreed = d.agreed_modality != null && d.agreed_modality !== '' ? String(d.agreed_modality) : '';
                                                        const modalityLabel = agreed
                                                          ? (participationModalities.find(m => m.value === agreed)?.label ?? agreed)
                                                          : mods.length > 0
                                                            ? mods.map((mod: string) => participationModalities.find(m => m.value === mod)?.label ?? mod).join(', ')
                                                            : 'N/A';
                                                        const stands = parseArr(d.selected_stands_updated);
                                                        const standsLabel = stands.length > 0 ? stands.join(', ') : 'N/A';
                                                        const speakers = parseParticipation(d.speakers);
                                                        const parallelEvents = parseParticipation(d.parallel_events);
                                                        const initiatives = parseParticipation(d.iniciativas).length > 0 ? parseParticipation(d.iniciativas) : parseParticipation(d.catalog_sections);
                                                        const businessRounds = parseParticipation(d.business_rounds_participants);
                                                        const hasAnyParticipation = speakers.length > 0 || parallelEvents.length > 0 || initiatives.length > 0 || businessRounds.length > 0;
                                                        const hasPendingApprovals = (speakers.length > 0 && speakers.some((s: any) => (getSpeakerStatusHelper(s).completionStatus === 'complete' || getSpeakerStatusHelper(s).hasRequestApproval) && s?.approval_status !== 'approved' && s?.approval_status !== 'rejected')) ||
                                                          (parallelEvents.length > 0 && parallelEvents.some((e: any) => (getEventStatusHelper(e).completionStatus === 'complete' || getEventStatusHelper(e).hasRequestApproval) && e?.approval_status !== 'approved' && e?.approval_status !== 'rejected')) ||
                                                          (initiatives.length > 0 && initiatives.some((i: any) => (getInitiativeStatusHelper(i).completionStatus === 'complete' || getInitiativeStatusHelper(i).hasRequestApproval) && i?.approval_status !== 'approved' && i?.approval_status !== 'rejected')) ||
                                                          (businessRounds.length > 0 && businessRounds.some((p: any) => getBusinessRoundsStatusHelper(p) === 'complete' && p?.approval_status !== 'approved' && p?.approval_status !== 'rejected'));
                                                        return (
                                                          <>
                                                            <div className="admin-crm-contact-info-item">
                                                              <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                                              <div>
                                                                <span className="info-label">Modalidad:</span>
                                                                <span className="info-value">{modalityLabel}</span>
                                                              </div>
                                                            </div>
                                                            <div className="admin-crm-contact-info-item">
                                                              <span className="info-icon"><MapPinIcon width={18} height={18} /></span>
                                                              <div>
                                                                <span className="info-label">Zona Expo:</span>
                                                                <span className="info-value">{standsLabel}</span>
                                                              </div>
                                                            </div>
                                                            {d.modality_observations != null && String(d.modality_observations).trim() !== '' && (
                                                              <div className="admin-crm-contact-info-item">
                                                                <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                                                <div>
                                                                  <span className="info-label">Observaciones:</span>
                                                                  <span className="info-value">{String(d.modality_observations)}</span>
                                                                </div>
                                                              </div>
                                                            )}
                                                            <div className="admin-crm-contact-participations">
                                                              <span className="info-label">Participa en:</span>
                                                              <div className="admin-crm-participations-badges">
                                                                {speakers.length > 0 && <span className="feature-badge-small speakers-badge"><MicIcon width={14} height={14} /> {speakers.length}</span>}
                                                                {parallelEvents.length > 0 && <span className="feature-badge-small events-badge"><CalendarIcon width={14} height={14} /> {parallelEvents.length}</span>}
                                                                {initiatives.length > 0 && <span className="feature-badge-small catalog-badge"><BookIcon width={14} height={14} /></span>}
                                                                {businessRounds.length > 0 && <span className="feature-badge-small rounds-badge"><UsersIcon width={14} height={14} /></span>}
                                                                {!hasAnyParticipation && <span style={{ color: '#9ca3af', fontSize: '12px' }}>Sin participaciones</span>}
                                                              </div>
                                                            </div>
                                                            {hasPendingApprovals && (
                                                              <div className="admin-crm-contact-pending-actions">
                                                                <span className="info-label">Acciones pendientes:</span>
                                                                <div className="admin-crm-pending-badges">
                                                                  <span className="status-badge-small approval-badge"><CheckCircleIcon width={14} height={14} /> Aprobaciones</span>
                                                                </div>
                                                              </div>
                                                            )}
                                                          </>
                                                        );
                                                      })()}
                                                    </div>
                                                  ) : (fieldSchema.length > 0 || (d && Object.keys(d).filter(k => k !== 'pipeline_stage' && k !== 'priority').length > 0)) ? (
                                                    <div className="admin-crm-contact-info-grid admin-crm-empresa-negocio-body">
                                                      {(() => {
                                                        const keysToShow = fieldSchema.length > 0
                                                          ? fieldSchema.map(f => f.key)
                                                          : Object.keys(d || {}).filter(k => k !== 'pipeline_stage' && k !== 'priority');
                                                        const getLabel = (key: string) => fieldSchema.find(f => f.key === key)?.label ?? key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                                                        const formatValue = (val: unknown): React.ReactNode => {
                                                          if (val == null) return '—';
                                                          if (typeof val === 'boolean') return val ? 'Sí' : 'No';
                                                          if (Array.isArray(val)) {
                                                            if (val.length === 0) return '—';
                                                            const first = val[0];
                                                            if (typeof first === 'object' && first !== null) return `${val.length} ítem(s)`;
                                                            return val.map(v => String(v)).join(', ');
                                                          }
                                                          if (typeof val === 'object') return JSON.stringify(val).length > 80 ? `${JSON.stringify(val).slice(0, 80)}…` : JSON.stringify(val);
                                                          return String(val);
                                                        };
                                                        return keysToShow.map((key) => {
                                                          const val = (d || {})[key];
                                                          return (
                                                            <div key={key} className="admin-crm-contact-info-item">
                                                              <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                                              <div>
                                                                <span className="info-label">{getLabel(key)}:</span>
                                                                <span className="info-value">{formatValue(val)}</span>
                                                              </div>
                                                            </div>
                                                          );
                                                        });
                                                      })()}
                                                    </div>
                                                  ) : null}
                                                </li>
                                              );
                                            })}
                                          </ul>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                  ) ) : activeCrmSubTab === 'empresas' ? (
                  /* Vista Organizaciones - cards o tabla crm_organizations */
                  <div className="admin-crm-empresas-view">
                    <div className="admin-crm-contacts-list">
                      {loadingOrganizations ? (
                        <div className="admin-empty-state"><p>Cargando organizaciones...</p></div>
                      ) : filteredCrmOrganizations.length === 0 ? (
                        <div className="admin-empty-state">
                          <p>{searchTerm ? 'No se encontraron organizaciones' : 'No hay organizaciones para mostrar'}</p>
                        </div>
                      ) : listViewMode === 'table' ? (
                        <div className="admin-users-table-container">
                          <table className="admin-users-table">
                            <thead>
                              <tr>
                                <th>Organización</th>
                                <th>Tipo</th>
                                <th>Importancia</th>
                                <th>Sitio web</th>
                                <th>País</th>
                                <th>Mercado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredCrmOrganizations.map((org) => (
                                <tr
                                  key={org.id}
                                  data-organization-id={org.id}
                                  onClick={() => { setSelectedCrmOrganization(org); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <td>{org.organizacion ?? '—'}</td>
                                  <td>{getOrganizationTypeLabel(org.tipo_organizacion)}</td>
                                  <td>{getImportanciaLabel(org.importancia)}</td>
                                  <td>
                                    {org.sitio_web ? (
                                      <a href={org.sitio_web.startsWith('http') ? org.sitio_web : `https://${org.sitio_web}`} target="_blank" rel="noopener noreferrer">{org.sitio_web}</a>
                                    ) : '—'}
                                  </td>
                                  <td>{org.pais_origen ?? '—'}</td>
                                  <td>{org.mercado ?? '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="admin-crm-contacts-grid">
                          {filteredCrmOrganizations.map((org) => (
                            <div
                              key={org.id}
                              className="admin-crm-contact-card"
                              data-organization-id={org.id}
                              onClick={() => { setSelectedCrmOrganization(org); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                            >
                              <div className="admin-crm-contact-card-header">
                                {org.logo ? (
                                  <div className="admin-crm-empresa-logo-container">
                                    <ProxyImage src={org.logo} alt="" className="admin-crm-empresa-logo-img" />
                                  </div>
                                ) : null}
                                <div className="admin-crm-contact-name-section" style={{ flex: 1, minWidth: 0 }}>
                                  <h3>{org.organizacion || 'Sin nombre'}</h3>
                                  {org.sitio_web && (
                                    <p className="admin-crm-contact-email">
                                      <a href={org.sitio_web.startsWith('http') ? org.sitio_web : `https://${org.sitio_web}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{org.sitio_web}</a>
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="admin-crm-contact-card-body">
                                <div className="admin-crm-contact-info-grid">
                                  <div className="admin-crm-contact-info-item">
                                    <span className="info-icon"><BookIcon width={18} height={18} /></span>
                                    <div>
                                      <span className="info-label">Tipo:</span>
                                      <span className="info-value">{getOrganizationTypeLabel(org.tipo_organizacion)}</span>
                                    </div>
                                  </div>
                                  <div className="admin-crm-contact-info-item">
                                    <span className="info-icon"><AwardIcon width={18} height={18} /></span>
                                    <div>
                                      <span className="info-label">Importancia:</span>
                                      <span className="info-value">{getImportanciaLabel(org.importancia)}</span>
                                    </div>
                                  </div>
                                  <div className="admin-crm-contact-info-item admin-crm-contact-info-item-label-block">
                                    <span className="info-icon"><TagIcon width={18} height={18} /></span>
                                    <div>
                                      <span className="info-label">Industrias:</span>
                                      <span className="info-value">
                                        {(() => {
                                          const raw = org.industrias;
                                          if (!raw || String(raw).trim() === '') return '—';
                                          let items: string[] = [];
                                          try {
                                            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                            items = Array.isArray(parsed) ? parsed.map((s: string) => String(s).trim()).filter(Boolean) : (typeof parsed === 'string' ? parsed.split(/[,;]/).map(s => s.trim()).filter(Boolean) : []);
                                          } catch {
                                            items = String(raw).split(/[,;]/).map(s => s.trim()).filter(Boolean);
                                          }
                                          if (items.length === 0) return '—';
                                          return (
                                            <div className="admin-crm-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                              {items.map((item, idx) => (
                                                <span key={idx} className="tag-badge-small">{item}</span>
                                              ))}
                                            </div>
                                          );
                                        })()}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="admin-crm-contact-info-item admin-crm-contact-info-item-label-block">
                                    <span className="info-icon"><MessageCircleIcon width={18} height={18} /></span>
                                    <div>
                                      <span className="info-label">Redes sociales:</span>
                                      <span className="info-value">
                                        {(() => {
                                          const raw = org.redes_sociales;
                                          if (!raw || String(raw).trim() === '') return '—';
                                          let obj: Record<string, string> = {};
                                          try {
                                            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                                              obj = parsed;
                                            }
                                          } catch {
                                            return raw;
                                          }
                                          const labels: Record<string, string> = {
                                            facebook: 'Facebook',
                                            twitter: 'Twitter',
                                            linkedin: 'LinkedIn',
                                            instagram: 'Instagram',
                                            youtube: 'YouTube',
                                            other: 'Otra'
                                          };
                                          const entries = Object.entries(obj).filter(([, url]) => url && String(url).trim() !== '');
                                          if (entries.length === 0) return '—';
                                          return (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                                              {entries.map(([key, url]) => {
                                                const label = labels[key] || key.charAt(0).toUpperCase() + key.slice(1);
                                                const href = url.startsWith('http') ? url : `https://${url}`;
                                                return (
                                                  <span key={key} style={{ display: 'block' }}>
                                                    <span style={{ fontWeight: 600, marginRight: '6px' }}>{label}:</span>
                                                    <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{url}</a>
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          );
                                        })()}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="admin-crm-contact-info-item">
                                    <span className="info-icon"><ClockIcon width={18} height={18} /></span>
                                    <div>
                                      <span className="info-label">Actualizado:</span>
                                      <span className="info-value">{org.updated_at ? formatDate(org.updated_at) : '—'}</span>
                                    </div>
                                  </div>
                                  {(() => {
                                    const contactos = contactsByOrgId.get(org.id) || [];
                                    if (contactos.length === 0) return null;
                                    const isExpandedContactos = empresaContactosExpandedId === org.id;
                                    return (
                                      <div className="admin-crm-contact-info-item admin-crm-empresa-contactos">
                                        <span className="info-icon"><UsersIcon width={18} height={18} /></span>
                                        <div className="admin-crm-empresa-negocios-wrap">
                                          <button
                                            type="button"
                                            className="admin-crm-empresa-negocios-toggle"
                                            onClick={(e) => { e.stopPropagation(); setEmpresaContactosExpandedId(isExpandedContactos ? null : org.id); }}
                                            aria-expanded={isExpandedContactos}
                                          >
                                            <span className="info-label">Contactos ({contactos.length})</span>
                                            {isExpandedContactos ? <ChevronUpIcon width={18} height={18} /> : <ChevronDownIcon width={18} height={18} />}
                                          </button>
                                          {isExpandedContactos && (
                                            <ul className="admin-crm-empresa-negocios-list admin-crm-empresa-contactos-list">
                                              {contactos.map((contact) => (
                                                <li key={contact.id} className="admin-crm-empresa-contacto-row">
                                                  <div className="admin-crm-empresa-contacto-header">
                                                    <strong>{[contact.nombre, contact.apellido].filter(Boolean).join(' ') || 'Sin nombre'}</strong>
                                                    {contact.correo && <span className="admin-crm-empresa-contacto-email">{contact.correo}</span>}
                                                  </div>
                                                  <div className="admin-crm-contact-info-grid admin-crm-empresa-contacto-body">
                                                    <div className="admin-crm-contact-info-item">
                                                      <span className="info-icon"><ClipboardIcon width={18} height={18} /></span>
                                                      <div>
                                                        <span className="info-label">Cargo:</span>
                                                        <span className="info-value">{contact.cargo ?? '—'}</span>
                                                      </div>
                                                    </div>
                                                    <div className="admin-crm-contact-info-item">
                                                      <span className="info-icon"><PhoneIcon width={18} height={18} /></span>
                                                      <div>
                                                        <span className="info-label">Teléfono:</span>
                                                        <span className="info-value">{contact.telefono ?? '—'}</span>
                                                      </div>
                                                    </div>
                                                    <div className="admin-crm-contact-info-item">
                                                      <span className="info-icon"><LinkedInIcon width={18} height={18} /></span>
                                                      <div>
                                                        <span className="info-label">LinkedIn:</span>
                                                        <span className="info-value">
                                                          {contact.linkedin ? (
                                                            <a href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{contact.linkedin}</a>
                                                          ) : '—'}
                                                        </span>
                                                      </div>
                                                    </div>
                                                    <div className="admin-crm-contact-info-item">
                                                      <span className="info-icon"><ClockIcon width={18} height={18} /></span>
                                                      <div>
                                                        <span className="info-label">Actualizado:</span>
                                                        <span className="info-value">{contact.updated_at ? formatDate(contact.updated_at) : '—'}</span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </li>
                                              ))}
                                            </ul>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                  {(() => {
                                    const negocios = negociosByOrgId.get(org.id) || [];
                                    if (negocios.length === 0) return null;
                                    const isExpanded = empresaNegociosExpandedId === org.id;
                                    return (
                                      <div className="admin-crm-contact-info-item admin-crm-empresa-negocios">
                                        <span className="info-icon"><BuildingIcon width={18} height={18} /></span>
                                        <div className="admin-crm-empresa-negocios-wrap">
                                          <button
                                            type="button"
                                            className="admin-crm-empresa-negocios-toggle"
                                            onClick={(e) => { e.stopPropagation(); setEmpresaNegociosExpandedId(isExpanded ? null : org.id); }}
                                            aria-expanded={isExpanded}
                                          >
                                            <span className="info-label">Negocios ({negocios.length})</span>
                                            {isExpanded ? <ChevronUpIcon width={18} height={18} /> : <ChevronDownIcon width={18} height={18} />}
                                          </button>
                                          {isExpanded && (
                                            <ul className="admin-crm-empresa-negocios-list">
                                              {negocios.map((neg, idx) => {
                                                const isScesde26 = neg.pipelineId === 1 || neg.pipelineName === 'Scesde26';
                                                const d = neg.data && typeof neg.data === 'object' ? (neg.data as Record<string, unknown>) : null;
                                                const fieldSchema = pipelinesList.find(p => p.id === neg.pipelineId)?.field_schema ?? [];
                                                const parseArr = (val: unknown): string[] => {
                                                  if (val == null) return [];
                                                  if (Array.isArray(val)) return val.map(v => String(v));
                                                  if (typeof val === 'string') {
                                                    try {
                                                      const p = JSON.parse(val);
                                                      return Array.isArray(p) ? p.map((x: unknown) => String(x)) : [val];
                                                    } catch {
                                                      return [val];
                                                    }
                                                  }
                                                  return [];
                                                };
                                                const parseParticipation = (val: unknown): unknown[] => {
                                                  if (val == null) return [];
                                                  if (Array.isArray(val)) return val;
                                                  if (typeof val === 'string') {
                                                    try {
                                                      const p = JSON.parse(val);
                                                      return Array.isArray(p) ? p : [];
                                                    } catch {
                                                      return [];
                                                    }
                                                  }
                                                  return [];
                                                };
                                                return (
                                                  <li key={idx} className="admin-crm-empresa-negocio-row">
                                                    <div className="admin-crm-empresa-negocio-header">
                                                      <strong>{neg.pipelineName}</strong>
                                                      <div className="admin-crm-empresa-negocio-selects-row" onClick={(e) => e.stopPropagation()}>
                                                        <label className="admin-crm-empresa-negocio-select-label">
                                                          <span className="admin-crm-empresa-negocio-select-label-text">Etapa</span>
                                                          <CustomSelect
                                                            className="admin-crm-priority-select-small admin-crm-empresa-etapa-select"
                                                            optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                                                            value={neg.pipelineStage}
                                                            onChange={(newStage) => saveEmpresaNegocioStage(org.id, neg.pipelineId, newStage)}
                                                            options={(stagesByPipelineId[neg.pipelineId] || DEFAULT_EMPRESA_STAGES).map((s) => ({
                                                              value: s.id,
                                                              label: s.label,
                                                              color: s.color
                                                            }))}
                                                          />
                                                        </label>
                                                        <label className="admin-crm-empresa-negocio-select-label">
                                                          <span className="admin-crm-empresa-negocio-select-label-text">Prioridad</span>
                                                          <CustomSelect
                                                            className="admin-crm-priority-select-small"
                                                            optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                                                            value={neg.priority}
                                                            onChange={(newPriority) => saveEmpresaNegocioPriority(org.id, neg.pipelineId, newPriority as 'high' | 'medium' | 'low')}
                                                            options={[
                                                              { value: 'high', label: 'Alta', color: '#ef4444' },
                                                              { value: 'medium', label: 'Media', color: '#fbbf24' },
                                                              { value: 'low', label: 'Baja', color: '#10b981' }
                                                            ]}
                                                          />
                                                        </label>
                                                      </div>
                                                    </div>
                                                    {d && isScesde26 ? (
                                                      <div className="admin-crm-contact-info-grid admin-crm-empresa-negocio-body">
                                                        {(() => {
                                                          const mods = parseArr(d.participation_modalities);
                                                          const agreed = d.agreed_modality != null && d.agreed_modality !== '' ? String(d.agreed_modality) : '';
                                                          const modalityLabel = agreed
                                                            ? (participationModalities.find(m => m.value === agreed)?.label ?? agreed)
                                                            : mods.length > 0
                                                              ? mods.map((mod: string) => participationModalities.find(m => m.value === mod)?.label ?? mod).join(', ')
                                                              : 'N/A';
                                                          const stands = parseArr(d.selected_stands_updated);
                                                          const standsLabel = stands.length > 0 ? stands.join(', ') : 'N/A';
                                                          const speakers = parseParticipation(d.speakers);
                                                          const parallelEvents = parseParticipation(d.parallel_events);
                                                          const initiatives = parseParticipation(d.iniciativas).length > 0 ? parseParticipation(d.iniciativas) : parseParticipation(d.catalog_sections);
                                                          const businessRounds = parseParticipation(d.business_rounds_participants);
                                                          const hasAnyParticipation = speakers.length > 0 || parallelEvents.length > 0 || initiatives.length > 0 || businessRounds.length > 0;
                                                          const hasPendingApprovals = (speakers.length > 0 && speakers.some((s: any) => (getSpeakerStatusHelper(s).completionStatus === 'complete' || getSpeakerStatusHelper(s).hasRequestApproval) && s?.approval_status !== 'approved' && s?.approval_status !== 'rejected')) ||
                                                            (parallelEvents.length > 0 && parallelEvents.some((e: any) => (getEventStatusHelper(e).completionStatus === 'complete' || getEventStatusHelper(e).hasRequestApproval) && e?.approval_status !== 'approved' && e?.approval_status !== 'rejected')) ||
                                                            (initiatives.length > 0 && initiatives.some((i: any) => (getInitiativeStatusHelper(i).completionStatus === 'complete' || getInitiativeStatusHelper(i).hasRequestApproval) && i?.approval_status !== 'approved' && i?.approval_status !== 'rejected')) ||
                                                            (businessRounds.length > 0 && businessRounds.some((p: any) => getBusinessRoundsStatusHelper(p) === 'complete' && p?.approval_status !== 'approved' && p?.approval_status !== 'rejected'));
                                                          return (
                                                            <>
                                                              <div className="admin-crm-contact-info-item">
                                                                <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                                                <div>
                                                                  <span className="info-label">Modalidad:</span>
                                                                  <span className="info-value">{modalityLabel}</span>
                                                                </div>
                                                              </div>
                                                              <div className="admin-crm-contact-info-item">
                                                                <span className="info-icon"><MapPinIcon width={18} height={18} /></span>
                                                                <div>
                                                                  <span className="info-label">Zona Expo:</span>
                                                                  <span className="info-value">{standsLabel}</span>
                                                                </div>
                                                              </div>
                                                              {d.modality_observations != null && String(d.modality_observations).trim() !== '' && (
                                                                <div className="admin-crm-contact-info-item">
                                                                  <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                                                  <div>
                                                                    <span className="info-label">Observaciones:</span>
                                                                    <span className="info-value">{String(d.modality_observations)}</span>
                                                                  </div>
                                                                </div>
                                                              )}
                                                              <div className="admin-crm-contact-participations">
                                                                <span className="info-label">Participa en:</span>
                                                                <div className="admin-crm-participations-badges">
                                                                  {speakers.length > 0 && <span className="feature-badge-small speakers-badge"><MicIcon width={14} height={14} /> {speakers.length}</span>}
                                                                  {parallelEvents.length > 0 && <span className="feature-badge-small events-badge"><CalendarIcon width={14} height={14} /> {parallelEvents.length}</span>}
                                                                  {initiatives.length > 0 && <span className="feature-badge-small catalog-badge"><BookIcon width={14} height={14} /></span>}
                                                                  {businessRounds.length > 0 && <span className="feature-badge-small rounds-badge"><UsersIcon width={14} height={14} /></span>}
                                                                  {!hasAnyParticipation && <span style={{ color: '#9ca3af', fontSize: '12px' }}>Sin participaciones</span>}
                                                                </div>
                                                              </div>
                                                              {hasPendingApprovals && (
                                                                <div className="admin-crm-contact-pending-actions">
                                                                  <span className="info-label">Acciones pendientes:</span>
                                                                  <div className="admin-crm-pending-badges">
                                                                    <span className="status-badge-small approval-badge"><CheckCircleIcon width={14} height={14} /> Aprobaciones</span>
                                                                  </div>
                                                                </div>
                                                              )}
                                                            </>
                                                          );
                                                        })()}
                                                      </div>
                                                    ) : (fieldSchema.length > 0 || (d && Object.keys(d).filter(k => k !== 'pipeline_stage' && k !== 'priority').length > 0)) ? (
                                                      <div className="admin-crm-contact-info-grid admin-crm-empresa-negocio-body">
                                                        {(() => {
                                                          const keysToShow = fieldSchema.length > 0
                                                            ? fieldSchema.map(f => f.key)
                                                            : Object.keys(d || {}).filter(k => k !== 'pipeline_stage' && k !== 'priority');
                                                          const getLabel = (key: string) => fieldSchema.find(f => f.key === key)?.label ?? key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                                                          const formatValue = (val: unknown): React.ReactNode => {
                                                            if (val == null) return '—';
                                                            if (typeof val === 'boolean') return val ? 'Sí' : 'No';
                                                            if (Array.isArray(val)) {
                                                              if (val.length === 0) return '—';
                                                              const first = val[0];
                                                              if (typeof first === 'object' && first !== null) return `${val.length} ítem(s)`;
                                                              return val.map(v => String(v)).join(', ');
                                                            }
                                                            if (typeof val === 'object') return JSON.stringify(val).length > 80 ? `${JSON.stringify(val).slice(0, 80)}…` : JSON.stringify(val);
                                                            return String(val);
                                                          };
                                                          return keysToShow.map((key) => {
                                                            const val = (d || {})[key];
                                                            return (
                                                              <div key={key} className="admin-crm-contact-info-item">
                                                                <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                                                <div>
                                                                  <span className="info-label">{getLabel(key)}:</span>
                                                                  <span className="info-value">{formatValue(val)}</span>
                                                                </div>
                                                              </div>
                                                            );
                                                          });
                                                        })()}
                                                      </div>
                                                    ) : null}
                                                  </li>
                                                );
                                              })}
                                            </ul>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
            ) : null}
              </div>
        ) : (
              /* Vista de detalle del contacto - Estilo HubSpot */
              <div className="admin-crm-detail-view">
                <div className="admin-crm-detail-header">
                  <button
                    className="admin-back-button"
                    onClick={() => {
                      const participationIdToScroll = selectedParticipationIdRef.current;
                      try {
                        const saved = sessionStorage.getItem('adminPanelState');
                        if (saved) {
                          const parsed = JSON.parse(saved);
                          parsed.selectedCrmUserId = null;
                          sessionStorage.setItem('adminPanelState', JSON.stringify(parsed));
                        }
                      } catch (_) { /* ignore */ }
                      setSelectedCrmUser(null);
                      // Restaurar la pestaña anterior si existe, sino volver a CRM
                      if (previousMainTab) {
                        setActiveMainTab(previousMainTab);
                        setPreviousMainTab(null);
                        // Esperar a que la pestaña cambie y luego hacer scroll a la tarjeta específica
                        if (participationIdToScroll) {
                          setTimeout(() => {
                            const element = document.querySelector(`[data-participation-id="${participationIdToScroll}"]`);
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                            selectedParticipationIdRef.current = null;
                          }, 300);
                        }
                      }
                    }}
                  >
                    ← Volver
                  </button>
                  <div className="admin-crm-detail-title">
                    <div className="admin-crm-detail-title-row">
                      <h2>{getUserDisplayName(selectedCrmUser)}</h2>
                      <span className={`admin-detail-origin-badge ${selectedCrmUser.created_by_admin ? 'origin-manual' : 'origin-registered'}`} title={selectedCrmUser.created_by_admin ? 'Contacto creado manualmente por el admin' : 'El usuario se registró por su cuenta'}>
                        {selectedCrmUser.created_by_admin ? 'Creado por admin' : 'Registrado'}
                      </span>
                      <CustomSelect
                        className="admin-filter-select admin-filter-select-small"
                        optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                        value={selectedCrmUser.is_draft === false ? 'authorized' : selectedCrmUser.is_draft === true ? 'draft' : 'undefined'}
                        onChange={(newValue) => {
                          const newIsDraft = newValue === 'draft' ? true : newValue === 'authorized' ? false : null;
                          saveDraftStatus(selectedCrmUser.id, newIsDraft);
                        }}
                        options={[
                          { value: 'authorized', label: 'Autorizado', color: '#10b981' },
                          { value: 'draft', label: 'Borrador', color: '#f59e0b' },
                          { value: 'undefined', label: 'Sin definir', color: '#64748b' }
                        ]}
                      />
                    </div>
                    <p className="admin-request-email">{selectedCrmUser.email}</p>
                  </div>
                  <div className="admin-crm-detail-header-actions">
                    <button
                      className="admin-action-button chat-button"
                      onClick={() => {
                        sessionStorage.setItem('chatsPage_asUserId', selectedCrmUser.id.toString());
                        window.open(`${window.location.origin}/soporte?asUserId=${selectedCrmUser.id}`, '_blank');
                      }}
                    >
                      Abrir Chat
                      {(selectedCrmUser.unreadCount ?? 0) > 0 && (
                        <span className="chat-unread-badge">{selectedCrmUser.unreadCount}</span>
                      )}
                    </button>
                  </div>
                </div>

                <div className="admin-crm-detail-content">
                  {/* Sistema de pestañas para el perfil completo */}
                  {loadingFullProfile ? (
                    <div className="admin-crm-loading">
                      <p>Cargando perfil completo...</p>
                    </div>
                  ) : fullProfileData ? (
                    <div className="admin-profile-tabs-container">
                      {/* Pestañas principales */}
                      <div className="admin-profile-tabs-header">
                        <button
                          className={`admin-profile-tab ${activeMainProfileTab === 'overview' ? 'active' : ''}`}
                          onClick={() => setActiveMainProfileTab('overview')}
                        >
                          <FileTextIcon width={16} height={16} /> Información de Contacto
                        </button>
                        {fullProfileData?.contactInfo?.wantsToManageProfile && (
                          <button
                            className={`admin-profile-tab ${activeMainProfileTab === 'profile' ? 'active' : ''}`}
                            onClick={() => setActiveMainProfileTab('profile')}
                          >
                            <BuildingIcon width={16} height={16} /> Perfil Institucional
                          </button>
                        )}
                        {fullProfileData?.contactInfo?.wantsToManageProfile && (
                        <button
                          className={`admin-profile-tab ${activeMainProfileTab === 'modality' ? 'active' : ''}`}
                          onClick={() => setActiveMainProfileTab('modality')}
                        >
                          <MapPinIcon width={16} height={16} /> Modalidad y Zona Expo
                        </button>
                        )}
                        {fullProfileData?.contactInfo?.wantsToManageProfile && (
                          <button
                            className={`admin-profile-tab ${activeMainProfileTab === 'participations' ? 'active' : ''}`}
                            onClick={() => setActiveMainProfileTab('participations')}
                          >
                            <AwardIcon width={16} height={16} /> Participaciones
                            {selectedCrmUser && (() => {
                              // Calcular pendientes totales del usuario usando las funciones helper globales para mantener consistencia
                              const pendingSpeakers = selectedCrmUser.speakers && Array.isArray(selectedCrmUser.speakers) 
                                ? selectedCrmUser.speakers.filter((speaker: any) => {
                                    const statusInfo = getSpeakerStatusHelper(speaker);
                                    return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                                           speaker.approval_status !== 'approved' && speaker.approval_status !== 'rejected';
                                  }).length
                                : 0;
                              const pendingEvents = selectedCrmUser.parallel_events && Array.isArray(selectedCrmUser.parallel_events)
                                ? selectedCrmUser.parallel_events.filter((event: any) => {
                                    const statusInfo = getEventStatusHelper(event);
                                    return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                                           event.approval_status !== 'approved' && event.approval_status !== 'rejected';
                                  }).length
                                : 0;
                              const pendingInitiatives = selectedCrmUser.initiatives && Array.isArray(selectedCrmUser.initiatives)
                                ? selectedCrmUser.initiatives.filter((init: any) => {
                                    const statusInfo = getInitiativeStatusHelper(init);
                                    return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                                           init.approval_status !== 'approved' && init.approval_status !== 'rejected';
                                  }).length
                                : 0;
                              const pendingBusinessRounds = selectedCrmUser.business_rounds_participants && Array.isArray(selectedCrmUser.business_rounds_participants)
                                ? selectedCrmUser.business_rounds_participants.filter((participant: any) => {
                                    const status = getBusinessRoundsStatusHelper(participant);
                                    return status === 'complete' &&
                                           participant.approval_status !== 'approved' && participant.approval_status !== 'rejected';
                                  }).length
                                : 0;
                              const totalPending = pendingSpeakers + pendingEvents + pendingInitiatives + pendingBusinessRounds;
                              return totalPending > 0 ? <span className="admin-tab-badge">{totalPending}</span> : null;
                            })()}
                          </button>
                        )}
                        <button
                          className={`admin-profile-tab ${activeMainProfileTab === 'missing' ? 'active' : ''}`}
                          onClick={() => setActiveMainProfileTab('missing')}
                        >
                          <AlertTriangleIcon width={16} height={16} /> Información faltante
                          {selectedCrmUser && fullProfileData && (() => {
                            // Calcular número de secciones incompletas (misma lógica que en el contenido de la pestaña)
                            const wantsToManageProfile = fullProfileData?.contactInfo?.wantsToManageProfile || false;
                            const personalProgress = fullProfileData ? calculateProfileProgress(fullProfileData, false) : { percentage: 0, isComplete: false };
                            const isPersonalProfileComplete = personalProgress.isComplete;
                            const hasInstitutionalProfile = wantsToManageProfile;
                            const institutionalProgress = hasInstitutionalProfile && fullProfileData ? calculateProfileProgress(fullProfileData, true) : { percentage: 0, isComplete: false };
                            const isInstitutionalProfileComplete = hasInstitutionalProfile ? institutionalProgress.isComplete : true;
                            
                            // Funciones helper para validar completitud
                            const isSpeakerComplete = (speaker: any): boolean => {
                              if (!speaker) return false;
                              const hasPhoto = speaker.photo && (typeof speaker.photo === 'string' ? speaker.photo.trim() !== '' : true);
                              const hasFirstName = speaker.firstName && speaker.firstName.toString().trim() !== '';
                              const hasLastName = speaker.lastName && speaker.lastName.toString().trim() !== '';
                              const hasNationality = speaker.nationality && speaker.nationality.toString().trim() !== '';
                              const hasOrganization = speaker.organization && speaker.organization.toString().trim() !== '';
                              const hasPosition = speaker.position && speaker.position.toString().trim() !== '';
                              const hasDescription = speaker.description && speaker.description.toString().trim() !== '';
                              const hasThematicAxes = speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0;
                              const hasContentDescription = speaker.contentDescription && speaker.contentDescription.toString().trim() !== '';
                              const hasLinkedin = speaker.linkedin && speaker.linkedin.toString().trim() !== '';
                              const hasBiography = speaker.biography && speaker.biography.toString().trim() !== '';
                              const hasValidBiography = hasLinkedin || hasBiography;
                              return hasPhoto && hasFirstName && hasLastName && hasNationality && hasOrganization && 
                                     hasPosition && hasDescription && hasThematicAxes && hasContentDescription && hasValidBiography;
                            };
                            
                            const isParallelEventComplete = (event: any): boolean => {
                              if (!event) return false;
                              const hasName = event.name && event.name.toString().trim() !== '';
                              const hasDescription = event.description && event.description.toString().trim() !== '';
                              const hasSpeakers = (event.speakerIds && event.speakerIds.length > 0) || 
                                                (event.localSpeakers && event.localSpeakers.length > 0);
                              const hasDuration = event.duration && event.duration.toString().trim() !== '';
                              const hasThematicAxes = event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0;
                              const hasAttendeeProfiles = event.attendeeProfiles && Array.isArray(event.attendeeProfiles) && event.attendeeProfiles.length > 0;
                              return hasName && hasDescription && hasSpeakers && hasDuration && hasThematicAxes && hasAttendeeProfiles;
                            };
                            
                            const isInitiativeComplete = (initiative: any): boolean => {
                              if (!initiative) return false;
                              const hasName = initiative.name && initiative.name.toString().trim() !== '';
                              const hasShortDescription = initiative.shortDescription && initiative.shortDescription.toString().trim() !== '';
                              const hasDescription = initiative.description && initiative.description.toString().trim() !== '';
                              const hasThematicAxes = initiative.thematicAxes && Array.isArray(initiative.thematicAxes) && initiative.thematicAxes.length > 0;
                              if (!hasName || !hasShortDescription || !hasDescription || !hasThematicAxes) return false;
                              if (!initiative.noContact) {
                                const hasContactFirstName = initiative.contactFirstName && initiative.contactFirstName.trim() !== '';
                                const hasContactLastName = initiative.contactLastName && initiative.contactLastName.trim() !== '';
                                const hasContactPosition = initiative.contactPosition && initiative.contactPosition.trim() !== '';
                                const hasContactEmail = initiative.contactEmail && initiative.contactEmail.trim() !== '';
                                if (!hasContactFirstName || !hasContactLastName || !hasContactPosition || !hasContactEmail) return false;
                              }
                              return true;
                            };
                            
                            const isBusinessRoundsParticipantComplete = (participant: any): boolean => {
                              if (!participant) return false;
                              const hasFirstName = participant.firstName && participant.firstName.trim() !== '';
                              const hasLastName = participant.lastName && participant.lastName.trim() !== '';
                              const hasPosition = participant.position && participant.position.trim() !== '';
                              const hasOrganization = participant.organization && participant.organization.trim() !== '';
                              const hasEmail = participant.email && participant.email.trim() !== '';
                              const hasPhone = participant.numeroTelefono && participant.numeroTelefono.trim() !== '';
                              const hasThematicAxes = participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0;
                              return hasFirstName && hasLastName && hasPosition && hasOrganization && hasEmail && hasPhone && hasThematicAxes;
                            };
                            
                            // Validar secciones
                            const hasSpeakers = selectedCrmUser.speakers && Array.isArray(selectedCrmUser.speakers) && selectedCrmUser.speakers.length > 0;
                            const areSpeakersComplete = hasSpeakers && selectedCrmUser.speakers ? selectedCrmUser.speakers.every((speaker: any) => isSpeakerComplete(speaker)) : true;
                            
                            const hasParallelEvents = selectedCrmUser.parallel_events && Array.isArray(selectedCrmUser.parallel_events) && selectedCrmUser.parallel_events.length > 0;
                            const areParallelEventsComplete = hasParallelEvents && selectedCrmUser.parallel_events ? selectedCrmUser.parallel_events.every((event: any) => isParallelEventComplete(event)) : true;
                            
                            const hasInitiatives = fullProfileData?.initiatives && Array.isArray(fullProfileData.initiatives) && fullProfileData.initiatives.length > 0;
                            const areInitiativesComplete = hasInitiatives && fullProfileData?.initiatives ? fullProfileData.initiatives.every((initiative: any) => isInitiativeComplete(initiative)) : true;
                            
                            const hasBusinessRoundsParticipants = fullProfileData?.businessRoundsParticipants && Array.isArray(fullProfileData.businessRoundsParticipants) && fullProfileData.businessRoundsParticipants.length > 0;
                            const areBusinessRoundsParticipantsComplete = hasBusinessRoundsParticipants && fullProfileData?.businessRoundsParticipants ? fullProfileData.businessRoundsParticipants.every((participant: any) => isBusinessRoundsParticipantComplete(participant)) : true;
                            
                            // Contar secciones incompletas
                            let incompleteSections = 0;
                            if (!isPersonalProfileComplete) incompleteSections++;
                            if (hasInstitutionalProfile && !isInstitutionalProfileComplete) incompleteSections++;
                            if (hasSpeakers && !areSpeakersComplete) incompleteSections++;
                            if (hasParallelEvents && !areParallelEventsComplete) incompleteSections++;
                            if (hasInitiatives && !areInitiativesComplete) incompleteSections++;
                            if (hasBusinessRoundsParticipants && !areBusinessRoundsParticipantsComplete) incompleteSections++;
                            
                            return incompleteSections > 0 ? <span className="admin-tab-badge">{incompleteSections}</span> : null;
                          })()}
                          </button>
                        </div>


                      {/* Contenido de las pestañas */}
                      <div className="admin-profile-tabs-content">
                        {/* Contenedor de dos columnas: contenido de pestañas + sidebar */}
                        <div style={{ display: 'flex', gap: '24px' }}>
                          {/* Columna izquierda - Contenido de las pestañas */}
                          <div className="admin-crm-detail-main" style={{ flex: 1 }}>
                            {activeMainProfileTab === 'overview' && (
                              <div className="admin-profile-tab-panel">
                    {/* Información de contacto */}
                    <div className="admin-crm-section-card">
                      <h3>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FileTextIcon width={18} height={18} /> Información de Contacto
                        </span>
                      </h3>
                      <div className="admin-crm-info-grid">
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Nombre:</span>
                          <span className="info-value">{fullProfileData?.contactInfo?.firstName || 'N/A'}</span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Apellido:</span>
                          <span className="info-value">{fullProfileData?.contactInfo?.lastName || 'N/A'}</span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Correo electrónico:</span>
                          <span className="info-value">{fullProfileData?.contactInfo?.email || selectedCrmUser.email || 'N/A'}</span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Teléfono:</span>
                          <span className="info-value">{fullProfileData?.contactInfo?.phone || 'N/A'}</span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Organización:</span>
                          <span className="info-value">{fullProfileData?.basicInfo?.organizationName || selectedCrmUser.organization_name || 'N/A'}</span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Tipo de Organización:</span>
                          <span className="info-value">{getOrganizationTypeLabel(fullProfileData?.basicInfo?.organizationType || selectedCrmUser.organization_type || '') || 'N/A'}</span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Cargo:</span>
                          <span className="info-value">{fullProfileData?.contactInfo?.position || selectedCrmUser.position || 'N/A'}</span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Área:</span>
                          <span className="info-value">{fullProfileData?.contactInfo?.area || 'N/A'}</span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Subárea:</span>
                          <span className="info-value">{fullProfileData?.contactInfo?.subarea || 'N/A'}</span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Sitio web:</span>
                          <span className="info-value">
                            {(() => {
                              const website = fullProfileData?.basicInfo?.website;
                              if (!website || website === 'N/A') return 'N/A';
                              const href = website.startsWith('http') ? website : `https://${website}`;
                              return (
                                <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#CE2B15' }}>
                                  {website}
                                </a>
                              );
                            })()}
                          </span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Industrias:</span>
                          <span className="info-value admin-profile-tags-wrap">
                            {fullProfileData?.basicInfo?.industries && fullProfileData.basicInfo.industries.length > 0
                              ? fullProfileData.basicInfo.industries.map((ind, idx) => (
                                  <span key={idx} className="tag-badge-small">{ind}</span>
                                ))
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Ejes de interés:</span>
                          <span className="info-value admin-profile-tags-wrap">
                            {fullProfileData?.contactInfo?.thematicAxes && fullProfileData.contactInfo.thematicAxes.length > 0
                              ? fullProfileData.contactInfo.thematicAxes.map((axis) => {
                                  const axisObj = thematicAxes.find(t => t.value === axis);
                                  return (
                                    <span key={axis} className="tag-badge-small">
                                      {axisObj?.label || String(axis)}
                                    </span>
                                  );
                                })
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Quiero participar en las rondas de negocios:</span>
                          <span className="info-value">
                            {fullProfileData?.contactInfo?.wantsToParticipateBusinessRounds ? 'Sí' : 'No'}
                          </span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Registrado:</span>
                          <span className="info-value">{formatDate(selectedCrmUser.created_at)}</span>
                        </div>
                        <div className="admin-crm-info-item-full">
                          <span className="info-label">Último acceso:</span>
                          <span className="info-value">{formatDate(selectedCrmUser.last_login_date)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Historial de mensajería: Correo | WhatsApp | Mensajería */}
                    <div className="admin-crm-section-card">
                      <h3><MessageCircleIcon width={18} height={18} /> Historial de Mensajería</h3>
                      <div className="admin-conversations-subtabs" style={{ marginTop: 12, marginBottom: 12 }}>
                        <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'correo' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('correo')}>
                          <EnvelopeIcon width={18} height={18} /> Correo
                        </button>
                        <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'whatsapp' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('whatsapp')}>
                          <WhatsAppIcon width={18} height={18} /> WhatsApp
                        </button>
                        <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'messaging' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('messaging')}>
                          <MessageCircleIcon width={18} height={18} /> Mensajería
                        </button>
                      </div>
                      <div>
                        {activeHistorialSubTab === 'correo' && (
                          <GmailSection isDarkMode={isDarkMode} filterByEmail={selectedCrmUser.email} />
                        )}
                        {activeHistorialSubTab === 'whatsapp' && (
                          <HistorialWhatsAppTab
                            userId={selectedCrmUser.id}
                            whatsapp={selectedCrmUser.whatsapp ?? null}
                            onSaveWhatsapp={(phone) => {
                              setUsers(prev => prev.map(u => u.id === selectedCrmUser.id ? { ...u, whatsapp: phone } : u));
                              setSelectedCrmUser(prev => prev ? { ...prev, whatsapp: phone } : null);
                            }}
                            getApiUrl={getApiUrl}
                            isDarkMode={isDarkMode}
                          />
                        )}
                        {activeHistorialSubTab === 'messaging' && (() => {
                          const userConversation = conversations.find(c => c.userId === selectedCrmUser.id);
                          if (!userConversation) {
                            return (
                              <div className="admin-crm-empty-state">
                                <p>No hay mensajes con este contacto</p>
                                <button
                                  className="admin-action-button-small chat-button"
                                  onClick={() => {
                                    sessionStorage.setItem('chatsPage_asUserId', selectedCrmUser.id.toString());
                                    window.open(`${window.location.origin}/soporte?asUserId=${selectedCrmUser.id}`, '_blank');
                                  }}
                                >
                                  Iniciar Conversación
                                </button>
                              </div>
                            );
                          }
                          const lastDate = userConversation.lastMessageDate ? new Date(userConversation.lastMessageDate) : null;
                          const lastMessageIsFromAdmin = currentUserId && userConversation.lastMessageFromUserId && parseInt(String(userConversation.lastMessageFromUserId)) === currentUserId;
                          const daysSince = lastDate && lastMessageIsFromAdmin ? Math.floor((Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
                          return (
                            <div className="admin-crm-messaging-info">
                              <div className="admin-crm-info-row">
                                <span className="info-label">Último mensaje:</span>
                                <span>{userConversation.lastMessage || 'Sin mensajes'}</span>
                              </div>
                              <div className="admin-crm-info-row">
                                <span className="info-label">Fecha:</span>
                                <span>{lastDate ? formatDate(userConversation.lastMessageDate) : 'Nunca'}</span>
                              </div>
                              <div className="admin-crm-info-row">
                                <span className="info-label">Mensajes no leídos:</span>
                                <span className={userConversation.unreadCount > 0 ? 'status-badge-small request-badge' : ''}>{userConversation.unreadCount}</span>
                              </div>
                              <div className="admin-crm-info-row">
                                <span className="info-label">Días sin respuesta:</span>
                                {daysSince !== null && daysSince > 0 ? (
                                  <span className={daysSince > 7 ? 'status-badge-small request-badge' : ''}>{daysSince} día(s)</span>
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>—</span>
                                )}
                              </div>
                              <div className="admin-crm-actions">
                                <button
                                  className="admin-action-button-small chat-button"
                                  onClick={() => {
                                    sessionStorage.setItem('chatsPage_asUserId', selectedCrmUser.id.toString());
                                    window.open(`${window.location.origin}/soporte?asUserId=${selectedCrmUser.id}`, '_blank');
                                  }}
                                >
                                  Abrir Chat
                                  {userConversation.unreadCount > 0 && <span className="chat-unread-badge-small">{userConversation.unreadCount}</span>}
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                              </div>
                            )}

                            {activeMainProfileTab === 'modality' && (
                              <div className="admin-profile-tab-panel">
                    {/* Modalidad y Zona Expo */}
                    <div className="admin-crm-section-card">
                      <h3>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <MapPinIcon width={18} height={18} /> Modalidad y Zona Expo
                        </span>
                      </h3>
                      <div>
                      
                      {/* Modalidades elegidas por el usuario */}
                      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <span className="info-label">Modalidades elegidas:</span>
                        {selectedCrmUser.participation_modalities && Array.isArray(selectedCrmUser.participation_modalities) && selectedCrmUser.participation_modalities.length > 0 ? (
                          <>
                            {selectedCrmUser.participation_modalities.map((modality: string, idx: number) => {
                              const modalityOption = participationModalities.find(m => m.value === modality);
                              return (
                                <span 
                                  key={idx}
                                  style={{
                                    padding: '6px 12px',
                                    background: '#f0fdf4',
                                    border: '1px solid #22c55e',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    color: '#15803d'
                                  }}
                                >
                                  {modalityOption ? modalityOption.label : modality}
                                </span>
                              );
                            })}
                          </>
                        ) : (
                          <span className="info-value">N/A</span>
                        )}
                      </div>

                      {/* Modalidad acordada */}
                      <div style={{ marginBottom: '20px' }}>
                        <span className="info-label" style={{ display: 'block', marginBottom: '8px' }}>Modalidad acordada:</span>
                        <CustomSelect
                          className="admin-filter-select"
                          optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                          value={editableAgreedModality}
                          onChange={(value) => setEditableAgreedModality(value)}
                          options={[
                            { value: '', label: 'Seleccionar modalidad...' },
                            ...participationModalities.map((modality) => ({
                              value: modality.value,
                              label: modality.label
                            }))
                          ]}
                        />
                      </div>

                      {/* Observaciones de la modalidad */}
                      <div style={{ marginBottom: '20px' }}>
                        <span className="info-label" style={{ display: 'block', marginBottom: '8px' }}>Observaciones de la modalidad:</span>
                        <textarea
                          value={editableModalityObservations}
                          onChange={(e) => setEditableModalityObservations(e.target.value)}
                          rows={4}
                          className="admin-filter-select"
                          style={{
                            width: '100%',
                            resize: 'vertical',
                            cursor: 'text'
                          }}
                          placeholder="Ingrese observaciones sobre la modalidad acordada..."
                        />
                      </div>

                      {/* Botón para guardar modalidad */}
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.nativeEvent?.stopImmediatePropagation?.();
                          if (!selectedCrmUser) return false;
                          try {
                            const response = await fetch(`${getApiUrl()}/admin/save-section/${selectedCrmUser.id}`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              credentials: 'include',
                              body: JSON.stringify({
                                section: 'participationModalities',
                                data: {
                                  participationModalities: selectedCrmUser.participation_modalities || [],
                                  agreedModality: editableAgreedModality || null,
                                  modalityObservations: editableModalityObservations || null,
                                  requestInformation: selectedCrmUser.request_information || false
                                }
                              })
                            });
                            const result = await response.json();
                            if (result.success) {
                              // Guardar posición del scroll antes de actualizar
                              const scrollPosition = window.scrollY || window.pageYOffset;
                              
                              // Actualizar el usuario en la lista
                              setUsers(prevUsers => prevUsers.map(u => 
                                u.id === selectedCrmUser.id 
                                  ? { ...u, agreed_modality: editableAgreedModality }
                                  : u
                              ));
                              setSelectedCrmUser({ ...selectedCrmUser, agreed_modality: editableAgreedModality });
                              
                              // Restaurar posición del scroll después de un breve delay
                              setTimeout(() => {
                                window.scrollTo({ top: scrollPosition, behavior: 'auto' });
                              }, 0);
                              
                              setModalitySaveMessage({ type: 'success', text: 'Modalidad guardada exitosamente' });
                              setTimeout(() => setModalitySaveMessage(null), 3000);
                            } else {
                              setModalitySaveMessage({ type: 'error', text: 'Error al guardar: ' + (result.error || 'Error desconocido') });
                              setTimeout(() => setModalitySaveMessage(null), 5000);
                            }
                          } catch (error) {
                            setModalitySaveMessage({ type: 'error', text: 'Error al guardar la modalidad' });
                            setTimeout(() => setModalitySaveMessage(null), 5000);
                          }
                          return false;
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                        style={{
                          padding: '10px 20px',
                          background: '#CE2B15',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontFamily: 'Montserrat, sans-serif',
                          marginBottom: '24px'
                        }}
                      >
                        Guardar Modalidad
                      </button>
                      {modalitySaveMessage && (
                        <div
                          style={{
                            marginTop: '12px',
                            padding: '10px 16px',
                            background: modalitySaveMessage.type === 'success' ? '#f0fdf4' : '#fef2f2',
                            border: `1px solid ${modalitySaveMessage.type === 'success' ? '#22c55e' : '#ef4444'}`,
                            borderRadius: '6px',
                            color: modalitySaveMessage.type === 'success' ? '#15803d' : '#dc2626',
                            fontSize: '14px',
                            fontFamily: 'Montserrat, sans-serif'
                          }}
                        >
                          {modalitySaveMessage.text}
                        </div>
                      )}

                      {/* Stands elegidos y confirmados */}
                      <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '2px solid #e5e7eb' }}>
                        <ExpoZoneSection
                          selectedStands={selectedCrmUser.selected_stands_updated || []}
                          confirmedStands={editableConfirmedStands}
                          onChange={() => {}} // No permitir cambiar stands seleccionados desde aquí
                          onConfirmedChange={(stands) => {
                            setEditableConfirmedStands(stands);
                          }}
                          readOnly={false}
                          hideTitle={false}
                          hideLegend={true}
                          hideCheckbox={true}
                          customTitle="Stands seleccionados y confirmados:"
                        />
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!selectedCrmUser) return;
                            try {
                              const response = await fetch(`${getApiUrl()}/admin/save-section/${selectedCrmUser.id}`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                credentials: 'include',
                                body: JSON.stringify({
                                  section: 'stands',
                                  data: {
                                    selectedStands: selectedCrmUser.selected_stands_updated || [],
                                    selectedStandsUpdated: selectedCrmUser.selected_stands_updated || [],
                                    confirmedStands: editableConfirmedStands,
                                    requestExpoInformation: selectedCrmUser.request_expo_information || false
                                  }
                                })
                              });
                              const result = await response.json();
                              if (result.success) {
                                // Actualizar el perfil completo
                                if (fullProfileData) {
                                  setFullProfileData({ ...fullProfileData, confirmedStands: editableConfirmedStands });
                                }
                                setStandsSaveMessage({ type: 'success', text: 'Stands confirmados guardados exitosamente' });
                                setTimeout(() => setStandsSaveMessage(null), 3000);
                              } else {
                                setStandsSaveMessage({ type: 'error', text: 'Error al guardar: ' + (result.error || 'Error desconocido') });
                                setTimeout(() => setStandsSaveMessage(null), 5000);
                              }
                            } catch (error) {
                              setStandsSaveMessage({ type: 'error', text: 'Error al guardar los stands confirmados' });
                              setTimeout(() => setStandsSaveMessage(null), 5000);
                            }
                          }}
                          style={{
                            marginTop: '16px',
                            padding: '10px 20px',
                            background: '#CE2B15',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontFamily: 'Montserrat, sans-serif'
                          }}
                        >
                          Guardar Stands Confirmados
                        </button>
                        {standsSaveMessage && (
                          <div
                            style={{
                              marginTop: '12px',
                              padding: '10px 16px',
                              background: standsSaveMessage.type === 'success' ? '#f0fdf4' : '#fef2f2',
                              border: `1px solid ${standsSaveMessage.type === 'success' ? '#22c55e' : '#ef4444'}`,
                              borderRadius: '6px',
                              color: standsSaveMessage.type === 'success' ? '#15803d' : '#dc2626',
                              fontSize: '14px',
                              fontFamily: 'Montserrat, sans-serif'
                            }}
                          >
                            {standsSaveMessage.text}
                          </div>
                        )}
                      </div>
                      </div>
                    </div>

                    {/* Historial de mensajería: Correo | WhatsApp | Mensajería */}
                    <div className="admin-crm-section-card">
                      <h3><MessageCircleIcon width={18} height={18} /> Historial de Mensajería</h3>
                      <div className="admin-conversations-subtabs" style={{ marginTop: 12, marginBottom: 12 }}>
                        <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'correo' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('correo')}>
                          <EnvelopeIcon width={18} height={18} /> Correo
                        </button>
                        <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'whatsapp' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('whatsapp')}>
                          <WhatsAppIcon width={18} height={18} /> WhatsApp
                        </button>
                        <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'messaging' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('messaging')}>
                          <MessageCircleIcon width={18} height={18} /> Mensajería
                        </button>
                      </div>
                      <div>
                        {activeHistorialSubTab === 'correo' && (
                          <GmailSection isDarkMode={isDarkMode} filterByEmail={selectedCrmUser.email} />
                        )}
                        {activeHistorialSubTab === 'whatsapp' && (
                          <HistorialWhatsAppTab
                            userId={selectedCrmUser.id}
                            whatsapp={selectedCrmUser.whatsapp ?? null}
                            onSaveWhatsapp={(phone) => {
                              setUsers(prev => prev.map(u => u.id === selectedCrmUser.id ? { ...u, whatsapp: phone } : u));
                              setSelectedCrmUser(prev => prev ? { ...prev, whatsapp: phone } : null);
                            }}
                            getApiUrl={getApiUrl}
                            isDarkMode={isDarkMode}
                          />
                        )}
                        {activeHistorialSubTab === 'messaging' && (() => {
                          const userConversation = conversations.find(c => c.userId === selectedCrmUser.id);
                          if (!userConversation) {
                            return (
                              <div className="admin-crm-empty-state">
                                <p>No hay mensajes con este contacto</p>
                                <button
                                  className="admin-action-button-small chat-button"
                                  onClick={() => {
                                    sessionStorage.setItem('chatsPage_asUserId', selectedCrmUser.id.toString());
                                    window.open(`${window.location.origin}/soporte?asUserId=${selectedCrmUser.id}`, '_blank');
                                  }}
                                >
                                  Iniciar Conversación
                                </button>
                              </div>
                            );
                          }
                          const lastDate = userConversation.lastMessageDate ? new Date(userConversation.lastMessageDate) : null;
                          const lastMessageIsFromAdmin = currentUserId && userConversation.lastMessageFromUserId && parseInt(String(userConversation.lastMessageFromUserId)) === currentUserId;
                          const daysSince = lastDate && lastMessageIsFromAdmin ? Math.floor((Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
                          return (
                            <div className="admin-crm-messaging-info">
                              <div className="admin-crm-info-row">
                                <span className="info-label">Último mensaje:</span>
                                <span>{userConversation.lastMessage || 'Sin mensajes'}</span>
                              </div>
                              <div className="admin-crm-info-row">
                                <span className="info-label">Fecha:</span>
                                <span>{lastDate ? formatDate(userConversation.lastMessageDate) : 'Nunca'}</span>
                              </div>
                              <div className="admin-crm-info-row">
                                <span className="info-label">Mensajes no leídos:</span>
                                <span className={userConversation.unreadCount > 0 ? 'status-badge-small request-badge' : ''}>{userConversation.unreadCount}</span>
                              </div>
                              <div className="admin-crm-info-row">
                                <span className="info-label">Días sin respuesta:</span>
                                {daysSince !== null && daysSince > 0 ? (
                                  <span className={daysSince > 7 ? 'status-badge-small request-badge' : ''}>{daysSince} día(s)</span>
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>—</span>
                                )}
                              </div>
                              <div className="admin-crm-actions">
                                <button
                                  className="admin-action-button-small chat-button"
                                  onClick={() => {
                                    sessionStorage.setItem('chatsPage_asUserId', selectedCrmUser.id.toString());
                                    window.open(`${window.location.origin}/soporte?asUserId=${selectedCrmUser.id}`, '_blank');
                                  }}
                                >
                                  Abrir Chat
                                  {userConversation.unreadCount > 0 && <span className="chat-unread-badge-small">{userConversation.unreadCount}</span>}
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                              </div>
                            )}

                            {activeMainProfileTab === 'missing' && (
                              <div className="admin-profile-tab-panel">
                    {/* Checklist de información faltante */}
                    <div className="admin-crm-section-card">
                      <h3>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <AlertTriangleIcon width={18} height={18} /> Información Faltante
                        </span>
                      </h3>
                      <div className="admin-crm-checklist">
                        {(() => {
                          // Función para validar si un speaker está completo
                          const isSpeakerComplete = (speaker: any): boolean => {
                            if (!speaker) return false;
                            const hasPhoto = speaker.photo && (typeof speaker.photo === 'string' ? speaker.photo.trim() !== '' : true);
                            const hasFirstName = speaker.firstName && speaker.firstName.toString().trim() !== '';
                            const hasLastName = speaker.lastName && speaker.lastName.toString().trim() !== '';
                            const hasNationality = speaker.nationality && speaker.nationality.toString().trim() !== '';
                            const hasOrganization = speaker.organization && speaker.organization.toString().trim() !== '';
                            const hasPosition = speaker.position && speaker.position.toString().trim() !== '';
                            const hasDescription = speaker.description && speaker.description.toString().trim() !== '';
                            const hasThematicAxes = speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0;
                            const hasContentDescription = speaker.contentDescription && speaker.contentDescription.toString().trim() !== '';
                            const hasLinkedin = speaker.linkedin && speaker.linkedin.toString().trim() !== '';
                            const hasBiography = speaker.biography && speaker.biography.toString().trim() !== '';
                            const hasValidBiography = hasLinkedin || hasBiography;
                            return hasPhoto && hasFirstName && hasLastName && hasNationality && hasOrganization && 
                                   hasPosition && hasDescription && hasThematicAxes && hasContentDescription && hasValidBiography;
                          };

                          // Función para validar si un evento paralelo está completo
                          const isParallelEventComplete = (event: any): boolean => {
                            if (!event) return false;
                            const hasName = event.name && event.name.toString().trim() !== '';
                            const hasDescription = event.description && event.description.toString().trim() !== '';
                            const hasSpeakers = (event.speakerIds && event.speakerIds.length > 0) || 
                                              (event.localSpeakers && event.localSpeakers.length > 0);
                            const hasDuration = event.duration && event.duration.toString().trim() !== '';
                            const hasThematicAxes = event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0;
                            const hasAttendeeProfiles = event.attendeeProfiles && Array.isArray(event.attendeeProfiles) && event.attendeeProfiles.length > 0;
                            
                            if (!hasName || !hasDescription || !hasSpeakers || !hasDuration || !hasThematicAxes || !hasAttendeeProfiles) {
                              return false;
                            }

                            // Validar oradores locales si existen
                            if (event.localSpeakers && event.localSpeakers.length > 0) {
                              for (const localSpeaker of event.localSpeakers) {
                                const hasPhoto = localSpeaker.photo && (typeof localSpeaker.photo === 'string' ? localSpeaker.photo.trim() !== '' : true);
                                const hasFirstName = localSpeaker.firstName && localSpeaker.firstName.toString().trim() !== '';
                                const hasLastName = localSpeaker.lastName && localSpeaker.lastName.toString().trim() !== '';
                                const hasNationality = localSpeaker.nationality && localSpeaker.nationality.toString().trim() !== '';
                                const hasOrganization = localSpeaker.organization && localSpeaker.organization.toString().trim() !== '';
                                const hasPosition = localSpeaker.position && localSpeaker.position.toString().trim() !== '';
                                const hasDescription = localSpeaker.description && localSpeaker.description.toString().trim() !== '';
                                const hasLinkedin = localSpeaker.linkedin && localSpeaker.linkedin.toString().trim() !== '';
                                const hasBiography = localSpeaker.biography && localSpeaker.biography.toString().trim() !== '';
                                const hasValidBiography = hasLinkedin || hasBiography;
                                if (!hasPhoto || !hasFirstName || !hasLastName || !hasNationality || 
                                    !hasOrganization || !hasPosition || !hasDescription || !hasValidBiography) {
                                  return false;
                                }
                              }
                            }
                            return true;
                          };

                          // Función para validar si una iniciativa está completa
                          const isInitiativeComplete = (initiative: any): boolean => {
                            if (!initiative) return false;
                            const hasName = initiative.name && initiative.name.toString().trim() !== '';
                            const hasShortDescription = initiative.shortDescription && initiative.shortDescription.toString().trim() !== '';
                            const hasDescription = initiative.description && initiative.description.toString().trim() !== '';
                            const hasThematicAxes = initiative.thematicAxes && Array.isArray(initiative.thematicAxes) && initiative.thematicAxes.length > 0;
                            
                            if (!hasName || !hasShortDescription || !hasDescription || !hasThematicAxes) {
                              return false;
                            }

                            // Validar campos de contacto si no está marcado "sin contacto"
                            if (!initiative.noContact) {
                              const hasContactFirstName = initiative.contactFirstName && initiative.contactFirstName.trim() !== '';
                              const hasContactLastName = initiative.contactLastName && initiative.contactLastName.trim() !== '';
                              const hasContactPosition = initiative.contactPosition && initiative.contactPosition.trim() !== '';
                              const hasContactEmail = initiative.contactEmail && initiative.contactEmail.trim() !== '';
                              if (!hasContactFirstName || !hasContactLastName || !hasContactPosition || !hasContactEmail) {
                                return false;
                              }
                            }
                            return true;
                          };

                          // Función para validar si un participante de rondas está completo
                          const isBusinessRoundsParticipantComplete = (participant: any): boolean => {
                            if (!participant) return false;
                            const hasFirstName = participant.firstName && participant.firstName.trim() !== '';
                            const hasLastName = participant.lastName && participant.lastName.trim() !== '';
                            const hasPosition = participant.position && participant.position.trim() !== '';
                            const hasOrganization = participant.organization && participant.organization.trim() !== '';
                            const hasEmail = participant.email && participant.email.trim() !== '';
                            const hasPhone = participant.numeroTelefono && participant.numeroTelefono.trim() !== '';
                            const hasThematicAxes = participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0;
                            return hasFirstName && hasLastName && hasPosition && hasOrganization && hasEmail && hasPhone && hasThematicAxes;
                          };

                          // Validar perfiles - recalcular en tiempo real usando la misma función que el frontend
                          // Esto asegura que el estado refleje la realidad actual de los datos, no solo el valor guardado en BD
                          const wantsToManageProfile = fullProfileData?.contactInfo?.wantsToManageProfile || false;
                          const personalProgress = fullProfileData ? calculateProfileProgress(fullProfileData, false) : { percentage: 0, isComplete: false };
                          const isPersonalProfileComplete = personalProgress.isComplete;
                          const hasInstitutionalProfile = wantsToManageProfile;
                          const institutionalProgress = hasInstitutionalProfile && fullProfileData ? calculateProfileProgress(fullProfileData, true) : { percentage: 0, isComplete: false };
                          const isInstitutionalProfileComplete = hasInstitutionalProfile ? institutionalProgress.isComplete : true;

                          // Validar oradores - solo si existen
                          const hasSpeakers = selectedCrmUser.speakers && Array.isArray(selectedCrmUser.speakers) && selectedCrmUser.speakers.length > 0;
                          const areSpeakersComplete = hasSpeakers && selectedCrmUser.speakers ? selectedCrmUser.speakers.every((speaker: any) => isSpeakerComplete(speaker)) : true;

                          // Validar eventos paralelos - solo si existen
                          const hasParallelEvents = selectedCrmUser.parallel_events && Array.isArray(selectedCrmUser.parallel_events) && selectedCrmUser.parallel_events.length > 0;
                          const areParallelEventsComplete = hasParallelEvents && selectedCrmUser.parallel_events ? selectedCrmUser.parallel_events.every((event: any) => isParallelEventComplete(event)) : true;

                          // Validar iniciativas - solo si existen
                          const hasInitiatives = fullProfileData?.initiatives && Array.isArray(fullProfileData.initiatives) && fullProfileData.initiatives.length > 0;
                          const areInitiativesComplete = hasInitiatives && fullProfileData?.initiatives ? fullProfileData.initiatives.every((initiative: any) => isInitiativeComplete(initiative)) : true;

                          // Validar participantes de rondas de negocios - solo si existen
                          const hasBusinessRoundsParticipants = fullProfileData?.businessRoundsParticipants && Array.isArray(fullProfileData.businessRoundsParticipants) && fullProfileData.businessRoundsParticipants.length > 0;
                          const areBusinessRoundsParticipantsComplete = hasBusinessRoundsParticipants && fullProfileData?.businessRoundsParticipants ? fullProfileData.businessRoundsParticipants.every((participant: any) => isBusinessRoundsParticipantComplete(participant)) : true;

                          // Funciones para obtener campos incompletos específicos
                          const getIncompletePersonalProfileFields = (): string[] => {
                            if (isPersonalProfileComplete) return [];
                            const fields: string[] = [];
                            
                            // Helper para obtener y limpiar valores
                            const getValue = (value: any): string => {
                              if (value === null || value === undefined) return '';
                              if (typeof value === 'string') return value.trim();
                              return String(value).trim();
                            };
                            
                            // Función para validar teléfono (misma lógica que Sidebar.tsx)
                            const validatePhoneNumber = (countryCode: string, phoneNumber: string): boolean => {
                              if (!phoneNumber || !phoneNumber.trim()) return false;
                              const cleanNumber = phoneNumber.replace(/\D/g, '');
                              const patterns: { [key: string]: RegExp } = {
                                'AR': /^\d{10,11}$/, 'BR': /^\d{10,11}$/, 'CL': /^\d{8,9}$/, 'CO': /^\d{10}$/, 'MX': /^\d{10}$/,
                                'PE': /^\d{9}$/, 'UY': /^\d{8,9}$/, 'VE': /^\d{10,11}$/, 'BO': /^\d{8}$/,
                                'EC': /^\d{9}$/, 'PY': /^\d{9}$/, 'CR': /^\d{8}$/, 'PA': /^\d{8}$/,
                                'GT': /^\d{8}$/, 'HN': /^\d{8}$/, 'SV': /^\d{8}$/, 'NI': /^\d{8}$/,
                                'CU': /^\d{8}$/, 'DO': /^\d{10}$/, 'HT': /^\d{8}$/, 'JM': /^\d{10}$/,
                                'TT': /^\d{10}$/, 'US': /^\d{10}$/, 'CA': /^\d{10}$/, 'ES': /^\d{9}$/,
                                'IT': /^\d{10,11}$/, 'FR': /^\d{10}$/, 'DE': /^\d{10,11}$/, 'GB': /^\d{10,11}$/,
                                'PT': /^\d{9}$/, 'NL': /^\d{9}$/, 'BE': /^\d{9}$/, 'CH': /^\d{9}$/,
                                'AT': /^\d{10,11}$/, 'SE': /^\d{9}$/, 'NO': /^\d{8}$/, 'DK': /^\d{8}$/,
                                'FI': /^\d{9,10}$/, 'PL': /^\d{9}$/, 'CZ': /^\d{9}$/, 'HU': /^\d{9}$/,
                                'RO': /^\d{10}$/, 'BG': /^\d{9}$/, 'GR': /^\d{10}$/, 'RU': /^\d{10,11}$/,
                                'UA': /^\d{9}$/, 'IE': /^\d{9}$/, 'IS': /^\d{7}$/, 'CN': /^\d{11}$/,
                                'JP': /^\d{10,11}$/, 'KR': /^\d{10,11}$/, 'IN': /^\d{10}$/, 'TH': /^\d{9}$/,
                                'SG': /^\d{8}$/, 'MY': /^\d{9,10}$/, 'ID': /^\d{10,12}$/, 'PH': /^\d{10}$/,
                                'VN': /^\d{9,10}$/, 'TW': /^\d{9}$/, 'HK': /^\d{8}$/, 'BD': /^\d{10}$/,
                                'PK': /^\d{10}$/, 'LK': /^\d{9}$/, 'NP': /^\d{10}$/, 'MM': /^\d{9,10}$/,
                                'KH': /^\d{8,9}$/, 'LA': /^\d{8,9}$/, 'MN': /^\d{8}$/, 'AU': /^\d{9,10}$/,
                                'NZ': /^\d{8,9}$/, 'FJ': /^\d{7,8}$/, 'PG': /^\d{8}$/, 'NC': /^\d{6}$/,
                                'ZA': /^\d{9}$/, 'EG': /^\d{10}$/, 'NG': /^\d{10,11}$/, 'KE': /^\d{9,10}$/,
                                'MA': /^\d{9}$/, 'TN': /^\d{8}$/, 'DZ': /^\d{9}$/, 'GH': /^\d{9}$/,
                                'ET': /^\d{9}$/, 'UG': /^\d{9}$/, 'TZ': /^\d{9}$/, 'ZW': /^\d{9}$/,
                                'BW': /^\d{7,8}$/, 'NA': /^\d{9}$/, 'ZM': /^\d{9}$/, 'MW': /^\d{9}$/,
                                'MZ': /^\d{9}$/, 'MG': /^\d{9}$/, 'MU': /^\d{7,8}$/, 'SC': /^\d{7}$/,
                                'SA': /^\d{9}$/, 'AE': /^\d{9}$/, 'QA': /^\d{8}$/, 'KW': /^\d{8}$/,
                                'BH': /^\d{8}$/, 'OM': /^\d{8}$/, 'JO': /^\d{9}$/, 'LB': /^\d{8}$/,
                                'SY': /^\d{9}$/, 'IQ': /^\d{10}$/, 'IR': /^\d{10}$/, 'IL': /^\d{9}$/,
                                'TR': /^\d{10}$/
                              };
                              const pattern = patterns[countryCode];
                              return pattern ? pattern.test(cleanNumber) : cleanNumber.length >= 7 && cleanNumber.length <= 15;
                            };
                            
                            // Función para validar URL (misma lógica que Sidebar.tsx)
                            const validateUrl = (url: string): boolean => {
                              if (!url || !url.trim()) return false;
                              try {
                                const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
                                return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
                              } catch {
                                return false;
                              }
                            };
                            
                            // Obtener datos de fullProfileData o selectedCrmUser como fallback
                            const firstName = getValue(fullProfileData?.contactInfo?.firstName || selectedCrmUser.first_name);
                            const lastName = getValue(fullProfileData?.contactInfo?.lastName || selectedCrmUser.last_name);
                            const email = getValue(fullProfileData?.contactInfo?.email || selectedCrmUser.email);
                            const codigoPais = fullProfileData?.contactInfo?.codigoPais || 'AR';
                            const numeroTelefono = getValue(fullProfileData?.contactInfo?.numeroTelefono || fullProfileData?.contactInfo?.phone);
                            const orgName = getValue(fullProfileData?.basicInfo?.organizationName || selectedCrmUser.organization_name);
                            const orgType = getValue(fullProfileData?.basicInfo?.organizationType || selectedCrmUser.organization_type);
                            const position = getValue(fullProfileData?.contactInfo?.position || selectedCrmUser.position);
                            const area = getValue(fullProfileData?.contactInfo?.area);
                            const subarea = getValue(fullProfileData?.contactInfo?.subarea);
                            const website = getValue(fullProfileData?.basicInfo?.website);
                            const thematicAxes = fullProfileData?.contactInfo?.thematicAxes || [];
                            
                            // Validar cada campo según la lógica del perfil personal (misma que Sidebar.tsx)
                            if (!firstName) fields.push('Nombre');
                            if (!lastName) fields.push('Apellido');
                            if (!email || !email.includes('@')) fields.push('Correo electrónico');
                            if (!numeroTelefono || !validatePhoneNumber(codigoPais, numeroTelefono)) fields.push('Teléfono');
                            if (!orgName) fields.push('Organización');
                            if (!orgType) fields.push('Tipo de organización');
                            if (!position) fields.push('Cargo');
                            if (!area) fields.push('Área');
                            if (!subarea) fields.push('Subárea');
                            if (!website || !validateUrl(website)) fields.push('Sitio web');
                            if (!Array.isArray(thematicAxes) || thematicAxes.length === 0) fields.push('Ejes de interés');
                            
                            // Si no se encontraron campos incompletos pero el perfil está marcado como incompleto,
                            // puede ser que el valor en la BD esté desactualizado o haya una discrepancia en la validación
                            // En este caso, mostramos un mensaje informativo
                            if (fields.length === 0) {
                              fields.push('El perfil está marcado como incompleto en el sistema, pero todos los campos visibles están completos. Puede ser necesario actualizar el perfil para sincronizar el estado.');
                            }
                            
                            return fields;
                          };

                          const getIncompleteInstitutionalProfileFields = (): string[] => {
                            if (isInstitutionalProfileComplete) return [];
                            const fields: string[] = [];
                            if (!fullProfileData?.basicInfo?.eventProgramParticipation || !Array.isArray(fullProfileData.basicInfo.eventProgramParticipation) || fullProfileData.basicInfo.eventProgramParticipation.length === 0) fields.push('Participación en el programa del evento');
                            if (!fullProfileData?.basicInfo?.logo || (typeof fullProfileData.basicInfo.logo === 'string' && !fullProfileData.basicInfo.logo.trim())) fields.push('Logo');
                            if (!fullProfileData?.basicInfo?.country?.toString().trim()) fields.push('País');
                            if (!fullProfileData?.basicInfo?.thematicAxes || !Array.isArray(fullProfileData.basicInfo.thematicAxes) || fullProfileData.basicInfo.thematicAxes.length === 0) fields.push('Ejes Temáticos de la organización');
                            if (!fullProfileData?.institutionalDescription?.description?.toString().trim()) fields.push('Descripción');
                            if (!fullProfileData?.participationModalities || !Array.isArray(fullProfileData.participationModalities) || fullProfileData.participationModalities.length === 0) fields.push('Modalidades de participación');
                            return fields;
                          };

                          const getIncompleteSpeakerFields = (): Array<{ speakerIndex: number; fields: string[] }> => {
                            if (areSpeakersComplete || !hasSpeakers || !selectedCrmUser.speakers) return [];
                            const incomplete: Array<{ speakerIndex: number; fields: string[] }> = [];
                            selectedCrmUser.speakers.forEach((speaker: any, index: number) => {
                              if (isSpeakerComplete(speaker)) return;
                              const fields: string[] = [];
                              if (!speaker.photo || (typeof speaker.photo === 'string' && !speaker.photo.trim())) fields.push('Foto');
                              if (!speaker.firstName?.toString().trim()) fields.push('Nombre');
                              if (!speaker.lastName?.toString().trim()) fields.push('Apellido');
                              if (!speaker.nationality?.toString().trim()) fields.push('Nacionalidad');
                              if (!speaker.organization?.toString().trim()) fields.push('Organización');
                              if (!speaker.position?.toString().trim()) fields.push('Cargo o especialidad');
                              if (!speaker.description?.toString().trim()) fields.push('Minibiografía');
                              if (!speaker.thematicAxes || !Array.isArray(speaker.thematicAxes) || speaker.thematicAxes.length === 0) fields.push('Temática de interés para su exposición');
                              if (!speaker.contentDescription?.toString().trim()) fields.push('Breve descripción del contenido');
                              if (!speaker.linkedin?.toString().trim() && !speaker.biography?.toString().trim()) fields.push('Biografía o LinkedIn');
                              if (fields.length > 0) {
                                incomplete.push({ speakerIndex: index + 1, fields });
                              }
                            });
                            return incomplete;
                          };

                          const getIncompleteParallelEventFields = (): Array<{ eventIndex: number; fields: string[] }> => {
                            if (areParallelEventsComplete || !hasParallelEvents || !selectedCrmUser.parallel_events) return [];
                            const incomplete: Array<{ eventIndex: number; fields: string[] }> = [];
                            selectedCrmUser.parallel_events.forEach((event: any, index: number) => {
                              if (isParallelEventComplete(event)) return;
                              const fields: string[] = [];
                              if (!event.name?.toString().trim()) fields.push('Título de la actividad');
                              if (!event.description?.toString().trim()) fields.push('Descripción');
                              if ((!event.speakerIds || event.speakerIds.length === 0) && (!event.localSpeakers || event.localSpeakers.length === 0)) fields.push('Oradores');
                              if (!event.duration?.toString().trim()) fields.push('Duración');
                              if (!event.thematicAxes || !Array.isArray(event.thematicAxes) || event.thematicAxes.length === 0) fields.push('Ejes de la actividad');
                              if (!event.attendeeProfiles || !Array.isArray(event.attendeeProfiles) || event.attendeeProfiles.length === 0) fields.push('Perfil de asistentes');
                              // Validar oradores locales
                              if (event.localSpeakers && event.localSpeakers.length > 0) {
                                event.localSpeakers.forEach((localSpeaker: any, localIndex: number) => {
                                  const localFields: string[] = [];
                                  if (!localSpeaker.photo || (typeof localSpeaker.photo === 'string' && !localSpeaker.photo.trim())) localFields.push(`Orador local ${localIndex + 1}: Foto`);
                                  if (!localSpeaker.firstName?.toString().trim()) localFields.push(`Orador local ${localIndex + 1}: Nombre`);
                                  if (!localSpeaker.lastName?.toString().trim()) localFields.push(`Orador local ${localIndex + 1}: Apellido`);
                                  if (!localSpeaker.nationality?.toString().trim()) localFields.push(`Orador local ${localIndex + 1}: Nacionalidad`);
                                  if (!localSpeaker.organization?.toString().trim()) localFields.push(`Orador local ${localIndex + 1}: Organización`);
                                  if (!localSpeaker.position?.toString().trim()) localFields.push(`Orador local ${localIndex + 1}: Cargo`);
                                  if (!localSpeaker.description?.toString().trim()) localFields.push(`Orador local ${localIndex + 1}: Descripción`);
                                  if (!localSpeaker.linkedin?.toString().trim() && !localSpeaker.biography?.toString().trim()) localFields.push(`Orador local ${localIndex + 1}: Biografía o LinkedIn`);
                                  if (localFields.length > 0) {
                                    fields.push(...localFields);
                                  }
                                });
                              }
                              if (fields.length > 0) {
                                incomplete.push({ eventIndex: index + 1, fields });
                              }
                            });
                            return incomplete;
                          };

                          const getIncompleteInitiativeFields = (): Array<{ initiativeIndex: number; fields: string[] }> => {
                            if (areInitiativesComplete || !hasInitiatives || !fullProfileData?.initiatives) return [];
                            const incomplete: Array<{ initiativeIndex: number; fields: string[] }> = [];
                            fullProfileData.initiatives.forEach((initiative: any, index: number) => {
                              if (isInitiativeComplete(initiative)) return;
                              const fields: string[] = [];
                              if (!initiative.name?.toString().trim()) fields.push('Nombre');
                              if (!initiative.shortDescription?.toString().trim()) fields.push('Descripción breve');
                              if (!initiative.description?.toString().trim()) fields.push('Descripción amplia');
                              if (!initiative.thematicAxes || !Array.isArray(initiative.thematicAxes) || initiative.thematicAxes.length === 0) fields.push('Ejes temáticos vinculados');
                              if (!initiative.noContact) {
                                if (!initiative.contactFirstName?.trim()) fields.push('Nombre de contacto');
                                if (!initiative.contactLastName?.trim()) fields.push('Apellido de contacto');
                                if (!initiative.contactPosition?.trim()) fields.push('Cargo de contacto');
                                if (!initiative.contactEmail?.trim()) fields.push('Correo de contacto');
                              }
                              if (fields.length > 0) {
                                incomplete.push({ initiativeIndex: index + 1, fields });
                              }
                            });
                            return incomplete;
                          };

                          const getIncompleteBusinessRoundsParticipantFields = (): Array<{ participantIndex: number; fields: string[] }> => {
                            if (areBusinessRoundsParticipantsComplete || !hasBusinessRoundsParticipants || !fullProfileData?.businessRoundsParticipants) return [];
                            const incomplete: Array<{ participantIndex: number; fields: string[] }> = [];
                            fullProfileData.businessRoundsParticipants.forEach((participant: any, index: number) => {
                              if (isBusinessRoundsParticipantComplete(participant)) return;
                              const fields: string[] = [];
                              if (!participant.firstName?.trim()) fields.push('Nombre');
                              if (!participant.lastName?.trim()) fields.push('Apellido');
                              if (!participant.position?.trim()) fields.push('Cargo');
                              if (!participant.organization?.trim()) fields.push('Organización');
                              if (!participant.email?.trim()) fields.push('Correo');
                              if (!participant.numeroTelefono?.trim()) fields.push('Teléfono');
                              if (!participant.thematicAxes || !Array.isArray(participant.thematicAxes) || participant.thematicAxes.length === 0) fields.push('Ejes temáticos de interés');
                              if (fields.length > 0) {
                                incomplete.push({ participantIndex: index + 1, fields });
                              }
                            });
                            return incomplete;
                          };

                          // Construir lista de items a mostrar
                          const itemsToShow = [
                            { 
                              id: 'personal', 
                              baseLabel: 'Perfil Personal', 
                              checked: isPersonalProfileComplete, 
                              show: true,
                              getIncompleteFields: () => getIncompletePersonalProfileFields()
                            },
                            { 
                              id: 'institutional', 
                              baseLabel: 'Perfil Institucional', 
                              checked: isInstitutionalProfileComplete, 
                              show: hasInstitutionalProfile,
                              getIncompleteFields: () => getIncompleteInstitutionalProfileFields()
                            },
                            { 
                              id: 'speakers', 
                              baseLabel: 'Orador', 
                              checked: areSpeakersComplete, 
                              show: hasSpeakers,
                              getIncompleteFields: () => getIncompleteSpeakerFields()
                            },
                            { 
                              id: 'events', 
                              baseLabel: 'Evento Paralelo', 
                              checked: areParallelEventsComplete, 
                              show: hasParallelEvents,
                              getIncompleteFields: () => getIncompleteParallelEventFields()
                            },
                            { 
                              id: 'initiatives', 
                              baseLabel: 'Iniciativa', 
                              checked: areInitiativesComplete, 
                              show: hasInitiatives,
                              getIncompleteFields: () => getIncompleteInitiativeFields()
                            },
                            { 
                              id: 'businessRounds', 
                              baseLabel: 'Participante en Rondas de Negocios', 
                              checked: areBusinessRoundsParticipantsComplete, 
                              show: hasBusinessRoundsParticipants,
                              getIncompleteFields: () => getIncompleteBusinessRoundsParticipantFields()
                            },
                          ].filter(item => item.show);

                          const toggleExpand = (itemId: string) => {
                            setExpandedChecklistItems(prev => {
                              const newSet = new Set(prev);
                              if (newSet.has(itemId)) {
                                newSet.delete(itemId);
                              } else {
                                newSet.add(itemId);
                              }
                              return newSet;
                            });
                          };

                          return itemsToShow.map((item) => {
                            const label = item.checked ? `${item.baseLabel} Completo` : `${item.baseLabel} Incompleto`;
                            const isExpanded = expandedChecklistItems.has(item.id);
                            // Solo obtener campos incompletos si el item no está marcado como completo
                            const incompleteFields = !item.checked ? item.getIncompleteFields() : [];
                            
                            return (
                              <div key={item.id} className={`admin-crm-checklist-item ${item.checked ? 'checked' : 'unchecked'}`}>
                                <div 
                                  className="admin-crm-checklist-item-header"
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '12px', 
                                    width: '100%',
                                    cursor: !item.checked ? 'pointer' : 'default'
                                  }}
                                  onClick={() => !item.checked && toggleExpand(item.id)}
                                >
                                  <span className="check-icon">{item.checked ? <CheckIcon width={16} height={16} /> : <XIcon width={16} height={16} />}</span>
                                  <span style={{ flex: 1 }}>{label}</span>
                                  {!item.checked && (
                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                      {isExpanded ? <ChevronUpIcon width={16} height={16} /> : <ChevronDownIcon width={16} height={16} />}
                                    </span>
                                  )}
                                </div>
                                {!item.checked && isExpanded && incompleteFields.length > 0 && (
                                  <div className="admin-crm-checklist-details" style={{ marginTop: '12px', paddingLeft: '28px', textAlign: 'left', width: '100%' }}>
                                    {incompleteFields.length > 0 && typeof incompleteFields[0] === 'object' && 'fields' in incompleteFields[0] ? (
                                      // Para oradores, eventos, iniciativas y participantes - mostrar por índice
                                      incompleteFields.map((incomplete: any, idx: number) => (
                                        <div key={idx} style={{ marginBottom: '12px', textAlign: 'left' }}>
                                          {incomplete.speakerIndex && (
                                            <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '13px', textAlign: 'left' }}>
                                              Orador {incomplete.speakerIndex}:
                                            </div>
                                          )}
                                          {incomplete.eventIndex && (
                                            <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '13px', textAlign: 'left' }}>
                                              Evento {incomplete.eventIndex}:
                                            </div>
                                          )}
                                          {incomplete.initiativeIndex && (
                                            <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '13px', textAlign: 'left' }}>
                                              Iniciativa {incomplete.initiativeIndex}:
                                            </div>
                                          )}
                                          {incomplete.participantIndex && (
                                            <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '13px', textAlign: 'left' }}>
                                              Participante {incomplete.participantIndex}:
                                            </div>
                                          )}
                                          <ul style={{ margin: '4px 0 0 16px', padding: 0, listStyle: 'disc', textAlign: 'left' }}>
                                            {incomplete.fields.map((field: string, fieldIdx: number) => (
                                              <li key={fieldIdx} style={{ fontSize: '13px', marginBottom: '2px', textAlign: 'left' }}>
                                                {field}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      ))
                                    ) : (
                                      // Para perfiles - mostrar lista simple
                                      <ul style={{ margin: 0, padding: 0, listStyle: 'disc', paddingLeft: '16px', textAlign: 'left' }}>
                                        {(incompleteFields as string[]).map((field: string, fieldIdx: number) => (
                                          <li key={fieldIdx} style={{ fontSize: '13px', marginBottom: '4px', textAlign: 'left' }}>
                                            {field}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>

                    {/* Historial de mensajería: Correo | WhatsApp | Mensajería */}
                    <div className="admin-crm-section-card">
                      <h3><MessageCircleIcon width={18} height={18} /> Historial de Mensajería</h3>
                      <div className="admin-conversations-subtabs" style={{ marginTop: 12, marginBottom: 12 }}>
                        <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'correo' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('correo')}>
                          <EnvelopeIcon width={18} height={18} /> Correo
                        </button>
                        <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'whatsapp' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('whatsapp')}>
                          <WhatsAppIcon width={18} height={18} /> WhatsApp
                        </button>
                        <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'messaging' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('messaging')}>
                          <MessageCircleIcon width={18} height={18} /> Mensajería
                        </button>
                      </div>
                      <div>
                        {activeHistorialSubTab === 'correo' && (
                          <GmailSection isDarkMode={isDarkMode} filterByEmail={selectedCrmUser.email} />
                        )}
                        {activeHistorialSubTab === 'whatsapp' && (
                          <HistorialWhatsAppTab
                            userId={selectedCrmUser.id}
                            whatsapp={selectedCrmUser.whatsapp ?? null}
                            onSaveWhatsapp={(phone) => {
                              setUsers(prev => prev.map(u => u.id === selectedCrmUser.id ? { ...u, whatsapp: phone } : u));
                              setSelectedCrmUser(prev => prev ? { ...prev, whatsapp: phone } : null);
                            }}
                            getApiUrl={getApiUrl}
                            isDarkMode={isDarkMode}
                          />
                        )}
                        {activeHistorialSubTab === 'messaging' && (() => {
                          const userConversation = conversations.find(c => c.userId === selectedCrmUser.id);
                          if (!userConversation) {
                            return (
                              <div className="admin-crm-empty-state">
                                <p>No hay mensajes con este contacto</p>
                                <button
                                  className="admin-action-button-small chat-button"
                                  onClick={() => {
                                    sessionStorage.setItem('chatsPage_asUserId', selectedCrmUser.id.toString());
                                    window.open(`${window.location.origin}/soporte?asUserId=${selectedCrmUser.id}`, '_blank');
                                  }}
                                >
                                  Iniciar Conversación
                                </button>
                              </div>
                            );
                          }
                          const lastDate = userConversation.lastMessageDate ? new Date(userConversation.lastMessageDate) : null;
                          const lastMessageIsFromAdmin = currentUserId && userConversation.lastMessageFromUserId && parseInt(String(userConversation.lastMessageFromUserId)) === currentUserId;
                          const daysSince = lastDate && lastMessageIsFromAdmin ? Math.floor((Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
                          return (
                            <div className="admin-crm-messaging-info">
                              <div className="admin-crm-info-row">
                                <span className="info-label">Último mensaje:</span>
                                <span>{userConversation.lastMessage || 'Sin mensajes'}</span>
                              </div>
                              <div className="admin-crm-info-row">
                                <span className="info-label">Fecha:</span>
                                <span>{lastDate ? formatDate(userConversation.lastMessageDate) : 'Nunca'}</span>
                              </div>
                              <div className="admin-crm-info-row">
                                <span className="info-label">Mensajes no leídos:</span>
                                <span className={userConversation.unreadCount > 0 ? 'status-badge-small request-badge' : ''}>{userConversation.unreadCount}</span>
                              </div>
                              <div className="admin-crm-info-row">
                                <span className="info-label">Días sin respuesta:</span>
                                {daysSince !== null && daysSince > 0 ? (
                                  <span className={daysSince > 7 ? 'status-badge-small request-badge' : ''}>{daysSince} día(s)</span>
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>—</span>
                                )}
                              </div>
                              <div className="admin-crm-actions">
                                <button
                                  className="admin-action-button-small chat-button"
                                  onClick={() => {
                                    sessionStorage.setItem('chatsPage_asUserId', selectedCrmUser.id.toString());
                                    window.open(`${window.location.origin}/soporte?asUserId=${selectedCrmUser.id}`, '_blank');
                                  }}
                                >
                                  Abrir Chat
                                  {userConversation.unreadCount > 0 && <span className="chat-unread-badge-small">{userConversation.unreadCount}</span>}
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                          </div>
                        )}

                            {activeMainProfileTab === 'profile' && (
                              <div className="admin-profile-tab-panel">
                                {/* Información Institucional */}
                                <div className="admin-crm-section-card">
                                  <h3>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <BuildingIcon width={18} height={18} /> Información Institucional
                                    </span>
                                  </h3>
                                  <div className="admin-crm-info-grid">
                                    {logoPreview && (
                                      <div className="admin-crm-info-item-full" style={{ marginBottom: '16px' }}>
                                        <span className="info-label">Logo:</span>
                                        <div style={{ marginTop: '8px' }}>
                                          <img 
                                            src={logoPreview} 
                                            alt="Logo de la organización" 
                                            style={{
                                              maxWidth: '200px',
                                              maxHeight: '200px',
                                              objectFit: 'contain',
                                              border: '2px solid #e5e7eb',
                                              borderRadius: '8px',
                                              padding: '8px',
                                              backgroundColor: '#ffffff'
                                            }}
                                            onError={(e) => {
                                            }}
                                          />
                          </div>
                      </div>
                      )}
                                    <div className="admin-crm-info-item-full">
                                      <span className="info-label">Nombre de la organización:</span>
                                      <span className="info-value">{fullProfileData?.basicInfo?.organizationName || 'N/A'}</span>
                                    </div>
                                    <div className="admin-crm-info-item-full">
                                      <span className="info-label">Tipo de organización:</span>
                                      <span className="info-value">{getOrganizationTypeLabel(fullProfileData?.basicInfo?.organizationType) || 'N/A'}</span>
                                    </div>
                                    <div className="admin-crm-info-item-full">
                                      <span className="info-label">Industrias:</span>
                                      <span className="info-value admin-profile-tags-wrap">
                                        {fullProfileData?.basicInfo?.industries && fullProfileData.basicInfo.industries.length > 0
                                          ? fullProfileData.basicInfo.industries.map((ind, idx) => (
                                              <span key={idx} className="tag-badge-small">{ind}</span>
                                            ))
                                          : 'N/A'}
                                      </span>
                                    </div>
                                    <div className="admin-crm-info-item-full">
                                      <span className="info-label">Sitio web:</span>
                                      <span className="info-value">
                                        {(() => {
                                          const website = fullProfileData?.basicInfo?.website;
                                          if (!website || website === 'N/A') return 'N/A';
                                          const href = website.startsWith('http') ? website : `https://${website}`;
                                          return (
                                            <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#CE2B15' }}>
                                              {website}
                                            </a>
                                          );
                                        })()}
                                      </span>
                                    </div>
                                    <div className="admin-crm-info-item-full">
                                      <span className="info-label">País:</span>
                                      <span className="info-value">
                                        {fullProfileData?.basicInfo?.country
                                          ? (countries.find(c => c.code === fullProfileData.basicInfo.country)?.name || fullProfileData.basicInfo.country)
                                          : 'N/A'}
                                      </span>
                                    </div>
                                    <div className="admin-crm-info-item-full">
                                      <span className="info-label">Descripción:</span>
                                      <span className="info-value" style={{ whiteSpace: 'pre-wrap' }}>
                                        {fullProfileData?.institutionalDescription?.description || 'N/A'}
                                      </span>
                                    </div>
                                    <div className="admin-crm-info-item-full">
                                      <span className="info-label">Ejes temáticos:</span>
                                      <span className="info-value admin-profile-tags-wrap">
                                        {fullProfileData?.basicInfo?.thematicAxes && fullProfileData.basicInfo.thematicAxes.length > 0
                                          ? fullProfileData.basicInfo.thematicAxes.map((axis) => {
                                              const axisObj = thematicAxes.find(t => t.value === axis);
                                              return (
                                                <span key={axis} className="tag-badge-small">
                                                  {axisObj?.label || String(axis)}
                                                </span>
                                              );
                                            })
                                          : 'N/A'}
                                      </span>
                                    </div>
                                    <div className="admin-crm-info-item-full">
                                      <span className="info-label">Participación en el programa del evento:</span>
                                      <span className="info-value admin-profile-tags-wrap">
                                        {fullProfileData?.basicInfo?.eventProgramParticipation && fullProfileData.basicInfo.eventProgramParticipation.length > 0
                                          ? fullProfileData.basicInfo.eventProgramParticipation.map((participation) => {
                                              const participationObj = eventProgramParticipation.find(p => p.value === participation);
                                              return (
                                                <span key={participation} className="tag-badge-small">
                                                  {participationObj?.label || String(participation)}
                                                </span>
                                              );
                                            })
                                          : 'N/A'}
                                      </span>
                                    </div>
                                    <div className="admin-crm-info-item-full">
                                      <span className="info-label">Facebook:</span>
                                      <span className="info-value">
                                        {fullProfileData?.basicInfo?.socialNetworks?.facebook ? (
                                          <a href={fullProfileData.basicInfo.socialNetworks.facebook} target="_blank" rel="noopener noreferrer" style={{ color: '#CE2B15' }}>
                                            {fullProfileData.basicInfo.socialNetworks.facebook}
                                          </a>
                                        ) : 'N/A'}
                                      </span>
                                    </div>
                                    <div className="admin-crm-info-item-full">
                                      <span className="info-label">Twitter/X:</span>
                                      <span className="info-value">
                                        {fullProfileData?.basicInfo?.socialNetworks?.twitter ? (
                                          <a href={fullProfileData.basicInfo.socialNetworks.twitter} target="_blank" rel="noopener noreferrer" style={{ color: '#CE2B15' }}>
                                            {fullProfileData.basicInfo.socialNetworks.twitter}
                                          </a>
                                        ) : 'N/A'}
                                      </span>
                                    </div>
                                    <div className="admin-crm-info-item-full">
                                      <span className="info-label">LinkedIn:</span>
                                      <span className="info-value">
                                        {fullProfileData?.basicInfo?.socialNetworks?.linkedin ? (
                                          <a href={fullProfileData.basicInfo.socialNetworks.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: '#CE2B15' }}>
                                            {fullProfileData.basicInfo.socialNetworks.linkedin}
                                          </a>
                                        ) : 'N/A'}
                                      </span>
                                    </div>
                                    <div className="admin-crm-info-item-full">
                                      <span className="info-label">Instagram:</span>
                                      <span className="info-value">
                                        {fullProfileData?.basicInfo?.socialNetworks?.instagram ? (
                                          <a href={fullProfileData.basicInfo.socialNetworks.instagram} target="_blank" rel="noopener noreferrer" style={{ color: '#CE2B15' }}>
                                            {fullProfileData.basicInfo.socialNetworks.instagram}
                                          </a>
                                        ) : 'N/A'}
                                      </span>
                                    </div>
                                    <div className="admin-crm-info-item-full">
                                      <span className="info-label">YouTube:</span>
                                      <span className="info-value">
                                        {fullProfileData?.basicInfo?.socialNetworks?.youtube ? (
                                          <a href={fullProfileData.basicInfo.socialNetworks.youtube} target="_blank" rel="noopener noreferrer" style={{ color: '#CE2B15' }}>
                                            {fullProfileData.basicInfo.socialNetworks.youtube}
                                          </a>
                                        ) : 'N/A'}
                                      </span>
                                    </div>
                                  </div>
                    </div>

                    {/* Historial de mensajería: Correo | WhatsApp | Mensajería */}
                                <div className="admin-crm-section-card" style={{ marginTop: '24px' }}>
                                  <h3><MessageCircleIcon width={18} height={18} /> Historial de Mensajería</h3>
                                  <div className="admin-conversations-subtabs" style={{ marginTop: 12, marginBottom: 12 }}>
                                    <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'correo' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('correo')}>
                                      <EnvelopeIcon width={18} height={18} /> Correo
                                    </button>
                                    <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'whatsapp' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('whatsapp')}>
                                      <WhatsAppIcon width={18} height={18} /> WhatsApp
                                    </button>
                                    <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'messaging' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('messaging')}>
                                      <MessageCircleIcon width={18} height={18} /> Mensajería
                                    </button>
                                  </div>
                                  <div>
                                    {activeHistorialSubTab === 'correo' && (
                                      <GmailSection isDarkMode={isDarkMode} filterByEmail={selectedCrmUser.email} />
                                    )}
                                    {activeHistorialSubTab === 'whatsapp' && (
                                      <HistorialWhatsAppTab
                                        userId={selectedCrmUser.id}
                                        whatsapp={selectedCrmUser.whatsapp ?? null}
                                        onSaveWhatsapp={(phone) => {
                                          setUsers(prev => prev.map(u => u.id === selectedCrmUser.id ? { ...u, whatsapp: phone } : u));
                                          setSelectedCrmUser(prev => prev ? { ...prev, whatsapp: phone } : null);
                                        }}
                                        getApiUrl={getApiUrl}
                                        isDarkMode={isDarkMode}
                                      />
                                    )}
                                    {activeHistorialSubTab === 'messaging' && (() => {
                                      const userConversation = conversations.find(c => c.userId === selectedCrmUser.id);
                                      if (!userConversation) {
                                        return (
                                          <div className="admin-crm-empty-state">
                                            <p>No hay mensajes con este contacto</p>
                                            <button
                                              className="admin-action-button-small chat-button"
                                              onClick={() => {
                                                sessionStorage.setItem('chatsPage_asUserId', selectedCrmUser.id.toString());
                                                window.open(`${window.location.origin}/soporte?asUserId=${selectedCrmUser.id}`, '_blank');
                                              }}
                                            >
                                              Iniciar Conversación
                                            </button>
                                          </div>
                                        );
                                      }
                                      const lastDate = userConversation.lastMessageDate ? new Date(userConversation.lastMessageDate) : null;
                                      const lastMessageIsFromAdmin = currentUserId && userConversation.lastMessageFromUserId && parseInt(String(userConversation.lastMessageFromUserId)) === currentUserId;
                                      const daysSince = lastDate && lastMessageIsFromAdmin ? Math.floor((Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
                                      return (
                                        <div className="admin-crm-messaging-info">
                                          <div className="admin-crm-info-row">
                                            <span className="info-label">Último mensaje:</span>
                                            <span>{userConversation.lastMessage || 'Sin mensajes'}</span>
                                          </div>
                                          <div className="admin-crm-info-row">
                                            <span className="info-label">Fecha:</span>
                                            <span>{lastDate ? formatDate(userConversation.lastMessageDate) : 'Nunca'}</span>
                                          </div>
                                          <div className="admin-crm-info-row">
                                            <span className="info-label">Mensajes no leídos:</span>
                                            <span className={userConversation.unreadCount > 0 ? 'status-badge-small request-badge' : ''}>{userConversation.unreadCount}</span>
                                          </div>
                                          <div className="admin-crm-info-row">
                                            <span className="info-label">Días sin respuesta:</span>
                                            {daysSince !== null && daysSince > 0 ? (
                                              <span className={daysSince > 7 ? 'status-badge-small request-badge' : ''}>{daysSince} día(s)</span>
                                            ) : (
                                              <span style={{ color: '#9ca3af' }}>—</span>
                                            )}
                                          </div>
                                          <div className="admin-crm-actions">
                                            <button
                                              className="admin-action-button-small chat-button"
                                              onClick={() => {
                                                sessionStorage.setItem('chatsPage_asUserId', selectedCrmUser.id.toString());
                                                window.open(`${window.location.origin}/soporte?asUserId=${selectedCrmUser.id}`, '_blank');
                                              }}
                                            >
                                              Abrir Chat
                                              {userConversation.unreadCount > 0 && <span className="chat-unread-badge-small">{userConversation.unreadCount}</span>}
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Participaciones - Mismo diseño que la pestaña general pero solo del usuario */}
                            {activeMainProfileTab === 'participations' && selectedCrmUser && (
                              <div className="admin-profile-tab-panel">
                                {/* Sub-pestañas */}
                                <div className="admin-participations-subtabs">
                                  <button
                                    className={`admin-participations-subtab ${activeParticipationsTab === 'speakers' ? 'active' : ''}`}
                                    onClick={() => setActiveParticipationsTab('speakers')}
                                  >
                                    <MicIcon width={16} height={16} /> Oradores
                                    {(() => {
                                      const getSpeakerStatus = (speaker: any) => {
                                        if (!speaker) return { completionStatus: 'incomplete' as const, hasRequestApproval: false };
                                        const hasPhoto = speaker.photo && (typeof speaker.photo === 'string' ? speaker.photo.trim() !== '' : true);
                                        const hasFirstName = speaker.firstName && speaker.firstName.toString().trim() !== '';
                                        const hasLastName = speaker.lastName && speaker.lastName.toString().trim() !== '';
                                        const hasNationality = speaker.nationality && speaker.nationality.toString().trim() !== '';
                                        const hasOrganization = speaker.organization && speaker.organization.toString().trim() !== '';
                                        const hasPosition = speaker.position && speaker.position.toString().trim() !== '';
                                        const hasDescription = speaker.description && speaker.description.toString().trim() !== '';
                                        const hasThematicAxes = speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0;
                                        const hasContentDescription = speaker.contentDescription && speaker.contentDescription.toString().trim() !== '';
                                        const hasLinkedin = speaker.linkedin && speaker.linkedin.toString().trim() !== '';
                                        const hasBiography = speaker.biography && speaker.biography.toString().trim() !== '';
                                        const hasValidBiography = hasLinkedin || hasBiography;
                                        const isComplete = hasPhoto && hasFirstName && hasLastName && hasNationality && hasOrganization && 
                                                           hasPosition && hasDescription && hasThematicAxes && hasContentDescription && hasValidBiography;
                                        return {
                                          completionStatus: isComplete ? 'complete' as const : 'incomplete' as const,
                                          hasRequestApproval: speaker.requestApproval === true
                                        };
                                      };
                                      const pending = selectedCrmUser.speakers && Array.isArray(selectedCrmUser.speakers) 
                                        ? selectedCrmUser.speakers.filter((speaker: any) => {
                                            const status = getSpeakerStatus(speaker);
                                            return (status.completionStatus === 'complete' || status.hasRequestApproval) &&
                                                   speaker.approval_status !== 'approved' && speaker.approval_status !== 'rejected';
                                          }).length
                                        : 0;
                                      return pending > 0 ? <span className="admin-tab-badge">{pending}</span> : null;
                                    })()}
                                  </button>
                                  <button
                                    className={`admin-participations-subtab ${activeParticipationsTab === 'events' ? 'active' : ''}`}
                                    onClick={() => setActiveParticipationsTab('events')}
                                  >
                                    <CalendarIcon width={16} height={16} /> Eventos Paralelos
                                    {(() => {
                                      const getEventStatus = (event: any) => {
                                        if (!event) return { completionStatus: 'incomplete' as const, hasRequestApproval: false };
                                        const hasName = event.name && event.name.toString().trim() !== '';
                                        const hasDescription = event.description && event.description.toString().trim() !== '';
                                        const hasSpeakers = (event.speakerIds && event.speakerIds.length > 0) || 
                                                          (event.localSpeakers && event.localSpeakers.length > 0);
                                        const hasDuration = event.duration && event.duration.toString().trim() !== '';
                                        const hasThematicAxes = event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0;
                                        const hasAttendeeProfiles = event.attendeeProfiles && Array.isArray(event.attendeeProfiles) && event.attendeeProfiles.length > 0;
                                        const isComplete = hasName && hasDescription && hasSpeakers && hasDuration && hasThematicAxes && hasAttendeeProfiles;
                                        return {
                                          completionStatus: isComplete ? 'complete' as const : 'incomplete' as const,
                                          hasRequestApproval: event.requestApproval === true
                                        };
                                      };
                                      const pending = selectedCrmUser.parallel_events && Array.isArray(selectedCrmUser.parallel_events)
                                        ? selectedCrmUser.parallel_events.filter((event: any) => {
                                            const status = getEventStatus(event);
                                            return (status.completionStatus === 'complete' || status.hasRequestApproval) &&
                                                   event.approval_status !== 'approved' && event.approval_status !== 'rejected';
                                          }).length
                                        : 0;
                                      return pending > 0 ? <span className="admin-tab-badge">{pending}</span> : null;
                                    })()}
                                  </button>
                                  <button
                                    className={`admin-participations-subtab ${activeParticipationsTab === 'initiatives' ? 'active' : ''}`}
                                    onClick={() => setActiveParticipationsTab('initiatives')}
                                  >
                                    <BookIcon width={16} height={16} /> Iniciativas
                                    {(() => {
                                      const getInitiativeStatus = (initiative: any) => {
                                        if (!initiative) return { completionStatus: 'incomplete' as const, hasRequestApproval: false };
                                        const hasName = initiative.name && initiative.name.toString().trim() !== '';
                                        const hasShortDescription = initiative.shortDescription && initiative.shortDescription.toString().trim() !== '';
                                        const hasDescription = initiative.description && initiative.description.toString().trim() !== '';
                                        const hasThematicAxes = initiative.thematicAxes && Array.isArray(initiative.thematicAxes) && initiative.thematicAxes.length > 0;
                                        let isComplete = hasName && hasShortDescription && hasDescription && hasThematicAxes;
                                        if (!initiative.noContact) {
                                          const hasContactFirstName = initiative.contactFirstName && initiative.contactFirstName.trim() !== '';
                                          const hasContactLastName = initiative.contactLastName && initiative.contactLastName.trim() !== '';
                                          const hasContactPosition = initiative.contactPosition && initiative.contactPosition.trim() !== '';
                                          const hasContactEmail = initiative.contactEmail && initiative.contactEmail.trim() !== '';
                                          isComplete = isComplete && hasContactFirstName && hasContactLastName && hasContactPosition && hasContactEmail;
                                        }
                                        return {
                                          completionStatus: isComplete ? 'complete' as const : 'incomplete' as const,
                                          hasRequestApproval: initiative.requestApproval === true
                                        };
                                      };
                                      const pending = selectedCrmUser.initiatives && Array.isArray(selectedCrmUser.initiatives)
                                        ? selectedCrmUser.initiatives.filter((init: any) => {
                                            const status = getInitiativeStatus(init);
                                            return (status.completionStatus === 'complete' || status.hasRequestApproval) &&
                                                   init.approval_status !== 'approved' && init.approval_status !== 'rejected';
                                          }).length
                                        : 0;
                                      return pending > 0 ? <span className="admin-tab-badge">{pending}</span> : null;
                                    })()}
                                  </button>
                                  <button
                                    className={`admin-participations-subtab ${activeParticipationsTab === 'business_rounds' ? 'active' : ''}`}
                                    onClick={() => setActiveParticipationsTab('business_rounds')}
                                  >
                                    <UsersIcon width={16} height={16} /> Rondas de Negocios
                                    {(() => {
                                      const pending = selectedCrmUser.business_rounds_participants && Array.isArray(selectedCrmUser.business_rounds_participants)
                                        ? selectedCrmUser.business_rounds_participants.filter((participant: any) => {
                                            const hasFirstName = participant.firstName && participant.firstName.trim() !== '';
                                            const hasLastName = participant.lastName && participant.lastName.trim() !== '';
                                            const hasPosition = participant.position && participant.position.trim() !== '';
                                            const hasOrganization = participant.organization && participant.organization.trim() !== '';
                                            const hasEmail = participant.email && participant.email.trim() !== '';
                                            const hasPhone = participant.phone && participant.phone.trim() !== '' || (participant.numeroTelefono && participant.numeroTelefono.trim() !== '');
                                            const hasThematicAxes = participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0;
                                            const isComplete = hasFirstName && hasLastName && hasPosition && hasOrganization && hasEmail && hasPhone && hasThematicAxes;
                                            return isComplete && participant.approval_status !== 'approved' && participant.approval_status !== 'rejected';
                                          }).length
                                        : 0;
                                      return pending > 0 ? <span className="admin-tab-badge">{pending}</span> : null;
                                    })()}
                                  </button>
                                </div>

                                {/* Contenido de las sub-pestañas - Reutilizar la misma lógica pero solo del usuario seleccionado */}
                                {(() => {
                                  // Usar solo el usuario seleccionado en lugar de filteredUsersForParticipations
                                  const singleUser = selectedCrmUser;
                                  
                                  // Funciones helper (las mismas que en la pestaña general)
                                  const getInitiativeTypeLabel = (type: string | undefined): string => {
                                    if (!type) return '';
                                    const typeMap: Record<string, string> = {
                                      'solution': 'Solución',
                                      'challenge': 'Desafío',
                                      'program': 'Programa de Impacto',
                                      'policy': 'Política Pública'
                                    };
                                    return typeMap[type] || type;
                                  };

                                  const getThematicAxisLabel = (value: string | { value?: string; label?: string }): string => {
                                    if (typeof value === 'object' && value !== null) {
                                      if (value.label) return value.label;
                                      if (value.value) {
                                        const axis = thematicAxes.find(t => t.value === value.value);
                                        return axis?.label || value.value;
                                      }
                                      return String(value);
                                    }
                                    const axis = thematicAxes.find(t => t.value === value);
                                    return axis?.label || value;
                                  };

                                  // Función helper para aplicar todos los filtros de participación (vista individual)
                                  const matchesParticipationFilters = (participation: any, statusInfo: { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } | string) => {
                                    // Si statusInfo es string (business rounds), convertir a objeto
                                    const completionStatus = typeof statusInfo === 'string' ? statusInfo : statusInfo.completionStatus;
                                    const hasRequestApproval = typeof statusInfo === 'string' ? false : statusInfo.hasRequestApproval;
                                    
                                    // Filtro de completitud
                                    if (filterParticipationCompletion !== 'all' && completionStatus !== filterParticipationCompletion) return false;
                                    
                                    // Filtro de solicitado
                                    if (filterParticipationRequested !== 'all') {
                                      const isRequested = participation.requestApproval === true;
                                      if (filterParticipationRequested === 'yes' && !isRequested) return false;
                                      if (filterParticipationRequested === 'no' && isRequested) return false;
                                    }
                                    
                                    // Filtro de aprobación
                                    if (filterParticipationApproval !== 'all') {
                                      const approvalStatus = participation.approval_status;
                                      if (filterParticipationApproval === 'pending' && (approvalStatus === 'approved' || approvalStatus === 'rejected')) return false;
                                      if (filterParticipationApproval === 'approved' && approvalStatus !== 'approved') return false;
                                      if (filterParticipationApproval === 'rejected' && approvalStatus !== 'rejected') return false;
                                    }
                                    
                                    // Mantener compatibilidad con el filtro antiguo
                                    if (filterParticipationStatus !== 'all') {
                                      if (filterParticipationStatus === 'pending_approval' && !hasRequestApproval) return false;
                                      if (filterParticipationStatus !== 'pending_approval' && completionStatus !== filterParticipationStatus) return false;
                                    }
                                    
                                    return true;
                                  };

                                  const getSpeakerStatus = (speaker: any): { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } => {
                                    if (!speaker) return { completionStatus: 'incomplete', hasRequestApproval: false };
                                    const hasPhoto = speaker.photo && (typeof speaker.photo === 'string' ? speaker.photo.trim() !== '' : true);
                                    const hasFirstName = speaker.firstName && speaker.firstName.toString().trim() !== '';
                                    const hasLastName = speaker.lastName && speaker.lastName.toString().trim() !== '';
                                    const hasNationality = speaker.nationality && speaker.nationality.toString().trim() !== '';
                                    const hasOrganization = speaker.organization && speaker.organization.toString().trim() !== '';
                                    const hasPosition = speaker.position && speaker.position.toString().trim() !== '';
                                    const hasDescription = speaker.description && speaker.description.toString().trim() !== '';
                                    const hasThematicAxes = speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0;
                                    const hasContentDescription = speaker.contentDescription && speaker.contentDescription.toString().trim() !== '';
                                    const hasLinkedin = speaker.linkedin && speaker.linkedin.toString().trim() !== '';
                                    const hasBiography = speaker.biography && speaker.biography.toString().trim() !== '';
                                    const hasValidBiography = hasLinkedin || hasBiography;
                                    const isComplete = hasPhoto && hasFirstName && hasLastName && hasNationality && hasOrganization && 
                                                       hasPosition && hasDescription && hasThematicAxes && hasContentDescription && hasValidBiography;
                                    return {
                                      completionStatus: isComplete ? 'complete' : 'incomplete',
                                      hasRequestApproval: speaker.requestApproval === true
                                    };
                                  };

                                  const getEventStatus = (event: any): { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } => {
                                    if (!event) return { completionStatus: 'incomplete', hasRequestApproval: false };
                                    const hasName = event.name && event.name.toString().trim() !== '';
                                    const hasDescription = event.description && event.description.toString().trim() !== '';
                                    const hasSpeakers = (event.speakerIds && event.speakerIds.length > 0) || 
                                                      (event.localSpeakers && event.localSpeakers.length > 0);
                                    const hasDuration = event.duration && event.duration.toString().trim() !== '';
                                    const hasThematicAxes = event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0;
                                    const hasAttendeeProfiles = event.attendeeProfiles && Array.isArray(event.attendeeProfiles) && event.attendeeProfiles.length > 0;
                                    const isComplete = hasName && hasDescription && hasSpeakers && hasDuration && hasThematicAxes && hasAttendeeProfiles;
                                    return {
                                      completionStatus: isComplete ? 'complete' : 'incomplete',
                                      hasRequestApproval: event.requestApproval === true
                                    };
                                  };

                                  const getInitiativeStatus = (initiative: any): { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } => {
                                    if (!initiative) return { completionStatus: 'incomplete', hasRequestApproval: false };
                                    const hasName = initiative.name && initiative.name.toString().trim() !== '';
                                    const hasShortDescription = initiative.shortDescription && initiative.shortDescription.toString().trim() !== '';
                                    const hasDescription = initiative.description && initiative.description.toString().trim() !== '';
                                    const hasThematicAxes = initiative.thematicAxes && Array.isArray(initiative.thematicAxes) && initiative.thematicAxes.length > 0;
                                    let isComplete = hasName && hasShortDescription && hasDescription && hasThematicAxes;
                                    if (!initiative.noContact) {
                                      const hasContactFirstName = initiative.contactFirstName && initiative.contactFirstName.trim() !== '';
                                      const hasContactLastName = initiative.contactLastName && initiative.contactLastName.trim() !== '';
                                      const hasContactPosition = initiative.contactPosition && initiative.contactPosition.trim() !== '';
                                      const hasContactEmail = initiative.contactEmail && initiative.contactEmail.trim() !== '';
                                      isComplete = isComplete && hasContactFirstName && hasContactLastName && hasContactPosition && hasContactEmail;
                                    }
                                    return {
                                      completionStatus: isComplete ? 'complete' : 'incomplete',
                                      hasRequestApproval: initiative.requestApproval === true
                                    };
                                  };

                                  const getBusinessRoundsStatus = (participant: any): 'complete' | 'incomplete' => {
                                    if (!participant) return 'incomplete';
                                    const hasFirstName = participant.firstName && participant.firstName.trim() !== '';
                                    const hasLastName = participant.lastName && participant.lastName.trim() !== '';
                                    const hasPosition = participant.position && participant.position.trim() !== '';
                                    const hasOrganization = participant.organization && participant.organization.trim() !== '';
                                    const hasEmail = participant.email && participant.email.trim() !== '';
                                    const hasPhone = participant.phone && participant.phone.trim() !== '' || (participant.numeroTelefono && participant.numeroTelefono.trim() !== '');
                                    const hasThematicAxes = participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0;
                                    const isComplete = hasFirstName && hasLastName && hasPosition && hasOrganization && hasEmail && hasPhone && hasThematicAxes;
                                    if (isComplete) return 'complete';
                                    return 'incomplete';
                                  };

                                  if (activeParticipationsTab === 'speakers') {
                                    return (
                                      <div className="admin-participations-grid">
                                        {singleUser.speakers && Array.isArray(singleUser.speakers) && singleUser.speakers.length > 0 ? (
                                          singleUser.speakers.map((speaker: any, idx: number) => {
                                            const statusInfo = getSpeakerStatus(speaker);
                                            // Aplicar todos los filtros de participación
                                            if (!matchesParticipationFilters(speaker, statusInfo)) return null;
                                            return (
                                              <div 
                                                key={`${singleUser.id}-speaker-${idx}`} 
                                                className="admin-participation-card"
                                                data-user-id={singleUser.id}
                                                data-participation-id={`speaker-${singleUser.id}-${idx}`}
                                                onClick={() => setSelectedSpeakerForModal({ speaker, user: singleUser })}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                {/* Badges de estado en el borde superior derecho */}
                                                <div className="admin-participation-card-status-badges">
                                                  <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
                                                    {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                                                    {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                                                  </span>
                                                  {statusInfo.hasRequestApproval && (
                                                    <span className="participation-status-badge status-requested">
                                                      <AlertTriangleIcon width={12} height={12} /> Solicitado
                                                    </span>
                                                  )}
                                                  {speaker.approval_status === 'approved' ? (
                                                    <span className="participation-status-badge status-approved">
                                                      <CheckCircleIcon width={12} height={12} /> Aprobado
                                                    </span>
                                                  ) : speaker.approval_status === 'rejected' ? (
                                                    <span className="participation-status-badge status-rejected">
                                                      <XCircleIcon width={12} height={12} /> Rechazado
                                                    </span>
                                                  ) : (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) ? (
                                                    <span className="participation-status-badge status-pending">
                                                      <AlertTriangleIcon width={12} height={12} /> Pendiente
                                                    </span>
                                                  ) : null}
                                                </div>
                                                <div className="admin-participation-card-header">
                                                  <div>
                                                    <h3>{speaker.firstName} {speaker.lastName}</h3>
                                                    <span className="admin-participation-org">{getUserDisplayName(singleUser)}</span>
                                                  </div>
                                                </div>
                                                <div className="admin-participation-card-body">
                                                  {speaker.position && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Cargo:</span>
                                                      <span className="info-value">{speaker.position}</span>
                                                    </div>
                                                  )}
                                                  {speaker.organization && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Organización:</span>
                                                      <span className="info-value">{speaker.organization}</span>
                                                    </div>
                                                  )}
                                                  {speaker.nationality && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Nacionalidad:</span>
                                                      <span className="info-value">{speaker.nationality}</span>
                                                    </div>
                                                  )}
                                                  {speaker.description && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Minibiografía:</span>
                                                      <span className="info-value">{speaker.description}</span>
                                                    </div>
                                                  )}
                                                  {speaker.biography && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Biografía:</span>
                                                      <span className="info-value">{speaker.biography}</span>
                                                    </div>
                                                  )}
                                                  {speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0 && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Ejes temáticos:</span>
                                                      <div className="admin-participation-tags">
                                                        {speaker.thematicAxes.map((axis: any, axisIdx: number) => (
                                                          <span key={axisIdx} className="tag-badge-small">{getThematicAxisLabel(axis)}</span>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="admin-participation-card-footer">
                                                  <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                    {/* Botones de aprobación/rechazo - solo si está completo o tiene solicitud de aprobación */}
                                                    {(statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (
                                                      <>
                                                        {speaker.approval_status !== 'approved' && (
                                                          <button
                                                            className="admin-action-button-small approve-button"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              updateParticipationApprovalStatus(singleUser.id, 'speakers', speaker.id || idx, 'approved');
                                                            }}
                                                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                          >
                                                            Aprobar
                                                          </button>
                                                        )}
                                                        {speaker.approval_status !== 'rejected' && (
                                                          <button
                                                            className="admin-action-button-small reject-button"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              setRejectionModal({
                                                                source: 'profile',
                                                                isOpen: true,
                                                                userId: singleUser.id,
                                                                participationType: 'speakers',
                                                                participationId: speaker.id || idx
                                                              });
                                                              setRejectionComment('');
                                                            }}
                                                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                          >
                                                            Rechazar
                                                          </button>
                                                        )}
                                                        {(speaker.approval_status === 'approved' || speaker.approval_status === 'rejected') && (
                                                          <button
                                                            className="admin-action-button-small pending-button"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              updateParticipationApprovalStatus(singleUser.id, 'speakers', speaker.id || idx, 'pending');
                                                            }}
                                                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                          >
                                                            Pendiente
                                                          </button>
                                                        )}
                                                      </>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          }).filter(Boolean)
                                        ) : (
                                          <div className="admin-empty-state">
                                            <p>No hay oradores cargados</p>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }

                                  if (activeParticipationsTab === 'events') {
                                    return (
                                      <div className="admin-participations-grid">
                                        {singleUser.parallel_events && Array.isArray(singleUser.parallel_events) && singleUser.parallel_events.length > 0 ? (
                                          singleUser.parallel_events.map((event: any, idx: number) => {
                                            const statusInfo = getEventStatus(event);
                                            // Aplicar todos los filtros de participación
                                            if (!matchesParticipationFilters(event, statusInfo)) return null;
                                            return (
                                              <div 
                                                key={`${singleUser.id}-event-${idx}`} 
                                                className="admin-participation-card"
                                                data-user-id={singleUser.id}
                                                data-participation-id={`event-${singleUser.id}-${idx}`}
                                                onClick={() => setSelectedEventForModal({ event, user: singleUser })}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                <div className="admin-participation-card-status-badges">
                                                  <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
                                                    {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                                                    {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                                                  </span>
                                                  {statusInfo.hasRequestApproval && (
                                                    <span className="participation-status-badge status-requested">
                                                      <AlertTriangleIcon width={12} height={12} /> Solicitado
                                                    </span>
                                                  )}
                                                  {event.approval_status === 'approved' ? (
                                                    <span className="participation-status-badge status-approved">
                                                      <CheckCircleIcon width={12} height={12} /> Aprobado
                                                    </span>
                                                  ) : event.approval_status === 'rejected' ? (
                                                    <span className="participation-status-badge status-rejected">
                                                      <XCircleIcon width={12} height={12} /> Rechazado
                                                    </span>
                                                  ) : (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) ? (
                                                    <span className="participation-status-badge status-pending">
                                                      <AlertTriangleIcon width={12} height={12} /> Pendiente
                                                    </span>
                                                  ) : null}
                                                </div>
                                                <div className="admin-participation-card-header">
                                                  <div>
                                                    <h3>{event.name || 'Evento sin nombre'}</h3>
                                                    <span className="admin-participation-org">{getUserDisplayName(singleUser)}</span>
                                                  </div>
                                                </div>
                                                <div className="admin-participation-card-body">
                                                  {event.description && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Descripción:</span>
                                                      <span className="info-value">{event.description}</span>
                                                    </div>
                                                  )}
                                                  {event.duration && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Duración:</span>
                                                      <span className="info-value">{event.duration}</span>
                                                    </div>
                                                  )}
                                                  {event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0 && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Ejes temáticos:</span>
                                                      <div className="admin-participation-tags">
                                                        {event.thematicAxes.map((axis: any, axisIdx: number) => (
                                                          <span key={axisIdx} className="tag-badge-small">{getThematicAxisLabel(axis)}</span>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="admin-participation-card-footer">
                                                  <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                    {(statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (
                                                      <>
                                                        {event.approval_status !== 'approved' && (
                                                          <button
                                                            className="admin-action-button-small approve-button"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              updateParticipationApprovalStatus(singleUser.id, 'parallelEvents', event.id || idx, 'approved');
                                                            }}
                                                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                          >
                                                            Aprobar
                                                          </button>
                                                        )}
                                                        {event.approval_status !== 'rejected' && (
                                                          <button
                                                            className="admin-action-button-small reject-button"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              setRejectionModal({
                                                                source: 'profile',
                                                                isOpen: true,
                                                                userId: singleUser.id,
                                                                participationType: 'parallelEvents',
                                                                participationId: event.id || idx
                                                              });
                                                              setRejectionComment('');
                                                            }}
                                                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                          >
                                                            Rechazar
                                                          </button>
                                                        )}
                                                        {(event.approval_status === 'approved' || event.approval_status === 'rejected') && (
                                                          <button
                                                            className="admin-action-button-small pending-button"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              updateParticipationApprovalStatus(singleUser.id, 'parallelEvents', event.id || idx, 'pending');
                                                            }}
                                                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                          >
                                                            Pendiente
                                                          </button>
                                                        )}
                                                      </>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          }).filter(Boolean)
                                        ) : (
                                          <div className="admin-empty-state">
                                            <p>No hay eventos paralelos cargados</p>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }

                                  if (activeParticipationsTab === 'initiatives') {
                                    return (
                                      <div className="admin-participations-grid">
                                        {singleUser.initiatives && Array.isArray(singleUser.initiatives) && singleUser.initiatives.length > 0 ? (
                                          singleUser.initiatives.map((initiative: any, idx: number) => {
                                            const statusInfo = getInitiativeStatus(initiative);
                                            // Aplicar todos los filtros de participación
                                            if (!matchesParticipationFilters(initiative, statusInfo)) return null;
                                            return (
                                              <div 
                                                key={`${singleUser.id}-initiative-${idx}`} 
                                                className="admin-participation-card"
                                                data-user-id={singleUser.id}
                                                data-participation-id={`initiative-${singleUser.id}-${idx}`}
                                                onClick={() => setSelectedInitiativeForModal({ initiative, user: singleUser })}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                <div className="admin-participation-card-status-badges">
                                                  <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
                                                    {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                                                    {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                                                  </span>
                                                  {statusInfo.hasRequestApproval && (
                                                    <span className="participation-status-badge status-requested">
                                                      <AlertTriangleIcon width={12} height={12} /> Solicitado
                                                    </span>
                                                  )}
                                                  {initiative.approval_status === 'approved' ? (
                                                    <span className="participation-status-badge status-approved">
                                                      <CheckCircleIcon width={12} height={12} /> Aprobado
                                                    </span>
                                                  ) : initiative.approval_status === 'rejected' ? (
                                                    <span className="participation-status-badge status-rejected">
                                                      <XCircleIcon width={12} height={12} /> Rechazado
                                                    </span>
                                                  ) : (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) ? (
                                                    <span className="participation-status-badge status-pending">
                                                      <AlertTriangleIcon width={12} height={12} /> Pendiente
                                                    </span>
                                                  ) : null}
                                                </div>
                                                <div className="admin-participation-card-header">
                                                  <div>
                                                    <h3>{initiative.title || initiative.name || 'Iniciativa sin título'}</h3>
                                                    <span className="admin-participation-org">{getUserDisplayName(singleUser)}</span>
                                                  </div>
                                                </div>
                                                <div className="admin-participation-card-body">
                                                  {initiative.type && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Tipo:</span>
                                                      <span className="info-value">{getInitiativeTypeLabel(initiative.type)}</span>
                                                    </div>
                                                  )}
                                                  {initiative.description && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Descripción:</span>
                                                      <span className="info-value">{initiative.description}</span>
                                                    </div>
                                                  )}
                                                  {initiative.thematicAxes && Array.isArray(initiative.thematicAxes) && initiative.thematicAxes.length > 0 && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Ejes temáticos:</span>
                                                      <div className="admin-participation-tags">
                                                        {initiative.thematicAxes.map((axis: any, axisIdx: number) => (
                                                          <span key={axisIdx} className="tag-badge-small">{getThematicAxisLabel(axis)}</span>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="admin-participation-card-footer">
                                                  <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                    {(statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (
                                                      <>
                                                        {initiative.approval_status !== 'approved' && (
                                                          <button
                                                            className="admin-action-button-small approve-button"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              updateParticipationApprovalStatus(singleUser.id, 'initiatives', initiative.id || idx, 'approved');
                                                            }}
                                                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                          >
                                                            Aprobar
                                                          </button>
                                                        )}
                                                        {initiative.approval_status !== 'rejected' && (
                                                          <button
                                                            className="admin-action-button-small reject-button"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              setRejectionModal({
                                                                source: 'profile',
                                                                isOpen: true,
                                                                userId: singleUser.id,
                                                                participationType: 'initiatives',
                                                                participationId: initiative.id || idx
                                                              });
                                                              setRejectionComment('');
                                                            }}
                                                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                          >
                                                            Rechazar
                                                          </button>
                                                        )}
                                                        {(initiative.approval_status === 'approved' || initiative.approval_status === 'rejected') && (
                                                          <button
                                                            className="admin-action-button-small pending-button"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              updateParticipationApprovalStatus(singleUser.id, 'initiatives', initiative.id || idx, 'pending');
                                                            }}
                                                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                          >
                                                            Pendiente
                                                          </button>
                                                        )}
                                                      </>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          }).filter(Boolean)
                                        ) : (
                                          <div className="admin-empty-state">
                                            <p>No hay iniciativas cargadas</p>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }

                                  if (activeParticipationsTab === 'business_rounds') {
                                    return (
                                      <div className="admin-participations-grid">
                                        {singleUser.business_rounds_participants && Array.isArray(singleUser.business_rounds_participants) && singleUser.business_rounds_participants.length > 0 ? (
                                          singleUser.business_rounds_participants.map((participant: any, idx: number) => {
                                            const status = getBusinessRoundsStatus(participant);
                                            // Aplicar todos los filtros de participación
                                            if (!matchesParticipationFilters(participant, status)) return null;
                                            return (
                                              <div 
                                                key={`${singleUser.id}-participant-${idx}`} 
                                                className="admin-participation-card"
                                                data-user-id={singleUser.id}
                                                data-participation-id={`participant-${singleUser.id}-${idx}`}
                                                onClick={() => setSelectedParticipantForModal({ participant, user: singleUser })}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                <div className="admin-participation-card-status-badges">
                                                  <span className={`participation-status-badge status-${status}`}>
                                                    {status === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                                                    {status === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                                                  </span>
                                                  {participant.approval_status === 'approved' ? (
                                                    <span className="participation-status-badge status-approved">
                                                      <CheckCircleIcon width={12} height={12} /> Aprobado
                                                    </span>
                                                  ) : participant.approval_status === 'rejected' ? (
                                                    <span className="participation-status-badge status-rejected">
                                                      <XCircleIcon width={12} height={12} /> Rechazado
                                                    </span>
                                                  ) : status === 'complete' ? (
                                                    <span className="participation-status-badge status-pending">
                                                      <AlertTriangleIcon width={12} height={12} /> Pendiente
                                                    </span>
                                                  ) : null}
                                                </div>
                                                <div className="admin-participation-card-header">
                                                  <div>
                                                    <h3>{participant.firstName} {participant.lastName}</h3>
                                                    <span className="admin-participation-org">{getUserDisplayName(singleUser)}</span>
                                                  </div>
                                                </div>
                                                <div className="admin-participation-card-body">
                                                  {participant.position && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Cargo:</span>
                                                      <span className="info-value">{participant.position}</span>
                                                    </div>
                                                  )}
                                                  {participant.organization && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Organización:</span>
                                                      <span className="info-value">{participant.organization}</span>
                                                    </div>
                                                  )}
                                                  {participant.email && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Email:</span>
                                                      <span className="info-value">{participant.email}</span>
                                                    </div>
                                                  )}
                                                  {participant.phone && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Teléfono:</span>
                                                      <span className="info-value">{participant.phone}</span>
                                                    </div>
                                                  )}
                                                  {participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0 && (
                                                    <div className="admin-participation-info-item">
                                                      <span className="info-label">Ejes temáticos:</span>
                                                      <div className="admin-participation-tags">
                                                        {participant.thematicAxes.map((axis: any, axisIdx: number) => (
                                                          <span key={axisIdx} className="tag-badge-small">{getThematicAxisLabel(axis)}</span>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="admin-participation-card-footer">
                                                  <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                    {status === 'complete' && (
                                                      <>
                                                        {participant.approval_status !== 'approved' && (
                                                          <button
                                                            className="admin-action-button-small approve-button"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              updateParticipationApprovalStatus(singleUser.id, 'businessRoundsParticipants', participant.id || idx, 'approved');
                                                            }}
                                                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                          >
                                                            Aprobar
                                                          </button>
                                                        )}
                                                        {participant.approval_status !== 'rejected' && (
                                                          <button
                                                            className="admin-action-button-small reject-button"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              setRejectionModal({
                                                                source: 'profile',
                                                                isOpen: true,
                                                                userId: singleUser.id,
                                                                participationType: 'businessRoundsParticipants',
                                                                participationId: participant.id || idx
                                                              });
                                                              setRejectionComment('');
                                                            }}
                                                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                          >
                                                            Rechazar
                                                          </button>
                                                        )}
                                                        {(participant.approval_status === 'approved' || participant.approval_status === 'rejected') && (
                                                          <button
                                                            className="admin-action-button-small pending-button"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              updateParticipationApprovalStatus(singleUser.id, 'businessRoundsParticipants', participant.id || idx, 'pending');
                                                            }}
                                                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                          >
                                                            Pendiente
                                                          </button>
                                                        )}
                                                      </>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          }).filter(Boolean)
                                        ) : (
                                          <div className="admin-empty-state">
                                            <p>No hay participantes de rondas de negocios cargados</p>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }

                                  return null;
                                })()}

                                {/* Historial de mensajería: Correo | WhatsApp | Mensajería */}
                                <div className="admin-crm-section-card" style={{ marginTop: '24px' }}>
                                  <h3><MessageCircleIcon width={18} height={18} /> Historial de Mensajería</h3>
                                  <div className="admin-conversations-subtabs" style={{ marginTop: 12, marginBottom: 12 }}>
                                    <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'correo' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('correo')}>
                                      <EnvelopeIcon width={18} height={18} /> Correo
                                    </button>
                                    <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'whatsapp' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('whatsapp')}>
                                      <WhatsAppIcon width={18} height={18} /> WhatsApp
                                    </button>
                                    <button type="button" className={`admin-conv-subtab ${activeHistorialSubTab === 'messaging' ? 'active' : ''}`} onClick={() => setActiveHistorialSubTab('messaging')}>
                                      <MessageCircleIcon width={18} height={18} /> Mensajería
                                    </button>
                                  </div>
                                  <div>
                                    {activeHistorialSubTab === 'correo' && (
                                      <GmailSection isDarkMode={isDarkMode} filterByEmail={selectedCrmUser.email} />
                                    )}
                                    {activeHistorialSubTab === 'whatsapp' && (
                                      <HistorialWhatsAppTab
                                        userId={selectedCrmUser.id}
                                        whatsapp={selectedCrmUser.whatsapp ?? null}
                                        onSaveWhatsapp={(phone) => {
                                          setUsers(prev => prev.map(u => u.id === selectedCrmUser.id ? { ...u, whatsapp: phone } : u));
                                          setSelectedCrmUser(prev => prev ? { ...prev, whatsapp: phone } : null);
                                        }}
                                        getApiUrl={getApiUrl}
                                        isDarkMode={isDarkMode}
                                      />
                                    )}
                                    {activeHistorialSubTab === 'messaging' && (() => {
                                      const userConversation = conversations.find(c => c.userId === selectedCrmUser.id);
                                      if (!userConversation) {
                                        return (
                                          <div className="admin-crm-empty-state">
                                            <p>No hay mensajes con este contacto</p>
                                            <button
                                              className="admin-action-button-small chat-button"
                                              onClick={() => {
                                                sessionStorage.setItem('chatsPage_asUserId', selectedCrmUser.id.toString());
                                                window.open(`${window.location.origin}/soporte?asUserId=${selectedCrmUser.id}`, '_blank');
                                              }}
                                            >
                                              Iniciar Conversación
                                            </button>
                                          </div>
                                        );
                                      }
                                      const lastDate = userConversation.lastMessageDate ? new Date(userConversation.lastMessageDate) : null;
                                      const lastMessageIsFromAdmin = currentUserId && userConversation.lastMessageFromUserId && parseInt(String(userConversation.lastMessageFromUserId)) === currentUserId;
                                      const daysSince = lastDate && lastMessageIsFromAdmin ? Math.floor((Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
                                      return (
                                        <div className="admin-crm-messaging-info">
                                          <div className="admin-crm-info-row">
                                            <span className="info-label">Último mensaje:</span>
                                            <span>{userConversation.lastMessage || 'Sin mensajes'}</span>
                                          </div>
                                          <div className="admin-crm-info-row">
                                            <span className="info-label">Fecha:</span>
                                            <span>{lastDate ? formatDate(userConversation.lastMessageDate) : 'Nunca'}</span>
                                          </div>
                                          <div className="admin-crm-info-row">
                                            <span className="info-label">Mensajes no leídos:</span>
                                            <span className={userConversation.unreadCount > 0 ? 'status-badge-small request-badge' : ''}>{userConversation.unreadCount}</span>
                                          </div>
                                          <div className="admin-crm-info-row">
                                            <span className="info-label">Días sin respuesta:</span>
                                            {daysSince !== null && daysSince > 0 ? (
                                              <span className={daysSince > 7 ? 'status-badge-small request-badge' : ''}>{daysSince} día(s)</span>
                                            ) : (
                                              <span style={{ color: '#9ca3af' }}>—</span>
                                            )}
                                          </div>
                                          <div className="admin-crm-actions">
                                            <button
                                              className="admin-action-button-small chat-button"
                                              onClick={() => {
                                                sessionStorage.setItem('chatsPage_asUserId', selectedCrmUser.id.toString());
                                                window.open(`${window.location.origin}/soporte?asUserId=${selectedCrmUser.id}`, '_blank');
                                              }}
                                            >
                                              Abrir Chat
                                              {userConversation.unreadCount > 0 && <span className="chat-unread-badge-small">{userConversation.unreadCount}</span>}
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Columna derecha - Herramientas de gestión (siempre visible) */}
                          <div className="admin-crm-detail-sidebar">
                    {/* Etapa y Prioridad ya no se muestran por contacto (están por organización/negocio) */}

                    {/* Notas internas */}
                    <div className="admin-crm-section-card">
                      <h3><FileTextIcon width={18} height={18} /> Notas Internas</h3>
                      <textarea
                        className="admin-crm-notes-textarea"
                        placeholder="Agregar notas sobre este contacto..."
                        value={crmNotes[selectedCrmUser.id] || ''}
                        onChange={(e) => {
                          const newNotes = e.target.value;
                          setCrmNotes({
                            ...crmNotes,
                            [selectedCrmUser.id]: newNotes
                          });
                        }}
                        onBlur={(e) => {
                          // Guardar cuando se pierde el foco
                          saveCrmNotes(selectedCrmUser.id, e.target.value);
                        }}
                        rows={10}
                      />
                    </div>

                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="admin-crm-empty-state">
                      <p>No se pudo cargar el perfil completo</p>
                      <p style={{ fontSize: '14px', color: '#64748b', marginTop: '8px' }}>
                        El usuario puede no tener un perfil completo o hubo un error al cargarlo. Revisa la consola para más detalles.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sidebar derecho de filtros (estilo HubSpot) - CRM y Participaciones */}
        {(activeMainTab === 'crm' || activeMainTab === 'participations') && (
          <div
            className={`admin-filters-sidebar ${filtersSidebarOpen ? 'open' : 'closed'}`}
            aria-hidden={!filtersSidebarOpen}
          >
            <div className="admin-filters-sidebar-content">
              <div className="admin-filters-sidebar-header">
                <h3>Todos los filtros</h3>
                <button
                  type="button"
                  className="admin-filters-sidebar-close"
                  onClick={() => setFiltersSidebarOpen(false)}
                  aria-label="Cerrar filtros"
                >
                  <XIcon width={20} height={20} />
                </button>
              </div>
              <div className="admin-filters-sidebar-body">
                <p className="admin-filters-sidebar-subtitle">Filtros avanzados</p>
                {activeMainTab === 'crm' && (
                  <div className="admin-filters-sidebar-form">
                    <div className="admin-filter-group">
                      <label className="admin-filter-label">Tipo Organización:</label>
                      <MultiSelectTags
                        options={tiposOrganizacion}
                        selectedValues={filterOrganizationType}
                        onChange={setFilterOrganizationType}
                        placeholder="Todos"
                        allowSearch={false}
                      />
                    </div>
                    <button type="button" className="admin-action-button admin-filters-sidebar-clear" onClick={handleClearFilters}>
                      <XIcon width={16} height={16} /> Limpiar Filtros
                    </button>
                  </div>
                )}
                {activeMainTab === 'participations' && (
                  <div className="admin-filters-sidebar-form">
                    <div className="admin-filter-group">
                      <label className="admin-filter-label">Tipo Organización:</label>
                      <MultiSelectTags
                        options={tiposOrganizacion}
                        selectedValues={filterOrganizationType}
                        onChange={setFilterOrganizationType}
                        placeholder="Todos"
                        allowSearch={false}
                      />
                    </div>
                    <button type="button" className="admin-action-button admin-filters-sidebar-clear" onClick={handleClearParticipationsFilters}>
                      <XIcon width={16} height={16} /> Limpiar Filtros
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Perfil: subtabs Perfiles (contactos) | Participaciones */}
        {activeMainTab === 'participations' && (
          <div className="admin-participations-section admin-perfil-section">
            <div className="admin-participations-header">
              <h2 style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AwardIcon width={24} height={24} /> Perfil
                </span>
                <span style={{ fontSize: '20px', fontWeight: '600', color: '#64748b' }}>
                  ({activePerfilSubTab === 'perfil-personal'
                    ? dashboardMetrics.totalUsers
                    : activePerfilSubTab === 'perfil-institucional'
                      ? dashboardMetrics.totalWithInstitutionalProfile
                      : dashboardMetrics.totalWithParticipations} usuarios)
                </span>
              </h2>
              <p>
                {activePerfilSubTab === 'perfil-personal' ? 'Contactos y perfiles personales de usuarios' : activePerfilSubTab === 'perfil-institucional' ? 'Organizaciones de los perfiles institucionales' : 'Gestiona todas las participaciones cargadas por los usuarios'}
              </p>
            </div>

            {/* Subtabs: Perfil Personal | Perfil Institucional | Participaciones */}
            <div className="admin-conversations-subtabs admin-perfil-subtabs">
              <button
                type="button"
                className={`admin-conv-subtab ${activePerfilSubTab === 'perfil-personal' ? 'active' : ''}`}
                onClick={() => setActivePerfilSubTab('perfil-personal')}
              >
                <UsersIcon width={18} height={18} /> Perfil Personal
              </button>
              <button
                type="button"
                className={`admin-conv-subtab ${activePerfilSubTab === 'perfil-institucional' ? 'active' : ''}`}
                onClick={() => setActivePerfilSubTab('perfil-institucional')}
              >
                <BuildingIcon width={18} height={18} /> Perfil Institucional
              </button>
              <button
                type="button"
                className={`admin-conv-subtab ${activePerfilSubTab === 'participaciones' ? 'active' : ''}`}
                onClick={() => setActivePerfilSubTab('participaciones')}
              >
                <AwardIcon width={18} height={18} /> Participaciones
                {pendingParticipationsCount > 0 && (
                  <span className="admin-tab-badge">{pendingParticipationsCount}</span>
                )}
              </button>
            </div>

            {/* Contenido Perfil Personal = mismo que CRM Contactos (toolbar + lista/tarjetas/tabla) */}
            {activePerfilSubTab === 'perfil-personal' && (
              <>
                <div className="admin-crm-filters-bar admin-crm-filters-bar-compact" style={{ marginBottom: '24px' }}>
                  <div className="admin-crm-toolbar-row">
                    <input
                      type="text"
                      placeholder="Buscar por nombre, email, organización..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="admin-search-input admin-toolbar-control"
                    />
                    <button
                      type="button"
                      className={`admin-filters-trigger-btn admin-toolbar-control ${filtersSidebarOpen ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
                      onClick={() => setFiltersSidebarOpen((open) => !open)}
                      aria-label={filtersSidebarOpen ? 'Cerrar filtros' : 'Abrir filtros'}
                    >
                      <FilterIcon width={18} height={18} />
                      <span>Filtros</span>
                      {hasActiveFilters && <span className="admin-filters-trigger-badge" aria-hidden />}
                    </button>
                    <div className="admin-view-toggle">
                      <button
                        className={`admin-view-toggle-btn admin-toolbar-control ${listViewMode === 'cards' ? 'active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setListViewMode('cards'); setPerfilPersonalPage(1); }}
                        title="Vista de tarjetas"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="7" height="7"></rect>
                          <rect x="14" y="3" width="7" height="7"></rect>
                          <rect x="3" y="14" width="7" height="7"></rect>
                          <rect x="14" y="14" width="7" height="7"></rect>
                        </svg>
                      </button>
                      <button
                        className={`admin-view-toggle-btn admin-toolbar-control ${listViewMode === 'table' ? 'active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setListViewMode('table'); setPerfilPersonalPage(1); }}
                        title="Vista de tabla"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="3" y1="3" x2="21" y2="3"></line>
                          <line x1="3" y1="8" x2="21" y2="8"></line>
                          <line x1="3" y1="13" x2="21" y2="13"></line>
                          <line x1="3" y1="18" x2="21" y2="18"></line>
                          <line x1="3" y1="23" x2="21" y2="23"></line>
                        </svg>
                      </button>
                    </div>
                    <div className="admin-toolbar-actions">
                      <button type="button" className="admin-toolbar-control admin-toolbar-btn-export" onClick={handleExportToExcel}>Exportar</button>
                      <button type="button" className="admin-toolbar-control admin-toolbar-btn-create" onClick={() => setShowCreateContactModal(true)}>
                        <PlusIcon width={16} height={16} /> Crear contacto
                      </button>
                    </div>
                  </div>
                </div>
                <div className="admin-perfil-contactos-content">
                  {listViewMode === 'table' ? (
                    <div className="admin-crm-contacts-list">
                    {sortedUsers.length === 0 ? (
                      <div className="admin-empty-state">
                        <p>{searchTerm ? 'No se encontraron contactos' : 'No hay contactos para mostrar'}</p>
                      </div>
                    ) : (
                      <>
                      <div className="admin-users-table-container">
                        <table className="admin-users-table">
                          <thead>
                            <tr>
                              <th>Organización</th>
                              <th className="admin-table-fixed-width">Tipo</th>
                              <th className="admin-table-fixed-width">Modalidad</th>
                              <th className="admin-table-fixed-width">Zona Expo</th>
                              <th className="admin-table-fixed-width">Email</th>
                              <th>Contacto</th>
                              <th>Perfil Institucional</th>
                              <th>Participaciones</th>
                              <th>Acciones Pendientes</th>
                              <th>Último Acceso</th>
                              <th>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {perfilPersonalPaginated.map((user) => {
                              return (
                                <tr key={user.id} data-user-id={user.id}>
                                  <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                      <div 
                                        style={{ 
                                          fontWeight: '600', 
                                          color: '#1e293b',
                                          textDecoration: 'underline',
                                          cursor: 'pointer'
                                        }}
                                        onClick={() => handleOpenCrmDetail(user)}
                                      >
                                        {getUserDisplayName(user)}
                                      </div>
                                      {user.is_draft !== null && user.is_draft !== undefined && (
                                        user.is_draft === false ? (
                                          <span className="status-badge-small profile-authorized"><CheckCircleIcon width={12} height={12} /> Autorizado</span>
                                        ) : (
                                          <span className="status-badge-small profile-draft"><FileTextIcon width={12} height={12} /> Borrador</span>
                                        )
                                      )}
                                      <span className={`origin-badge-table ${user.created_by_admin ? 'origin-manual' : 'origin-registered'}`}>
                                        {user.created_by_admin ? 'Creado por admin' : 'Registrado'}
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    {user.organization_type ? (
                                      <span className="organization-type-badge-small">{getOrganizationTypeLabel(user.organization_type)}</span>
                                    ) : (
                                      <span style={{ color: '#9ca3af' }}>—</span>
                                    )}
                                  </td>
                                  <td>
                                    {(() => {
                                      if (user.agreed_modality) {
                                        const modalityOption = participationModalities.find(m => m.value === user.agreed_modality);
                                        const label = modalityOption ? modalityOption.label : user.agreed_modality;
                                        return <span style={{ fontSize: '12px', color: '#1e293b' }}>{label}</span>;
                                      }
                                      if (user.participation_modalities) {
                                        const mods = Array.isArray(user.participation_modalities) 
                                          ? user.participation_modalities 
                                          : (typeof user.participation_modalities === 'string' 
                                            ? (() => {
                                                try {
                                                  const parsed = JSON.parse(user.participation_modalities);
                                                  return Array.isArray(parsed) ? parsed : [parsed];
                                                } catch {
                                                  return [user.participation_modalities];
                                                }
                                              })()
                                            : []);
                                        if (mods.length > 0) {
                                          return (
                                            <span style={{ fontSize: '12px', color: '#1e293b' }}>
                                              {mods.map((mod: string) => {
                                                const modality = participationModalities.find(m => m.value === mod);
                                                return modality ? modality.label : mod;
                                              }).join(', ')}
                                            </span>
                                          );
                                        }
                                      }
                                      return <span style={{ color: '#9ca3af' }}>—</span>;
                                    })()}
                                  </td>
                                  <td>
                                    {(() => {
                                      if (user.selected_stands_updated) {
                                        const stands = Array.isArray(user.selected_stands_updated)
                                          ? user.selected_stands_updated
                                          : (typeof user.selected_stands_updated === 'string'
                                            ? (() => {
                                                try {
                                                  const parsed = JSON.parse(user.selected_stands_updated);
                                                  return Array.isArray(parsed) ? parsed : [parsed];
                                                } catch {
                                                  return [user.selected_stands_updated];
                                                }
                                              })()
                                            : []);
                                        if (stands.length > 0) {
                                          return <span style={{ fontSize: '12px', color: '#1e293b' }}>{stands.join(', ')}</span>;
                                        }
                                      }
                                      return <span style={{ color: '#9ca3af' }}>—</span>;
                                    })()}
                                  </td>
                                  <td style={{ color: '#64748b' }}>{user.email}</td>
                                  <td>
                                    {user.first_name || user.last_name ? (
                                      <div>
                                        <div>{user.first_name} {user.last_name}</div>
                                        {user.position && (
                                          <div style={{ fontSize: '12px', color: '#9ca3af' }}>{user.position}</div>
                                        )}
                                      </div>
                                    ) : (
                                      <span style={{ color: '#9ca3af' }}>—</span>
                                    )}
                                  </td>
                                  <td>
                                    {user.institutional_profile_id ? (
                                      <span className="status-badge-small profile-exists"><CheckIcon width={12} height={12} /> Sí</span>
                                    ) : (
                                      <span className="status-badge-small profile-not-exists"><XIcon width={12} height={12} /> No</span>
                                    )}
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                      {user.speakers && Array.isArray(user.speakers) && user.speakers.length > 0 && (
                                        <span className="feature-badge-small speakers-badge" title={`${user.speakers.length} orador(es)`}>
                                          <MicIcon width={14} height={14} /> {user.speakers.length}
                                        </span>
                                      )}
                                      {user.parallel_events && Array.isArray(user.parallel_events) && user.parallel_events.length > 0 && (
                                        <span className="feature-badge-small events-badge" title={`${user.parallel_events.length} evento(s)`}>
                                          <CalendarIcon width={14} height={14} /> {user.parallel_events.length}
                                        </span>
                                      )}
                                      {user.initiatives && Array.isArray(user.initiatives) && user.initiatives.length > 0 && (
                                        <span className="feature-badge-small catalog-badge" title="En catálogo">
                                          <BookIcon width={14} height={14} />
                                        </span>
                                      )}
                                      {user.business_rounds_participants && Array.isArray(user.business_rounds_participants) && user.business_rounds_participants.length > 0 && (
                                        <span className="feature-badge-small rounds-badge" title={`${user.business_rounds_participants.length} participante(s)`}>
                                          <UsersIcon width={14} height={14} />
                                        </span>
                                      )}
                                      {(!user.speakers || (Array.isArray(user.speakers) && user.speakers.length === 0)) &&
                                       (!user.parallel_events || (Array.isArray(user.parallel_events) && user.parallel_events.length === 0)) &&
                                       (!user.initiatives || (Array.isArray(user.initiatives) && user.initiatives.length === 0)) &&
                                       (!user.business_rounds_participants || (Array.isArray(user.business_rounds_participants) && user.business_rounds_participants.length === 0)) && (
                                        <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    {(() => {
                                      const hasPendingApprovals = (user.speakers && Array.isArray(user.speakers) && user.speakers.some((speaker: any) => {
                                        const statusInfo = getSpeakerStatusHelper(speaker);
                                        return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                                               speaker.approval_status !== 'approved' && speaker.approval_status !== 'rejected';
                                      })) ||
                                        (user.parallel_events && Array.isArray(user.parallel_events) && user.parallel_events.some((event: any) => {
                                          const statusInfo = getEventStatusHelper(event);
                                          return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                                                 event.approval_status !== 'approved' && event.approval_status !== 'rejected';
                                        })) ||
                                        (user.initiatives && Array.isArray(user.initiatives) && user.initiatives.some((init: any) => {
                                          const statusInfo = getInitiativeStatusHelper(init);
                                          return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                                                 init.approval_status !== 'approved' && init.approval_status !== 'rejected';
                                        })) ||
                                        (user.business_rounds_participants && Array.isArray(user.business_rounds_participants) && user.business_rounds_participants.some((participant: any) => {
                                          const status = getBusinessRoundsStatusHelper(participant);
                                          return status === 'complete' &&
                                                 participant.approval_status !== 'approved' && participant.approval_status !== 'rejected';
                                        }));
                                      
                                      if (!hasPendingApprovals) {
                                        return <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>;
                                      }
                                      
                                      return (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                          {hasPendingApprovals && (
                                            <span className="status-badge-small approval-badge"><CheckCircleIcon width={14} height={14} /> Aprobaciones</span>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </td>
                                  <td style={{ color: '#64748b', fontSize: '13px' }}>
                                    {formatDate(user.last_login_date)}
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                      <button
                                        className="admin-action-button-small chat-button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          sessionStorage.setItem('chatsPage_asUserId', user.id.toString());
                                          const url = `${window.location.origin}/soporte?asUserId=${user.id}`;
                                          window.open(url, '_blank');
                                        }}
                                        title="Abrir chat"
                                      >
                                        Chat
                                        {(user.unreadCount ?? 0) > 0 && (
                                          <span className="chat-unread-badge-small">{user.unreadCount}</span>
                                        )}
                                      </button>
                                      <button
                                        className="admin-action-button-small crm-button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenCrmDetail(user);
                                        }}
                                        title="Ver detalles CRM"
                                      >
                                        <BarChartIcon width={14} height={14} /> CRM
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {sortedUsers.length > perfilPersonalPerPage && (
                        <div className="admin-pagination-bar">
                          <span className="admin-pagination-info">
                            Mostrando {((perfilPersonalPage - 1) * perfilPersonalPerPage) + 1}-{Math.min(perfilPersonalPage * perfilPersonalPerPage, sortedUsers.length)} de {sortedUsers.length}
                          </span>
                          <div className="admin-pagination-buttons">
                            <button type="button" className="admin-pagination-btn" disabled={perfilPersonalPage <= 1} onClick={() => setPerfilPersonalPage(p => Math.max(1, p - 1))}>Anterior</button>
                            <span className="admin-pagination-page">Página {perfilPersonalPage} de {totalPerfilPersonalPages}</span>
                            <button type="button" className="admin-pagination-btn" disabled={perfilPersonalPage >= totalPerfilPersonalPages} onClick={() => setPerfilPersonalPage(p => Math.min(totalPerfilPersonalPages, p + 1))}>Siguiente</button>
                          </div>
                        </div>
                      )}
                    </>
                    )}
                  </div>
                  ) : (
                  <div className="admin-crm-contacts-list">
                    {sortedUsers.length === 0 ? (
                      <div className="admin-empty-state">
                        <p>{searchTerm ? 'No se encontraron contactos' : 'No hay contactos para mostrar'}</p>
                      </div>
                    ) : (
                      <>
                      <div className="admin-crm-contacts-grid">
                      {perfilPersonalPaginated.map((user) => {
                        const userPriority = crmPriority[user.id] || 'medium';
                        const userTags = crmTags[user.id] || [];
                        const userNotes = crmNotes[user.id] || '';
                        const userConversation = conversations.find(c => c.userId === user.id);
                        const lastMessageDate = userConversation?.lastMessageDate;
                        const daysSinceLastMessage = lastMessageDate 
                          ? Math.floor((Date.now() - new Date(lastMessageDate).getTime()) / (1000 * 60 * 60 * 24))
                          : null;

                        const hasPendingApprovals = (user.speakers && Array.isArray(user.speakers) && user.speakers.some((speaker: any) => {
                          const statusInfo = getSpeakerStatusHelper(speaker);
                          return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                                 speaker.approval_status !== 'approved' && speaker.approval_status !== 'rejected';
                        })) ||
                          (user.parallel_events && Array.isArray(user.parallel_events) && user.parallel_events.some((event: any) => {
                            const statusInfo = getEventStatusHelper(event);
                            return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                                   event.approval_status !== 'approved' && event.approval_status !== 'rejected';
                          })) ||
                          (user.initiatives && Array.isArray(user.initiatives) && user.initiatives.some((init: any) => {
                            const statusInfo = getInitiativeStatusHelper(init);
                            return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) &&
                                   init.approval_status !== 'approved' && init.approval_status !== 'rejected';
                          })) ||
                          (user.business_rounds_participants && Array.isArray(user.business_rounds_participants) && user.business_rounds_participants.some((participant: any) => {
                            const status = getBusinessRoundsStatusHelper(participant);
                            return status === 'complete' &&
                                   participant.approval_status !== 'approved' && participant.approval_status !== 'rejected';
                          }));

                        return (
                          <div 
                            key={user.id}
                            data-user-id={user.id}
                            className={`admin-crm-contact-card priority-${userPriority}`}
                            onClick={() => handleOpenCrmDetail(user)}
                          >
                            <div className="admin-crm-card-status-badge admin-crm-card-badges-row">
                              {user.is_draft !== null && user.is_draft !== undefined && (
                                user.is_draft === false ? (
                                  <span className="status-badge-corner profile-authorized"><CheckCircleIcon width={12} height={12} /> Autorizado</span>
                                ) : (
                                  <span className="status-badge-corner profile-draft"><FileTextIcon width={12} height={12} /> Borrador</span>
                                )
                              )}
                              {user.created_by_admin ? (
                                <span className="origin-badge origin-manual" title="Contacto creado manualmente por el admin">Creado por admin</span>
                              ) : (
                                <span className="origin-badge origin-registered" title="El usuario se registró por su cuenta">Registrado</span>
                              )}
                            </div>

                            <div className="admin-crm-contact-card-header">
                              <div className="admin-crm-contact-name-section">
                                <h3>{getUserDisplayName(user)}</h3>
                                <p className="admin-crm-contact-email">{user.email}</p>
                              </div>
                            </div>

                            <div className="admin-crm-contact-card-body">
                              <div className="admin-crm-contact-info-grid">
                                {(user.first_name || user.last_name || user.position) && (
                                  <div className="admin-crm-contact-info-item">
                                    <span className="info-icon"><UsersIcon width={18} height={18} /></span>
                                    <div>
                                      <span className="info-label">Contacto:</span>
                                      <span className="info-value">
                                        {user.first_name || user.last_name ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'N/A'}
                                        {user.position && (
                                          <span style={{ display: 'block', fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                            {user.position}
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                )}
                                
                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><BuildingIcon width={18} height={18} /></span>
                                  <div>
                                    <span className="info-label">Tipo:</span>
                                    <span className="info-value">{getOrganizationTypeLabel(user.organization_type) || 'N/A'}</span>
                                  </div>
                                </div>

                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                  <div>
                                    <span className="info-label">Modalidad:</span>
                                    <span className="info-value">
                                      {(() => {
                                        if (user.agreed_modality) {
                                          const modalityOption = participationModalities.find(m => m.value === user.agreed_modality);
                                          return modalityOption ? modalityOption.label : user.agreed_modality;
                                        }
                                        if (user.participation_modalities) {
                                          const mods = Array.isArray(user.participation_modalities) 
                                            ? user.participation_modalities 
                                            : (typeof user.participation_modalities === 'string' 
                                              ? (() => {
                                                  try {
                                                    const parsed = JSON.parse(user.participation_modalities);
                                                    return Array.isArray(parsed) ? parsed : [parsed];
                                                  } catch {
                                                    return [user.participation_modalities];
                                                  }
                                                })()
                                              : []);
                                          if (mods.length > 0) {
                                            return mods.map((mod: string) => {
                                              const modality = participationModalities.find(m => m.value === mod);
                                              return modality ? modality.label : mod;
                                            }).join(', ');
                                          }
                                        }
                                        return 'N/A';
                                      })()}
                                    </span>
                                  </div>
                                </div>

                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><MapPinIcon width={18} height={18} /></span>
                                  <div>
                                    <span className="info-label">Zona Expo:</span>
                                    <span className="info-value">
                                      {(() => {
                                        if (user.selected_stands_updated) {
                                          const stands = Array.isArray(user.selected_stands_updated)
                                            ? user.selected_stands_updated
                                            : (typeof user.selected_stands_updated === 'string'
                                              ? (() => {
                                                  try {
                                                    const parsed = JSON.parse(user.selected_stands_updated);
                                                    return Array.isArray(parsed) ? parsed : [parsed];
                                                  } catch {
                                                    return [user.selected_stands_updated];
                                                  }
                                                })()
                                              : []);
                                          if (stands.length > 0) {
                                            return stands.join(', ');
                                          }
                                        }
                                        return 'N/A';
                                      })()}
                                    </span>
                                  </div>
                                </div>
                                
                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><ClockIcon width={18} height={18} /></span>
                                  <div>
                                    <span className="info-label">Último acceso:</span>
                                    <span className="info-value">{formatDate(user.last_login_date)}</span>
                                  </div>
                                </div>

                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                  <div>
                                    <span className="info-label">Perfil Institucional:</span>
                                    <span className={`info-value ${user.institutional_profile_id ? 'profile-complete' : 'profile-incomplete'}`}>
                                      {user.institutional_profile_id ? 'Sí' : 'No'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="admin-crm-contact-participations">
                                <span className="info-label">Participa en:</span>
                                <div className="admin-crm-participations-badges">
                                  {user.speakers && Array.isArray(user.speakers) && user.speakers.length > 0 && (
                                    <span className="feature-badge-small speakers-badge" title={`${user.speakers.length} orador(es)`}>
                                      <MicIcon width={14} height={14} /> {user.speakers.length}
                                    </span>
                                  )}
                                  {user.parallel_events && Array.isArray(user.parallel_events) && user.parallel_events.length > 0 && (
                                    <span className="feature-badge-small events-badge" title={`${user.parallel_events.length} evento(s)`}>
                                      <CalendarIcon width={14} height={14} /> {user.parallel_events.length}
                                    </span>
                                  )}
                                  {user.initiatives && Array.isArray(user.initiatives) && user.initiatives.length > 0 && (
                                    <span className="feature-badge-small catalog-badge" title="En catálogo">
                                      <BookIcon width={14} height={14} />
                                    </span>
                                  )}
                                  {user.business_rounds_participants && Array.isArray(user.business_rounds_participants) && user.business_rounds_participants.length > 0 && (
                                    <span className="feature-badge-small rounds-badge" title={`${user.business_rounds_participants.length} participante(s)`}>
                                      <UsersIcon width={14} height={14} />
                                    </span>
                                  )}
                                  {(!user.speakers || (Array.isArray(user.speakers) && user.speakers.length === 0)) &&
                                   (!user.parallel_events || (Array.isArray(user.parallel_events) && user.parallel_events.length === 0)) &&
                                   (!user.initiatives || (Array.isArray(user.initiatives) && user.initiatives.length === 0)) &&
                                   (!user.business_rounds_participants || (Array.isArray(user.business_rounds_participants) && user.business_rounds_participants.length === 0)) && (
                                    <span style={{ color: '#9ca3af', fontSize: '12px' }}>Sin participaciones</span>
                                  )}
                                </div>
                              </div>

                              {hasPendingApprovals && (
                                <div className="admin-crm-contact-pending-actions">
                                  <span className="info-label">Acciones pendientes:</span>
                                  <div className="admin-crm-pending-badges">
                                    {hasPendingApprovals && (
                                      <span className="status-badge-small approval-badge"><CheckCircleIcon width={14} height={14} /> Aprobaciones</span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {userTags.length > 0 && (
                                <div className="admin-crm-contact-tags">
                                  <div className="admin-crm-tags-list">
                                    {userTags.slice(0, 3).map((tag, idx) => (
                                      <span key={idx} className="tag-badge-small">{tag}</span>
                                    ))}
                                    {userTags.length > 3 && (
                                      <span className="tag-badge-small">+{userTags.length - 3}</span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {userNotes && (
                                <div className="admin-crm-contact-note-preview">
                                  <span className="note-icon"><FileTextIcon width={14} height={14} /></span>
                                  <span className="note-text">{userNotes.substring(0, 100)}{userNotes.length > 100 ? '...' : ''}</span>
                                </div>
                              )}
                            </div>

                            <div className="admin-crm-contact-card-footer">
                              <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                <button
                                  className="admin-action-button-small chat-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    sessionStorage.setItem('chatsPage_asUserId', user.id.toString());
                                    window.open(`${window.location.origin}/soporte?asUserId=${user.id}`, '_blank');
                                  }}
                                  title="Abrir chat"
                                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                >
                                  Chat
                                  {(user.unreadCount ?? 0) > 0 && (
                                    <span className="chat-unread-badge-small">{user.unreadCount}</span>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                      {sortedUsers.length > perfilPersonalPerPage && (
                        <div className="admin-pagination-bar">
                          <span className="admin-pagination-info">
                            Mostrando {((perfilPersonalPage - 1) * perfilPersonalPerPage) + 1}-{Math.min(perfilPersonalPage * perfilPersonalPerPage, sortedUsers.length)} de {sortedUsers.length}
                          </span>
                          <div className="admin-pagination-buttons">
                            <button type="button" className="admin-pagination-btn" disabled={perfilPersonalPage <= 1} onClick={() => setPerfilPersonalPage(p => Math.max(1, p - 1))}>Anterior</button>
                            <span className="admin-pagination-page">Página {perfilPersonalPage} de {totalPerfilPersonalPages}</span>
                            <button type="button" className="admin-pagination-btn" disabled={perfilPersonalPage >= totalPerfilPersonalPages} onClick={() => setPerfilPersonalPage(p => Math.min(totalPerfilPersonalPages, p + 1))}>Siguiente</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                  )}
                </div>
              </>
            )}

            {/* Contenido Perfil Institucional: mismas tarjetas que Empresas, contenido Scesde26 en la tarjeta, desplegable Contacto = usuario del perfil */}
            {activePerfilSubTab === 'perfil-institucional' && (
              <div className="admin-perfil-institucional-content">
                <div className="admin-crm-filters-bar admin-crm-filters-bar-compact" style={{ marginBottom: '24px' }}>
                  <div className="admin-crm-toolbar-row">
                    <input
                      type="text"
                      placeholder="Buscar por organización, tipo..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="admin-search-input admin-toolbar-control"
                    />
                    <div className="admin-view-toggle">
                      <button
                        type="button"
                        className={`admin-view-toggle-btn admin-toolbar-control ${listViewMode === 'cards' ? 'active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setListViewMode('cards'); setPerfilInstitucionalPage(1); }}
                        title="Vista de tarjetas"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="7" height="7"></rect>
                          <rect x="14" y="3" width="7" height="7"></rect>
                          <rect x="3" y="14" width="7" height="7"></rect>
                          <rect x="14" y="14" width="7" height="7"></rect>
                        </svg>
                      </button>
                      <button
                        type="button"
                        className={`admin-view-toggle-btn admin-toolbar-control ${listViewMode === 'table' ? 'active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setListViewMode('table'); setPerfilInstitucionalPage(1); }}
                        title="Vista de tabla"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="3" y1="3" x2="21" y2="3"></line>
                          <line x1="3" y1="8" x2="21" y2="8"></line>
                          <line x1="3" y1="13" x2="21" y2="13"></line>
                          <line x1="3" y1="18" x2="21" y2="18"></line>
                          <line x1="3" y1="23" x2="21" y2="23"></line>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="admin-crm-contacts-list">
                  {loadingPerfilOrganizations ? (
                    <div className="admin-empty-state"><p>Cargando organizaciones...</p></div>
                  ) : filteredPerfilOrganizations.length === 0 ? (
                    <div className="admin-empty-state">
                      <p>{searchTerm ? 'No se encontraron organizaciones' : 'No hay organizaciones de perfiles institucionales'}</p>
                    </div>
                  ) : listViewMode === 'table' ? (
                    <>
                    <div className="admin-users-table-container">
                      <table className="admin-users-table">
                        <thead>
                          <tr>
                            <th>Organización</th>
                            <th className="admin-table-fixed-width">Tipo</th>
                            <th className="admin-table-fixed-width">Estado</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {perfilInstitucionalPaginated.map((org) => {
                            const linkedUser = org.source_user_id != null ? users.find((u) => u.id === org.source_user_id) : null;
                            const status = org.approval_status === 'approved' || org.approval_status === 'rejected' || org.approval_status === 'pending' ? org.approval_status : 'pending';
                            return (
                              <tr key={org.id} data-organization-id={org.id}>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span
                                      style={{ fontWeight: 600, color: '#1e293b', textDecoration: 'underline', cursor: 'pointer' }}
                                      onClick={() => linkedUser && handleOpenCrmDetail(linkedUser)}
                                    >
                                      {org.organizacion || 'Sin nombre'}
                                    </span>
                                    {org.sitio_web && (
                                      <span style={{ fontSize: '12px', color: '#64748b' }}>
                                        <a href={org.sitio_web.startsWith('http') ? org.sitio_web : `https://${org.sitio_web}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{org.sitio_web}</a>
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <span className="organization-type-badge-small">{getOrganizationTypeLabel(org.tipo_organizacion) || '—'}</span>
                                </td>
                                <td>
                                  <span className={`participation-status-badge status-${status}`}>
                                    {status === 'approved' ? 'Aprobado' : status === 'rejected' ? 'Rechazado' : 'Pendiente'}
                                  </span>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                    {status !== 'approved' && (
                                      <button type="button" className="admin-action-button-small approve-button" onClick={() => updatePerfilInstOrgApproval(org.id, 'approved')}>Aprobar</button>
                                    )}
                                    {status !== 'rejected' && (
                                      <button type="button" className="admin-action-button-small reject-button" onClick={() => updatePerfilInstOrgApproval(org.id, 'rejected')}>Rechazar</button>
                                    )}
                                    {status !== 'pending' && (
                                      <button type="button" className="admin-action-button-small pending-button" onClick={() => updatePerfilInstOrgApproval(org.id, 'pending')}>Pendiente</button>
                                    )}
                                    {linkedUser && (
                                      <button
                                        type="button"
                                        className="admin-action-button-small chat-button"
                                        onClick={() => {
                                          sessionStorage.setItem('chatsPage_asUserId', linkedUser.id.toString());
                                          window.open(`${window.location.origin}/soporte?asUserId=${linkedUser.id}`, '_blank');
                                        }}
                                        title="Abrir chat"
                                      >
                                        Chat
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                      {filteredPerfilOrganizations.length > perfilInstitucionalPerPage && (
                        <div className="admin-pagination-bar">
                          <span className="admin-pagination-info">
                            Mostrando {((perfilInstitucionalPage - 1) * perfilInstitucionalPerPage) + 1}-{Math.min(perfilInstitucionalPage * perfilInstitucionalPerPage, filteredPerfilOrganizations.length)} de {filteredPerfilOrganizations.length}
                          </span>
                          <div className="admin-pagination-buttons">
                            <button type="button" className="admin-pagination-btn" disabled={perfilInstitucionalPage <= 1} onClick={() => setPerfilInstitucionalPage(p => Math.max(1, p - 1))}>Anterior</button>
                            <span className="admin-pagination-page">Página {perfilInstitucionalPage} de {totalPerfilInstitucionalPages}</span>
                            <button type="button" className="admin-pagination-btn" disabled={perfilInstitucionalPage >= totalPerfilInstitucionalPages} onClick={() => setPerfilInstitucionalPage(p => Math.min(totalPerfilInstitucionalPages, p + 1))}>Siguiente</button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                    <div className="admin-crm-contacts-grid">
                      {perfilInstitucionalPaginated.map((org) => {
                        const linkedUser = org.source_user_id != null ? users.find((u) => u.id === org.source_user_id) : null;
                        if (!linkedUser) {
                          return (
                            <div key={org.id} className="admin-crm-contact-card" data-organization-id={org.id}>
                              <div className="admin-crm-contact-card-header">
                                {org.logo ? (
                                  <div className="admin-crm-empresa-logo-container">
                                    <ProxyImage src={org.logo} alt="" className="admin-crm-empresa-logo-img" />
                                  </div>
                                ) : null}
                                <div className="admin-crm-contact-name-section" style={{ flex: 1, minWidth: 0 }}>
                                  <h3>{org.organizacion || 'Sin nombre'}</h3>
                                  {org.sitio_web && (
                                    <p className="admin-crm-contact-email">
                                      <a href={org.sitio_web.startsWith('http') ? org.sitio_web : `https://${org.sitio_web}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{org.sitio_web}</a>
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="admin-crm-contact-card-body">
                                <div className="admin-crm-contact-info-grid">
                                  <div className="admin-crm-contact-info-item">
                                    <span className="info-icon"><BuildingIcon width={18} height={18} /></span>
                                    <div>
                                      <span className="info-label">Tipo:</span>
                                      <span className="info-value">{getOrganizationTypeLabel(org.tipo_organizacion)}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        const u = linkedUser;
                        const parseArr = (val: unknown): string[] => {
                          if (val == null) return [];
                          if (Array.isArray(val)) return val.map(v => String(v));
                          if (typeof val === 'string') {
                            try {
                              const p = JSON.parse(val);
                              return Array.isArray(p) ? p.map((x: unknown) => String(x)) : [val];
                            } catch { return [val]; }
                          }
                          return [];
                        };
                        const parseParticipation = (val: unknown): unknown[] => {
                          if (val == null) return [];
                          if (Array.isArray(val)) return val;
                          if (typeof val === 'string') {
                            try {
                              const p = JSON.parse(val);
                              return Array.isArray(p) ? p : [];
                            } catch { return []; }
                          }
                          return [];
                        };
                        const mods = parseArr(u.participation_modalities);
                        const agreed = u.agreed_modality != null && u.agreed_modality !== '' ? String(u.agreed_modality) : '';
                        const modalityLabel = agreed
                          ? (participationModalities.find(m => m.value === agreed)?.label ?? agreed)
                          : mods.length > 0
                            ? mods.map((mod: string) => participationModalities.find(m => m.value === mod)?.label ?? mod).join(', ')
                            : 'N/A';
                        const stands = parseArr(u.selected_stands_updated);
                        const standsLabel = stands.length > 0 ? stands.join(', ') : 'N/A';
                        const speakers = parseParticipation(u.speakers);
                        const parallelEvents = parseParticipation(u.parallel_events);
                        const initiatives = parseParticipation(u.initiatives).length > 0 ? parseParticipation(u.initiatives) : parseParticipation(u.catalog_sections);
                        const businessRounds = parseParticipation(u.business_rounds_participants);
                        const hasAnyParticipation = speakers.length > 0 || parallelEvents.length > 0 || initiatives.length > 0 || businessRounds.length > 0;
                        const hasPendingApprovals = (speakers.length > 0 && speakers.some((s: any) => {
                          const statusInfo = getSpeakerStatusHelper(s);
                          return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (s?.approval_status !== 'approved' && s?.approval_status !== 'rejected');
                        })) || (parallelEvents.length > 0 && parallelEvents.some((e: any) => {
                          const statusInfo = getEventStatusHelper(e);
                          return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (e?.approval_status !== 'approved' && e?.approval_status !== 'rejected');
                        })) || (initiatives.length > 0 && initiatives.some((i: any) => {
                          const statusInfo = getInitiativeStatusHelper(i);
                          return (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (i?.approval_status !== 'approved' && i?.approval_status !== 'rejected');
                        })) || (businessRounds.length > 0 && businessRounds.some((p: any) => {
                          const status = getBusinessRoundsStatusHelper(p);
                          return status === 'complete' && (p?.approval_status !== 'approved' && p?.approval_status !== 'rejected');
                        }));
                        const modalityObs = (u as { modality_observations?: string }).modality_observations;
                        const isContactExp = perfilInstContactExpandedId === org.id;
                        const scesde26PipelineId = 1;
                        const negocioOrgId = org.crm_organization_id ?? org.id;
                        const neg = perfilInstScesde26Negocios[negocioOrgId];
                        const perfilInstStages = stagesByPipelineId[scesde26PipelineId] || DEFAULT_EMPRESA_STAGES;
                        const userTagsPerfil = crmTags[u.id] || [];
                        const approvalStatus = org.approval_status === 'approved' || org.approval_status === 'rejected' || org.approval_status === 'pending' ? org.approval_status : 'pending';
                        return (
                          <div key={org.id} className="admin-crm-contact-card admin-perfil-inst-card" data-organization-id={org.id} onClick={() => handleOpenCrmDetail(linkedUser)}>
                            {/* Etiqueta de estado en borde superior derecho (como Perfil Personal) */}
                            <div className="admin-perfil-inst-approval-badge" onClick={(e) => e.stopPropagation()}>
                              <span className={`participation-status-badge status-${approvalStatus}`}>
                                {approvalStatus === 'approved' ? 'Aprobado' : approvalStatus === 'rejected' ? 'Rechazado' : 'Pendiente'}
                              </span>
                            </div>
                            {/* Etapa: arriba de todo en la tarjeta, desplegable editable */}
                            <div className="admin-crm-contact-card-body admin-perfil-inst-etapa-row" onClick={(e) => e.stopPropagation()}>
                              <CustomSelect
                                className="admin-crm-priority-select-small admin-perfil-inst-etapa-select"
                                optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                                value={neg?.pipelineStage ?? 'nuevo'}
                                onChange={(newStage) => negocioOrgId && savePerfilInstNegocioStage(negocioOrgId, newStage)}
                                options={perfilInstStages.map((s) => ({ value: s.id, label: s.label, color: s.color }))}
                              />
                            </div>
                            <div className="admin-crm-contact-card-header admin-perfil-inst-header-with-priority">
                              {org.logo ? (
                                <div className="admin-perfil-inst-logo-row">
                                  <div className="admin-crm-empresa-logo-container">
                                    <ProxyImage src={org.logo} alt="" className="admin-crm-empresa-logo-img" />
                                  </div>
                                </div>
                              ) : null}
                              <div className="admin-perfil-inst-name-priority-row">
                                <div className="admin-crm-contact-name-section" style={{ flex: 1, minWidth: 0 }}>
                                  <h3>{org.organizacion || 'Sin nombre'}</h3>
                                  {org.sitio_web && (
                                    <p className="admin-crm-contact-email">
                                      <a href={org.sitio_web.startsWith('http') ? org.sitio_web : `https://${org.sitio_web}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{org.sitio_web}</a>
                                    </p>
                                  )}
                                </div>
                                <div className="admin-perfil-inst-priority-wrap" onClick={(e) => e.stopPropagation()}>
                                  <CustomSelect
                                    className="admin-crm-priority-select-small admin-perfil-inst-priority-select"
                                    optionsClassName={isDarkMode ? 'custom-select-options-dark' : ''}
                                    value={neg?.priority === 'high' ? 'high' : neg?.priority === 'low' ? 'low' : 'medium'}
                                    onChange={(newValue) => negocioOrgId && savePerfilInstNegocioPriority(negocioOrgId, (newValue === 'high' || newValue === 'low' ? newValue : 'medium') as 'high' | 'medium' | 'low')}
                                    options={[
                                      { value: 'high', label: 'Alta', color: '#ef4444' },
                                      { value: 'medium', label: 'Media', color: '#fbbf24' },
                                      { value: 'low', label: 'Baja', color: '#10b981' }
                                    ]}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="admin-crm-contact-card-body" onClick={(e) => e.stopPropagation()}>
                              {/* Mismo contenido que tarjetas Empresas Scesde26: todo en la tarjeta (sin desplegable para el negocio) */}
                              <div className="admin-crm-contact-info-grid">
                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><BuildingIcon width={18} height={18} /></span>
                                  <div>
                                    <span className="info-label">Tipo:</span>
                                    <span className="info-value">{getOrganizationTypeLabel(org.tipo_organizacion) || 'N/A'}</span>
                                  </div>
                                </div>
                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                  <div>
                                    <span className="info-label">Modalidad:</span>
                                    <span className="info-value">{modalityLabel}</span>
                                  </div>
                                </div>
                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><MapPinIcon width={18} height={18} /></span>
                                  <div>
                                    <span className="info-label">Zona Expo:</span>
                                    <span className="info-value">{standsLabel}</span>
                                  </div>
                                </div>
                                {modalityObs != null && String(modalityObs).trim() !== '' && (
                                  <div className="admin-crm-contact-info-item">
                                    <span className="info-icon"><FileTextIcon width={18} height={18} /></span>
                                    <div>
                                      <span className="info-label">Observaciones modalidad:</span>
                                      <span className="info-value">{String(modalityObs)}</span>
                                    </div>
                                  </div>
                                )}
                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><AwardIcon width={18} height={18} /></span>
                                  <div><span className="info-label">Importancia:</span><span className="info-value">{getImportanciaLabel(org.importancia)}</span></div>
                                </div>
                                {org.industrias && (() => {
                                  let items: string[] = [];
                                  try { const p = typeof org.industrias === 'string' ? JSON.parse(org.industrias) : org.industrias; items = Array.isArray(p) ? p : []; } catch { }
                                  if (items.length === 0) return null;
                                  return (
                                    <div className="admin-crm-contact-info-item admin-crm-contact-info-item-label-block">
                                      <span className="info-icon"><TagIcon width={18} height={18} /></span>
                                      <div>
                                        <span className="info-label">Industrias:</span>
                                        <span className="info-value">
                                          <div className="admin-crm-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>{items.map((item, idx) => <span key={idx} className="tag-badge-small">{item}</span>)}</div>
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })()}
                                {org.redes_sociales && (() => {
                                  let obj: Record<string, string> = {};
                                  try { const parsed = typeof org.redes_sociales === 'string' ? JSON.parse(org.redes_sociales) : org.redes_sociales; if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) obj = parsed; } catch { }
                                  const labels: Record<string, string> = { facebook: 'Facebook', twitter: 'Twitter', linkedin: 'LinkedIn', instagram: 'Instagram', youtube: 'YouTube', other: 'Otra' };
                                  const entries = Object.entries(obj).filter(([, url]) => url && String(url).trim() !== '');
                                  if (entries.length === 0) return null;
                                  return (
                                    <div className="admin-crm-contact-info-item admin-crm-contact-info-item-label-block">
                                      <span className="info-icon"><MessageCircleIcon width={18} height={18} /></span>
                                      <div>
                                        <span className="info-label">Redes sociales:</span>
                                        <span className="info-value">
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>{entries.map(([key, url]) => { const label = labels[key] || key.charAt(0).toUpperCase() + key.slice(1); const href = url.startsWith('http') ? url : `https://${url}`; return <span key={key} style={{ display: 'block' }}><span style={{ fontWeight: 600, marginRight: '6px' }}>{label}:</span><a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{url}</a></span>; })}</div>
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })()}
                                <div className="admin-crm-contact-info-item">
                                  <span className="info-icon"><ClockIcon width={18} height={18} /></span>
                                  <div><span className="info-label">Actualizado:</span><span className="info-value">{org.updated_at ? formatDate(org.updated_at) : '—'}</span></div>
                                </div>
                              </div>
                              <div className="admin-crm-contact-participations">
                                <span className="info-label">Participa en:</span>
                                <div className="admin-crm-participations-badges">
                                  {speakers.length > 0 && (
                                    <span className="feature-badge-small speakers-badge" title={`${speakers.length} orador(es)`}>
                                      <MicIcon width={14} height={14} /> {speakers.length}
                                    </span>
                                  )}
                                  {parallelEvents.length > 0 && (
                                    <span className="feature-badge-small events-badge" title={`${parallelEvents.length} evento(s)`}>
                                      <CalendarIcon width={14} height={14} /> {parallelEvents.length}
                                    </span>
                                  )}
                                  {initiatives.length > 0 && (
                                    <span className="feature-badge-small catalog-badge" title="En catálogo">
                                      <BookIcon width={14} height={14} />
                                    </span>
                                  )}
                                  {businessRounds.length > 0 && (
                                    <span className="feature-badge-small rounds-badge" title={`${businessRounds.length} participante(s) en rondas`}>
                                      <UsersIcon width={14} height={14} />
                                    </span>
                                  )}
                                  {!hasAnyParticipation && (
                                    <span style={{ color: '#9ca3af', fontSize: '12px' }}>Sin participaciones</span>
                                  )}
                                </div>
                              </div>
                              {hasPendingApprovals && (
                                <div className="admin-crm-contact-pending-actions">
                                  <span className="info-label">Acciones pendientes:</span>
                                  <div className="admin-crm-pending-badges">
                                    <span className="status-badge-small approval-badge"><CheckCircleIcon width={14} height={14} /> Aprobaciones</span>
                                  </div>
                                </div>
                              )}
                              {userTagsPerfil.length > 0 && (
                                <div className="admin-crm-contact-tags">
                                  <div className="admin-crm-tags-list">
                                    {userTagsPerfil.slice(0, 3).map((tag, idx) => (
                                      <span key={idx} className="tag-badge-small">{tag}</span>
                                    ))}
                                    {userTagsPerfil.length > 3 && (
                                      <span className="tag-badge-small">+{userTagsPerfil.length - 3}</span>
                                    )}
                                  </div>
                                </div>
                              )}
                              {/* Desplegable Contacto = solo el usuario de este perfil (no contactos asociados) */}
                              <div className="admin-crm-contact-info-item admin-crm-empresa-contactos" style={{ marginBottom: 8 }}>
                                <span className="info-icon"><UsersIcon width={18} height={18} /></span>
                                <div className="admin-crm-empresa-negocios-wrap">
                                  <button type="button" className="admin-crm-empresa-negocios-toggle" onClick={(e) => { e.stopPropagation(); setPerfilInstContactExpandedId(isContactExp ? null : org.id); }} aria-expanded={isContactExp}>
                                    <span className="info-label">Contacto</span>
                                    {isContactExp ? <ChevronUpIcon width={18} height={18} /> : <ChevronDownIcon width={18} height={18} />}
                                  </button>
                                  {isContactExp && (
                                    <ul className="admin-crm-empresa-negocios-list admin-crm-empresa-contactos-list">
                                      <li className="admin-crm-empresa-contacto-row">
                                        <div className="admin-crm-empresa-contacto-header">
                                          <strong>{[u.first_name, u.last_name].filter(Boolean).join(' ') || 'Sin nombre'}</strong>
                                          {u.email && <span className="admin-crm-empresa-contacto-email">{u.email}</span>}
                                        </div>
                                        <div className="admin-crm-contact-info-grid admin-crm-empresa-contacto-body">
                                          <div className="admin-crm-contact-info-item">
                                            <span className="info-icon"><ClipboardIcon width={18} height={18} /></span>
                                            <div><span className="info-label">Cargo:</span><span className="info-value">{u.position ?? '—'}</span></div>
                                          </div>
                                          <div className="admin-crm-contact-info-item">
                                            <span className="info-icon"><PhoneIcon width={18} height={18} /></span>
                                            <div><span className="info-label">Teléfono:</span><span className="info-value">{((u as unknown) as Record<string, unknown>).phone != null ? String(((u as unknown) as Record<string, unknown>).phone) : '—'}</span></div>
                                          </div>
                                          <div className="admin-crm-contact-info-item">
                                            <span className="info-icon"><LinkedInIcon width={18} height={18} /></span>
                                            <div>
                                              <span className="info-label">LinkedIn:</span>
                                              <span className="info-value">
                                                {(() => {
                                                  const linkedin = ((u as unknown) as Record<string, unknown>).linkedin;
                                                  const url = linkedin != null && String(linkedin).trim() !== '' ? String(linkedin).trim() : null;
                                                  return url ? <a href={url.startsWith('http') ? url : `https://${url}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{url}</a> : '—';
                                                })()}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </li>
                                    </ul>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="admin-crm-contact-card-footer">
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', width: '100%', alignItems: 'center' }}>
                                {approvalStatus !== 'approved' && (
                                  <button
                                    type="button"
                                    className="admin-action-button-small approve-button"
                                    onClick={(e) => { e.stopPropagation(); updatePerfilInstOrgApproval(org.id, 'approved'); }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Aprobar
                                  </button>
                                )}
                                {approvalStatus !== 'rejected' && (
                                  <button
                                    type="button"
                                    className="admin-action-button-small reject-button"
                                    onClick={(e) => { e.stopPropagation(); updatePerfilInstOrgApproval(org.id, 'rejected'); }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Rechazar
                                  </button>
                                )}
                                {approvalStatus !== 'pending' && (
                                  <button
                                    type="button"
                                    className="admin-action-button-small pending-button"
                                    onClick={(e) => { e.stopPropagation(); updatePerfilInstOrgApproval(org.id, 'pending'); }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Pendiente
                                  </button>
                                )}
                                <button
                                  className="admin-action-button-small chat-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    sessionStorage.setItem('chatsPage_asUserId', u.id.toString());
                                    window.open(`${window.location.origin}/soporte?asUserId=${u.id}`, '_blank');
                                  }}
                                  title="Abrir chat"
                                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                >
                                  Chat
                                  {(u.unreadCount ?? 0) > 0 && (
                                    <span className="chat-unread-badge-small">{u.unreadCount}</span>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                      {filteredPerfilOrganizations.length > perfilInstitucionalPerPage && (
                        <div className="admin-pagination-bar">
                          <span className="admin-pagination-info">
                            Mostrando {((perfilInstitucionalPage - 1) * perfilInstitucionalPerPage) + 1}-{Math.min(perfilInstitucionalPage * perfilInstitucionalPerPage, filteredPerfilOrganizations.length)} de {filteredPerfilOrganizations.length}
                          </span>
                          <div className="admin-pagination-buttons">
                            <button type="button" className="admin-pagination-btn" disabled={perfilInstitucionalPage <= 1} onClick={() => setPerfilInstitucionalPage(p => Math.max(1, p - 1))}>Anterior</button>
                            <span className="admin-pagination-page">Página {perfilInstitucionalPage} de {totalPerfilInstitucionalPages}</span>
                            <button type="button" className="admin-pagination-btn" disabled={perfilInstitucionalPage >= totalPerfilInstitucionalPages} onClick={() => setPerfilInstitucionalPage(p => Math.min(totalPerfilInstitucionalPages, p + 1))}>Siguiente</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Contenido Participaciones (Oradores, Eventos, etc.) */}
            {activePerfilSubTab === 'participaciones' && (
              <>
            {/* Barra de búsqueda + Filtros */}
            <div className="admin-crm-filters-bar admin-crm-filters-bar-compact" style={{ marginBottom: '24px' }}>
              <div className="admin-crm-toolbar-row">
                <input
                  type="text"
                  placeholder="Buscar por nombre, email, organización..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="admin-search-input admin-toolbar-control"
                />
                <button
                  type="button"
                  className={`admin-filters-trigger-btn admin-toolbar-control ${filtersSidebarOpen ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
                  onClick={() => setFiltersSidebarOpen((open) => !open)}
                  aria-label={filtersSidebarOpen ? 'Cerrar filtros' : 'Abrir filtros'}
                >
                  <FilterIcon width={18} height={18} />
                  <span>Filtros</span>
                  {hasActiveFilters && <span className="admin-filters-trigger-badge" aria-hidden />}
                </button>
              </div>
            </div>

            {/* Subpestañas: Oradores | Eventos Paralelos | Iniciativas | Rondas de Negocios */}
            <div className="admin-participations-tabs">
              <button
                className={`admin-participations-tab ${activeParticipationsTab === 'speakers' ? 'active' : ''}`}
                onClick={() => setActiveParticipationsTab('speakers')}
              >
                <MicIcon width={18} height={18} /> Oradores
                {pendingSpeakersCount > 0 && (
                  <span className="admin-tab-badge">{pendingSpeakersCount}</span>
                )}
              </button>
              <button
                className={`admin-participations-tab ${activeParticipationsTab === 'events' ? 'active' : ''}`}
                onClick={() => setActiveParticipationsTab('events')}
              >
                <CalendarIcon width={18} height={18} /> Eventos Paralelos
                {pendingEventsCount > 0 && (
                  <span className="admin-tab-badge">{pendingEventsCount}</span>
                )}
              </button>
              <button
                className={`admin-participations-tab ${activeParticipationsTab === 'initiatives' ? 'active' : ''}`}
                onClick={() => setActiveParticipationsTab('initiatives')}
              >
                <BookIcon width={18} height={18} /> Iniciativas
                {pendingInitiativesCount > 0 && (
                  <span className="admin-tab-badge">{pendingInitiativesCount}</span>
                )}
              </button>
              <button
                className={`admin-participations-tab ${activeParticipationsTab === 'business_rounds' ? 'active' : ''}`}
                onClick={() => setActiveParticipationsTab('business_rounds')}
              >
                <UsersIcon width={18} height={18} /> Rondas de Negocios
                {pendingBusinessRoundsCount > 0 && (
                  <span className="admin-tab-badge">{pendingBusinessRoundsCount}</span>
                )}
              </button>
            </div>

            {/* Contenido según subpestaña */}
            <div className="admin-participations-content">
              {(() => {
                // Función helper para obtener el label del tipo de iniciativa
                const getInitiativeTypeLabel = (type: string | undefined): string => {
                  if (!type) return '';
                  const typeMap: Record<string, string> = {
                    'solution': 'Solución',
                    'challenge': 'Desafío',
                    'program': 'Programa de Impacto',
                    'policy': 'Política Pública'
                  };
                  return typeMap[type] || type;
                };

                // Función helper para obtener el label de un eje temático
                const getThematicAxisLabel = (value: string | { value?: string; label?: string }): string => {
                  if (typeof value === 'object' && value !== null) {
                    // Si ya tiene label, usarlo
                    if (value.label) return value.label;
                    // Si tiene value, buscar el label
                    if (value.value) {
                      const axis = thematicAxes.find(t => t.value === value.value);
                      return axis?.label || value.value;
                    }
                    return String(value);
                  }
                  // Si es string, buscar el label
                  const axis = thematicAxes.find(t => t.value === value);
                  return axis?.label || value;
                };
                
                // Funciones helper para determinar el estado de cada participación
                const getSpeakerStatus = (speaker: any): { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } => {
                  if (!speaker) return { completionStatus: 'incomplete', hasRequestApproval: false };
                  
                  const hasPhoto = speaker.photo && (typeof speaker.photo === 'string' ? speaker.photo.trim() !== '' : true);
                  const hasFirstName = speaker.firstName && speaker.firstName.toString().trim() !== '';
                  const hasLastName = speaker.lastName && speaker.lastName.toString().trim() !== '';
                  const hasNationality = speaker.nationality && speaker.nationality.toString().trim() !== '';
                  const hasOrganization = speaker.organization && speaker.organization.toString().trim() !== '';
                  const hasPosition = speaker.position && speaker.position.toString().trim() !== '';
                  const hasDescription = speaker.description && speaker.description.toString().trim() !== '';
                  const hasThematicAxes = speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0;
                  const hasContentDescription = speaker.contentDescription && speaker.contentDescription.toString().trim() !== '';
                  const hasLinkedin = speaker.linkedin && speaker.linkedin.toString().trim() !== '';
                  const hasBiography = speaker.biography && speaker.biography.toString().trim() !== '';
                  const hasValidBiography = hasLinkedin || hasBiography;
                  const isComplete = hasPhoto && hasFirstName && hasLastName && hasNationality && hasOrganization && 
                                     hasPosition && hasDescription && hasThematicAxes && hasContentDescription && hasValidBiography;
                  
                  return {
                    completionStatus: isComplete ? 'complete' : 'incomplete',
                    hasRequestApproval: speaker.requestApproval === true
                  };
                };

                const getEventStatus = (event: any): { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } => {
                  if (!event) return { completionStatus: 'incomplete', hasRequestApproval: false };
                  
                  const hasName = event.name && event.name.toString().trim() !== '';
                  const hasDescription = event.description && event.description.toString().trim() !== '';
                  const hasSpeakers = (event.speakerIds && event.speakerIds.length > 0) || 
                                    (event.localSpeakers && event.localSpeakers.length > 0);
                  const hasDuration = event.duration && event.duration.toString().trim() !== '';
                  const hasThematicAxes = event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0;
                  const hasAttendeeProfiles = event.attendeeProfiles && Array.isArray(event.attendeeProfiles) && event.attendeeProfiles.length > 0;
                  const isComplete = hasName && hasDescription && hasSpeakers && hasDuration && hasThematicAxes && hasAttendeeProfiles;
                  
                  return {
                    completionStatus: isComplete ? 'complete' : 'incomplete',
                    hasRequestApproval: event.requestApproval === true
                  };
                };

                const getInitiativeStatus = (initiative: any): { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } => {
                  if (!initiative) return { completionStatus: 'incomplete', hasRequestApproval: false };
                  
                  const hasName = initiative.name && initiative.name.toString().trim() !== '';
                  const hasShortDescription = initiative.shortDescription && initiative.shortDescription.toString().trim() !== '';
                  const hasDescription = initiative.description && initiative.description.toString().trim() !== '';
                  const hasThematicAxes = initiative.thematicAxes && Array.isArray(initiative.thematicAxes) && initiative.thematicAxes.length > 0;
                  let isComplete = hasName && hasShortDescription && hasDescription && hasThematicAxes;
                  
                  if (!initiative.noContact) {
                    const hasContactFirstName = initiative.contactFirstName && initiative.contactFirstName.trim() !== '';
                    const hasContactLastName = initiative.contactLastName && initiative.contactLastName.trim() !== '';
                    const hasContactPosition = initiative.contactPosition && initiative.contactPosition.trim() !== '';
                    const hasContactEmail = initiative.contactEmail && initiative.contactEmail.trim() !== '';
                    isComplete = isComplete && hasContactFirstName && hasContactLastName && hasContactPosition && hasContactEmail;
                  }
                  
                  return {
                    completionStatus: isComplete ? 'complete' : 'incomplete',
                    hasRequestApproval: initiative.requestApproval === true
                  };
                };

                const getBusinessRoundsStatus = (participant: any): 'complete' | 'incomplete' | 'pending_approval' => {
                  if (!participant) return 'incomplete';
                  const hasFirstName = participant.firstName && participant.firstName.trim() !== '';
                  const hasLastName = participant.lastName && participant.lastName.trim() !== '';
                  const hasPosition = participant.position && participant.position.trim() !== '';
                  const hasOrganization = participant.organization && participant.organization.trim() !== '';
                  const hasEmail = participant.email && participant.email.trim() !== '';
                  const hasPhone = participant.phone && participant.phone.trim() !== '' || (participant.numeroTelefono && participant.numeroTelefono.trim() !== '');
                  const hasThematicAxes = participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0;
                  const isComplete = hasFirstName && hasLastName && hasPosition && hasOrganization && hasEmail && hasPhone && hasThematicAxes;
                  
                  // Los participantes de rondas no tienen requestApproval, así que solo pueden ser completo o incompleto
                  if (isComplete) return 'complete';
                  return 'incomplete';
                };

                // Función helper para aplicar todos los filtros de participación
                const matchesParticipationFilters = (participation: any, statusInfo: { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } | string) => {
                  // Si statusInfo es string (business rounds), convertir a objeto
                  const completionStatus = typeof statusInfo === 'string' ? statusInfo : statusInfo.completionStatus;
                  const hasRequestApproval = typeof statusInfo === 'string' ? false : statusInfo.hasRequestApproval;
                  
                  // Filtro de completitud
                  if (filterParticipationCompletion !== 'all' && completionStatus !== filterParticipationCompletion) return false;
                  
                  // Filtro de solicitado
                  if (filterParticipationRequested !== 'all') {
                    const isRequested = participation.requestApproval === true;
                    if (filterParticipationRequested === 'yes' && !isRequested) return false;
                    if (filterParticipationRequested === 'no' && isRequested) return false;
                  }
                  
                  // Filtro de aprobación
                  if (filterParticipationApproval !== 'all') {
                    const approvalStatus = participation.approval_status;
                    if (filterParticipationApproval === 'pending' && (approvalStatus === 'approved' || approvalStatus === 'rejected')) return false;
                    if (filterParticipationApproval === 'approved' && approvalStatus !== 'approved') return false;
                    if (filterParticipationApproval === 'rejected' && approvalStatus !== 'rejected') return false;
                  }
                  
                  // Mantener compatibilidad con el filtro antiguo
                  if (filterParticipationStatus !== 'all') {
                    if (filterParticipationStatus === 'pending_approval' && !hasRequestApproval) return false;
                    if (filterParticipationStatus !== 'pending_approval' && completionStatus !== filterParticipationStatus) return false;
                  }
                  
                  return true;
                };

                // Filtrar usuarios según los filtros aplicados
                const filteredUsersForParticipations = users.filter(user => {
                  // Filtro de búsqueda por texto
                  const searchLower = searchTerm.toLowerCase();
                  const matchesSearch = !searchTerm || (
                    user.email.toLowerCase().includes(searchLower) ||
                    (user.organization_name && user.organization_name.toLowerCase().includes(searchLower)) ||
                    (user.first_name && user.first_name.toLowerCase().includes(searchLower)) ||
                    (user.last_name && user.last_name.toLowerCase().includes(searchLower))
                  );

                  // Filtro de tipo de organización
                  const matchesOrganizationType = filterOrganizationType.length === 0 || 
                    (user.organization_type && filterOrganizationType.includes(user.organization_type));

                  // Filtro por modalidad
                  let matchesModality = true;
                  if (filterModalities.length > 0) {
                    const userMods = user.participation_modalities
                      ? (Array.isArray(user.participation_modalities)
                          ? user.participation_modalities
                          : (typeof user.participation_modalities === 'string'
                            ? (() => {
                                try {
                                  const parsed = JSON.parse(user.participation_modalities);
                                  return Array.isArray(parsed) ? parsed : [parsed];
                                } catch {
                                  return [user.participation_modalities];
                                }
                              })()
                            : []))
                      : [];
                    matchesModality = userMods.some((mod: string) => filterModalities.includes(mod));
                  }

                  // Filtro por Zona Expo (selección única)
                  let matchesExpoZone = true;
                  if (filterExpoZone !== 'all') {
                    let hasStands = false;
                    if (user.selected_stands_updated) {
                      if (Array.isArray(user.selected_stands_updated)) {
                        hasStands = user.selected_stands_updated.length > 0;
                      } else if (typeof user.selected_stands_updated === 'string') {
                        try {
                          const parsed = JSON.parse(user.selected_stands_updated);
                          hasStands = Array.isArray(parsed) ? parsed.length > 0 : (parsed && String(parsed).trim() !== '');
                        } catch {
                          const strValue = String(user.selected_stands_updated);
                          hasStands = strValue.trim() !== '';
                        }
                      } else {
                        hasStands = Boolean(user.selected_stands_updated);
                      }
                    }
                    matchesExpoZone = filterExpoZone === 'yes' ? hasStands : !hasStands;
                  }

                  // Filtro por participaciones
                  let matchesParticipations = true;
                  if (filterParticipations.length > 0) {
                    const hasSpeakers = !!(user.speakers && Array.isArray(user.speakers) && user.speakers.length > 0);
                    const hasEvents = !!(user.parallel_events && Array.isArray(user.parallel_events) && user.parallel_events.length > 0);
                    const hasCatalog = !!(user.initiatives && Array.isArray(user.initiatives) && user.initiatives.length > 0);
                    const hasBusinessRounds = !!(user.business_rounds_participants && Array.isArray(user.business_rounds_participants) && user.business_rounds_participants.length > 0);
                    matchesParticipations =
                      (filterParticipations.includes('speakers') && hasSpeakers) ||
                      (filterParticipations.includes('parallel_events') && hasEvents) ||
                      (filterParticipations.includes('catalog') && hasCatalog) ||
                      (filterParticipations.includes('business_rounds') && hasBusinessRounds);
                  }

                  // Filtro por estado
                  let matchesState = true;
                  if (filterStates.length > 0) {
                    const isDraft = user.is_draft === true || (user.is_draft !== null && user.is_draft !== undefined && typeof user.is_draft === 'number' && user.is_draft === 1);
                    const isUndefined = user.is_draft === null || user.is_draft === undefined || (typeof user.is_draft !== 'boolean' && typeof user.is_draft !== 'number');
                    const userState = isDraft ? 'draft' : isUndefined ? 'undefined' : 'authorized';
                    matchesState = filterStates.includes(userState);
                  }

                  return matchesSearch && matchesOrganizationType && matchesModality && matchesExpoZone && matchesParticipations && matchesState;
                });

                if (activeParticipationsTab === 'speakers') {
                  return (
                    <div className="admin-participations-grid">
                      {filteredUsersForParticipations.flatMap(user => {
                        if (!user.speakers || !Array.isArray(user.speakers) || user.speakers.length === 0) return [];
                        return user.speakers
                          .map((speaker: any, idx: number) => {
                            const statusInfo = getSpeakerStatus(speaker);
                            return { speaker, idx, statusInfo, user };
                          })
                          .filter(({ speaker, statusInfo }) => matchesParticipationFilters(speaker, statusInfo))
                          .map(({ speaker, idx, statusInfo, user }) => (
                      <div 
                        key={`${user.id}-speaker-${idx}`} 
                        className="admin-participation-card"
                        data-user-id={user.id}
                        data-participation-id={`speaker-${user.id}-${idx}`}
                        onClick={() => setSelectedSpeakerForModal({ speaker, user })}
                        style={{ cursor: 'pointer' }}
                      >
                        {/* Badges de estado en el borde superior derecho */}
                        <div className="admin-participation-card-status-badges">
                          <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
                            {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                            {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                          </span>
                          {statusInfo.hasRequestApproval && (
                            <span className="participation-status-badge status-requested">
                              <AlertTriangleIcon width={12} height={12} /> Solicitado
                            </span>
                          )}
                          {speaker.approval_status === 'approved' ? (
                            <span className="participation-status-badge status-approved">
                              <CheckCircleIcon width={12} height={12} /> Aprobado
                            </span>
                          ) : speaker.approval_status === 'rejected' ? (
                            <span className="participation-status-badge status-rejected">
                              <XCircleIcon width={12} height={12} /> Rechazado
                            </span>
                          ) : (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) ? (
                            <span className="participation-status-badge status-pending">
                              <AlertTriangleIcon width={12} height={12} /> Pendiente
                            </span>
                          ) : null}
                        </div>
                        <div className="admin-participation-card-header">
                          <div>
                            <h3>{speaker.firstName} {speaker.lastName}</h3>
                            <span className="admin-participation-org">{getUserDisplayName(user)}</span>
                          </div>
                        </div>
                        <div className="admin-participation-card-body">
                          {speaker.position && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Cargo:</span>
                              <span className="info-value">{speaker.position}</span>
                            </div>
                          )}
                          {speaker.organization && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Organización:</span>
                              <span className="info-value">{speaker.organization}</span>
                            </div>
                          )}
                          {speaker.nationality && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Nacionalidad:</span>
                              <span className="info-value">{speaker.nationality}</span>
                            </div>
                          )}
                          {speaker.description && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Minibiografía:</span>
                              <span className="info-value">{speaker.description}</span>
                            </div>
                          )}
                          {speaker.biography && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Biografía:</span>
                              <span className="info-value">{speaker.biography}</span>
                            </div>
                          )}
                          {speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0 && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Ejes temáticos:</span>
                              <div className="admin-participation-tags">
                                {speaker.thematicAxes.map((axis: any, axisIdx: number) => (
                                  <span key={axisIdx} className="tag-badge-small">{getThematicAxisLabel(axis)}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="admin-participation-card-footer">
                          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                            <button
                              className="admin-action-button-small chat-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Guardar el ID de la tarjeta específica
                                selectedParticipationIdRef.current = `speaker-${user.id}-${idx}`;
                                handleOpenCrmDetail(user);
                              }}
                              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                            >
                              Ver Usuario
                            </button>
                            {/* Botones de aprobación/rechazo - solo si está completo o tiene solicitud de aprobación */}
                            {(statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (
                              <>
                                {speaker.approval_status !== 'approved' && (
                                  <button
                                    className="admin-action-button-small approve-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateParticipationApprovalStatus(user.id, 'speakers', speaker.id || idx, 'approved');
                                    }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Aprobar
                                  </button>
                                )}
                                {speaker.approval_status !== 'rejected' && (
                                  <button
                                    className="admin-action-button-small reject-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRejectionModal({
                                        source: 'profile',
                                        isOpen: true,
                                        userId: user.id,
                                        participationType: 'speakers',
                                        participationId: speaker.id || idx
                                      });
                                      setRejectionComment('');
                                    }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Rechazar
                                  </button>
                                )}
                                {(speaker.approval_status === 'approved' || speaker.approval_status === 'rejected') && (
                                  <button
                                    className="admin-action-button-small pending-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateParticipationApprovalStatus(user.id, 'speakers', speaker.id || idx, 'pending');
                                    }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Pendiente
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                      })}
                      {(() => {
                        const hasSpeakers = filteredUsersForParticipations.some(user => {
                          if (!user.speakers || !Array.isArray(user.speakers) || user.speakers.length === 0) return false;
                          return user.speakers.some((speaker: any) => {
                            const statusInfo = getSpeakerStatus(speaker);
                            return matchesParticipationFilters(speaker, statusInfo);
                          });
                        });
                        if (!hasSpeakers) {
                          return (
                            <div className="admin-empty-state">
                              <p>No hay oradores {filterParticipationStatus !== 'all' ? `con estado "${filterParticipationStatus === 'complete' ? 'Completo' : filterParticipationStatus === 'pending_approval' ? 'Pendiente Aprobación' : 'Incompleto'}"` : 'cargados'}</p>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  );
                }

                if (activeParticipationsTab === 'events') {
                  return (
                    <div className="admin-participations-grid">
                      {filteredUsersForParticipations.flatMap(user => {
                        if (!user.parallel_events || !Array.isArray(user.parallel_events) || user.parallel_events.length === 0) return [];
                        return user.parallel_events
                          .map((event: any, idx: number) => {
                            const statusInfo = getEventStatus(event);
                            return { event, idx, statusInfo, user };
                          })
                          .filter(({ event, statusInfo }) => matchesParticipationFilters(event, statusInfo))
                          .map(({ event, idx, statusInfo, user }) => (
                      <div 
                        key={`${user.id}-event-${idx}`} 
                        className="admin-participation-card"
                        data-user-id={user.id}
                        data-participation-id={`event-${user.id}-${idx}`}
                        onClick={() => setSelectedEventForModal({ event, user })}
                        style={{ cursor: 'pointer' }}
                      >
                        {/* Badges de estado en el borde superior derecho */}
                        <div className="admin-participation-card-status-badges">
                          <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
                            {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                            {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                          </span>
                          {statusInfo.hasRequestApproval && (
                            <span className="participation-status-badge status-requested">
                              <AlertTriangleIcon width={12} height={12} /> Solicitado
                            </span>
                          )}
                          {event.approval_status === 'approved' ? (
                            <span className="participation-status-badge status-approved">
                              <CheckCircleIcon width={12} height={12} /> Aprobado
                            </span>
                          ) : event.approval_status === 'rejected' ? (
                            <span className="participation-status-badge status-rejected">
                              <XCircleIcon width={12} height={12} /> Rechazado
                            </span>
                          ) : (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) ? (
                            <span className="participation-status-badge status-pending">
                              <AlertTriangleIcon width={12} height={12} /> Pendiente
                            </span>
                          ) : null}
                        </div>
                        <div className="admin-participation-card-header">
                          <div>
                            <h3>{event.name || 'Evento sin nombre'}</h3>
                            <span className="admin-participation-org">{getUserDisplayName(user)}</span>
                          </div>
                        </div>
                        <div className="admin-participation-card-body">
                          {event.description && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Descripción:</span>
                              <span className="info-value">{event.description}</span>
                            </div>
                          )}
                          {event.duration && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Duración:</span>
                              <span className="info-value">{event.duration}</span>
                            </div>
                          )}
                          {event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0 && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Ejes temáticos:</span>
                              <div className="admin-participation-tags">
                                {event.thematicAxes.map((axis: any, axisIdx: number) => (
                                  <span key={axisIdx} className="tag-badge-small">{getThematicAxisLabel(axis)}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="admin-participation-card-footer">
                          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                            <button
                              className="admin-action-button-small chat-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Guardar el ID de la tarjeta específica
                                selectedParticipationIdRef.current = `event-${user.id}-${idx}`;
                                handleOpenCrmDetail(user);
                              }}
                              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                            >
                              Ver Usuario
                            </button>
                            {/* Botones de aprobación/rechazo - solo si está completo o tiene solicitud de aprobación */}
                            {(statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (
                              <>
                                {event.approval_status !== 'approved' && (
                                  <button
                                    className="admin-action-button-small approve-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateParticipationApprovalStatus(user.id, 'parallelEvents', event.id || idx, 'approved');
                                    }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Aprobar
                                  </button>
                                )}
                                {event.approval_status !== 'rejected' && (
                                  <button
                                    className="admin-action-button-small reject-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRejectionModal({
                                        source: 'profile',
                                        isOpen: true,
                                        userId: user.id,
                                        participationType: 'parallelEvents',
                                        participationId: event.id || idx
                                      });
                                      setRejectionComment('');
                                    }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Rechazar
                                  </button>
                                )}
                                {(event.approval_status === 'approved' || event.approval_status === 'rejected') && (
                                  <button
                                    className="admin-action-button-small pending-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateParticipationApprovalStatus(user.id, 'parallelEvents', event.id || idx, 'pending');
                                    }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Pendiente
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        </div>
                      ));
                      })}
                      {(() => {
                        const hasEvents = filteredUsersForParticipations.some(user => {
                          if (!user.parallel_events || !Array.isArray(user.parallel_events) || user.parallel_events.length === 0) return false;
                          return user.parallel_events.some((event: any) => {
                            const statusInfo = getEventStatus(event);
                            return matchesParticipationFilters(event, statusInfo);
                          });
                        });
                        if (!hasEvents) {
                          return (
                            <div className="admin-empty-state">
                              <p>No hay eventos paralelos {filterParticipationStatus !== 'all' ? `con estado "${filterParticipationStatus === 'complete' ? 'Completo' : filterParticipationStatus === 'pending_approval' ? 'Pendiente Aprobación' : 'Incompleto'}"` : 'cargados'}</p>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  );
                }

                if (activeParticipationsTab === 'initiatives') {
                  return (
                    <div className="admin-participations-grid">
                      {filteredUsersForParticipations.flatMap(user => {
                        if (!user.initiatives || !Array.isArray(user.initiatives) || user.initiatives.length === 0) return [];
                        return user.initiatives
                          .map((initiative: any, idx: number) => {
                            const statusInfo = getInitiativeStatus(initiative);
                            return { initiative, idx, statusInfo, user };
                          })
                          .filter(({ initiative, statusInfo }) => matchesParticipationFilters(initiative, statusInfo))
                          .map(({ initiative, idx, statusInfo, user }) => (
                      <div 
                        key={`${user.id}-initiative-${idx}`} 
                        className="admin-participation-card"
                        data-user-id={user.id}
                        data-participation-id={`initiative-${user.id}-${idx}`}
                        onClick={() => setSelectedInitiativeForModal({ initiative, user })}
                        style={{ cursor: 'pointer' }}
                      >
                        {/* Badges de estado en el borde superior derecho */}
                        <div className="admin-participation-card-status-badges">
                          <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
                            {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                            {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                          </span>
                          {statusInfo.hasRequestApproval && (
                            <span className="participation-status-badge status-requested">
                              <AlertTriangleIcon width={12} height={12} /> Solicitado
                            </span>
                          )}
                          {initiative.approval_status === 'approved' ? (
                            <span className="participation-status-badge status-approved">
                              <CheckCircleIcon width={12} height={12} /> Aprobado
                            </span>
                          ) : initiative.approval_status === 'rejected' ? (
                            <span className="participation-status-badge status-rejected">
                              <XCircleIcon width={12} height={12} /> Rechazado
                            </span>
                          ) : (statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) ? (
                            <span className="participation-status-badge status-pending">
                              <AlertTriangleIcon width={12} height={12} /> Pendiente
                            </span>
                          ) : null}
                        </div>
                        <div className="admin-participation-card-header">
                          <div>
                            <h3>{initiative.title || initiative.name || 'Iniciativa sin título'}</h3>
                            <span className="admin-participation-org">{getUserDisplayName(user)}</span>
                          </div>
                        </div>
                        <div className="admin-participation-card-body">
                          {initiative.type && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Tipo:</span>
                              <span className="info-value">{getInitiativeTypeLabel(initiative.type)}</span>
                            </div>
                          )}
                          {initiative.description && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Descripción:</span>
                              <span className="info-value">{initiative.description}</span>
                            </div>
                          )}
                          {initiative.thematicAxes && Array.isArray(initiative.thematicAxes) && initiative.thematicAxes.length > 0 && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Ejes temáticos:</span>
                              <div className="admin-participation-tags">
                                {initiative.thematicAxes.map((axis: any, axisIdx: number) => (
                                  <span key={axisIdx} className="tag-badge-small">{getThematicAxisLabel(axis)}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="admin-participation-card-footer">
                          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                            <button
                              className="admin-action-button-small chat-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Guardar el ID de la tarjeta específica
                                selectedParticipationIdRef.current = `initiative-${user.id}-${idx}`;
                                handleOpenCrmDetail(user);
                              }}
                              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                            >
                              Ver Usuario
                            </button>
                            {/* Botones de aprobación/rechazo - solo si está completo o tiene solicitud de aprobación */}
                            {(statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval) && (
                              <>
                                {initiative.approval_status !== 'approved' && (
                                  <button
                                    className="admin-action-button-small approve-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateParticipationApprovalStatus(user.id, 'initiatives', initiative.id || idx, 'approved');
                                    }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Aprobar
                                  </button>
                                )}
                                {initiative.approval_status !== 'rejected' && (
                                  <button
                                    className="admin-action-button-small reject-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRejectionModal({
                                        source: 'profile',
                                        isOpen: true,
                                        userId: user.id,
                                        participationType: 'initiatives',
                                        participationId: initiative.id || idx
                                      });
                                      setRejectionComment('');
                                    }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Rechazar
                                  </button>
                                )}
                                {(initiative.approval_status === 'approved' || initiative.approval_status === 'rejected') && (
                                  <button
                                    className="admin-action-button-small pending-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateParticipationApprovalStatus(user.id, 'initiatives', initiative.id || idx, 'pending');
                                    }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Pendiente
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                          </div>
                        </div>
                      ));
                      })}
                      {(() => {
                        const hasInitiatives = filteredUsersForParticipations.some(user => {
                          if (!user.initiatives || !Array.isArray(user.initiatives) || user.initiatives.length === 0) return false;
                          return user.initiatives.some((initiative: any) => {
                            const statusInfo = getInitiativeStatus(initiative);
                            return matchesParticipationFilters(initiative, statusInfo);
                          });
                        });
                        if (!hasInitiatives) {
                          return (
                            <div className="admin-empty-state">
                              <p>No hay iniciativas {filterParticipationStatus !== 'all' ? `con estado "${filterParticipationStatus === 'complete' ? 'Completo' : filterParticipationStatus === 'pending_approval' ? 'Pendiente Aprobación' : 'Incompleto'}"` : 'cargadas'}</p>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  );
                }

                if (activeParticipationsTab === 'business_rounds') {
                  return (
                    <div className="admin-participations-grid">
                      {filteredUsersForParticipations.flatMap(user => {
                        if (!user.business_rounds_participants || !Array.isArray(user.business_rounds_participants) || user.business_rounds_participants.length === 0) return [];
                        return user.business_rounds_participants
                          .map((participant: any, idx: number) => {
                            const status = getBusinessRoundsStatus(participant);
                            return { participant, idx, status, user };
                          })
                          .filter(({ participant, status }) => matchesParticipationFilters(participant, status))
                          .map(({ participant, idx, status, user }) => (
                      <div 
                        key={`${user.id}-participant-${idx}`} 
                        className="admin-participation-card"
                        data-user-id={user.id}
                        data-participation-id={`participant-${user.id}-${idx}`}
                        onClick={() => setSelectedParticipantForModal({ participant, user })}
                        style={{ cursor: 'pointer' }}
                      >
                        {/* Badge de estado en el borde superior derecho */}
                        <div className="admin-participation-card-status-badges">
                          <span className={`participation-status-badge status-${status}`}>
                            {status === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                            {status === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
                          </span>
                          {participant.approval_status === 'approved' ? (
                            <span className="participation-status-badge status-approved">
                              <CheckCircleIcon width={12} height={12} /> Aprobado
                            </span>
                          ) : participant.approval_status === 'rejected' ? (
                            <span className="participation-status-badge status-rejected">
                              <XCircleIcon width={12} height={12} /> Rechazado
                            </span>
                          ) : status === 'complete' ? (
                            <span className="participation-status-badge status-pending">
                              <AlertTriangleIcon width={12} height={12} /> Pendiente
                            </span>
                          ) : null}
                        </div>
                        <div className="admin-participation-card-header">
                          <div>
                            <h3>{participant.firstName} {participant.lastName}</h3>
                            <span className="admin-participation-org">{getUserDisplayName(user)}</span>
                          </div>
                        </div>
                        <div className="admin-participation-card-body">
                          {participant.position && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Cargo:</span>
                              <span className="info-value">{participant.position}</span>
                            </div>
                          )}
                          {participant.organization && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Organización:</span>
                              <span className="info-value">{participant.organization}</span>
                            </div>
                          )}
                          {participant.email && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Email:</span>
                              <span className="info-value">{participant.email}</span>
                            </div>
                          )}
                          {participant.phone && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Teléfono:</span>
                              <span className="info-value">{participant.phone}</span>
                            </div>
                          )}
                          {participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0 && (
                            <div className="admin-participation-info-item">
                              <span className="info-label">Ejes temáticos:</span>
                              <div className="admin-participation-tags">
                                {participant.thematicAxes.map((axis: any, axisIdx: number) => (
                                  <span key={axisIdx} className="tag-badge-small">{getThematicAxisLabel(axis)}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="admin-participation-card-footer">
                          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                            <button
                              className="admin-action-button-small chat-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Guardar el ID de la tarjeta específica
                                selectedParticipationIdRef.current = `participant-${user.id}-${idx}`;
                                handleOpenCrmDetail(user);
                              }}
                              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                            >
                              Ver Usuario
                            </button>
                            {/* Botones de aprobación/rechazo - solo si está completo (business rounds no tiene requestApproval) */}
                            {status === 'complete' && (
                              <>
                                {participant.approval_status !== 'approved' && (
                                  <button
                                    className="admin-action-button-small approve-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateParticipationApprovalStatus(user.id, 'businessRoundsParticipants', participant.id || idx, 'approved');
                                    }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Aprobar
                                  </button>
                                )}
                                {participant.approval_status !== 'rejected' && (
                                  <button
                                    className="admin-action-button-small reject-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRejectionModal({
                                        source: 'profile',
                                        isOpen: true,
                                        userId: user.id,
                                        participationType: 'businessRoundsParticipants',
                                        participationId: participant.id || idx
                                      });
                                      setRejectionComment('');
                                    }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Rechazar
                                  </button>
                                )}
                                {(participant.approval_status === 'approved' || participant.approval_status === 'rejected') && (
                                  <button
                                    className="admin-action-button-small pending-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateParticipationApprovalStatus(user.id, 'businessRoundsParticipants', participant.id || idx, 'pending');
                                    }}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                  >
                                    Pendiente
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                          </div>
                        </div>
                      ));
                      })}
                      {(() => {
                        const hasParticipants = filteredUsersForParticipations.some(user => {
                          if (!user.business_rounds_participants || !Array.isArray(user.business_rounds_participants) || user.business_rounds_participants.length === 0) return false;
                          return user.business_rounds_participants.some((participant: any) => {
                            const status = getBusinessRoundsStatus(participant);
                            return matchesParticipationFilters(participant, status);
                          });
                        });
                        if (!hasParticipants) {
                          return (
                            <div className="admin-empty-state">
                              <p>No hay participantes de rondas de negocios {filterParticipationStatus !== 'all' ? `con estado "${filterParticipationStatus === 'complete' ? 'Completo' : filterParticipationStatus === 'pending_approval' ? 'Pendiente Aprobación' : 'Incompleto'}"` : 'cargados'}</p>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  );
                }

                return null;
              })()}
            </div>
              </>
            )}
          </div>
        )}

        {/* Usuarios (con CRM integrado) */}
        {/* Contenido de Monitoreo */}
        {activeMainTab === 'monitoring' && (
          <div className="admin-monitoring-section" style={{ padding: '20px', background: '#fff', borderRadius: '8px', marginBottom: '20px' }}>
            <h2 style={{ marginBottom: '20px', fontSize: '24px', fontWeight: '600', color: '#1e293b' }}><BarChartIcon width={24} height={24} /> Monitoreo del Sistema</h2>
            
            {/* Métricas del Sistema */}
            {systemMetrics && (() => {
              const mainUsagePercent = systemMetrics.mainPool?.usagePercent || 0;
              const sessionUsagePercent = systemMetrics.sessionPool?.usagePercent || 0;
              const mainWaiting = systemMetrics.mainPool?.waiting || 0;
              const sessionWaiting = systemMetrics.sessionPool?.waiting || 0;
              
              // Determinar color según uso: Verde (<70%), Amarillo (70-90%), Rojo (>90%)
              const getPoolColor = (usagePercent: number, waiting: number) => {
                if (waiting > 0) return { bg: '#fef2f2', border: '#fecaca', text: '#dc2626', status: <><CircleIcon width={14} height={14} color="#ef4444" /> CRÍTICO</> };
                if (usagePercent >= 90) return { bg: '#fef2f2', border: '#fecaca', text: '#dc2626', status: <><CircleIcon width={14} height={14} color="#ef4444" /> CRÍTICO</> };
                if (usagePercent >= 70) return { bg: '#fffbeb', border: '#fde68a', text: '#d97706', status: <><AlertTriangleIcon width={14} height={14} /> ADVERTENCIA</> };
                return { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', status: <><CheckCircleIcon width={14} height={14} /> OK</> };
              };
              
              const mainColor = getPoolColor(mainUsagePercent, mainWaiting);
              const sessionColor = getPoolColor(sessionUsagePercent, sessionWaiting);
              
              return (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                    Estado de Conexiones
                    <span style={{ fontSize: '12px', fontWeight: '400', color: '#6b7280', marginLeft: '10px' }}>
                      (Actualización cada 10 segundos)
                    </span>
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                    <div style={{ 
                      background: mainColor.bg, 
                      padding: '15px', 
                      borderRadius: '8px', 
                      border: `2px solid ${mainColor.border}` 
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                        <div style={{ fontSize: '14px', color: '#6b7280' }}>Pool Principal</div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: mainColor.text }}>
                          {mainColor.status}
                        </div>
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: '600', color: mainColor.text }}>
                        {systemMetrics.mainPool?.used || 0} / {systemMetrics.mainPool?.max || 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '5px' }}>
                        {systemMetrics.mainPool?.idle || 0} idle, {systemMetrics.mainPool?.waiting || 0} esperando
                        {mainUsagePercent > 0 && (
                          <span style={{ marginLeft: '8px', color: mainColor.text, fontWeight: '600' }}>
                            ({mainUsagePercent}% usado)
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ 
                      background: sessionColor.bg, 
                      padding: '15px', 
                      borderRadius: '8px', 
                      border: `2px solid ${sessionColor.border}` 
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                        <div style={{ fontSize: '14px', color: '#6b7280' }}>Pool de Sesiones</div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: sessionColor.text }}>
                          {sessionColor.status}
                        </div>
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: '600', color: sessionColor.text }}>
                        {systemMetrics.sessionPool?.used || 0} / {systemMetrics.sessionPool?.max || 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '5px' }}>
                        {systemMetrics.sessionPool?.idle || 0} idle, {systemMetrics.sessionPool?.waiting || 0} esperando
                        {sessionUsagePercent > 0 && (
                          <span style={{ marginLeft: '8px', color: sessionColor.text, fontWeight: '600' }}>
                            ({sessionUsagePercent}% usado)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {(mainWaiting > 0 || sessionWaiting > 0 || mainUsagePercent >= 90 || sessionUsagePercent >= 90) && (
                    <div style={{ 
                      padding: '12px', 
                      background: '#fef2f2', 
                      borderRadius: '6px', 
                      border: '1px solid #fecaca',
                      color: '#dc2626',
                      marginTop: '10px'
                    }}>
                      <AlertTriangleIcon width={18} height={18} /> <strong>ATENCIÓN:</strong> El sistema está bajo carga. Hay {mainWaiting + sessionWaiting} requests esperando conexiones.
                    </div>
                  )}
                  
                  {/* Visitas en Tiempo Real */}
                  {activeVisits && (
                    <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                      <h3 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                        <UsersIcon width={18} height={18} /> Visitas en Tiempo Real
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                        <div style={{ 
                          background: '#f0f9ff', 
                          padding: '20px', 
                          borderRadius: '8px', 
                          border: '2px solid #bae6fd',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '14px', color: '#0369a1', marginBottom: '8px', fontWeight: '600' }}>
                            Sesiones Activas
                          </div>
                          <div style={{ fontSize: '32px', fontWeight: '700', color: '#0284c7' }}>
                            {activeVisits.total || 0}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '5px' }}>
                            Sesiones no expiradas
                          </div>
                        </div>
                        <div style={{ 
                          background: '#fef3c7', 
                          padding: '20px', 
                          borderRadius: '8px', 
                          border: '2px solid #fde68a',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '14px', color: '#92400e', marginBottom: '8px', fontWeight: '600' }}>
                            Usuarios Únicos
                          </div>
                          <div style={{ fontSize: '32px', fontWeight: '700', color: '#b45309' }}>
                            {activeVisits.uniqueUsers || 0}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '5px' }}>
                            Usuarios con sesión activa
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            
            {/* Errores Recientes */}
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>Errores Recientes (Última Hora)</h3>
              {(systemMetrics?.recentErrors?.byType && systemMetrics.recentErrors.byType.length > 0) || (errorsStats && errorsStats.length > 0) ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '20px' }}>
                  {(systemMetrics?.recentErrors?.byType || errorsStats || []).map((stat: any, idx: number) => (
                    <div key={idx} style={{ background: '#fef2f2', padding: '12px', borderRadius: '6px', border: '1px solid #fecaca' }}>
                      <div style={{ fontSize: '12px', color: '#991b1b', marginBottom: '5px' }}>{stat.error_type || 'Desconocido'}</div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>{stat.count || 0}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '20px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0', color: '#166534' }}>
                  <CheckIcon width={16} height={16} /> No hay errores en la última hora
                </div>
              )}
            </div>

            {/* Tabla de Errores */}
            <div>
              <h3 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Todos los Errores ({errorsTotal || 0})
              </h3>
              {errors && errors.length > 0 ? (
                <div style={{ overflowX: 'auto', background: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Fecha</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Tipo</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Mensaje</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Situación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {errors.map((err: any, idx: number) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px', fontSize: '12px', color: '#1e293b' }}>
                            {new Date(err.created_at).toLocaleString('es-AR', { 
                              timeZone: 'America/Argentina/Buenos_Aires',
                              dateStyle: 'short', 
                              timeStyle: 'short' 
                            })}
                          </td>
                          <td style={{ padding: '12px', fontSize: '12px', color: '#1e293b', fontWeight: '600' }}>
                            {err.error_type || 'Desconocido'}
                          </td>
                          <td style={{ padding: '12px', fontSize: '12px', color: '#1e293b', maxWidth: '400px', wordBreak: 'break-word' }}>
                            {err.error_message || 'Sin mensaje'}
                          </td>
                          <td style={{ padding: '12px', fontSize: '12px', color: '#1e293b', maxWidth: '300px', wordBreak: 'break-word' }}>
                            {err.situation || 'Sin información'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: '20px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0', color: '#166534' }}>
                  <CheckIcon width={16} height={16} /> No hay errores registrados
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contenido de Monitoreo */}
        {activeMainTab === 'monitoring' && (
          <div className="admin-monitoring-section" style={{ padding: '20px', background: '#fff', borderRadius: '8px', marginBottom: '20px' }}>
            <h2 style={{ marginBottom: '20px', fontSize: '24px', fontWeight: '600', color: '#1e293b' }}><BarChartIcon width={24} height={24} /> Monitoreo del Sistema</h2>
            
            {/* Métricas del Sistema */}
            {systemMetrics && (() => {
              const mainUsagePercent = systemMetrics.mainPool?.usagePercent || 0;
              const sessionUsagePercent = systemMetrics.sessionPool?.usagePercent || 0;
              const mainWaiting = systemMetrics.mainPool?.waiting || 0;
              const sessionWaiting = systemMetrics.sessionPool?.waiting || 0;
              
              // Determinar color según uso: Verde (<70%), Amarillo (70-90%), Rojo (>90%)
              const getPoolColor = (usagePercent: number, waiting: number) => {
                if (waiting > 0) return { bg: '#fef2f2', border: '#fecaca', text: '#dc2626', status: <><CircleIcon width={14} height={14} color="#ef4444" /> CRÍTICO</> };
                if (usagePercent >= 90) return { bg: '#fef2f2', border: '#fecaca', text: '#dc2626', status: <><CircleIcon width={14} height={14} color="#ef4444" /> CRÍTICO</> };
                if (usagePercent >= 70) return { bg: '#fffbeb', border: '#fde68a', text: '#d97706', status: <><AlertTriangleIcon width={14} height={14} /> ADVERTENCIA</> };
                return { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', status: <><CheckCircleIcon width={14} height={14} /> OK</> };
              };
              
              const mainColor = getPoolColor(mainUsagePercent, mainWaiting);
              const sessionColor = getPoolColor(sessionUsagePercent, sessionWaiting);
              
              return (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                    Estado de Conexiones
                    <span style={{ fontSize: '12px', fontWeight: '400', color: '#6b7280', marginLeft: '10px' }}>
                      (Actualización cada 10 segundos)
                    </span>
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                    <div style={{ 
                      background: mainColor.bg, 
                      padding: '15px', 
                      borderRadius: '8px', 
                      border: `2px solid ${mainColor.border}` 
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                        <div style={{ fontSize: '14px', color: '#6b7280' }}>Pool Principal</div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: mainColor.text }}>
                          {mainColor.status}
                        </div>
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: '600', color: mainColor.text }}>
                        {systemMetrics.mainPool?.used || 0} / {systemMetrics.mainPool?.max || 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '5px' }}>
                        {systemMetrics.mainPool?.idle || 0} idle, {systemMetrics.mainPool?.waiting || 0} esperando
                        {mainUsagePercent > 0 && (
                          <span style={{ marginLeft: '8px', color: mainColor.text, fontWeight: '600' }}>
                            ({mainUsagePercent}% usado)
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ 
                      background: sessionColor.bg, 
                      padding: '15px', 
                      borderRadius: '8px', 
                      border: `2px solid ${sessionColor.border}` 
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                        <div style={{ fontSize: '14px', color: '#6b7280' }}>Pool de Sesiones</div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: sessionColor.text }}>
                          {sessionColor.status}
                        </div>
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: '600', color: sessionColor.text }}>
                        {systemMetrics.sessionPool?.used || 0} / {systemMetrics.sessionPool?.max || 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '5px' }}>
                        {systemMetrics.sessionPool?.idle || 0} idle, {systemMetrics.sessionPool?.waiting || 0} esperando
                        {sessionUsagePercent > 0 && (
                          <span style={{ marginLeft: '8px', color: sessionColor.text, fontWeight: '600' }}>
                            ({sessionUsagePercent}% usado)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {(mainWaiting > 0 || sessionWaiting > 0 || mainUsagePercent >= 90 || sessionUsagePercent >= 90) && (
                    <div style={{ 
                      padding: '12px', 
                      background: '#fef2f2', 
                      borderRadius: '6px', 
                      border: '1px solid #fecaca',
                      color: '#dc2626',
                      marginTop: '10px'
                    }}>
                      <AlertTriangleIcon width={18} height={18} /> <strong>ATENCIÓN:</strong> El sistema está bajo carga. Hay {mainWaiting + sessionWaiting} requests esperando conexiones.
            </div>
          )}

                  {/* Visitas en Tiempo Real */}
                  {activeVisits && (
                    <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                      <h3 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                        <UsersIcon width={18} height={18} /> Visitas en Tiempo Real
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                        <div style={{ 
                          background: '#f0f9ff', 
                          padding: '20px', 
                          borderRadius: '8px', 
                          border: '2px solid #bae6fd',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '14px', color: '#0369a1', marginBottom: '8px', fontWeight: '600' }}>
                            Sesiones Activas
            </div>
                          <div style={{ fontSize: '32px', fontWeight: '700', color: '#0284c7' }}>
                            {activeVisits.total || 0}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '5px' }}>
                            Sesiones no expiradas
                          </div>
                        </div>
                        <div style={{ 
                          background: '#fef3c7', 
                          padding: '20px', 
                          borderRadius: '8px', 
                          border: '2px solid #fde68a',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '14px', color: '#92400e', marginBottom: '8px', fontWeight: '600' }}>
                            Usuarios Únicos
                          </div>
                          <div style={{ fontSize: '32px', fontWeight: '700', color: '#b45309' }}>
                            {activeVisits.uniqueUsers || 0}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '5px' }}>
                            Usuarios con sesión activa
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            
            {/* Errores Recientes */}
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>Errores Recientes (Última Hora)</h3>
              {(systemMetrics?.recentErrors?.byType && systemMetrics.recentErrors.byType.length > 0) || (errorsStats && errorsStats.length > 0) ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '20px' }}>
                  {(systemMetrics?.recentErrors?.byType || errorsStats || []).map((stat: any, idx: number) => (
                    <div key={idx} style={{ background: '#fef2f2', padding: '12px', borderRadius: '6px', border: '1px solid #fecaca' }}>
                      <div style={{ fontSize: '12px', color: '#991b1b', marginBottom: '5px' }}>{stat.error_type || 'Desconocido'}</div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>{stat.count || 0}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '20px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0', color: '#166534' }}>
                  <CheckIcon width={16} height={16} /> No hay errores en la última hora
                </div>
              )}
                        </div>

            {/* Tabla de Errores */}
            <div>
              <h3 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Todos los Errores ({errorsTotal || 0})
              </h3>
              {errors && errors.length > 0 ? (
                <div style={{ overflowX: 'auto', background: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Fecha</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Tipo</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Mensaje</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Situación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {errors.map((err: any, idx: number) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px', fontSize: '12px', color: '#1e293b' }}>
                            {new Date(err.created_at).toLocaleString('es-AR', { 
                              timeZone: 'America/Argentina/Buenos_Aires',
                              dateStyle: 'short', 
                              timeStyle: 'short' 
                            })}
                      </td>
                          <td style={{ padding: '12px', fontSize: '12px', color: '#1e293b', fontWeight: '600' }}>
                            {err.error_type || 'Desconocido'}
                              </td>
                          <td style={{ padding: '12px', fontSize: '12px', color: '#1e293b', maxWidth: '400px', wordBreak: 'break-word' }}>
                            {err.error_message || 'Sin mensaje'}
                          </td>
                          <td style={{ padding: '12px', fontSize: '12px', color: '#1e293b', maxWidth: '300px', wordBreak: 'break-word' }}>
                            {err.situation || 'Sin información'}
                          </td>
                          </tr>
                      ))}
                </tbody>
              </table>
            </div>
          ) : (
                <div style={{ padding: '20px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0', color: '#166534' }}>
                  <CheckIcon width={16} height={16} /> No hay errores registrados
                  </div>
                    )}
                  </div>
                      </div>
          )}
                      </div>
      
      {/* Modal de configuración de etapas del pipeline */}
      <PipelineStagesModal
        isOpen={isPipelineStagesModalOpen}
        onClose={() => setIsPipelineStagesModalOpen(false)}
        stages={pipelineStages.map(s => ({ id: s.id, label: s.label, color: s.color, position: s.position }))}
        onSave={(stages) => savePipelineStages(stages, selectedPipelineId)}
        pipelineId={selectedPipelineId}
        pipelineName={pipelinesList.find(p => p.id === selectedPipelineId)?.name ?? ''}
        onUpdatePipelineName={(name) => selectedPipelineId != null ? updatePipelineName(selectedPipelineId, name) : Promise.resolve()}
        onOpenProperties={() => setIsPipelinePropertiesModalOpen(true)}
      />

      <PipelinePropertiesModal
        isOpen={isPipelinePropertiesModalOpen}
        onClose={() => setIsPipelinePropertiesModalOpen(false)}
        pipelineName={pipelinesList.find(p => p.id === selectedPipelineId)?.name ?? ''}
        pipelineId={selectedPipelineId}
        fieldSchema={pipelinesList.find(p => p.id === selectedPipelineId)?.field_schema ?? []}
        onSave={(fieldSchema) => selectedPipelineId != null ? savePipelineFieldSchema(selectedPipelineId, fieldSchema) : Promise.resolve()}
      />

      {/* Modal crear pipeline */}
      {isCreatePipelineModalOpen && (
        <div className="admin-create-pipeline-modal-overlay" onClick={() => setIsCreatePipelineModalOpen(false)}>
          <div className="admin-create-pipeline-modal" onClick={e => e.stopPropagation()}>
            <h3>Nuevo pipeline</h3>
            <p>Creá un pipeline y luego configurá sus etapas (por ejemplo: Ventas, Soporte, etc.).</p>
            <input
              type="text"
              className="admin-create-pipeline-input"
              placeholder="Ej: Ventas, Leads, Post-venta"
              value={newPipelineName}
              onChange={e => setNewPipelineName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createPipeline(newPipelineName).catch(alert)}
            />
            <div className="admin-create-pipeline-modal-actions">
              <button type="button" className="admin-create-pipeline-cancel" onClick={() => { setIsCreatePipelineModalOpen(false); setNewPipelineName(''); }}>Cancelar</button>
              <button type="button" className="admin-create-pipeline-submit" onClick={() => createPipeline(newPipelineName).catch(alert)} disabled={!newPipelineName.trim()}>
                <PlusIcon width={16} height={16} />
                Crear pipeline
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar derecho Crear contacto / Crear organización / Crear negocio (solo en CRM) */}
      {activeMainTab === 'crm' && (
        <div
          className={`admin-create-contact-sidebar ${showCreateContactModal || showCreateOrganizationModal || showCreateNegocioModal ? 'open' : 'closed'}`}
          aria-hidden={!showCreateContactModal && !showCreateOrganizationModal && !showCreateNegocioModal}
        >
          {showCreateNegocioModal && (
            <CreateNegocioModal
              asSidebar
              isOpen={showCreateNegocioModal}
              onClose={() => setShowCreateNegocioModal(false)}
              organizationsList={crmOrganizations.map((o) => ({ id: o.id, organizacion: o.organizacion, dominio: o.dominio }))}
              pipelinesList={pipelinesList.map((p) => ({ id: p.id, name: p.name, field_schema: p.field_schema }))}
              organizationNegociosList={organizationNegociosList}
              stagesByPipelineId={stagesByPipelineId}
              contactsList={crmContacts.map((c) => ({ id: c.id, nombre: c.nombre, apellido: c.apellido, correo: c.correo }))}
              participationModalityOptions={participationModalities}
              onRequestCreateOrganization={() => {
                setShowCreateNegocioModal(false);
                setShowCreateOrganizationModal(true);
              }}
              isDarkMode={isDarkMode}
            />
          )}
          {showCreateOrganizationModal && (
            <CreateOrganizationModal
              asSidebar
              isOpen={showCreateOrganizationModal}
              onClose={() => setShowCreateOrganizationModal(false)}
              contactsList={crmContacts.map((c) => ({ id: c.id, nombre: c.nombre, apellido: c.apellido, correo: c.correo, organization_name: c.organization_name }))}
              pipelinesList={pipelinesList.map((p) => ({ id: p.id, name: p.name, field_schema: p.field_schema }))}
              stagesByPipelineId={stagesByPipelineId}
              isDarkMode={isDarkMode}
              participationModalityOptions={participationModalities}
            />
          )}
          {showCreateContactModal && (
            <CreateContactModal
              asSidebar
              isOpen={showCreateContactModal}
              onClose={() => setShowCreateContactModal(false)}
              onSuccess={() => {
                loadUsers();
                setShowCreateContactModal(false);
              }}
              onCrmContactCreated={() => setCrmContactsRefreshTrigger((t) => t + 1)}
              getApiUrl={getApiUrl}
              isDarkMode={isDarkMode}
              organizationsList={crmOrganizations.map((o) => ({ id: o.id, organizacion: o.organizacion, dominio: o.dominio }))}
              pipelinesList={pipelinesList.map((p) => ({ id: p.id, name: p.name, field_schema: p.field_schema }))}
              organizationNegociosList={organizationNegociosList}
              stagesByPipelineId={stagesByPipelineId}
              participationModalityOptions={participationModalities}
            />
          )}
        </div>
      )}

      {/* Modal para ver detalles del orador */}
      <Modal
        isOpen={selectedSpeakerForModal !== null}
        onClose={() => setSelectedSpeakerForModal(null)}
        customFooter={
          <button 
            className="modal-button" 
            onClick={() => setSelectedSpeakerForModal(null)}
          >
            Cerrar
          </button>
        }
      >
        {selectedSpeakerForModal && (() => {
          const user = selectedSpeakerForModal.user;
          const mainSpeaker = selectedSpeakerForModal.speaker;
          const allSpeakers = user.speakers && Array.isArray(user.speakers) ? user.speakers : [];
          const otherSpeakers = allSpeakers.filter((s: any) => s.id !== mainSpeaker.id);
          
          return (
            <div>
              {/* Orador principal - expandido */}
              <SpeakersSection
                speakers={[mainSpeaker]}
                onChange={() => {}}
                readOnly={true}
                hideTitle={true}
                initialExpandedSpeakerId={mainSpeaker.id}
                showApprovalBadge={true}
                onApprovalStatusChange={async (speakerId, status) => {
                  if (status === 'rejected') {
                    // Si es rechazo, abrir el modal de comentario
                    setRejectionModal({
                      source: 'profile',
                      isOpen: true,
                      userId: user.id,
                      participationType: 'speakers',
                      participationId: speakerId
                    });
                  } else {
                    // Para otros estados, actualizar directamente
                    await updateParticipationApprovalStatus(user.id, 'speakers', speakerId, status);
                  }
                }}
                userId={user.id}
              />
              
              {/* Otros oradores de la organización */}
              {otherSpeakers.length > 0 && (
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{ 
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1e293b',
                    marginBottom: '16px'
                  }}>
                    Otros oradores de la organización
                  </h3>
                  <SpeakersSection
                    speakers={otherSpeakers}
                    onChange={() => {}}
                    readOnly={true}
                    hideTitle={true}
                    showApprovalBadge={true}
                    onApprovalStatusChange={async (speakerId, status) => {
                      if (status === 'rejected') {
                        // Si es rechazo, abrir el modal de comentario
                        setRejectionModal({
                          source: 'profile',
                          isOpen: true,
                          userId: user.id,
                          participationType: 'speakers',
                          participationId: speakerId
                        });
                      } else {
                        // Para otros estados, actualizar directamente
                        await updateParticipationApprovalStatus(user.id, 'speakers', speakerId, status);
                      }
                    }}
                    userId={user.id}
                  />
                        </div>
              )}
                    </div>
          );
        })()}
      </Modal>

      {/* Modal para ver detalles del evento */}
      <Modal
        isOpen={selectedEventForModal !== null}
        onClose={() => setSelectedEventForModal(null)}
        customFooter={
                      <button
            className="modal-button" 
            onClick={() => setSelectedEventForModal(null)}
          >
            Cerrar
                      </button>
        }
      >
        {selectedEventForModal && (() => {
          const user = selectedEventForModal.user;
          const mainEvent = selectedEventForModal.event;
          const allEvents = user.parallel_events && Array.isArray(user.parallel_events) ? user.parallel_events : [];
          const otherEvents = allEvents.filter((e: any) => String(e.id) !== String(mainEvent.id));
          
          // Buscar el evento actualizado en el array para asegurar que tenemos la versión más reciente
          const currentMainEvent = allEvents.find((e: any) => String(e.id) === String(mainEvent.id)) || mainEvent;
          
          return (
            <div>
              {/* Evento principal - expandido */}
              <ParallelEventsSection
                key={`event-${currentMainEvent.id}-${currentMainEvent.approval_status || 'pending'}`}
                parallelEvents={[currentMainEvent]}
                speakers={user.speakers || []}
                onChange={() => {}}
                readOnly={true}
                hideTitle={true}
                initialExpandedEventId={currentMainEvent.id}
                showApprovalBadge={true}
                onApprovalStatusChange={async (eventId, status) => {
                  if (status === 'rejected') {
                    // Si es rechazo, abrir el modal de comentario
                    setRejectionModal({
                      source: 'profile',
                      isOpen: true,
                      userId: user.id,
                      participationType: 'parallelEvents',
                      participationId: eventId
                    });
                  } else {
                    // Para otros estados, actualizar directamente
                    await updateParticipationApprovalStatus(user.id, 'parallelEvents', eventId, status);
                  }
                }}
                userId={user.id}
              />
              
              {/* Otros eventos de la organización */}
              {otherEvents.length > 0 && (
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{ 
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1e293b',
                    marginBottom: '16px'
                  }}>
                    Otros eventos de la organización
                  </h3>
                  <ParallelEventsSection
                    parallelEvents={otherEvents}
                    speakers={user.speakers || []}
                    onChange={() => {}}
                    readOnly={true}
                    hideTitle={true}
                    showApprovalBadge={true}
                    onApprovalStatusChange={async (eventId, status) => {
                      if (status === 'rejected') {
                        setRejectionModal({
                          source: 'profile',
                          isOpen: true,
                          userId: user.id,
                          participationType: 'parallelEvents',
                          participationId: eventId
                        });
                            } else {
                        await updateParticipationApprovalStatus(user.id, 'parallelEvents', eventId, status);
                      }
                    }}
                    userId={user.id}
                  />
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* Modal para ver detalles de la iniciativa */}
      <Modal
        isOpen={selectedInitiativeForModal !== null}
        onClose={() => setSelectedInitiativeForModal(null)}
        customFooter={
          <button 
            className="modal-button" 
            onClick={() => setSelectedInitiativeForModal(null)}
          >
            Cerrar
                      </button>
        }
      >
        {selectedInitiativeForModal && (() => {
          const user = selectedInitiativeForModal.user;
          const mainInitiative = selectedInitiativeForModal.initiative;
          const allInitiatives = user.initiatives && Array.isArray(user.initiatives) ? user.initiatives : [];
          const otherInitiatives = allInitiatives.filter((i: any) => i.id !== mainInitiative.id);
          
          return (
            <div>
              {/* Iniciativa principal - expandida */}
              <InitiativesSection
                initiatives={[mainInitiative]}
                catalogSections={[]}
                onInitiativesChange={() => {}}
                readOnly={true}
                hideTitle={true}
                initialExpandedInitiativeId={mainInitiative.id}
                showApprovalBadge={true}
                onApprovalStatusChange={async (initiativeId, status) => {
                  if (status === 'rejected') {
                    setRejectionModal({
                      source: 'profile',
                      isOpen: true,
                      userId: user.id,
                      participationType: 'initiatives',
                      participationId: initiativeId
                    });
                  } else {
                    await updateParticipationApprovalStatus(user.id, 'initiatives', initiativeId, status);
                  }
                }}
                userId={user.id}
              />
              
              {/* Otras iniciativas de la organización */}
              {otherInitiatives.length > 0 && (
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{ 
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1e293b',
                    marginBottom: '16px'
                  }}>
                    Otras iniciativas de la organización
                  </h3>
                  <InitiativesSection
                    initiatives={otherInitiatives}
                    catalogSections={[]}
                    onInitiativesChange={() => {}}
                    readOnly={true}
                    hideTitle={true}
                    showApprovalBadge={true}
                    onApprovalStatusChange={async (initiativeId, status) => {
                      if (status === 'rejected') {
                        setRejectionModal({
                          source: 'profile',
                          isOpen: true,
                          userId: user.id,
                          participationType: 'initiatives',
                          participationId: initiativeId
                        });
                      } else {
                        await updateParticipationApprovalStatus(user.id, 'initiatives', initiativeId, status);
                      }
                    }}
                    userId={user.id}
                  />
                  </div>
              )}
                </div>
          );
        })()}
      </Modal>

      {/* Modal para ver detalles del participante de rondas de negocios */}
      <Modal
        isOpen={selectedParticipantForModal !== null}
        onClose={() => setSelectedParticipantForModal(null)}
        customFooter={
          <button 
            className="modal-button" 
            onClick={() => setSelectedParticipantForModal(null)}
          >
            Cerrar
                    </button>
        }
      >
        {selectedParticipantForModal && (() => {
          const user = selectedParticipantForModal.user;
          const mainParticipant = selectedParticipantForModal.participant;
          const allParticipants = user.business_rounds_participants && Array.isArray(user.business_rounds_participants) ? user.business_rounds_participants : [];
          const otherParticipants = allParticipants.filter((p: any) => p.id !== mainParticipant.id);
          
          return (
            <div>
              {/* Participante principal - expandido */}
              <BusinessRoundsSection
                participants={[mainParticipant]}
                onChange={() => {}}
                readOnly={true}
                hideTitle={true}
                initialExpandedParticipantId={mainParticipant.id}
                showApprovalBadge={true}
                onApprovalStatusChange={async (participantId, status) => {
                  if (status === 'rejected') {
                    setRejectionModal({
                      source: 'profile',
                      isOpen: true,
                      userId: user.id,
                      participationType: 'businessRoundsParticipants',
                      participationId: participantId
                    });
                  } else {
                    await updateParticipationApprovalStatus(user.id, 'businessRoundsParticipants', participantId, status);
                  }
                }}
                userId={user.id}
              />
              
              {/* Otros participantes de la organización */}
              {otherParticipants.length > 0 && (
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{ 
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1e293b',
                    marginBottom: '16px'
                  }}>
                    Otros participantes de la organización
                  </h3>
                  <BusinessRoundsSection
                    participants={otherParticipants}
                    onChange={() => {}}
                    readOnly={true}
                    hideTitle={true}
                    showApprovalBadge={true}
                    onApprovalStatusChange={async (participantId, status) => {
                      if (status === 'rejected') {
                        setRejectionModal({
                          source: 'profile',
                          isOpen: true,
                          userId: user.id,
                          participationType: 'businessRoundsParticipants',
                          participationId: participantId
                        });
                      } else {
                        await updateParticipationApprovalStatus(user.id, 'businessRoundsParticipants', participantId, status);
                      }
                    }}
                    userId={user.id}
                  />
            </div>
          )}
        </div>
          );
        })()}
      </Modal>

      {/* Modal de detalle de participación (contacto > Negocios > Participaciones) - Idéntico a Perfil > Participaciones */}
      <Modal
        isOpen={selectedContactNegocioParticipation !== null}
        onClose={() => setSelectedContactNegocioParticipation(null)}
        customFooter={
          <button type="button" className="modal-button" onClick={() => setSelectedContactNegocioParticipation(null)}>Cerrar</button>
        }
      >
        {selectedContactNegocioParticipation && (() => {
          const sel = selectedContactNegocioParticipation;
          const { type, organizationId, pipelineId, itemIndex } = sel;
          const neg = contactDetailNegocios.find((n) => n.organizationId === organizationId && n.pipelineId === pipelineId);
          const parsePart = (val: unknown): unknown[] => {
            if (val == null) return [];
            if (Array.isArray(val)) return val;
            if (typeof val === 'string') {
              try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; }
            }
            return [];
          };
          let liveItem: any = null;
          let speakersFromNeg: any[] = [];
          if (neg && neg.data && typeof neg.data === 'object') {
            const data = neg.data as Record<string, unknown>;
            speakersFromNeg = parsePart(data.speakers) as any[];
            const arr = type === 'speakers' ? speakersFromNeg
              : type === 'events' ? parsePart(data.parallel_events)
              : type === 'initiatives' ? (parsePart(data.iniciativas).length > 0 ? parsePart(data.iniciativas) : parsePart(data.catalog_sections))
              : type === 'business_rounds' ? parsePart(data.business_rounds_participants) : [];
            liveItem = arr[itemIndex] ?? null;
          }
          const item = liveItem != null ? liveItem : sel.item;
          const handleApprovalContactNegocio = (participationType: 'speakers' | 'events' | 'initiatives' | 'business_rounds', status: 'pending' | 'approved' | 'rejected') => {
            if (status === 'rejected') {
              setRejectionModal({ source: 'contactNegocio', isOpen: true, organizationId, pipelineId, participationType, itemIndex });
              setRejectionComment('');
            } else {
              updateContactNegocioParticipationApproval(organizationId, pipelineId, participationType, itemIndex, status);
            }
          };
          if (type === 'speakers') {
            return (
              <div>
                <SpeakersSection
                  speakers={[item]}
                  onChange={() => {}}
                  readOnly={true}
                  hideTitle={true}
                  initialExpandedSpeakerId={item.id}
                  showApprovalBadge={true}
                  onApprovalStatusChange={(_speakerId, status) => handleApprovalContactNegocio('speakers', status)}
                />
              </div>
            );
          }
          if (type === 'events') {
            return (
              <div>
                <ParallelEventsSection
                  parallelEvents={[item]}
                  speakers={speakersFromNeg}
                  onChange={() => {}}
                  readOnly={true}
                  hideTitle={true}
                  initialExpandedEventId={item.id}
                  showApprovalBadge={true}
                  onApprovalStatusChange={(_eventId, status) => handleApprovalContactNegocio('events', status)}
                />
              </div>
            );
          }
          if (type === 'initiatives') {
            return (
              <div>
                <InitiativesSection
                  initiatives={[item]}
                  catalogSections={[]}
                  onInitiativesChange={() => {}}
                  readOnly={true}
                  hideTitle={true}
                  initialExpandedInitiativeId={item.id}
                  showApprovalBadge={true}
                  onApprovalStatusChange={(_initiativeId, status) => handleApprovalContactNegocio('initiatives', status)}
                />
              </div>
            );
          }
          if (type === 'business_rounds') {
            return (
              <div>
                <BusinessRoundsSection
                  participants={[item]}
                  onChange={() => {}}
                  readOnly={true}
                  hideTitle={true}
                  initialExpandedParticipantId={item.id}
                  showApprovalBadge={true}
                  onApprovalStatusChange={(_participantId, status) => handleApprovalContactNegocio('business_rounds', status)}
                />
              </div>
            );
          }
          return null;
        })()}
      </Modal>

      {/* Modal para comentario de rechazo */}
      <Modal
        isOpen={rejectionModal !== null}
        onClose={() => {
          setRejectionModal(null);
          setRejectionComment('');
        }}
        onConfirm={async () => {
          if (!rejectionModal) return;
          if (rejectionModal.source === 'profile') {
            await updateParticipationApprovalStatus(
              rejectionModal.userId,
              rejectionModal.participationType,
              rejectionModal.participationId,
              'rejected',
              rejectionComment
            );
          } else {
            await updateContactNegocioParticipationApproval(
              rejectionModal.organizationId,
              rejectionModal.pipelineId,
              rejectionModal.participationType,
              rejectionModal.itemIndex,
              'rejected',
              rejectionComment
            );
            setSelectedContactNegocioParticipation(null);
          }
          setRejectionModal(null);
          setRejectionComment('');
        }}
        confirmText="Confirmar rechazo"
        cancelText="Cancelar"
      >
        <div style={{ marginBottom: '16px' }}>
          <p style={{ 
            marginBottom: '12px', 
            color: '#64748b',
            fontSize: '14px',
            fontFamily: 'Montserrat, sans-serif'
          }}>
            Puedes dejar un comentario opcional explicando el motivo del rechazo. Si lo dejas vacío, no se guardará ningún comentario.
          </p>
          <textarea
            value={rejectionComment}
            onChange={(e) => setRejectionComment(e.target.value)}
            placeholder="Comentario (opcional)..."
            className="rejection-comment-textarea"
            style={{
              width: '100%',
              minHeight: '120px',
              padding: '12px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Montserrat, sans-serif',
              resize: 'vertical',
              boxSizing: 'border-box',
              backgroundColor: '#ffffff',
              color: '#1e293b'
            }}
          />
      </div>
      </Modal>
    </div>
  );
};

export default AdminPanel;
